import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import {
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  Checkbox,
  Space,
  Card,
  message,
  InputNumber,
  Result,
  Modal,
  Switch,
  Divider,
  Radio,
  Tag,
  Spin,
  Drawer,
  Upload
} from 'antd'
import {
  PlusOutlined,
  DeleteOutlined,
  ReloadOutlined,
  CheckOutlined,
  SwapOutlined,
  UserAddOutlined,
  DownOutlined,
  EditOutlined,
  FileSearchOutlined,
  CameraOutlined
} from '@ant-design/icons'
import styled, { createGlobalStyle } from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { usePermission } from '../hooks/usePermission'
import { uploadServicePhotos } from '../api/servicePhoto'
import InstallmentPlanModal from '../components/InstallmentPlanModal'
import CustomerInstallmentsPanel from '../components/installment/CustomerInstallmentsPanel'
import api from '../utils/api'
import { postTopupWithDuplicateConfirm } from '../utils/duplicateTopup'
import TopupSchemeKeyInput from '../components/TopupSchemeKeyInput'
import PurchaseConsentModal from '../components/PurchaseConsentModal'
import dayjs from 'dayjs'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { Picker as MobilePicker } from 'antd-mobile'
import { getInventoryProducts, resolveServiceConsumables } from '../api/inventory'
import ResponsiveSelect from '../components/common/ResponsiveSelect'
import Hint from '../components/common/Hint'
import CustomerNameLink from '../components/common/CustomerNameLink'
import {
  resolvePaymentFeeSettings,
  calcCustomerSurcharge
} from '../utils/paymentSurcharge'
import { formatTz, now as tzNow } from '../utils/timezone'
import {
  describeValidity,
  formatTemplateExpiry
} from '../utils/classTicketExpiry'

// Booking helpers: normalize services & display name (services優先，兼容舊欄位)
const normalizeBookingServices = (booking) => {
  if (booking?.services?.length) return booking.services
  if (booking?.productId || booking?.productName) {
    return [
      {
        serviceId: booking.productId,
        serviceName: booking.productName,
        basePrice: booking.originalPrice,
        duration: booking.duration,
        category: booking.category
      }
    ]
  }
  return []
}

const getBookingDisplayName = (booking) => {
  const services = normalizeBookingServices(booking)
  if (services.length) return services.map((s) => s.serviceName).join(' + ')
  return booking?.productName || ''
}

// 判斷服務是否應該啟用第一/第二劑（多來源 + 偵錯）
// 判斷是否啟用第一/第二劑：優先抽成規則（含 doseCommissions），再看 service/fullService 的顯式設定，最後看是否已有劑次人員
const shouldEnableDose = ({ service, fullService, rule }) => {
  if (rule?.supportDoseTypes === true || rule?.doseCommissions?.length)
    return true
  if (service?.supportDoseTypes !== undefined) return service.supportDoseTypes
  if (fullService?.supportDoseTypes !== undefined)
    return fullService.supportDoseTypes
  if (rule?.supportDoseTypes !== undefined) return rule.supportDoseTypes
  return !!(service?.firstDosePersonnelId || service?.secondDosePersonnelId)
}

const { Option } = Select
const { TextArea } = Input

const theme = {
  primary: '#d5728a',
  secondary: '#af863e',
  text: '#2c2c2c',
  muted: '#7c7c7c',
  border: '#e5dde0',
  surface: '#ffffff',
  canvas: '#f7f6f5'
}

const controlShadow = '0 0 0 2px rgba(213, 114, 138, 0.12)'

const FieldInput = styled(Input)`
  height: 44px;
  border: 1px solid ${theme.border};
  border-radius: 10px;
  font-size: 14px;
  color: ${theme.text};
  padding: 0 14px;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.primary};
  }

  &:focus {
    border-color: ${theme.primary};
    box-shadow: ${controlShadow};
  }
`

const FieldNumberInput = styled(InputNumber)`
  width: 100%;
  height: 44px;
  border: 1px solid ${theme.border};
  border-radius: 10px;
  font-size: 14px;
  color: ${theme.text};
  padding: 0 12px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;

  &:hover {
    border-color: ${theme.primary};
  }

  &.ant-input-number-focused {
    border-color: ${theme.primary};
    box-shadow: ${controlShadow};
  }

  .ant-input-number-input {
    height: 42px;
    font-size: 14px;
    color: ${theme.text};
    display: flex;
    align-items: center;
    line-height: 42px;
  }
`

/* styled(ResponsiveSelect)：桌機仍是 antd Select（下面的 .ant-select-*
   規則照舊生效），手機／平板會換成底部抽屜，那些規則不匹配也無害。 */
const FieldSelect = styled(ResponsiveSelect)`
  width: 100%;

  .ant-select-selector {
    height: 44px !important;
    padding: 0 12px !important;
    border: 1px solid ${theme.border} !important;
    border-radius: 10px !important;
    transition: all 0.2s ease;
    box-shadow: none !important;
  }

  &:hover .ant-select-selector {
    border-color: ${theme.primary} !important;
  }

  &.ant-select-focused .ant-select-selector {
    border-color: ${theme.primary} !important;
    box-shadow: ${controlShadow} !important;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    line-height: 42px !important;
    font-size: 14px !important;
    color: ${theme.text};
  }
`

const FieldDatePicker = styled(DatePicker)`
  width: 100%;
  height: 44px;
  border: 1px solid ${theme.border};
  border-radius: 10px;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.primary};
  }

  &.ant-picker-focused {
    border-color: ${theme.primary};
    box-shadow: ${controlShadow};
  }

  .ant-picker-input > input {
    font-size: 14px;
    color: ${theme.text};
  }
`

const PageContainer = styled.div`
  /* 統一壓低 padding —— App.js 外層已有 24px padding，這裡再加只會疊加雙重邊距 */
  padding: 0;
  min-height: 100vh;
  overflow-x: hidden;
  box-sizing: border-box;

  @media (max-width: 768px) {
    /* 手機版加上對稱左右 padding，讓圓角卡片不會貼齊螢幕邊緣 */
    padding: 0 12px;
    padding-bottom: 100px; /* 為固定按鈕留出空間 */
    min-height: 100vh;
    min-height: 100dvh; /* 動態 viewport，避免手機網址列縮放造成跳動 */
  }
`

const CheckoutGlobalStyle = createGlobalStyle`
  /* 針對結帳頁面移除父級 Layout 的間距（僅手機版） */

  @media (max-width: 768px) {
    .ant-layout-content {
      margin: 0 !important;
      padding: 0 !important;
      border-radius: 0 !important;
      background: transparent !important;
    }
  }

  /* ===== 捲動範圍收斂（所有尺寸都適用）=====
     overscroll-behavior: contain 讓清單捲到底之後不再把動量交給整頁。
     iPad 上「想滑選單卻整頁在跑」就是這個 scroll chaining 造成的。 */
  .ant-select-dropdown .rc-virtual-list-holder,
  .ant-select-dropdown .ant-select-item-group-list,
  .ant-dropdown-menu,
  .ant-cascader-menu,
  .ant-modal-body,
  .ant-drawer-body,
  .ant-table-body,
  .checkout-scroll-list {
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
  }

  /* 實際收費輸入框的樣式 */
  .actual-price-input {
    .ant-input-number-input {
      line-height: normal !important;
    }
  }

  /* 平板/手機：結帳頁整體優化，避免畫面跑版、點選困難 */
  /* 防止橫向滑動與版面跳動。這條要涵蓋平板（class 由 JS 在 <=1024px 時掛上），
     所以不放在 768px 的 media 裡 —— 原本放在裡面等於 iPad 完全沒吃到 */
  body.checkout-page-mobile {
    overflow-x: hidden;
    touch-action: pan-y;
  }

  @media (max-width: 768px) {
    /* 日期選擇器：點擊一律開面板，避免小日曆難點；加大觸控區 */
    .ant-picker {
      min-height: 48px !important;
      padding: 8px 12px !important;
    }
    .ant-picker-input > input {
      font-size: 16px !important; /* 避免 iOS 縮放 */
      min-height: 44px !important;
    }
    /* 日期面板在行動裝置上更大、更好點 */
    .ant-picker-dropdown {
      position: fixed !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      top: auto !important;
      padding: 0 !important;
    }
    .ant-picker-dropdown .ant-picker-panel-container {
      border-radius: 16px 16px 0 0;
      box-shadow: 0 -4px 20px rgba(0,0,0,0.15);
    }
    .ant-picker-content th,
    .ant-picker-content td {
      min-width: 40px !important;
      height: 40px !important;
      line-height: 40px !important;
    }
    .ant-picker-header button {
      min-width: 44px !important;
      min-height: 44px !important;
    }
    /* Select 下拉在行動裝置上加大觸控區 */
    .ant-select-item {
      min-height: 48px !important;
      padding: 12px 16px !important;
      font-size: 15px !important;
    }
    /* 按鈕與可點元素：與輸入框統一高度 48px */
    .ant-btn {
      min-height: 48px !important;
      padding: 8px 16px !important;
      touch-action: manipulation;
    }
    /* ===== 輸入元件統一：高度 48px、字級 16px =====
       iOS Safari 聚焦在字級 <16px 的輸入框會自動放大頁面，
       因此所有可輸入元件字級一律 ≥16px */
    .ant-input-number,
    .ant-input {
      min-height: 48px !important;
    }
    .ant-input,
    textarea.ant-input {
      font-size: 16px !important;
    }
    /* 帶前後綴的輸入框（如 allowClear）：外框統一 48px，內層不再疊加高度 */
    .ant-input-affix-wrapper {
      min-height: 48px !important;
      padding-top: 0 !important;
      padding-bottom: 0 !important;
      align-items: center !important;
    }
    .ant-input-affix-wrapper > input.ant-input {
      min-height: 0 !important;
      font-size: 16px !important;
    }
    .ant-input-number .ant-input-number-input {
      min-height: 46px !important;
      font-size: 16px !important;
    }
    /* Select 與輸入框統一高度、字級（含搜尋輸入，避免 iOS 聚焦縮放） */
    .ant-select .ant-select-selector {
      min-height: 48px !important;
      align-items: center !important;
    }
    .ant-select-single .ant-select-selector .ant-select-selection-item,
    .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
      line-height: 46px !important;
      font-size: 16px !important;
    }
    .ant-select .ant-select-selection-search-input {
      height: 46px !important;
      font-size: 16px !important;
    }
    /* 表單欄位垂直間距統一 */
    .ant-form-item {
      margin-bottom: 12px !important;
    }
    .ant-checkbox-wrapper {
      min-height: 44px;
      display: inline-flex;
      align-items: center;
    }
  }
`

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 500;
  color: ${theme.text};
  margin: 0 0 24px 0;
  letter-spacing: -0.3px;

  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 18px;
  }
`

const CheckoutContentContainer = styled.div`
  /* 純粹的內容容器，視覺由 FormCard 負責，這裡完全透明，避免「框中框」 */
  background: transparent;
  padding: 0;
  border: none;
  box-shadow: none;
  border-radius: 0;
  margin-bottom: 20px;
  overflow: visible;

  /* 電腦／平板：ButtonGroup 是 sticky 且自帶 margin-bottom: -24px，
     這裡給 24px 剛好抵銷，讓按鈕列貼齊內容底部。
     （原本寫 100px 是為了手機的固定按鈕留位，但在 sticky 版型下
     只會在按鈕列「下方」多出一大片空白。） */
  padding-bottom: 24px;

  @media (max-width: 768px) {
    /* 手機：按鈕列改為 fixed，需要實際留出等高的空間避免遮住內容 */
    padding-bottom: 160px;
  }
`

const CheckoutPageLoadingWrap = styled.div`
  padding: 28px;
  background: ${theme.canvas};
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
  @media (max-width: 768px) {
    padding: 0;
  }
`

/**
 * 「預約資訊核對」的懸浮開關
 * 貼在畫面右緣中段，點了才把核對內容用抽屜滑出來，平時不佔版面高度。
 */
const BookingReviewToggle = styled.button`
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 900;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px 12px;
  border: none;
  border-radius: 14px 0 0 14px;
  background: ${theme.primary};
  color: #ffffff;
  cursor: pointer;

  /* 陰影用主色本身調暗，貼在色塊上比灰黑陰影乾淨 */
  box-shadow: -3px 4px 14px rgba(213, 114, 138, 0.32);

  /* 直書，讓按鈕細長不擋內容 */
  writing-mode: vertical-rl;
  text-orientation: upright;
  letter-spacing: 3px;
  font-size: 13px;
  font-weight: 600;
  transition: box-shadow 0.18s ease, transform 0.18s ease,
    background 0.18s ease;

  .review-icon {
    /* 桌機不需要圖示，靠文字就夠 */
    display: none;
  }

  .review-total {
    writing-mode: horizontal-tb;
    font-size: 12px;
    font-weight: 700;
    color: #ffffff;
    letter-spacing: 0;
    opacity: 0.92;
  }

  @media (hover: hover) {
    &:hover {
      background: #c86279;
      box-shadow: -5px 6px 20px rgba(213, 114, 138, 0.42);
      transform: translateY(-50%) translateX(-2px);
    }
  }

  &:active {
    box-shadow: -2px 2px 8px rgba(213, 114, 138, 0.3);
  }

  /* 平板與手機：只留圖示的圓形按鈕，不顯示文字與金額。
     平板維持貼在右緣中段（那裡沒有任何固定元件會擋到）。 */
  @media screen and (max-width: 1024px) {
    width: 48px;
    height: 48px;
    padding: 0;
    gap: 0;
    writing-mode: horizontal-tb;
    text-orientation: mixed;
    letter-spacing: 0;
    font-size: 0; /* 隱藏按鈕內的純文字節點 */
    border-radius: 999px 0 0 999px;

    .review-icon {
      display: inline-flex;
      font-size: 20px;
      line-height: 1;
    }

    .review-total {
      display: none;
    }
  }

  /* 手機：底部有 fixed 的「儲存草稿／完成訂單」按鈕列（約 140px 高），
     改成貼右下角並抬高到按鈕列上方，避免被蓋住。 */
  @media screen and (max-width: 768px) {
    top: auto;
    bottom: calc(156px + env(safe-area-inset-bottom, 0px));
    transform: none;

    @media (hover: hover) {
      &:hover {
        transform: translateX(-2px);
      }
    }
  }
`

const FormCard = styled(Card)`
  /* 唯一的視覺單元：淡邊框 + 細微陰影 + 圓角，現代 app 風 */
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(15, 37, 98, 0.04);
  border: 1px solid #eef0f4;
  margin-bottom: 14px;
  overflow: visible;
  position: relative;
  pointer-events: auto;
  background: ${theme.surface};
  transition: box-shadow 0.18s ease;

  /* 滑鼠懸停時微微浮起，提供互動回饋（桌機限定） */
  @media (hover: hover) {
    &:hover {
      box-shadow: 0 4px 12px rgba(15, 37, 98, 0.06);
    }
  }

  .ant-card-head {
    background: transparent;
    border-bottom: 1px solid #f5f5f5;
    border-radius: 12px 12px 0 0;
    padding: 14px 18px 12px 18px;
    min-height: auto;

    .ant-card-head-title {
      font-size: 15px;
      font-weight: 600;
      color: ${theme.text};
      letter-spacing: -0.2px;
      padding: 0;
    }

    @media (max-width: 768px) {
      padding: 12px 14px 10px 14px;
      .ant-card-head-title {
        font-size: 14px;
      }
    }
  }

  .ant-card-body {
    padding: 16px 18px;
    overflow: visible;
    position: relative;
    pointer-events: auto;
    box-sizing: border-box;

    @media (max-width: 768px) {
      padding: 14px;
    }

    @media (max-width: 480px) {
      padding: 12px;
    }
  }

  /* Form Item 样式 — 縮小垂直密度 */
  .ant-form-item {
    margin-bottom: 14px;

    .ant-form-item-label {
      padding-bottom: 4px;

      label {
        font-size: 13.5px;
        font-weight: 500;
        color: ${theme.text};
        height: auto;
        line-height: 1.4;
      }
    }
  }

  /* 最後一個 form item 不要多餘下方空白 */
  .ant-form-item:last-child {
    margin-bottom: 0;
  }
`

/* ===== 堂票購買列：桌機彈性橫排、手機固定網格 ===== */
const TicketRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  /* 欄位都有小標了，改成靠下對齊，輸入框才會在同一條水平線上 */
  align-items: flex-end;
  padding: 10px 12px;
  margin-bottom: 8px;
  background: #fff7fa;
  border: 1px solid #f0d8de;
  border-radius: 10px;

  .ct-tpl {
    flex: 1 1 200px;
    min-width: 200px;
  }
  .ct-price {
    width: 120px;
  }
  .ct-qty {
    width: 90px;
  }
  /* 買了就先用掉幾堂（今天買課、今天做第一次） */
  .ct-redeem {
    width: 110px;
  }
  .ct-sales {
    flex: 1 1 120px;
    min-width: 120px;
  }
  /* 小計與移除沒有小標，補上與小標等高的下緣，才會跟輸入框對齊 */
  .ct-subtotal,
  .ct-remove {
    padding-bottom: 4px;
  }

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      'tpl tpl'
      'price qty'
      'redeem redeem'
      'sales sales'
      'subtotal remove';
    /* 列與列之間拉開間距，避免「單價」標籤貼著上方的範本選單 */
    gap: 16px 10px;
    align-items: end;

    .ct-tpl {
      grid-area: tpl;
      min-width: 0;
    }
    .ct-price {
      grid-area: price;
      width: 100%;
    }
    .ct-qty {
      grid-area: qty;
      width: 100%;
    }
    .ct-redeem {
      grid-area: redeem;
      width: 100%;
    }
    .ct-sales {
      grid-area: sales;
      min-width: 0;
    }
    .ct-subtotal {
      grid-area: subtotal;
      align-self: center;
    }
    .ct-remove {
      grid-area: remove;
      justify-self: end;
      align-self: center;
    }
  }
`

/* 欄位小標籤：僅手機顯示（桌機維持精簡單列） */
/**
 * 欄位小標。
 *
 * 原本只在手機版顯示，桌機版靠欄位順序辨認 —— 但堂票購買列上「單價 / 數量 /
 * 本次核銷」是連續三個數字框，沒有標籤根本分不出哪個是哪個（實際發生過：
 * 店家找不到「本次核銷」在哪，因為它就是中間那個沒有名字的 0）。
 * 改成兩邊都顯示，字很小不佔空間。
 */
const MobileFieldLabel = styled.div`
  display: block;
  font-size: 12px;
  color: #8c96a5;
  margin-bottom: 4px;
  line-height: 1.3;
  white-space: nowrap;
`

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 24px;
  padding-top: 18px;
  border-top: 1px solid ${theme.border};
  position: sticky;
  bottom: 0;
  background: ${theme.surface};
  z-index: 100;
  padding: 18px 24px;
  margin-left: -24px;
  margin-right: -24px;
  margin-bottom: -24px;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: ${theme.surface};
    padding: 16px 18px;
    border-top: 1px solid ${theme.border};
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.08);
    z-index: 1000;
    margin-top: 0;
    margin-left: 0;
    margin-right: 0;
    margin-bottom: 0;

    button {
      width: 100%;
    }
  }

  .ant-btn {
    height: 48px;
    font-size: 15px;
    font-weight: 500;
    border-radius: 8px;
    padding: 0 24px;
    transition: all 0.2s ease;
  }

  .ant-btn-primary {
    background: ${theme.primary};
    border-color: ${theme.primary};
    color: #ffffff;

    &:hover {
      background: #c05f7a;
      border-color: #c05f7a;
    }
  }

  .ant-btn-default {
    border-color: ${theme.border};
    color: ${theme.text};

    &:hover {
      border-color: ${theme.primary};
      color: ${theme.primary};
    }
  }
`

const TabButton = styled(Button)`
  margin-bottom: 16px;
  border-radius: 8px;
  height: 48px;
  font-weight: 500;
  font-size: 15px;
  border-color: ${theme.border};
  color: ${theme.text};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.primary};
    color: ${theme.primary};
  }

  &.active {
    background: ${theme.primary};
    border-color: ${theme.primary};
    color: #ffffff;
  }
`

const RevenueDiscountContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
  margin-bottom: 12px;
  padding: 8px 0;

  .revenue-discount-inputs {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
    padding-left: 24px;
    width: 100%;

    > div {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      padding-left: 0;
      gap: 12px;

      > div {
        width: 100%;
      }

      .ant-input-number {
        flex: 1;
      }
    }
  }
`

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  /* 桌機 + 平板：欄位間距收緊 */
  gap: 14px 18px;
  /* 群組（FormRow）之間與群組內列距一致（14px），最後一個群組不留多餘空白 */
  margin-bottom: 14px;

  &:last-of-type {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    /* 群組（FormRow）之間也保持 12px，與群組內欄位間距一致 */
    margin-bottom: 12px;

    &:last-of-type {
      margin-bottom: 0;
    }

    /* 欄位間距交給 grid gap 統一控制，避免與 Form.Item 的 margin 疊加造成間距不一 */
    .ant-form-item {
      margin-bottom: 0 !important;
    }

    /* 手機版時，確保選擇框寬度撐滿 */
    .ant-form-item-control-input-content {
      width: 100%;
    }

    .ant-select {
      width: 100% !important;
    }

    .ant-select-selector {
      width: 100% !important;
    }
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 16px;

    /* 手機版時，確保選擇框寬度撐滿 */
    .ant-form-item-control-input-content {
      width: 100%;
    }

    .ant-select {
      width: 100% !important;
    }

    .ant-select-selector {
      width: 100% !important;
    }
  }

  /* 確保 Form.Item 不會溢出 */
  .ant-form-item {
    margin-bottom: 0;
    width: 100%;
    box-sizing: border-box;
  }

  .ant-form-item-label {
    padding-bottom: 8px;
  }

  .ant-form-item-control {
    width: 100%;
    box-sizing: border-box;
  }

  .ant-form-item-control-input {
    width: 100%;
    box-sizing: border-box;
  }

  .ant-form-item-control-input-content {
    width: 100%;
    box-sizing: border-box;
  }

  /* 確保 Select 組件不會溢出 */
  .ant-select {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
  }

  .ant-select-selector {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
  }
`

// 平板/手機：時間改為網格點選，避免下拉難操作
const TimeSelectDesktop = styled.div`
  width: 100%;
  @media (max-width: 768px) {
    display: none;
  }
`
const TimeSelectMobile = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    width: 100%;
  }
`
const MobileTimePickerShell = styled.div`
  .adm-picker {
    --item-height: 44px;
    --title-font-size: 16px;
  }
`

const MobileTimeTrigger = styled.button`
  width: 100%;
  min-height: 44px;
  padding: 0 14px;
  border: 1px solid ${theme.border};
  border-radius: 10px;
  background: ${theme.surface};
  font-size: 16px;
  font-weight: 500;
  color: ${theme.text};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &:hover:not(:disabled) {
    border-color: ${theme.primary};
  }
  &:focus-visible {
    outline: none;
    border-color: ${theme.primary};
    box-shadow: ${controlShadow};
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    background: #f5f5f5;
  }
`

// 行動友善時間選擇：桌面用 Select、手機用 antd-mobile 滾輪（開啟時對齊目前時分）
function MobileFriendlyTimeSelect({
  value,
  onChange,
  timeOptions = [],
  placeholder = '選擇時間',
  disabled = false,
  selectHtmlId
}) {
  const hourMinutePair = useMemo(() => {
    if (value == null || value === '') return null
    if (typeof value !== 'string') return null
    const parts = value.split(':')
    if (parts.length < 2) return null
    return [String(parts[0]).padStart(2, '0'), String(parts[1]).padStart(2, '0')]
  }, [value])

  const fallbackPair = useMemo(() => {
    if (!timeOptions.length) return ['09', '00']
    const [h, m] = timeOptions[0].split(':')
    return [String(h).padStart(2, '0'), String(m).padStart(2, '0')]
  }, [timeOptions])

  const wheelValue = hourMinutePair ?? fallbackPair

  const getColumns = useCallback(
    (selected) => {
      if (!timeOptions.length) return [[], []]
      const hours = [
        ...new Set(
          timeOptions.map((t) => String(t.split(':')[0]).padStart(2, '0'))
        )
      ].sort((a, b) => Number(a) - Number(b))
      const hourColumn = hours.map((h) => ({
        label: `${h} 時`,
        value: h
      }))
      const hourVal =
        selected[0] != null && selected[0] !== ''
          ? String(selected[0]).padStart(2, '0')
          : wheelValue[0]
      const minuteColumn = timeOptions
        .filter(
          (t) => String(t.split(':')[0]).padStart(2, '0') === hourVal
        )
        .map((t) => {
          const mm = String(t.split(':')[1]).padStart(2, '0')
          return { label: `${mm} 分`, value: mm }
        })
      return [hourColumn, minuteColumn]
    },
    [timeOptions, wheelValue]
  )

  const handleConfirm = useCallback(
    (vals) => {
      const h = String(vals[0] ?? '').padStart(2, '0')
      const m = String(vals[1] ?? '').padStart(2, '0')
      const str = `${h}:${m}`
      if (timeOptions.includes(str)) onChange?.(str)
      else {
        const hit = timeOptions.find(
          (t) => String(t.split(':')[0]).padStart(2, '0') === h
        )
        if (hit) onChange?.(hit)
      }
    },
    [timeOptions, onChange]
  )

  return (
    <>
      <TimeSelectDesktop>
        <ResponsiveSelect
          id={selectHtmlId}
          style={{ width: '100%' }}
          size='large'
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          disabled={disabled}
        >
          {timeOptions.map((time) => (
            <Option key={time} value={time}>
              {time}
            </Option>
          ))}
        </ResponsiveSelect>
      </TimeSelectDesktop>
      <TimeSelectMobile>
        <MobileTimePickerShell>
          <MobilePicker
            columns={getColumns}
            value={wheelValue}
            onConfirm={handleConfirm}
            title={placeholder}
            confirmText='確定'
            cancelText='取消'
            mouseWheel
          >
            {(_, { open }) => (
              <MobileTimeTrigger
                type='button'
                disabled={disabled}
                onClick={() => !disabled && open()}
                aria-label={placeholder}
              >
                <span
                  style={{
                    color: value ? theme.text : theme.muted,
                    fontWeight: value ? 600 : 400
                  }}
                >
                  {value || placeholder}
                </span>
                <DownOutlined style={{ color: theme.muted, fontSize: 12 }} />
              </MobileTimeTrigger>
            )}
          </MobilePicker>
        </MobileTimePickerShell>
      </TimeSelectMobile>
    </>
  )
}

// 服務卡片（統一使用，不分桌面和手機）
const ServiceCard = styled.div`
  background: ${theme.surface};
  border: 1px solid ${theme.border};
  border-radius: 12px;
  padding: 18px;
  margin-bottom: 14px;
  position: relative;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    border-color: ${theme.primary};
  }
`

/**
 * 「預約服務」標記。
 *
 * 原本是 position: absolute 貼在卡片右上角，靠卡片的 padding-right 讓位；
 * 但標籤實際寬度比預留的空間寬，會壓在「服務項目」下拉選單上。
 * 改成正常流排在第一列上方、靠右對齊，任何寬度都不會重疊。
 */
const BookingServiceTag = styled.span`
  /* 高度與刪除鈕一致（32px），視覺上是同一列的兩顆同尺寸元件 */
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 12px;
  background: ${theme.primary};
  color: #ffffff;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(213, 114, 138, 0.2);
`

/**
 * 服務／商品卡片的頂端列：右側放「預約服務」標記與刪除鈕。
 *
 * 刪除鈕原本是 position: absolute 釘在卡片右下角，會壓在「實際收費」
 * 輸入框上，手指也不好點。移到頂端跟標記併排後，位置固定、不擋任何欄位。
 */
const ServiceCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  /* 往上收 4px 抵銷卡片 padding，往下只留 8px —— 這一列是標記與操作，
     不需要跟內容欄位一樣寬鬆的呼吸空間 */
  margin: -4px 0 8px;
`

/**
 * 服務耗材摘要區。
 *
 * 耗材原本與一般商品共用同一張大卡片，一個服務帶 5 樣耗材就多出
 * 5 張含分類／單價／業績人員的完整表單 —— 但店員多數時候只需要
 * 確認「有帶到、數量對」。收成一列一筆，需要改再展開。
 */
const ConsumablePanel = styled.div`
  margin: 10px 0 16px;
  border: 1px solid ${theme.border};
  border-radius: 12px;
  background: #fbfafc;
  overflow: hidden;

  .head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    padding: 10px 14px;
    border-bottom: 1px solid ${theme.border};
    background: #f6f4f8;
  }

  .title {
    font-size: 13.5px;
    font-weight: 600;
    color: ${theme.text};

    em {
      font-style: normal;
      font-weight: 500;
      margin-left: 6px;
      color: ${theme.primary};
    }
  }

  .note {
    font-size: 11.5px;
    color: #9a94a3;
  }

  .row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 14px;
    border-bottom: 1px solid #f2eff4;
    font-size: 13.5px;

    &:last-child {
      border-bottom: none;
    }
  }

  .name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${theme.text};
  }

  .qty {
    flex: none;
    color: #6b6472;
    font-variant-numeric: tabular-nums;
  }

  .billed {
    flex: none;
    font-size: 11px;
    padding: 1px 7px;
    border-radius: 999px;
    background: #fdf0f3;
    color: #b95a72;
  }

  .edit,
  .collapse {
    flex: none;
    border: none;
    background: none;
    padding: 4px 2px;
    font-size: 12.5px;
    color: ${theme.primary};
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }

  .collapse {
    display: block;
    margin: -6px auto 10px;
  }
`

const ServiceCardRow = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }

  .label {
    font-size: 13px;
    color: ${theme.muted};
    font-weight: 500;
    margin-bottom: 4px;
  }

  .value {
    font-size: 15px;
    color: ${theme.text};
    font-weight: 400;
    width: 100%;
  }

  /* 选择框和输入框在卡片中的样式 */
  .ant-select,
  .ant-input-number {
    width: 100%;
    height: 44px;
    font-size: 14px;
    border-radius: 10px;
    border: 1px solid ${theme.border};
    transition: all 0.2s ease;

    &:hover {
      border-color: ${theme.primary};
    }

    &:focus,
    &.ant-picker-focused {
      border-color: ${theme.primary};
      box-shadow: ${controlShadow};
    }
  }

  .ant-select-selector {
    height: 44px !important;
    padding: 0 12px !important;
    border: 1px solid ${theme.border} !important;
    border-radius: 10px !important;

    .ant-select-selection-item {
      line-height: 42px !important;
      font-size: 14px;
      color: ${theme.text};
    }

    .ant-select-selection-placeholder {
      line-height: 42px !important;
      font-size: 14px;
      color: ${theme.muted};
    }
  }

  .ant-input-number {
    height: 44px;
    padding: 0 12px;

    .ant-input-number-input {
      height: 42px;
      font-size: 14px;
      color: ${theme.text};
      line-height: 42px;
    }

    .ant-input-number-prefix {
      margin-right: 4px;
      display: flex;
      align-items: center;
    }
  }

  /* 數量輸入框文字左對齊，並確保可以正常輸入和刪除 */
  .quantity-input {
    /* 減少外層容器的 padding，減少左側空白 */
    padding: 0 4px !important;

    .ant-input-number-input {
      text-align: left !important;
      padding-left: 8px !important;
      padding-right: 0 !important;
      /* 確保輸入框可以正常編輯 */
      pointer-events: auto !important;
      user-select: text !important;
    }

    /* 確保輸入框容器可以正常交互 */
    .ant-input-number-input-wrap {
      pointer-events: auto !important;
    }
  }
`

const PersonnelFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;

  @media (min-width: 768px) {
    flex-direction: row;
    gap: 16px;

    ${ServiceCardRow} {
      flex: 1;
      margin-bottom: 0;
    }
  }
`

const ServiceCardDelete = styled(Button)`
  /* 排在 ServiceCardHeader 裡，不再絕對定位（原本會蓋住「實際收費」） */
  flex: none;
  color: ${theme.muted};
  border: 1px solid ${theme.border};
  background: ${theme.surface};
  min-width: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: all 0.2s ease;

  /* 尺寸與「預約服務」標記對齊。
     手機／平板有一條全域的 .ant-btn { min-height: 48px } 會把這顆撐大，
     導致同一列兩個元件高度不一致，所以這裡要用 !important 蓋回去。 */
  width: 32px !important;
  height: 32px !important;
  min-height: 32px !important;
  padding: 0 !important;

  .anticon {
    font-size: 15px;
  }

  &:hover {
    color: ${theme.primary};
    border-color: ${theme.primary};
    background: ${theme.surface};
  }
`

const PerformanceSwitch = styled(Switch)`
  width: auto !important;
  min-width: 60px !important;
  height: 36px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;

  .ant-switch-handle {
    width: 28px !important;
    height: 28px !important;
    top: 4px !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }

  .ant-switch-inner {
    font-size: 12px;
    line-height: 36px !important;
    height: 36px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 0 8px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  }

  .ant-switch-loading-icon,
  .ant-switch-inner-checked,
  .ant-switch-inner-unchecked {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    height: 100% !important;
    line-height: 36px !important;
  }
`

// 新增服務卡片
const NewServiceCard = styled.div`
  background: ${theme.canvas};
  border: 1px dashed ${theme.border};
  border-radius: 12px;
  padding: 18px;
  margin-bottom: 16px;
  position: relative;
  overflow: visible;
  pointer-events: auto;

  .ant-select {
    pointer-events: auto;
  }

  .ant-select-selector {
    pointer-events: auto;
  }

  /* 统一选择框样式 */
  .ant-select,
  .ant-input-number {
    width: 100%;
    height: 44px;
    font-size: 14px;
    border-radius: 10px;
    border: 1px solid ${theme.border};
    transition: all 0.2s ease;

    &:hover {
      border-color: ${theme.primary};
    }

    &:focus,
    &.ant-picker-focused {
      border-color: ${theme.primary};
      box-shadow: ${controlShadow};
    }
  }

  .ant-select-selector {
    height: 44px !important;
    padding: 0 12px !important;
    border: 1px solid ${theme.border} !important;
    border-radius: 10px !important;

    .ant-select-selection-item {
      line-height: 42px !important;
      font-size: 14px;
      color: ${theme.text};
    }

    .ant-select-selection-placeholder {
      line-height: 42px !important;
      font-size: 14px;
      color: ${theme.muted};
    }
  }

  .ant-form-item {
    margin-bottom: 16px;

    .ant-form-item-label {
      padding-bottom: 6px;

      label {
        font-size: 14px;
        font-weight: 500;
        color: ${theme.text};
        height: auto;
        line-height: 1.5;
      }
    }
  }

  /* 數量輸入框文字左對齊，並確保可以正常輸入 */
  .quantity-input {
    .ant-input-number-input {
      text-align: left !important;
      padding-left: 8px !important;
      padding-right: 0 !important;
    }
  }

  /* 單價、小計輸入框內容上下居中 */
  .price-input-center.ant-input-number,
  .price-input-center {
    display: flex !important;
    align-items: center !important;
  }
  .price-input-center .ant-input-number-input,
  .price-input-center.ant-input-number .ant-input-number-input {
    line-height: 42px !important;
    height: 42px !important;
  }
`

const PaymentRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    display: flex;
    flex-direction: column;
  }
`

const PaymentInput = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  .ant-input-number,
  .ant-input {
    flex: 1;
    height: 44px;
    border: 1px solid ${theme.border};
    border-radius: 10px;
    font-size: 14px;
    padding: 0 12px;
    transition: all 0.2s ease;

    &:hover {
      border-color: ${theme.primary};
    }

    &:focus {
      border-color: ${theme.primary};
      box-shadow: ${controlShadow};
    }
  }

  .ant-input-number-input {
    height: 42px;
    padding: 0;
    font-size: 16px;
  }

  .ant-btn {
    height: 44px;
    min-width: 48px;
    border-radius: 10px;
    border: 1px solid ${theme.border};
    color: ${theme.text};
    transition: all 0.2s ease;

    &:hover {
      border-color: ${theme.primary};
      color: ${theme.primary};
    }
  }
`

// 預約信息卡片
const BookingInfoCard = styled(Card)`
  margin-bottom: 18px;
  border-radius: 14px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.05);
  border: 1px solid ${theme.border};
  background: ${theme.surface};

  .ant-card-head {
    background: transparent;
    border-bottom: 1px solid ${theme.border};
    border-radius: 14px 14px 0 0;
    padding: 18px 18px 14px 18px;

    .ant-card-head-title {
      color: ${theme.text};
      font-size: 18px;
      font-weight: 500;
    }
  }

  .ant-card-body {
    padding: 18px;
  }
`

const BookingInfoRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  gap: 16px;

  &:last-child {
    margin-bottom: 0;
  }

  .label {
    font-size: 14px;
    color: #8b8b8b;
    font-weight: 500;
    min-width: 100px;
  }

  .value {
    font-size: 15px;
    color: #2c2c2c;
    font-weight: 400;
    flex: 1;
  }

  .edit-icon {
    color: #8b8b8b;
    cursor: pointer;
    font-size: 16px;
    transition: all 0.2s;

    &:hover {
      color: ${theme.primary};
      transform: scale(1.1);
    }
  }
`

const ServiceTag = styled.div`
  display: inline-block;
  padding: 6px 12px;
  background: white;
  border-radius: 6px;
  border: 1px solid #d5728a;
  color: #d5728a;
  font-size: 13px;
  font-weight: 500;
  margin-right: 8px;
  margin-bottom: 8px;
`

// 儲值金輸入框組件
const StoredValueInput = ({ id, form }) => {
  const storedValue = Form.useWatch('storedValue', form)

  return (
    <InputNumber
      id={id}
      style={{ width: '100%' }}
      min={0}
      prefix='NT$'
      value={storedValue}
      onChange={(value) => {
        form.setFieldsValue({ storedValue: value })
      }}
    />
  )
}

// 現金輸入框組件
const CashInput = ({ id, form }) => {
  const cashValue = Form.useWatch('cash', form)

  return (
    <InputNumber
      id={id}
      style={{ width: '100%' }}
      min={0}
      prefix='NT$'
      value={cashValue}
      onChange={(value) => {
        form.setFieldsValue({ cash: value })
      }}
    />
  )
}

/**
 * 付款金額輸入框（自行綁定表單）
 *
 * 為什麼不能直接用 <Form.Item name='x'><PaymentInput><InputNumber/></PaymentInput></Form.Item>：
 *   Form.Item 只會把 value / onChange 注入「直接子元素」，而這裡的直接子元素是
 *   PaymentInput —— 一個 styled.div。value / onChange 被 div 吃掉，裡面的
 *   InputNumber 什麼都沒收到，等於雙向綁定整個斷掉：
 *     · 表單有值也不會顯示（setFieldsValue 進得去、畫面不動）
 *     · 手動輸入也不會寫回表單（送出時該筆金額是 0）
 *   cash / storedValue 早就各自寫了一個綁定元件繞開這件事，
 *   這裡把它一般化，讓其他金額欄位共用。
 *
 * @param {string} name  表單欄位名稱
 * @param {Function} onChange 欄位自己的副作用（例如刷卡要重算手續費與現金）
 */
const PaymentAmountInput = ({ id, name, form, onChange, ...rest }) => {
  const value = Form.useWatch(name, form)

  return (
    <InputNumber
      id={id || name}
      style={{ width: '100%' }}
      min={0}
      prefix='NT$'
      {...rest}
      value={value}
      onChange={(val) => {
        form.setFieldsValue({ [name]: val })
        onChange?.(val)
      }}
    />
  )
}

// 業績金額顯示組件（實時響應支付方式變化）
const RevenueAmountDisplay = ({ form, revenueDiscountRate }) => {
  // 使用 Form.useWatch 實時監聽所有支付方式字段，並確保轉換為數字
  const storedValue = Number(Form.useWatch('storedValue', form) || 0)
  const cash = Number(Form.useWatch('cash', form) || 0)
  const transfer = Number(Form.useWatch('transfer', form) || 0)
  const deposit = Number(Form.useWatch('deposit', form) || 0)
  const bonus = Number(Form.useWatch('bonus', form) || 0)
  const debt = Number(Form.useWatch('debt', form) || 0)
  const installment = Number(Form.useWatch('installment', form) || 0)
  const voucher = Number(Form.useWatch('voucher', form) || 0)
  // 新增：刷卡 / 行動支付
  const cardPayment = Number(Form.useWatch('cardPayment', form) || 0)
  const mobilePayment = Number(Form.useWatch('mobilePayment', form) || 0)

  // 計算非儲值金付款金額
  const nonStoredValuePayment =
    cash +
    transfer +
    deposit +
    bonus +
    debt +
    installment +
    voucher +
    cardPayment +
    mobilePayment

  // 使用應用業績折扣中輸入的折扣率（如果未設置則為100）
  const validRevenueDiscountRate =
    revenueDiscountRate !== null &&
    revenueDiscountRate !== undefined &&
    revenueDiscountRate >= 0 &&
    revenueDiscountRate <= 100
      ? revenueDiscountRate
      : 100

  // 計算儲值金打折後金額（使用應用業績折扣的折扣率）
  const storedValueAfterDiscount =
    storedValue > 0 && validRevenueDiscountRate < 100
      ? Math.round((storedValue * validRevenueDiscountRate) / 100)
      : storedValue

  // 計算實際總付款金額（儲值金 + 非儲值金付款）
  const actualTotalPaid = storedValue + nonStoredValuePayment

  // 計算業績金額 = 儲值金金額打折後金額 + 現金或其他非儲值金付款
  // 業績金額不應超過實際總付款金額（因為業績折扣是對實際收到的金額的折扣）
  const revenueAmount = Math.min(
    storedValueAfterDiscount + nonStoredValuePayment,
    actualTotalPaid
  )

  return (
    <div
      style={{
        marginTop: 8,
        fontSize: 14,
        color: '#8c4a00',
        fontWeight: 500,
        paddingLeft: 24
      }}
    >
      本次業績的金額為：{revenueAmount.toLocaleString()}
    </div>
  )
}

/**
 * 付款總額即時提示條
 * - 即時顯示：應收金額、已填付款總額、差額
 * - 差額 > 0：不足（黃底）  差額 < 0：超額（紅底）  差額 = 0：一致（綠底）
 * - 顯示在付款欄位上方，讓店家隨時掌握收款是否對得上
 *
 * 注意：paymentFee 是店家支出，不算客戶已付金額，所以不計入 totalPaid
 *
 * 服務費（客人加收服務費）刻意**不**進這裡的收支平衡計算：
 *   店家在刷卡欄填的是「這筆要刷多少貨款」，付款欄位總和仍然要等於應收 ——
 *   整個結帳頁的自動分配、溢收防護都建立在這個等式上，動它風險太大。
 *   服務費是外掛在最後的一行，只在送出時併進 cardPayment 與 receivable。
 *   所以這裡把它當成附註顯示：讓店家知道要跟客人收多少，但不影響平衡判定。
 */
const PaymentTotalIndicator = ({ form, receivable, feeSettings }) => {
  const storedValue = Number(Form.useWatch('storedValue', form) || 0)
  const cash = Number(Form.useWatch('cash', form) || 0)
  const transfer = Number(Form.useWatch('transfer', form) || 0)
  const deposit = Number(Form.useWatch('deposit', form) || 0)
  const voucher = Number(Form.useWatch('voucher', form) || 0)
  const bonus = Number(Form.useWatch('bonus', form) || 0)
  const debt = Number(Form.useWatch('debt', form) || 0)
  const installment = Number(Form.useWatch('installment', form) || 0)
  const cardPayment = Number(Form.useWatch('cardPayment', form) || 0)
  const mobilePayment = Number(Form.useWatch('mobilePayment', form) || 0)

  // 優惠券視同現金，屬於付款方式，計入已填付款
  const totalPaid =
    storedValue +
    cash +
    transfer +
    deposit +
    voucher +
    bonus +
    debt +
    installment +
    cardPayment +
    mobilePayment

  const target = Number(receivable) || 0
  const diff = target - totalPaid // > 0 不足；< 0 超額

  // 要向客人多收的服務費（沒開加收服務費時恆為 0，整段不顯示）
  const surcharge = calcCustomerSurcharge({
    cardBase: cardPayment,
    mobileBase: mobilePayment,
    settings: feeSettings
  }).total
  const feeLabel = (feeSettings && feeSettings.feeLabel) || '服務費'

  let status = 'match' // match / short / over
  if (Math.abs(diff) > 0.5) {
    status = diff > 0 ? 'short' : 'over'
  }

  const palette = {
    match: { bg: '#f0fdf4', border: '#86efac', text: '#15803d', icon: '✓' },
    short: { bg: '#fefce8', border: '#fde047', text: '#a16207', icon: '⚠' },
    over: { bg: '#fef2f2', border: '#fca5a5', text: '#b91c1c', icon: '⚠' }
  }[status]

  return (
    <div
      style={{
        marginBottom: 12,
        padding: '10px 14px',
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: 8,
        fontSize: 13,
        color: palette.text,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 12,
        justifyContent: 'space-between'
      }}
    >
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <span>
          應收金額：
          <strong>NT$ {target.toLocaleString()}</strong>
        </span>
        <span>
          已填付款總額：
          <strong>NT$ {totalPaid.toLocaleString()}</strong>
        </span>
      </div>
      <span style={{ fontWeight: 600 }}>
        {palette.icon}{' '}
        {status === 'match' && '付款金額一致'}
        {status === 'short' && `尚不足 NT$ ${Math.abs(diff).toLocaleString()}`}
        {status === 'over' && `超額 NT$ ${Math.abs(diff).toLocaleString()}（請檢查現金欄位）`}
      </span>

      {surcharge > 0 && (
        <div
          style={{
            width: '100%',
            marginTop: 8,
            paddingTop: 8,
            borderTop: '1px dashed rgba(0,0,0,0.12)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
            color: '#8a4a5c'
          }}
        >
          <span>
            {feeLabel}：<strong>NT$ {surcharge.toLocaleString()}</strong>
          </span>
          <span>
            客人實付：
            <strong>NT$ {(target + surcharge).toLocaleString()}</strong>
          </span>
          <span style={{ fontSize: 12, opacity: 0.75 }}>
            （刷卡機請刷這個金額；上方欄位填的是貨款，不用自己加）
          </span>
        </div>
      )}
    </div>
  )
}

const StepFourDateTimeSection = ({
  // 施作照片（品牌方案限定）：這裡只負責選檔與預覽，
  // 真正上傳是在訂單建立、拿到 bookingId 之後才做（見 CheckoutPage.handleSubmit）
  servicePhotoEnabled,
  servicePhotoFiles,
  setServicePhotoFiles,
  // 店內自行分期（高階方案）：這裡只收集設定，計畫是訂單建立後由後端一次建立
  installmentEnabled,
  installmentDraft,
  setInstallmentDraft,
  installmentModalOpen,
  setInstallmentModalOpen,
  selectedTopupBonus,
  setSelectedTopupBonus,
  form,
  checkoutMode,
  bookingData,
  subtotalAmount,
  discountAmount,
  tempDiscountAmount,
  setTempDiscountAmount,
  handleApplyDiscount,
  totalAmount,
  displayTotalAmount,
  recheckoutLoadedTotal,
  loadingBalance,
  selectedCustomerId,
  customerBalance,
  fetchCustomerBalance,
  setTopupModalVisible,
  topupModalVisible,
  topupAmount,
  setTopupAmount,
  topupLoading,
  handleTopupSubmit,
  selectedServices,
  selectedProducts,
  employees,
  serviceCommissionRules,
  onOpenTransferModal,
  // 儲值金轉讓相關狀態（由 CheckoutPage 傳入）
  transferModalVisible,
  setTransferModalVisible,
  transferFromCustomerName,
  setTransferFromCustomerName,
  transferFromBalance,
  setTransferFromBalance,
  transferAmount,
  setTransferAmount,
  transferDate,
  setTransferDate,
  transferLoading,
  handleTransferSubmit,
  transferSearchOptions,
  transferSearching,
  onTransferSearch,
  onSelectTransferCustomer,
  onClearTransferSearch,
  topupSalesPersonnel,
  setTopupSalesPersonnel,
  topupDiscount,
  setTopupDiscount,
  topupPaymentMethod,
  setTopupPaymentMethod,
  topupPaymentDetails,
  setTopupPaymentDetails,
  participantCount,
  setParticipantCount,
  topupDate,
  setTopupDate,
  topupNote,
  setTopupNote,
  topupSchemeKey,
  setTopupSchemeKey,
  topupExpiryDate,
  setTopupExpiryDate,
  // 會員等級相關（由 CheckoutPage 傳入）
  customerMembershipTier,
  customerTierAccumulated = 0,
  membershipTiers,
  membershipDiscountRate,
  // 儲值詳細信息（包括業績折扣率）
  customerBalanceInfo,
  // 業績折扣相關（由 CheckoutPage 傳入）
  applyRevenueDiscount,
  setApplyRevenueDiscount,
  revenueDiscountRate,
  setRevenueDiscountRate,
  includeZeroYuanInRevenue,
  setIncludeZeroYuanInRevenue,
  serviceQuantities,
  // 折扣趴數相關（由 CheckoutPage 傳入）
  discountPercentage,
  tempDiscountPercentage,
  setTempDiscountPercentage,
  handleApplyDiscountPercentage,
  finalDiscountAmount,
  loadingBooking,
  customerNameValue,
  getServiceOrAdditionalById,
  businessId,
  renderCheckoutCouponPicker,
  couponLocked = false,
  // 金流手續費設定（由 CheckoutPage 傳入，已經過 resolvePaymentFeeSettings 正規化）
  paymentFeeSettings = {
    cardFeeRate: 0,
    mobilePaymentFeeRate: 0,
    autoCalculate: false,
    // 加收服務費預設一律關閉：拿不到設定時絕不會擅自向客人加收
    feeBearer: 'shop',
    applyToCard: false,
    applyToMobile: false,
    feeLabel: '服務費'
  },
  // 標記手續費是否由店家手動指定（true=覆寫、false=交給後端依費率重算）
  markPaymentFeeOverridden = () => {},
  // 堂票購買清單（由 CheckoutPage 傳入）
  selectedClassTickets = [],
  // 不套用活動方案
  topupBonusOptedOut,
  setTopupBonusOptedOut
}) => {
  const watchedStoredValue = Form.useWatch('storedValue', form) || 0
  const watchedCustomerName = Form.useWatch('customerName', form)
  /**
   * 這筆預約的定金刷卡手續費（送出後會併入本單的金流服務費）。
   *
   * feeSettledOrderId 有值代表已經被別張單認列過（作廢後重新結帳的情形），
   * 那時後端不會再加一次，畫面上也就不該再提示，否則店員會以為要多收。
   */
  const depositCardFee =
    bookingData?.depositPayment?.method === 'credit' &&
    !bookingData?.depositPayment?.feeSettledOrderId
      ? Number(bookingData.depositPayment.fee) || 0
      : 0
  const watchedVoucher = Number(Form.useWatch('voucher', form) || 0)
  // 預約資訊核對抽屜：預設收起，由右側懸浮開關開啟
  const [bookingReviewOpen, setBookingReviewOpen] = useState(false)
  // 生成5分鐘間隔的時間選項（00:00 到 23:45）
  const timeOptions = useMemo(() => {
    const options = []
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 5) {
        const timeStr = `${String(hour).padStart(2, '0')}:${String(
          minute
        ).padStart(2, '0')}`
        options.push(timeStr)
      }
    }
    return options
  }, [])

  // 監聽開始時間
  const startTime = Form.useWatch('serviceStartTime', form)

  // 根據開始時間過濾結束時間選項（結束時間不能比開始時間早）
  const endTimeOptions = useMemo(() => {
    if (!startTime) {
      return timeOptions
    }
    return timeOptions.filter((time) => time > startTime)
  }, [startTime, timeOptions])

  // 調試：監聽 topupModalVisible 變化
  useEffect(() => {}, [topupModalVisible])

  // 獲取當前時間並四捨五入到最近的5分鐘。
  // ⚠️ 一定要用店家時區的「現在」：現場結帳的時間戳原本跟著操作者的裝置走，
  // 店家人在清邁結帳會蓋成台灣的前一小時，23:00 之後還會記到前一天，日營收對不起來。
  const getCurrentTimeRounded = () => {
    const now = tzNow()
    const minutes = now.minute()
    const roundedMinutes = Math.ceil(minutes / 5) * 5
    const roundedTime = now.minute(roundedMinutes).second(0).millisecond(0)
    // 如果超過60分鐘，進位到下一小時
    if (roundedMinutes >= 60) {
      return roundedTime.add(1, 'hour').minute(0)
    }
    return roundedTime
  }

  // 設置日期預設值（預約結帳從預約獲取，現場結帳為當天）
  useEffect(() => {
    const currentDate = form.getFieldValue('serviceDate')
    if (!currentDate) {
      // 預約結帳模式：如果預約數據中有日期，使用預約日期；否則使用當天
      if (checkoutMode === 'booking' && bookingData?.date) {
        // 確保日期格式正確，使用 ISO 格式字符串
        const bookingDate = bookingData.date
        const dateValue =
          typeof bookingDate === 'string'
            ? dayjs(bookingDate)
            : dayjs(bookingDate)
        form.setFieldsValue({
          serviceDate: dateValue.isValid() ? dateValue : tzNow()
        })
      } else {
        // 現場結帳模式：使用當天
        form.setFieldsValue({
          serviceDate: tzNow()
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutMode, bookingData])

  // 設置時間預設值（使用當前時間，四捨五入到最近的5分鐘）
  useEffect(() => {
    const currentStartTime = form.getFieldValue('serviceStartTime')
    if (!currentStartTime) {
      const roundedTime = getCurrentTimeRounded()
      const timeStr = roundedTime.format('HH:mm')
      form.setFieldsValue({
        serviceStartTime: timeStr
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 如果有服務且結束時間未設置，設置默認結束時間（開始時間 + 1小時）
  useEffect(() => {
    if (selectedServices.length > 0) {
      const currentStartTime = form.getFieldValue('serviceStartTime')
      const currentEndTime = form.getFieldValue('serviceEndTime')
      if (currentStartTime && !currentEndTime) {
        // 解析開始時間並加1小時
        const [hours, minutes] = currentStartTime.split(':').map(Number)
        const startTimeObj = dayjs().hour(hours).minute(minutes)
        const endTimeObj = startTimeObj.add(1, 'hour')
        const endTimeStr = endTimeObj.format('HH:mm')
        // 確保結束時間在可選時間範圍內
        if (endTimeStr <= '23:45') {
          form.setFieldsValue({
            serviceEndTime: endTimeStr
          })
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServices.length])

  // 當開始時間改變時，處理結束時間
  useEffect(() => {
    if (startTime) {
      const endTime = form.getFieldValue('serviceEndTime')
      // 如果結束時間比開始時間早，清空結束時間
      if (endTime && endTime <= startTime) {
        form.setFieldsValue({ serviceEndTime: undefined })
      }
      // 如果有服務但結束時間未設置，自動設置為開始時間 + 1小時
      if (selectedServices.length > 0 && !endTime) {
        const [hours, minutes] = startTime.split(':').map(Number)
        const startTimeObj = dayjs().hour(hours).minute(minutes)
        const endTimeObj = startTimeObj.add(1, 'hour')
        const endTimeStr = endTimeObj.format('HH:mm')
        // 確保結束時間在可選時間範圍內
        if (endTimeStr <= '23:45') {
          form.setFieldsValue({
            serviceEndTime: endTimeStr
          })
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startTime, selectedServices.length])

  // 計算總金額（添加默認值處理）
  const servicesList = selectedServices || []
  const productsList = selectedProducts || []
  const employeesList = employees || []

  const totalServiceAmount = servicesList.reduce((sum, item) => {
    // service.price 已經是總價（已考慮數量），不需要再乘以數量
    const servicePrice = Number(item.price) || 0
    return sum + servicePrice
  }, 0)
  const totalProductAmount = productsList.reduce(
    (sum, item) => sum + (Number(item.totalPrice) || 0),
    0
  )
  // 堂票購買小計（每筆 = 單價 × 數量）
  const totalClassTicketAmount = (selectedClassTickets || []).reduce(
    (sum, t) => sum + (Number(t.price) || 0) * (Number(t.quantity) || 0),
    0
  )
  const calculatedSubtotal =
    totalServiceAmount + totalProductAmount + totalClassTicketAmount
  // 優惠券視同現金（付款方式之一），不從應收扣除：應收 = 小計 − 手動折扣
  const calculatedTotal = Math.max(
    0,
    calculatedSubtotal - (discountAmount || 0)
  )
  // 上方「總計」：重新結帳時 state 未更新前用 recheckoutLoadedTotal，否則用計算值（與總付款金額一致）
  const effectiveSummaryTotal =
    calculatedTotal > 0
      ? calculatedTotal
      : recheckoutLoadedTotal != null && Number.isFinite(recheckoutLoadedTotal)
        ? recheckoutLoadedTotal
        : 0
  // 扣掉優惠券後、其餘付款方式需要收的金額（僅供顯示，應收本身不扣券）
  const payableAfterVoucher = Math.max(0, effectiveSummaryTotal - watchedVoucher)
  // 總付款金額顯示：父層傳入 displayTotalAmount（含 recheckoutLoadedTotal 備援），否則用 totalAmount
  const effectiveDisplayTotal =
    displayTotalAmount != null && Number.isFinite(displayTotalAmount)
      ? displayTotalAmount
      : Number.isFinite(totalAmount)
        ? totalAmount
        : 0

  const servicesMissingDesignerInStep4 = (selectedServices || []).filter(
    (service) => {
      const commissionRule = serviceCommissionRules?.[service.serviceId]
      const supportDoseTypes =
        service.supportDoseTypes !== undefined
          ? service.supportDoseTypes
          : commissionRule?.supportDoseTypes || false
      if (supportDoseTypes) {
        return !service.firstDosePersonnelId && !service.secondDosePersonnelId
      }
      return !service.designerId && !service.personnelId
    }
  )

  return (
    <>
      {servicesMissingDesignerInStep4.length > 0 && (
        <Hint
          tone='warning'
          title='請為每個服務選擇設計師'
          style={{ marginBottom: 16 }}
        >
          {
            <>
              以下服務尚未選擇設計師，無法進行下一步或送出訂單，請回到上一步選擇設計師：
              <ul
                style={{
                  margin: '8px 0 0',
                  paddingLeft: 20
                }}
              >
                {servicesMissingDesignerInStep4.map((s) => (
                  <li key={s.key}>
                    {getServiceOrAdditionalById?.(s.serviceId)?.name ||
                      s.name ||
                      s.serviceName ||
                      '未命名服務'}
                    {s.supportDoseTypes
                      ? '（須選擇第一劑、第二劑操作人員與設計師）'
                      : ''}
                  </li>
                ))}
              </ul>
            </>
          }
        </Hint>
      )}
      {/* 預約資訊核對：改為右側懸浮開關 + 抽屜，預設收起以節省版面 */}
      <BookingReviewToggle
        type='button'
        onClick={() => setBookingReviewOpen(true)}
        aria-label='開啟預約資訊核對'
        title='預約資訊核對'
      >
        <FileSearchOutlined className='review-icon' />
        預約資訊核對
        <span className='review-total'>
          NT$ {effectiveSummaryTotal.toLocaleString()}
        </span>
      </BookingReviewToggle>

      <Drawer
        title='預約資訊核對'
        placement='right'
        width='min(520px, 92vw)'
        open={bookingReviewOpen}
        onClose={() => setBookingReviewOpen(false)}
        styles={{ body: { paddingTop: 8 } }}
      >
        <div style={{ padding: '16px 0' }}>
          {/* 服務項目 */}
          {servicesList.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: '#1d1d1f',
                  marginBottom: 12
                }}
              >
                服務項目
              </div>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
              >
                {servicesList.map((service, index) => {
                  const supportDoseTypes = service.supportDoseTypes || false

                  // 第一劑/第二劑模式
                  const firstDosePersonnel = employeesList.find(
                    (e) => e._id === service.firstDosePersonnelId
                  )
                  const secondDosePersonnel = employeesList.find(
                    (e) => e._id === service.secondDosePersonnelId
                  )
                  const firstDosePersonnelName =
                    service.firstDosePersonnelName ||
                    firstDosePersonnel?.name ||
                    null
                  const secondDosePersonnelName =
                    service.secondDosePersonnelName ||
                    secondDosePersonnel?.name ||
                    null

                  // 一般模式（設計師/助理）
                  const designer = employeesList.find(
                    (e) => e._id === (service.designerId || service.personnelId)
                  )
                  const assistant = employeesList.find(
                    (e) => e._id === service.assistantId
                  )
                  const designerName =
                    service.designerName ||
                    service.personnelName ||
                    designer?.name ||
                    null
                  const assistantName =
                    service.assistantName || assistant?.name || null

                  return (
                    <div
                      key={service.key || index}
                      style={{
                        padding: 16,
                        background: '#fafafa',
                        borderRadius: 8,
                        border: '1px solid #e5e5e7'
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: 8
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: 15,
                              fontWeight: 600,
                              color: '#1d1d1f',
                              marginBottom: 6
                            }}
                          >
                            {getServiceOrAdditionalById?.(service.serviceId)
                              ?.name ||
                              service.name ||
                              service.serviceName ||
                              '服務項目'}
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              color: '#666',
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 12
                            }}
                          >
                            {supportDoseTypes ? (
                              <>
                                {Boolean(firstDosePersonnelName) && (
                                  <span>
                                    <strong>第一劑：</strong>
                                    {firstDosePersonnelName}
                                  </span>
                                )}
                                {Boolean(secondDosePersonnelName) && (
                                  <span>
                                    <strong>第二劑：</strong>
                                    {secondDosePersonnelName}
                                  </span>
                                )}
                              </>
                            ) : (
                              <>
                                {Boolean(designerName) && (
                                  <span>
                                    <strong>設計師：</strong>
                                    {designerName}
                                  </span>
                                )}
                                {Boolean(assistantName) && (
                                  <span>
                                    <strong>助理：</strong>
                                    {assistantName}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                          {/* 價格為 0 時不顯示計算明細；用 != null 避免條件短路渲染成數字 0 */}
                          {service.originalPrice != null &&
                            Number(service.originalPrice) > 0 && (
                              <div
                                style={{
                                  fontSize: 12,
                                  color: '#999',
                                  marginTop: 4,
                                  lineHeight: 1.5
                                }}
                              >
                                {(() => {
                                  const unitPrice =
                                    Number(service.originalPrice) || 0
                                  const totalPrice = Number(service.price) || 0
                                  const quantity = service.quantity || 1
                                  const expectedTotal = unitPrice * quantity
                                  const isManualPrice =
                                    service.isManualPrice &&
                                    totalPrice !== expectedTotal

                                  if (isManualPrice) {
                                    // 手動更改價格的情況
                                    return (
                                      <>
                                        <div>
                                          原價 NT$ {unitPrice.toLocaleString()}
                                          （單價） × 數量 {quantity} = NT${' '}
                                          {expectedTotal.toLocaleString()}
                                        </div>
                                        <div
                                          style={{
                                            color: '#1890ff',
                                            fontWeight: 600
                                          }}
                                        >
                                          手動更改總價：NT${' '}
                                          {totalPrice.toLocaleString()}
                                        </div>
                                      </>
                                    )
                                  } else {
                                    // 自動計算的情況
                                    return (
                                      <div>
                                        原價 NT$ {unitPrice.toLocaleString()}
                                        （單價） × 數量 {quantity} = NT${' '}
                                        {totalPrice.toLocaleString()}（總價）
                                      </div>
                                    )
                                  }
                                })()}
                              </div>
                            )}
                        </div>
                        <div
                          style={{
                            fontSize: 16,
                            fontWeight: 600,
                            color: '#1d1d1f',
                            minWidth: 100,
                            textAlign: 'right'
                          }}
                        >
                          NT$ {Number(service.price || 0).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* 商品項目 */}
          {productsList.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: '#1d1d1f',
                  marginBottom: 12
                }}
              >
                商品項目
              </div>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
              >
                {productsList
                  .filter((p) => !p.isConsumable || p.countAsSales)
                  .map((product, index) => (
                  <div
                    key={product.key || index}
                    style={{
                      padding: 16,
                      background: '#fafafa',
                      borderRadius: 8,
                      border: '1px solid #e5e5e7'
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: '#1d1d1f',
                            marginBottom: 4
                          }}
                        >
                          {product.name || '商品'}
                        </div>
                        <div style={{ fontSize: 13, color: '#666' }}>
                          數量：{product.quantity || 1} × NT${' '}
                          {Number(product.unitPrice || 0).toLocaleString()}
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: 16,
                          fontWeight: 600,
                          color: '#1d1d1f',
                          minWidth: 100,
                          textAlign: 'right'
                        }}
                      >
                        NT$ {(Number(product.totalPrice) || 0).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  ))}
              </div>

              {/* 耗材只列品名與數量。這一區是核對畫面，
                  每樣耗材佔一個大方塊又印著 NT$ 0，只會擋住真正要核對的金額 */}
              {(() => {
                const usedConsumables = productsList.filter(
                  (p) => p.isConsumable && !p.countAsSales
                )
                if (!usedConsumables.length) return null
                return (
                  <div
                    style={{
                      marginTop: 10,
                      padding: '10px 14px',
                      background: '#fbfafc',
                      border: '1px solid #efecf1',
                      borderRadius: 8,
                      fontSize: 13,
                      lineHeight: 1.8,
                      color: '#6b6472'
                    }}
                  >
                    <span style={{ color: '#9a94a3', marginRight: 6 }}>
                      服務耗材
                    </span>
                    {usedConsumables
                      .map(
                        (p) =>
                          `${p.name || '耗材'} ×${p.quantity || 1}${
                            p.unit ? ` ${p.unit}` : ''
                          }`
                      )
                      .join('、')}
                  </div>
                )
              })()}
            </div>
          )}

          {/* 堂票購買 */}
          {(selectedClassTickets || []).length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: '#1d1d1f',
                  marginBottom: 12
                }}
              >
                堂票購買
              </div>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
              >
                {selectedClassTickets.map((ticket, index) => {
                  const price = Number(ticket.price) || 0
                  const quantity = Number(ticket.quantity) || 0
                  return (
                    <div
                      key={ticket.key || index}
                      style={{
                        padding: 16,
                        background: '#fafafa',
                        borderRadius: 8,
                        border: '1px solid #e5e5e7'
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: 8,
                          gap: 12
                        }}
                      >
                        <span
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: '#1d1d1f'
                          }}
                        >
                          {ticket.templateName || '未選擇堂票範本'}
                        </span>
                        <span
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: '#d5728a',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          NT$ {(price * quantity).toLocaleString()}
                        </span>
                      </div>
                      <div style={{ fontSize: 13, color: '#666' }}>
                        單價 NT$ {price.toLocaleString()} × {quantity} 組
                        {ticket.totalCount ? `／每組 ${ticket.totalCount} 張` : ''}
                        {ticket.validityDays || ticket.validUntil
                          ? `／效期 ${describeValidity(ticket, {
                              short: true
                            })}`
                          : ''}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* 金額總計 */}
          <div
            style={{
              padding: 16,
              background: '#fff7e6',
              borderRadius: 8,
              border: '1px solid #ffd591',
              marginTop: 16
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8
              }}
            >
              <span style={{ fontSize: 14, color: '#666' }}>服務小計：</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#1d1d1f' }}>
                NT$ {totalServiceAmount.toLocaleString()}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8
              }}
            >
              <span style={{ fontSize: 14, color: '#666' }}>商品小計：</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#1d1d1f' }}>
                NT$ {(totalProductAmount || 0).toLocaleString()}
              </span>
            </div>
            {totalClassTicketAmount > 0 && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8
                }}
              >
                <span style={{ fontSize: 14, color: '#666' }}>堂票小計：</span>
                <span
                  style={{ fontSize: 14, fontWeight: 500, color: '#1d1d1f' }}
                >
                  NT$ {totalClassTicketAmount.toLocaleString()}
                </span>
              </div>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8
              }}
            >
              <span style={{ fontSize: 14, color: '#666' }}>合計：</span>
              <span style={{ fontSize: 14, fontWeight: 500, color: '#1d1d1f' }}>
                NT$ {calculatedSubtotal.toLocaleString()}
              </span>
            </div>
            {/* 會員折扣顯示（僅顯示狀態，不可修改） */}
            {customerMembershipTier &&
              membershipDiscountRate < 100 &&
              (() => {
                // 檢查是否有服務應用了折扣
                const hasAppliedDiscount = selectedServices.some((s) => {
                  const discountedPrice = Math.round(
                    ((s.originalPrice || s.price || 0) *
                      membershipDiscountRate) /
                      100
                  )
                  return s.price === discountedPrice
                })
                return (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 8,
                      padding: '8px 12px',
                      background: hasAppliedDiscount ? '#f0f9ff' : '#fafafa',
                      borderRadius: 6,
                      border: `1px solid ${
                        hasAppliedDiscount ? '#bae6fd' : '#d9d9d9'
                      }`
                    }}
                  >
                    <span style={{ fontSize: 14, color: '#0369a1' }}>
                      會員折扣（{customerMembershipTier}{' '}
                      {membershipDiscountRate}
                      折）：
                    </span>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: hasAppliedDiscount ? '#0369a1' : '#8c8c8c'
                      }}
                    >
                      {hasAppliedDiscount ? '已套用' : '未套用'}
                    </span>
                  </div>
                )
              })()}
            {discountAmount > 0 && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8
                }}
              >
                <span style={{ fontSize: 14, color: '#666' }}>手動折扣：</span>
                <span
                  style={{ fontSize: 14, fontWeight: 500, color: '#ff4d4f' }}
                >
                  - NT$ {discountAmount.toLocaleString()}
                </span>
              </div>
            )}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingTop: 12,
                borderTop: '1px solid #ffd591',
                marginTop: 8
              }}
            >
              <span style={{ fontSize: 18, fontWeight: 600, color: '#1d1d1f' }}>
                總計：
              </span>
              <span
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: '#d5728a'
                }}
              >
                NT$ {effectiveSummaryTotal.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </Drawer>

      <FormCard
        title={
          checkoutMode === 'booking'
            ? '選擇日期和時間'
            : '步驟三 選擇日期和時間'
        }
      >
        <Form form={form} layout='vertical'>
          {/* 日期總是顯示（不管是服務還是商品都需要銷售日期） */}
          <Form.Item
            name='serviceDate'
            label={selectedServices.length > 0 ? '服務日期' : '銷售日期'}
            rules={[{ required: true, message: '請選擇日期' }]}
            getValueFromEvent={(date) => {
              return date ? date.format('YYYY-MM-DD') : null
            }}
            getValueProps={(value) => {
              return {
                value: value ? dayjs(value, 'YYYY-MM-DD') : null
              }
            }}
          >
            <DatePicker
              id='serviceDate'
              style={{ width: '100%' }}
              size='large'
              placeholder='選擇日期'
              format='YYYY-MM-DD'
              inputReadOnly
            />
          </Form.Item>

          {/* 時間總是顯示（不管是服務還是商品都需要時間） */}
          <FormRow>
            <Form.Item
              name='serviceStartTime'
              label={selectedServices.length > 0 ? '服務開始時間' : '時間'}
              rules={[{ required: true, message: '請選擇時間' }]}
            >
              <MobileFriendlyTimeSelect
                timeOptions={timeOptions}
                placeholder='選擇時間'
                selectHtmlId='serviceStartTime'
              />
            </Form.Item>

            {selectedServices.length > 0 && (
              <Form.Item
                name='serviceEndTime'
                label='服務結束時間'
                rules={[{ required: true, message: '請選擇結束時間' }]}
              >
                <MobileFriendlyTimeSelect
                  timeOptions={endTimeOptions.length > 0 ? endTimeOptions : []}
                  placeholder={
                    startTime ? '請選擇比開始時間晚的時間' : '請先選擇開始時間'
                  }
                  disabled={!startTime}
                  selectHtmlId='serviceEndTime'
                />
              </Form.Item>
            )}
          </FormRow>

          {selectedServices.length > 0 && (
            <Form.Item
              name='participantCount'
              label='預約人數'
              rules={[
                { required: true, message: '請輸入預約人數' },
                { type: 'number', min: 1, message: '預約人數至少為1人' },
                { type: 'number', max: 10, message: '預約人數最多為10人' }
              ]}
              initialValue={1}
            >
              <InputNumber
                min={1}
                max={10}
                precision={0}
                style={{ width: '100%' }}
                size='large'
                placeholder='請輸入預約人數'
                onChange={(value) => {
                  form.setFieldsValue({ participantCount: value || 1 })
                }}
              />
            </Form.Item>
          )}

          <Form.Item name='remarks' label='備註'>
            <div>
              <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
                快捷詞組 (編輯): 新增快捷詞組...
              </div>
              <TextArea id='remarks' rows={4} placeholder='請輸入備註' />
            </div>
          </Form.Item>

          {servicePhotoEnabled && (
            <Form.Item label='施作照片（客人看得到）'>
              <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
                最多 8 張。照片會出現在客人的預約紀錄裡；備註只有店家看得到。
                結帳沒傳也沒關係，之後在預約詳情還可以補。
              </div>
              <Upload
                multiple
                accept='image/*'
                listType='picture-card'
                fileList={servicePhotoFiles}
                beforeUpload={() => false}
                onChange={({ fileList }) => {
                  // beforeUpload 回 false 時 antd 不會幫忙產縮圖，
                  // 自己補一個 blob 網址，不然 picture-card 會是一片空白
                  const next = fileList.slice(0, 8).map((file) => {
                    const raw = file.originFileObj || file
                    if (file.thumbUrl || !(raw instanceof Blob)) return file
                    return Object.assign(file, {
                      originFileObj: raw,
                      thumbUrl: URL.createObjectURL(raw)
                    })
                  })
                  setServicePhotoFiles(next)
                }}
                onRemove={(file) => {
                  if (file.thumbUrl?.startsWith('blob:')) {
                    URL.revokeObjectURL(file.thumbUrl)
                  }
                  setServicePhotoFiles((prev) =>
                    prev.filter((f) => f.uid !== file.uid)
                  )
                }}
              >
                {servicePhotoFiles.length >= 8 ? null : (
                  <div>
                    <CameraOutlined />
                    <div style={{ marginTop: 8 }}>選擇照片</div>
                  </div>
                )}
              </Upload>
            </Form.Item>
          )}
        </Form>
      </FormCard>

      <FormCard title='付費資訊'>
        <Form form={form} layout='vertical'>
          {/* 目前結帳顧客與儲值金使用後餘額（原頂部固定 bar 資訊移入此區） */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 16,
              marginBottom: 16,
              padding: '12px 16px',
              background: '#fafafa',
              borderRadius: 8,
              border: '1px solid #e5e5e7',
              fontSize: 14
            }}
          >
            <span>
              目前結帳顧客：
              <span style={{ fontWeight: 600, color: '#1d1d1f' }}>
                {/* 開新分頁：結帳填到一半，不能因為點了姓名就把資料弄丟 */}
                <CustomerNameLink customerId={selectedCustomerId} newTab>
                  {checkoutMode === 'booking'
                    ? bookingData?.userName ||
                      bookingData?.customerName ||
                      customerNameValue ||
                      (loadingBooking ? '載入中...' : '—')
                    : customerNameValue || watchedCustomerName || '尚未選擇'}
                </CustomerNameLink>
              </span>
            </span>
            {selectedCustomerId &&
              customerBalance !== null &&
              Number(watchedStoredValue) > 0 && (
                <span style={{ color: '#0369a1', fontWeight: 500 }}>
                  儲值金使用後餘額：NT${' '}
                  {Math.max(
                    0,
                    Number(customerBalance) - Number(watchedStoredValue)
                  ).toLocaleString()}
                </span>
              )}
          </div>

          {/* 這位顧客的分期：之前開的分期還沒收完時，在付款區直接提醒並可當場收款。
              現場結帳與帶入預約結帳都走這一段（同一個 StepFourDateTimeSection）。
              分期款登錄在分期帳上（InstallmentPayment），不進這張新訂單的付款欄位 ——
              否則會變成同一筆錢記兩次，而且現金流日期也會跑掉。 */}
          <CustomerInstallmentsPanel
            customerId={selectedCustomerId}
            variant='checkout'
            onChanged={() =>
              selectedCustomerId && fetchCustomerBalance(selectedCustomerId)
            }
          />

          {/* 付款總額即時提示（應收 vs 已填總額；超額/不足即時警示）
              用 effectiveDisplayTotal（含重新結帳的訂單總額備援），
              與「總付款金額」顯示一致，避免重新結帳時應收顯示 0 而誤報超額 */}
          <PaymentTotalIndicator
            form={form}
            receivable={effectiveDisplayTotal}
            feeSettings={paymentFeeSettings}
          />

          <PaymentRow>
            <div>
              <Form.Item
                name='storedValue'
                label={
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <span>儲值金</span>
                    {loadingBalance ? (
                      <span style={{ fontSize: 12, color: '#999' }}>
                        載入中...
                      </span>
                    ) : selectedCustomerId ? (
                      customerBalance !== null && customerBalance > 0 ? (
                        <span style={{ fontSize: 12, color: '#666' }}>
                          ( 剩餘：NT$ {customerBalance.toLocaleString()} )
                        </span>
                      ) : (
                        <span style={{ fontSize: 12, color: '#999' }}>
                          ( 此客戶目前沒有儲值金。 )
                        </span>
                      )
                    ) : null}
                  </div>
                }
              >
                <PaymentInput>
                  <StoredValueInput id='storedValue' form={form} />
                  <Button
                    icon={<PlusOutlined />}
                    onClick={() => {
                      if (!selectedCustomerId) {
                        message.error('請先選擇客戶再進行儲值')
                        return
                      }
                      if (setTopupModalVisible) {
                        setTopupModalVisible(true)
                      } else {
                        console.error('setTopupModalVisible 未定義！')
                        message.error('系統錯誤：無法開啟儲值視窗')
                      }
                    }}
                  />
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => fetchCustomerBalance(selectedCustomerId)}
                    loading={loadingBalance}
                  />
                  <Button
                    icon={<SwapOutlined />}
                    onClick={() => {
                      if (!selectedCustomerId) {
                        message.error('請先選擇目前結帳的客戶（轉入方）')
                        return
                      }
                      if (onOpenTransferModal) {
                        onOpenTransferModal()
                      }
                    }}
                  />
                </PaymentInput>
                {/* 儲值業績折扣提示 */}
                <Form.Item
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.storedValue !== currentValues.storedValue
                  }
                  noStyle
                >
                  {({ getFieldValue }) => {
                    // 判斷是否有業績折扣：檢查 customerBalanceInfo 中是否有 revenueDiscountRate 且不等於 100
                    const hasRevenueDiscount =
                      customerBalanceInfo?.revenueDiscountRate !== undefined &&
                      customerBalanceInfo?.revenueDiscountRate !== null &&
                      customerBalanceInfo?.revenueDiscountRate < 100

                    const storedValueAmount = Number(
                      getFieldValue('storedValue') || 0
                    )
                    const discountRate =
                      customerBalanceInfo?.revenueDiscountRate || 100
                    const calculatedRevenue =
                      storedValueAmount > 0
                        ? Math.round((storedValueAmount * discountRate) / 100)
                        : 0

                    if (!hasRevenueDiscount || storedValueAmount === 0) {
                      return null
                    }

                    return (
                      <div
                        style={{
                          marginTop: 8,
                          padding: 12,
                          background: '#fff7e6',
                          border: '1px solid #ffd591',
                          borderRadius: 6,
                          fontSize: 12,
                          color: '#8c4a00'
                        }}
                      >
                        <div style={{ fontWeight: 500, marginBottom: 4 }}>
                          ⚠️ 過渡期儲值業績折扣
                        </div>
                        <div>
                          此客戶的儲值為過渡期儲值，使用儲值支付時：
                          <br />• 客戶扣款：全額扣除（NT${' '}
                          {storedValueAmount.toLocaleString()}）
                          <br />• 業績計算：NT${' '}
                          {storedValueAmount.toLocaleString()} × {discountRate}%
                          = NT$ {calculatedRevenue.toLocaleString()}
                          <br />
                          <span style={{ fontSize: 11, opacity: 0.8 }}>
                            （業績金額將由系統自動計算）
                          </span>
                        </div>
                      </div>
                    )
                  }}
                </Form.Item>
              </Form.Item>

              {/* 各儲值活動餘額（多筆分桶時顯示） */}
              {customerBalanceInfo?.balanceByScheme?.length > 0 && (
                <div
                  style={{
                    marginBottom: 12,
                    padding: '10px 12px',
                    background: '#f6ffed',
                    border: '1px solid #b7eb8f',
                    borderRadius: 8,
                    fontSize: 13
                  }}
                >
                  <div
                    style={{
                      fontWeight: 500,
                      marginBottom: 6,
                      color: '#389e0d'
                    }}
                  >
                    各儲值活動餘額
                  </div>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {customerBalanceInfo.balanceByScheme.map((s) => (
                      <li key={s.schemeKey} style={{ marginBottom: 4 }}>
                        <span style={{ color: '#333' }}>{s.schemeKey}</span> NT${' '}
                        {Number(s.balance || 0).toLocaleString()}
                        {(s.expiryDateDisplay || s.expiryDate) && (
                          <span style={{ color: '#888', marginLeft: 8 }}>
                            期限{' '}
                            {s.expiryDateDisplay ||
                              dayjs(s.expiryDate).format('YYYY/MM/DD')}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* 使用儲值時可選擇從哪一儲值活動扣款 */}
              <Form.Item
                noStyle
                shouldUpdate={(prev, cur) =>
                  prev.storedValue !== cur.storedValue
                }
              >
                {({ getFieldValue }) => {
                  const sv = Number(getFieldValue('storedValue') || 0)
                  const schemes = customerBalanceInfo?.balanceByScheme || []
                  if (sv <= 0 || schemes.length === 0) return null
                  return (
                    <Form.Item
                      name='storedValueSchemeKey'
                      label='從哪一儲值活動扣款'
                      extra='不指定則由系統依總餘額扣款'
                      style={{ marginBottom: 16 }}
                    >
                      <Radio.Group>
                        <Radio value=''>不指定儲值活動</Radio>
                        {schemes.map((s) => (
                          <Radio key={s.schemeKey} value={s.schemeKey}>
                            {s.schemeKey}（NT${' '}
                            {Number(s.balance || 0).toLocaleString()}）
                          </Radio>
                        ))}
                      </Radio.Group>
                    </Form.Item>
                  )
                }}
              </Form.Item>

              {typeof renderCheckoutCouponPicker === 'function'
                ? renderCheckoutCouponPicker()
                : null}
              <Form.Item name='voucher' label='優惠券折扣'>
                <PaymentInput>
                  <PaymentAmountInput
                    id='voucher'
                    name='voucher'
                    form={form}
                    disabled={couponLocked}
                  />
                </PaymentInput>
              </Form.Item>

              <div style={{ marginBottom: 16 }}>
                <div style={{ marginBottom: 8, fontSize: 14, fontWeight: 500 }}>
                  折扣 (%)
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 12,
                      color: '#999',
                      fontWeight: 400
                    }}
                  >
                    （輸入「折抵幾 %」後點擊套用，例如 10 = 折抵 10%，即打 9 折）
                  </span>
                </div>
                <div
                  style={{ display: 'flex', gap: 8, alignItems: 'center' }}
                  onWheel={(e) => e && e.stopPropagation()}
                  onScroll={(e) => e && e.stopPropagation()}
                >
                  <InputNumber
                    id='discountPercentage'
                    style={{ flex: 1 }}
                    min={0}
                    max={100}
                    suffix='%'
                    value={tempDiscountPercentage}
                    onChange={(value) => {
                      const numValue = value || 0
                      const finalValue = numValue > 100 ? 100 : numValue
                      setTempDiscountPercentage(finalValue < 0 ? 0 : finalValue)
                    }}
                    placeholder='折抵百分比，例如 10'
                    onPressEnter={(e) => {
                      if (e) {
                        e.preventDefault?.()
                        e.stopPropagation?.()
                      }
                      handleApplyDiscountPercentage()
                    }}
                    autoFocus={false}
                    onBlur={(e) => {
                      if (e) {
                        e.stopPropagation?.()
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e) {
                        const scrollKeys = [
                          'ArrowUp',
                          'ArrowDown',
                          'PageUp',
                          'PageDown',
                          'Home',
                          'End'
                        ]
                        if (scrollKeys.includes(e.key)) {
                          e.preventDefault?.()
                          e.stopPropagation?.()
                        }
                        if (e.key === 'Enter') {
                          e.preventDefault?.()
                          e.stopPropagation?.()
                          handleApplyDiscountPercentage()
                        }
                        if (e.key >= '0' && e.key <= '9') {
                          e.stopPropagation?.()
                        }
                      }
                    }}
                    onClick={(e) => e && e.stopPropagation?.()}
                    onFocus={(e) => e && e.stopPropagation?.()}
                    onInput={(e) => e && e.stopPropagation?.()}
                    onMouseDown={(e) => e && e.stopPropagation?.()}
                  />
                  <Button
                    type='primary'
                    htmlType='button'
                    onClick={(e) => {
                      if (e && typeof e.preventDefault === 'function') {
                        e.preventDefault()
                      }
                      handleApplyDiscountPercentage()
                    }}
                    disabled={
                      tempDiscountPercentage < 0 || tempDiscountPercentage > 100
                    }
                  >
                    套用
                  </Button>
                </div>
                {/* 折抵金額預覽：讓店家確認這個趴數到底折了多少錢（與 App 端一致） */}
                {tempDiscountPercentage > 0 &&
                  tempDiscountPercentage <= 100 && (
                    <div
                      style={{ marginTop: 8, fontSize: 12, color: '#c2185b' }}
                    >
                      折抵 NT${' '}
                      {Math.round(
                        (subtotalAmount * tempDiscountPercentage) / 100
                      ).toLocaleString()}
                      {tempDiscountPercentage !== discountPercentage && (
                        <span style={{ color: '#999' }}>（尚未套用）</span>
                      )}
                    </div>
                  )}
              </div>

              <Form.Item
                name='totalPayment'
                label={
                  <span>
                    總付款金額{' '}
                    <span
                      style={{
                        fontWeight: 400,
                        color: '#8c8c8c',
                        fontSize: 13
                      }}
                    >
                      （＝應收金額，優惠券屬付款方式之一）
                    </span>
                  </span>
                }
              >
                {/* 這裡顯示的必須是「應收金額」，且與送出後端的 amounts.receivable
                    綁同一個變數（effectiveSummaryTotal），否則會出現
                    「預覽 10,610 / 訂單應收 10,611」這種對不起來的情況。
                    優惠券是付款方式（見 CheckoutOrder.calculatePaidAmount），
                    不可從應收再扣一次，扣掉後的餘額只用於下方「其餘付款方式需收」。 */}
                <div
                  style={{
                    fontSize: '24px',
                    fontWeight: 'bold',
                    color: '#1a1a1a',
                    padding: '12px 0'
                  }}
                >
                  NT${' '}
                  {(Number.isFinite(effectiveSummaryTotal)
                    ? effectiveSummaryTotal
                    : 0
                  ).toLocaleString()}
                </div>
                {/* 檢查是否有服務應用了會員折扣 */}
                {(() => {
                  const hasAppliedMembershipDiscount = selectedServices.some(
                    (s) => {
                      const discountedPrice = Math.round(
                        ((s.originalPrice || s.price || 0) *
                          membershipDiscountRate) /
                          100
                      )
                      return s.price === discountedPrice
                    }
                  )
                  if (
                    hasAppliedMembershipDiscount &&
                    customerMembershipTier &&
                    membershipDiscountRate < 100
                  ) {
                    return (
                      <div
                        style={{
                          marginTop: 4,
                          fontSize: 11,
                          color: '#0369a1',
                          fontStyle: 'italic'
                        }}
                      >
                        * 已套用會員折扣（{customerMembershipTier}{' '}
                        {membershipDiscountRate}折），請注意避免重複套用折扣
                      </div>
                    )
                  }
                  return null
                })()}
                {(finalDiscountAmount > 0 || discountPercentage > 0) && (
                  <div
                    style={{
                      marginTop: 8,
                      fontSize: 12,
                      color: '#666'
                    }}
                  >
                    小計：NT${' '}
                    {(Number.isFinite(subtotalAmount)
                      ? subtotalAmount
                      : 0
                    ).toLocaleString()}
                    {discountPercentage > 0 && (
                      <span>
                        {' '}
                        - 折扣 {discountPercentage}%（折抵 NT${' '}
                        {(Number.isFinite(finalDiscountAmount)
                          ? finalDiscountAmount
                          : 0
                        ).toLocaleString()}
                        ）
                      </span>
                    )}
                    <span>
                      = 應收 NT${' '}
                      {(Number.isFinite(effectiveSummaryTotal)
                        ? effectiveSummaryTotal
                        : 0
                      ).toLocaleString()}
                    </span>
                  </div>
                )}
                {watchedVoucher > 0 && (
                  <div
                    style={{
                      marginTop: 6,
                      fontSize: 12,
                      color: '#8c4a00'
                    }}
                  >
                    其中優惠券折抵 NT$ {watchedVoucher.toLocaleString()}，其餘付款方式需收
                    NT${' '}
                    {(Number.isFinite(payableAfterVoucher)
                      ? payableAfterVoucher
                      : 0
                    ).toLocaleString()}
                  </div>
                )}
              </Form.Item>
            </div>

            <div>
              <Form.Item name='cash' label='現金'>
                <PaymentInput>
                  <CashInput id='cash' form={form} />
                </PaymentInput>
              </Form.Item>

              <Form.Item name='transfer' label='匯款'>
                <PaymentInput>
                  <PaymentAmountInput
                    id='transfer'
                    name='transfer'
                    form={form}
                  />
                </PaymentInput>
              </Form.Item>

              <Form.Item
                name='deposit'
                label={
                  checkoutMode === 'booking' &&
                  bookingData?.depositStatus === 'paid' &&
                  bookingData?.depositAmount > 0 ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      定金
                      <span style={{
                        fontSize: 11,
                        color: '#52c41a',
                        background: '#f6ffed',
                        border: '1px solid #b7eb8f',
                        borderRadius: 4,
                        padding: '0 5px',
                        lineHeight: '18px'
                      }}>
                        已付 NT${bookingData.depositAmount.toLocaleString()}
                      </span>
                      {/* 定金是刷卡收的，這裡要講出來：那筆手續費送出後會併進
                          本單的金流服務費，店員看到的數字才不會跟存進去的對不上 */}
                      {bookingData?.depositPayment?.method === 'credit' && (
                        <span
                          style={{
                            fontSize: 11,
                            color: '#5b3f86',
                            background: '#f4effa',
                            border: '1px solid #d9c9ef',
                            borderRadius: 4,
                            padding: '0 5px',
                            lineHeight: '18px'
                          }}
                        >
                          {bookingData.depositPayment.source === 'online'
                            ? '線上刷卡'
                            : '櫃檯刷卡'}
                          {bookingData.depositPayment.cardLastFour
                            ? ` ****${bookingData.depositPayment.cardLastFour}`
                            : ''}
                        </span>
                      )}
                    </span>
                  ) : '定金'
                }
                extra={
                  depositCardFee > 0
                    ? `定金刷卡手續費 NT$${depositCardFee}（費率 ${
                        bookingData?.depositPayment?.feeRate || 0
                      }%），送出後會併入本單的金流服務費`
                    : undefined
                }
              >
                <PaymentInput>
                  <PaymentAmountInput id='deposit' name='deposit' form={form} />
                </PaymentInput>
              </Form.Item>

              {/* === 新增：刷卡 === */}
              <Form.Item
                name='cardPayment'
                label={
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                    <span>
                      刷卡
                      {paymentFeeSettings.cardFeeRate > 0
                        ? `（手續費 ${paymentFeeSettings.cardFeeRate}%）`
                        : ''}
                    </span>
                    <a
                      href='/settings/paymentFee'
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{
                        fontSize: 12,
                        color: '#d5728a',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 2,
                        textDecoration: 'none'
                      }}
                      title='前往「設定 → 金流手續費」修改預設費率'
                    >
                      <EditOutlined style={{ fontSize: 12 }} />
                      修改
                    </a>
                  </span>
                }
              >
                <PaymentInput>
                  <PaymentAmountInput
                    id='cardPayment'
                    name='cardPayment'
                    form={form}
                    onChange={(val) => {
                      const card = Number(val) || 0
                      const mobile =
                        Number(form.getFieldValue('mobilePayment')) || 0

                      // 1. 自動重算手續費
                      if (paymentFeeSettings.autoCalculate) {
                        const cardFee =
                          (card * paymentFeeSettings.cardFeeRate) / 100
                        const mobileFee =
                          (mobile * paymentFeeSettings.mobilePaymentFeeRate) /
                          100
                        form.setFieldsValue({
                          paymentFee:
                            Math.round((cardFee + mobileFee) * 100) / 100
                        })
                        // 金額改變後重新自動帶入，先前的手動覆寫視為失效
                        markPaymentFeeOverridden(false)
                      }

                      // 2. 自動調整現金：保持「付款總額 = 應收金額」
                      //    邏輯：其他付款欄位維持不變，用現金當作緩衝
                      //    若其他欄位已超額，現金歸零（用戶可再手動調整）
                      const storedValue =
                        Number(form.getFieldValue('storedValue')) || 0
                      const transfer =
                        Number(form.getFieldValue('transfer')) || 0
                      const deposit =
                        Number(form.getFieldValue('deposit')) || 0
                      const voucher =
                        Number(form.getFieldValue('voucher')) || 0
                      const bonus = Number(form.getFieldValue('bonus')) || 0
                      const debt = Number(form.getFieldValue('debt')) || 0
                      const installment =
                        Number(form.getFieldValue('installment')) || 0
                      const nonCashSum =
                        storedValue +
                        transfer +
                        deposit +
                        voucher +
                        bonus +
                        debt +
                        installment +
                        card +
                        mobile
                      const nextCash = Math.max(
                        0,
                        (Number(totalAmount) || 0) - nonCashSum
                      )
                      form.setFieldsValue({ cash: nextCash })
                    }}
                  />
                </PaymentInput>
              </Form.Item>

              {/* === 新增：行動支付 === */}
              <Form.Item
                name='mobilePayment'
                label={
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                    <span>
                      行動支付
                      {paymentFeeSettings.mobilePaymentFeeRate > 0
                        ? `（手續費 ${paymentFeeSettings.mobilePaymentFeeRate}%）`
                        : ''}
                    </span>
                    <a
                      href='/settings/paymentFee'
                      target='_blank'
                      rel='noopener noreferrer'
                      style={{
                        fontSize: 12,
                        color: '#d5728a',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 2,
                        textDecoration: 'none'
                      }}
                      title='前往「設定 → 金流手續費」修改預設費率'
                    >
                      <EditOutlined style={{ fontSize: 12 }} />
                      修改
                    </a>
                  </span>
                }
              >
                <PaymentInput>
                  <PaymentAmountInput
                    id='mobilePayment'
                    name='mobilePayment'
                    form={form}
                    onChange={(val) => {
                      const mobile = Number(val) || 0
                      const card =
                        Number(form.getFieldValue('cardPayment')) || 0

                      // 1. 自動重算手續費
                      if (paymentFeeSettings.autoCalculate) {
                        const cardFee =
                          (card * paymentFeeSettings.cardFeeRate) / 100
                        const mobileFee =
                          (mobile * paymentFeeSettings.mobilePaymentFeeRate) /
                          100
                        form.setFieldsValue({
                          paymentFee:
                            Math.round((cardFee + mobileFee) * 100) / 100
                        })
                        // 金額改變後重新自動帶入，先前的手動覆寫視為失效
                        markPaymentFeeOverridden(false)
                      }

                      // 2. 自動調整現金：保持「付款總額 = 應收金額」
                      const storedValue =
                        Number(form.getFieldValue('storedValue')) || 0
                      const transfer =
                        Number(form.getFieldValue('transfer')) || 0
                      const deposit =
                        Number(form.getFieldValue('deposit')) || 0
                      const voucher =
                        Number(form.getFieldValue('voucher')) || 0
                      const bonus = Number(form.getFieldValue('bonus')) || 0
                      const debt = Number(form.getFieldValue('debt')) || 0
                      const installment =
                        Number(form.getFieldValue('installment')) || 0
                      const nonCashSum =
                        storedValue +
                        transfer +
                        deposit +
                        voucher +
                        bonus +
                        debt +
                        installment +
                        card +
                        mobile
                      const nextCash = Math.max(
                        0,
                        (Number(totalAmount) || 0) - nonCashSum
                      )
                      form.setFieldsValue({ cash: nextCash })
                    }}
                  />
                </PaymentInput>
              </Form.Item>

              {/* === 新增：金流手續費（非客戶付款，係店家支出）=== */}
              <Form.Item
                name='paymentFee'
                label='金流手續費(將自動帶入，可自行修改)'
                extra={
                  depositCardFee > 0
                    ? `另有定金刷卡手續費 NT$${depositCardFee}，送出後由系統併入，此欄不用自己加`
                    : undefined
                }
                tooltip='刷卡／行動支付由收單行或第三方支付扣除的手續費。此為店家支出，不計入客戶付款，會在數據報表中從收入扣除以計算真實收益。'
              >
                <InputNumber
                  id='paymentFee'
                  style={{ width: '100%' }}
                  min={0}
                  prefix='NT$'
                  placeholder='依費率自動計算'
                  onChange={(val) => {
                    // 店家自己動過這個欄位（包含刻意填 0 或清空）才算覆寫；
                    // 沒動過就交給後端依費率快照重算，避免手續費被靜靜吃掉。
                    form.setFieldsValue({ paymentFee: val })
                    markPaymentFeeOverridden(true)
                  }}
                />
              </Form.Item>

              {/* === 店內自行分期（高階方案）=== */}
              {installmentEnabled && (
                <Form.Item
                  label='分期（店內自行分期）'
                  tooltip='客人分期繳款給店家。這筆錢結帳當下還沒收到，會留在「未付金額」，之後到「分期收款」頁一期一期登錄。'
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                      flexWrap: 'wrap'
                    }}
                  >
                    <Form.Item name='installment' noStyle>
                      <InputNumber
                        id='installment'
                        style={{ flex: 1, minWidth: 140 }}
                        min={0}
                        prefix='NT$'
                        placeholder='分期本金'
                        readOnly
                      />
                    </Form.Item>
                    <Button onClick={() => setInstallmentModalOpen(true)}>
                      {installmentDraft ? '修改分期' : '設定分期'}
                    </Button>
                    {installmentDraft && (
                      <Button
                        danger
                        type='text'
                        onClick={() => {
                          setInstallmentDraft(null)
                          form.setFieldsValue({ installment: 0 })
                        }}
                      >
                        清除
                      </Button>
                    )}
                  </div>
                  {installmentDraft && (
                    <div
                      style={{
                        marginTop: 8,
                        padding: '8px 10px',
                        background: '#fdf0f3',
                        border: '1px solid #f4d6de',
                        borderRadius: 8,
                        fontSize: 12,
                        lineHeight: 1.8,
                        color: '#8a5c68'
                      }}
                    >
                      分 {installmentDraft.periods} 期 ·{' '}
                      {installmentDraft.frequency === 'monthly'
                        ? '每月一期'
                        : installmentDraft.frequency === 'biweekly'
                          ? '每兩週一期'
                          : installmentDraft.frequency === 'weekly'
                            ? '每週一期'
                            : '每次來店繳'}{' '}
                      · 首期 {installmentDraft.firstDueDate}
                      {installmentDraft.downPayment > 0 && (
                        <>
                          <br />
                          今天先收 NT${' '}
                          {installmentDraft.downPayment.toLocaleString()}
                          （會記成分期的第 1 筆收款，現金欄位不用再填）
                        </>
                      )}
                      {installmentDraft.feeAmount > 0 && (
                        <>
                          <br />
                          分期手續費 NT${' '}
                          {installmentDraft.feeAmount.toLocaleString()}
                          （送出時會加進應收金額，不計入業績）
                        </>
                      )}
                    </div>
                  )}
                </Form.Item>
              )}

              <InstallmentPlanModal
                open={installmentModalOpen}
                onCancel={() => setInstallmentModalOpen(false)}
                initialValue={installmentDraft}
                suggestedPrincipal={(() => {
                  // 預設值：應收扣掉其他付款方式後還沒有著落的金額
                  const paid = [
                    'storedValue',
                    'voucher',
                    'cash',
                    'transfer',
                    'deposit',
                    'cardPayment',
                    'mobilePayment',
                    'bonus'
                  ].reduce(
                    (sum, key) => sum + (Number(form.getFieldValue(key)) || 0),
                    0
                  )
                  return Math.max(0, (Number(totalAmount) || 0) - paid)
                })()}
                onConfirm={(plan) => {
                  setInstallmentDraft(plan)
                  // 表單欄位只放本金：手續費是在送出時才加進應收的，
                  // 一起放進來會讓「付款總額 = 應收」的驗證永遠差一個手續費
                  form.setFieldsValue({ installment: plan.principal })
                  setInstallmentModalOpen(false)
                }}
              />
              {/* === END 新增 === */}
            </div>
          </PaymentRow>

          {/* 業績折扣設置（選填）- 放在付費資訊最下方，業績金額計算的上面 */}
          <RevenueDiscountContainer style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Checkbox
                checked={applyRevenueDiscount}
                onChange={(e) => {
                  setApplyRevenueDiscount(e.target.checked)
                  if (!e.target.checked) {
                    setRevenueDiscountRate(100)
                  }
                }}
                style={{ margin: 0 }}
              />
              <span style={{ fontSize: 14, color: '#666' }}>
                應用業績折扣（選填）
              </span>
            </div>
            {applyRevenueDiscount && (
              <>
                <div className='revenue-discount-inputs'>
                  <div>
                    <span
                      style={{
                        fontSize: 14,
                        color: '#999',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      折扣率：
                    </span>
                    <InputNumber
                      size='middle'
                      value={revenueDiscountRate}
                      onChange={(value) => {
                        // 直接更新狀態，不做任何限制，允許 null/undefined
                        setRevenueDiscountRate(value)
                      }}
                      style={{
                        width: 100,
                        border: 'none',
                        borderBottom: '1px solid #d9d9d9',
                        borderRadius: 0,
                        padding: '4px 0'
                      }}
                      addonAfter='%'
                      controls={false}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: 13,
                      color: '#8c4a00',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    折扣率：{revenueDiscountRate}%
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    marginTop: 10
                  }}
                >
                  <Checkbox
                    checked={includeZeroYuanInRevenue}
                    onChange={(e) =>
                      setIncludeZeroYuanInRevenue(e.target.checked)
                    }
                    style={{ margin: 0 }}
                  />
                  <span style={{ fontSize: 13, color: '#666' }}>
                    0元服務計入應用業績（不勾選則該項業績為
                    0，整單業績只分配給有收費項目）
                  </span>
                </div>
              </>
            )}
          </RevenueDiscountContainer>

          {/* 業績金額計算 - 僅在勾選「應用業績折扣」時顯示 */}
          <Form.Item noStyle shouldUpdate={() => true}>
            {({ getFieldValue }) => {
              if (!applyRevenueDiscount) return null
              const validRate =
                revenueDiscountRate !== null &&
                revenueDiscountRate !== undefined &&
                revenueDiscountRate >= 0 &&
                revenueDiscountRate <= 100
                  ? revenueDiscountRate
                  : 100
              const serviceParts = []
              let totalServiceBasis = 0
              selectedServices.forEach((s) => {
                const op = s.originalPrice ?? s.basePrice ?? (s.price || 0)
                const qty = serviceQuantities[s.key] ?? s.quantity ?? 1
                const rawPrice = s.price ?? op
                // 表單慣例：s.price 一律為「該行總價」→ 單價 = 總價 ÷ 數量
                const actualUnit =
                  rawPrice !== undefined && rawPrice !== null
                    ? qty > 0
                      ? Math.round((Number(rawPrice) / qty) * 100) / 100
                      : Number(rawPrice)
                    : op
                const isZeroYuan = actualUnit === 0
                const lineBasis =
                  isZeroYuan && !includeZeroYuanInRevenue
                    ? 0
                    : (actualUnit > 0 ? actualUnit : op) * qty
                totalServiceBasis += lineBasis
                if (lineBasis > 0) {
                  serviceParts.push(lineBasis.toLocaleString())
                }
              })
              const totalProductBasis = selectedProducts.reduce(
                (sum, p) =>
                  sum +
                  (p.includeInRevenue !== false
                    ? Number(p.totalPrice) || 0
                    : 0),
                0
              )
              const totalBasis = totalServiceBasis + totalProductBasis
              // 自動區分付款組合：折扣率只套「儲值金支付的部分」，其餘付款全額計
              const storedValuePaid = Math.max(
                0,
                Number(getFieldValue('storedValue')) || 0
              )
              const discountedPortion = Math.min(storedValuePaid, totalBasis)
              const fullPortion = totalBasis - discountedPortion
              const appliedRevenue =
                totalBasis >= 0
                  ? storedValuePaid > 0
                    ? Math.round(
                        (discountedPortion * validRate) / 100 + fullPortion
                      )
                    : Math.round((totalBasis * validRate) / 100)
                  : 0
              const formula =
                serviceParts.length > 0
                  ? serviceParts.join(' + ') +
                    (totalProductBasis > 0
                      ? ' + 商品 ' + totalProductBasis.toLocaleString()
                      : '')
                  : totalProductBasis > 0
                    ? '商品 ' + totalProductBasis.toLocaleString()
                    : '0'
              const hasZeroYuanExcluded =
                !includeZeroYuanInRevenue &&
                selectedServices.some((s) => {
                  const op = s.originalPrice ?? s.basePrice ?? (s.price || 0)
                  const qty = serviceQuantities[s.key] ?? s.quantity ?? 1
                  const raw = s.price ?? op
                  // 表單慣例：s.price 一律為「該行總價」→ 單價 = 總價 ÷ 數量
                  const unit =
                    raw !== undefined && raw !== null
                      ? qty > 0
                        ? Math.round((Number(raw) / qty) * 100) / 100
                        : Number(raw)
                      : op
                  return unit === 0
                })
              return (
                <div style={{ lineHeight: 1.8 }}>
                  <div>
                    服務業績基準：{formula} = NT$ {totalBasis.toLocaleString()}
                    {hasZeroYuanExcluded && '（0 元服務不計入）'}
                  </div>
                  <div>
                    {storedValuePaid > 0 ? (
                      <>
                        儲值金 NT$ {discountedPortion.toLocaleString()} ×{' '}
                        {validRate}%
                        {fullPortion > 0 && (
                          <>
                            {' '}
                            + 其他付款 NT$ {fullPortion.toLocaleString()} ×
                            100%
                          </>
                        )}{' '}
                        = NT$ {appliedRevenue.toLocaleString()}
                      </>
                    ) : (
                      <>
                        NT$ {totalBasis.toLocaleString()} × {validRate}% = NT${' '}
                        {appliedRevenue.toLocaleString()}
                      </>
                    )}
                  </div>
                  <div
                    style={{
                      marginTop: 6,
                      paddingTop: 6,
                      borderTop: '1px solid #bae6fd',
                      fontWeight: 600,
                      fontSize: 15,
                      color: '#1e40af'
                    }}
                  >
                    業績金額：NT$ {appliedRevenue.toLocaleString()}
                  </div>
                </div>
              )
            }}
          </Form.Item>
        </Form>
      </FormCard>

      {/* 儲值金加值 Modal */}
      <Modal
        title='儲值金加值'
        open={topupModalVisible}
        onCancel={() => {
          setTopupModalVisible(false)
          setTopupAmount(0)
          setSelectedTopupBonus(null)
          setTopupBonusOptedOut(false)
          setTopupSalesPersonnel(null)
          setTopupDiscount(100)
          setTopupPaymentMethod('由店務系統付費')
          setTopupPaymentDetails({
            cash: 0,
            bankTransfer: 0,
            accountsReceivable: 0,
            mobileCardSwipe: 0,
            cardPayment: 0,
            mobilePayment: 0
          })
          setTopupDate(tzNow())
          setTopupNote('')
          setTopupSchemeKey('')
          setTopupExpiryDate(null)
        }}
        onOk={() => {
          if (handleTopupSubmit) {
            handleTopupSubmit()
          } else {
            console.error('handleTopupSubmit 未定義！')
            message.error('系統錯誤：無法執行儲值操作')
          }
        }}
        okText='確認儲值'
        cancelText='取消'
        confirmLoading={topupLoading}
        destroyOnClose
        maskClosable={false}
        getContainer={() => document.body}
        zIndex={1000}
        width={600}
      >
        <Space direction='vertical' style={{ width: '100%' }} size='middle'>
          <div style={{ color: '#666' }}>儲值金將加到目前選擇的客戶帳戶。</div>

          {/* 顯示客戶會員等級 */}
          {customerMembershipTier && (
            <div
              style={{
                padding: '12px 16px',
                background: '#f0f9ff',
                borderRadius: 8,
                border: '1px solid #bae6fd'
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 500, color: '#0369a1' }}>
                客戶會員等級：{customerMembershipTier}
              </div>
            </div>
          )}

          {/* 儲值金額（必填） */}
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>儲值金額 *</div>
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              precision={0}
              value={topupAmount}
              onChange={(val) => {
                setTopupAmount(val || 0)
                setSelectedTopupBonus(null) // 手動輸入時清除選定的等級儲值回饋
                setTopupBonusOptedOut(false) // 手動輸入新金額時，重置不套用狀態
                // 手動輸入金額時自動匹配（optedOut 已被重置，使用本地判斷避免非同步問題）
                if (val) {
                  let matchedBonus = null
                  membershipTiers.forEach((tier) => {
                    if (tier.isEnabled && tier.topupBonuses) {
                      const bonus = tier.topupBonuses.find(
                        (b) => Number(b.amount) === Number(val)
                      )
                      if (bonus) {
                        // 如果手動輸入，且當前客戶有對應等級，優先匹配當前等級
                        if (tier.name === customerMembershipTier) {
                          matchedBonus = { ...bonus, tierName: tier.name }
                        } else if (!matchedBonus) {
                          matchedBonus = { ...bonus, tierName: tier.name }
                        }
                      }
                    }
                  })
                  if (matchedBonus) {
                    setSelectedTopupBonus(matchedBonus)
                  }
                }
              }}
              placeholder='輸入儲值金額'
              prefix='NT$'
            />
          </div>

          {/* 快速選擇按鈕（彙整所有等級的等級儲值回饋） */}
          <div>
            <div style={{ marginBottom: 8, fontSize: 13, color: '#666' }}>
              等級儲值回饋（點選後自動帶入金額）：
            </div>
            <Space wrap style={{ width: '100%' }}>
              {(() => {
                // 獲取所有等級的等級儲值回饋檔位
                const allBonuses = []
                membershipTiers.forEach((tier) => {
                  if (tier.isEnabled && tier.topupBonuses) {
                    tier.topupBonuses.forEach((bonus) => {
                      allBonuses.push({
                        ...bonus,
                        tierName: tier.name
                      })
                    })
                  }
                })

                if (allBonuses.length === 0) {
                  return (
                    <span style={{ fontSize: 12, color: '#999' }}>
                      目前未設置任何等級儲值回饋
                    </span>
                  )
                }

                // 按金額排序
                const sortedBonuses = allBonuses.sort(
                  (a, b) => Number(a.amount) - Number(b.amount)
                )

                return sortedBonuses.map((bonus, index) => {
                  const amount = Number(bonus.amount) || 0
                  const bonusAmount = Number(bonus.bonus) || 0
                  const totalAmount = amount + bonusAmount
                  // 檢查是否選中（同時匹配金額與等級，因為同金額可能在不同等級各有一組回饋檔位）
                  const isSelected =
                    selectedTopupBonus &&
                    selectedTopupBonus.amount === amount &&
                    selectedTopupBonus.tierName === bonus.tierName

                  return (
                    <Button
                      key={index}
                      type={isSelected ? 'primary' : 'default'}
                      onClick={() => {
                        setTopupAmount(amount)
                        setSelectedTopupBonus(bonus)
                        setTopupBonusOptedOut(false)
                      }}
                      style={{
                        borderRadius: 8,
                        height: 44,
                        padding: '0 12px',
                        fontSize: 13,
                        textAlign: 'left',
                        borderColor: isSelected ? undefined : '#d9d9d9',
                        boxShadow: isSelected
                          ? '0 2px 4px rgba(0,0,0,0.1)'
                          : 'none'
                      }}
                    >
                      <div style={{ lineHeight: '1.2' }}>
                        <span style={{ fontWeight: 600 }}>
                          儲 {amount.toLocaleString()} 送{' '}
                          {bonusAmount.toLocaleString()}
                        </span>
                        <br />
                        <span style={{ fontSize: 10, opacity: 0.7 }}>
                          (等級：{bonus.tierName})
                        </span>
                      </div>
                    </Button>
                  )
                })
              })()}
              {/* 不套用活動方案按鈕 */}
              <Button
                type={topupBonusOptedOut ? 'primary' : 'default'}
                danger={topupBonusOptedOut}
                onClick={() => {
                  setTopupBonusOptedOut(true)
                  setSelectedTopupBonus(null)
                }}
                style={{
                  borderRadius: 8,
                  height: 44,
                  padding: '0 12px',
                  fontSize: 13,
                }}
              >
                不套用活動方案
              </Button>
            </Space>
            {/* 顯示當前選擇之等級儲值回饋的詳細資訊 */}
            {topupAmount > 0 && (
              <div
                style={{
                  marginTop: 12,
                  padding: '10px 12px',
                  background: '#f6ffed',
                  borderRadius: 8,
                  border: '1px solid #b7eb8f',
                  fontSize: 13,
                  color: '#389e0d'
                }}
              >
                {(() => {
                  const matchedBonus = selectedTopupBonus

                  if (matchedBonus) {
                    const bonusAmount = Number(matchedBonus.bonus) || 0
                    const totalAmount = topupAmount + bonusAmount
                    // 員工主動選等級回饋方案：只看 topupUpgradeAmount（單筆門檻）
                    // upgradeAmount（累計實付）是結帳自動升等用的，不限制儲值購買升等
                    const targetTier = membershipTiers.find(
                      (t) => t.name === matchedBonus.tierName
                    )
                    const topupRule =
                      Number(targetTier?.topupUpgradeAmount) || 0
                    // topupRule=0 代表無單筆門檻限制，直接允許升等
                    const willUpgrade =
                      topupRule === 0 || Number(topupAmount) >= topupRule
                    const conditionsText = topupRule > 0
                      ? `單筆需 NT$ ${topupRule.toLocaleString()}`
                      : ''
                    return (
                      <>
                        <div style={{ fontWeight: 500 }}>
                          等級儲值回饋匹配：{matchedBonus.tierName} 優惠檔位
                        </div>
                        <div style={{ marginTop: 4 }}>
                          贈送金額：NT$ {bonusAmount.toLocaleString()}
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>
                          預計到帳：NT${' '}
                          {(Number.isFinite(totalAmount)
                            ? totalAmount
                            : 0
                          ).toLocaleString()}
                        </div>
                        {willUpgrade ? (
                          <div
                            style={{
                              marginTop: 4,
                              fontSize: 11,
                              fontStyle: 'italic',
                              opacity: 0.85
                            }}
                          >
                            * 儲值成功後，客戶等級將升等為「
                            {matchedBonus.tierName}」
                          </div>
                        ) : (
                          <div
                            style={{
                              marginTop: 6,
                              fontSize: 12,
                              color: '#d4380d',
                              lineHeight: 1.6
                            }}
                          >
                            * 本次儲值 NT$ {Number(topupAmount).toLocaleString()} 未達「{matchedBonus.tierName}」單筆升等門檻（需 NT$ {topupRule.toLocaleString()}），
                            <strong>本次儲值不會升等</strong>。
                          </div>
                        )}
                      </>
                    )
                  }
                  return (
                    <div style={{ color: '#666' }}>
                      自定義儲值金額：NT$ {topupAmount.toLocaleString()}
                      <br />
                      <span style={{ fontSize: 11 }}>
                        (未匹配到特定等級儲值回饋)
                      </span>
                    </div>
                  )
                })()}
              </div>
            )}
          </div>

          {/* 操作備註（選填） */}
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>
              操作備註（選填）
            </div>
            <Input
              value={topupNote}
              onChange={(e) => setTopupNote(e.target.value)}
              placeholder='可輸入簡短備註（非必填）'
            />
          </div>

          <Divider style={{ margin: '12px 0' }} />

          {/* 銷售人員（必填） */}
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>銷售人員 *</div>
            <ResponsiveSelect
              style={{ width: '100%' }}
              placeholder='請選擇銷售人員'
              value={topupSalesPersonnel}
              onChange={(value) => setTopupSalesPersonnel(value)}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={employees.map((emp) => ({
                value: emp._id,
                label: emp.name
              }))}
            />
          </div>

          {/* 折扣（預設 100，可調整） */}
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>
              折扣百分比（0-100，100 = 無折扣）
            </div>
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={100}
              value={topupDiscount}
              onChange={(val) => setTopupDiscount(val ?? 100)}
              placeholder='請輸入折扣百分比'
              suffix='%'
            />
            {topupAmount > 0 && (
              <div style={{ marginTop: 4, color: '#666', fontSize: 12 }}>
                實際付款金額：NT{' '}
                {Math.round(
                  topupAmount * (topupDiscount / 100)
                ).toLocaleString()}
              </div>
            )}
          </div>

          {/* 付款方式 */}
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>付款方式 *</div>
            <ResponsiveSelect
              style={{ width: '100%' }}
              placeholder='可選擇付款方式'
              value={topupPaymentMethod}
              onChange={(value) => setTopupPaymentMethod(value)}
              allowClear
              options={[
                { value: '店內行動支付', label: '店內行動支付' },
                { value: '由消費者APP付費', label: '由消費者APP付費' },
                { value: '由店務系統付費', label: '由店務系統付費' }
              ]}
            />
          </div>

          {/* 付款詳情（必填） */}
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>付款詳情 *</div>
            <Space direction='vertical' style={{ width: '100%' }} size='small'>
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={0}
                value={topupPaymentDetails.cash}
                onChange={(val) =>
                  setTopupPaymentDetails((prev) => ({
                    ...prev,
                    cash: val || 0
                  }))
                }
                placeholder='現金'
                prefix='現金：NT$'
              />
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={0}
                value={topupPaymentDetails.bankTransfer}
                onChange={(val) =>
                  setTopupPaymentDetails((prev) => ({
                    ...prev,
                    bankTransfer: val || 0
                  }))
                }
                placeholder='匯款'
                prefix='匯款：NT$'
              />
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={0}
                value={topupPaymentDetails.accountsReceivable}
                onChange={(val) =>
                  setTopupPaymentDetails((prev) => ({
                    ...prev,
                    accountsReceivable: val || 0
                  }))
                }
                placeholder='應收款'
                prefix='應收款：NT$'
              />
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={0}
                value={topupPaymentDetails.cardPayment}
                onChange={(val) =>
                  setTopupPaymentDetails((prev) => {
                    const card = val || 0
                    const mobile = prev.mobilePayment || 0
                    return {
                      ...prev,
                      cardPayment: card,
                      // 同步更新 mobileCardSwipe（向後相容）
                      mobileCardSwipe: card + mobile
                    }
                  })
                }
                placeholder='刷卡'
                prefix={`刷卡${
                  paymentFeeSettings.cardFeeRate > 0
                    ? `（手續費 ${paymentFeeSettings.cardFeeRate}%）`
                    : ''
                }：NT$`}
              />
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={0}
                value={topupPaymentDetails.mobilePayment}
                onChange={(val) =>
                  setTopupPaymentDetails((prev) => {
                    const mobile = val || 0
                    const card = prev.cardPayment || 0
                    return {
                      ...prev,
                      mobilePayment: mobile,
                      mobileCardSwipe: card + mobile
                    }
                  })
                }
                placeholder='行動支付'
                prefix={`行動支付${
                  paymentFeeSettings.mobilePaymentFeeRate > 0
                    ? `（手續費 ${paymentFeeSettings.mobilePaymentFeeRate}%）`
                    : ''
                }：NT$`}
              />
              {(topupPaymentDetails.cardPayment > 0 ||
                topupPaymentDetails.mobilePayment > 0) && (
                <div
                  style={{
                    marginTop: 4,
                    color: '#0369a1',
                    fontSize: 12,
                    background: '#f0f9ff',
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: '1px solid #bae6fd'
                  }}
                >
                  金流手續費：NT${' '}
                  {(
                    Math.round(
                      ((topupPaymentDetails.cardPayment || 0) *
                        paymentFeeSettings.cardFeeRate +
                        (topupPaymentDetails.mobilePayment || 0) *
                          paymentFeeSettings.mobilePaymentFeeRate) *
                        100
                    ) / 10000
                  ).toLocaleString()}
                  <span style={{ color: '#8c8c8c', marginLeft: 6 }}>
                    （將從營收扣除，呈現實際入帳）
                  </span>
                </div>
              )}
              {topupAmount > 0 && (
                <div style={{ marginTop: 4, color: '#666', fontSize: 12 }}>
                  付款詳情總和：NT{' '}
                  {(
                    topupPaymentDetails.cash +
                    topupPaymentDetails.bankTransfer +
                    topupPaymentDetails.accountsReceivable +
                    (topupPaymentDetails.cardPayment || 0) +
                    (topupPaymentDetails.mobilePayment || 0)
                  ).toLocaleString()}
                  {' / '}
                  應付金額：NT{' '}
                  {Math.round(
                    topupAmount * (topupDiscount / 100)
                  ).toLocaleString()}
                </div>
              )}
            </Space>
          </div>

          {/* 儲值日期（必填，預設今日） */}
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>儲值日期 *</div>
            <DatePicker
              style={{ width: '100%' }}
              value={topupDate}
              onChange={(date) => setTopupDate(date || tzNow())}
              format='YYYY-MM-DD'
              placeholder='選擇儲值日期'
            />
          </div>

          {/* 儲值活動（選填）：此筆儲值歸屬的活動／分桶，結帳時可指定從此儲值活動扣款 */}
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>
              儲值活動（選填）
            </div>
            <TopupSchemeKeyInput
              businessId={businessId}
              style={{ width: '100%' }}
              value={topupSchemeKey}
              onChange={setTopupSchemeKey}
              placeholder='可輸入或從下拉選擇曾用過的儲值活動'
              extraSchemeKeys={
                customerBalanceInfo?.balanceByScheme
                  ?.map((s) => s.schemeKey)
                  .filter(Boolean) ?? []
              }
            />
            <div style={{ marginTop: 4, fontSize: 12, color: '#888' }}>
              填寫後，結帳時可選擇從此儲值活動餘額扣款
            </div>
          </div>

          {/* 使用期限（選填） */}
          <div>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>
              使用期限（選填）
            </div>
            <DatePicker
              style={{ width: '100%' }}
              value={topupExpiryDate}
              onChange={(date) => setTopupExpiryDate(date || null)}
              format='YYYY-MM-DD'
              placeholder='選擇使用期限'
            />
          </div>
        </Space>
      </Modal>

      {/* 儲值金轉讓 Modal（僅 UI，實際轉讓需後端 API） */}
      <Modal
        title='儲值金轉讓'
        open={transferModalVisible}
        onCancel={() => {
          setTransferModalVisible(false)
          setTransferFromCustomerName('')
          setTransferFromBalance(0)
          setTransferAmount(null)
          setTransferDate(tzNow()) // 重置為今天
          if (onClearTransferSearch) {
            onClearTransferSearch()
          }
        }}
        onOk={handleTransferSubmit}
        okText='確定'
        cancelText='取消'
        confirmLoading={transferLoading}
        destroyOnClose
        maskClosable={false}
      >
        <Form layout='vertical'>
          <Form.Item
            label='轉讓客戶名稱（將會減少儲值金）'
            required
            style={{ marginBottom: 8 }}
          >
            <Input
              placeholder='請輸入轉讓客戶名稱'
              value={transferFromCustomerName}
              onChange={(e) => {
                const value = e.target.value
                if (onTransferSearch) {
                  onTransferSearch(value)
                } else {
                  setTransferFromCustomerName(value)
                }
              }}
            />
          </Form.Item>

          {transferSearching && (
            <div style={{ fontSize: 12, color: '#999', marginBottom: 4 }}>
              搜尋中...
            </div>
          )}
          {transferSearchOptions &&
            transferSearchOptions.length > 0 &&
            transferFromCustomerName && (
              <div
                style={{
                  marginBottom: 12,
                  maxHeight: 180,
                  overflowY: 'auto',
                  // 捲到底不要把動量交給整頁
                  overscrollBehavior: 'contain',
                  WebkitOverflowScrolling: 'touch',
                  border: '1px solid #f0f0f0',
                  borderRadius: 6,
                  background: '#fff'
                }}
              >
                {transferSearchOptions.map((opt) => (
                  <div
                    key={opt.id}
                    style={{
                      padding: '6px 10px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f5f5f5'
                    }}
                    onClick={() => onSelectTransferCustomer?.(opt)}
                  >
                    <div style={{ fontSize: 14, color: '#333' }}>
                      {opt.name || '未命名客戶'}
                    </div>
                    {opt.phone && (
                      <div style={{ fontSize: 12, color: '#999' }}>
                        {opt.phone}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

          <div style={{ marginBottom: 16, fontSize: 14, color: '#666' }}>
            此客戶尚餘
            <span style={{ fontWeight: 600, margin: '0 4px' }}>
              {transferFromBalance ?? 0}
            </span>
            儲值金
          </div>

          <Form.Item label='轉入金額' required style={{ marginBottom: 8 }}>
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              precision={0}
              placeholder='請輸入轉入金額'
              value={transferAmount}
              onChange={(val) => setTransferAmount(val || null)}
              prefix='NT$'
            />
          </Form.Item>

          <Form.Item label='轉讓日期' required style={{ marginBottom: 8 }}>
            <DatePicker
              style={{ width: '100%' }}
              format='YYYY-MM-DD'
              placeholder='請選擇轉讓日期'
              value={transferDate}
              onChange={(date) => setTransferDate(date || tzNow())}
            />
          </Form.Item>
        </Form>

        <div
          style={{
            marginTop: 24,
            textAlign: 'center',
            fontSize: 13,
            color: '#666',
            lineHeight: 1.6
          }}
        >
          <span style={{ fontWeight: 600 }}>
            ＊＊ 若儲值金有損失，平台將不負責任 ＊＊
          </span>
        </div>
      </Modal>
    </>
  )
}

const CheckoutPage = () => {
  const [form] = Form.useForm()
  const { user, businessPlan } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { hasFeature } = useMembership()
  const { can } = usePermission()

  /**
   * 施作照片：品牌方案 + 有 servicePhoto:manage 才出現在結帳頁。
   *
   * 結帳當下不會馬上上傳 —— 現場客（walkin）的預約是在送出訂單那一刻
   * 才建立的，這時還沒有 bookingId 可以掛。所以這裡只把檔案留在畫面上，
   * 等訂單建立成功、finalBookingId 確定之後再一次送出。
   */
  const servicePhotoEnabled =
    hasFeature('servicePhoto') && can('servicePhoto:manage')
  const [servicePhotoFiles, setServicePhotoFiles] = useState([])

  /**
   * 店內自行分期（高階方案）。
   *
   * 表單欄位 installment 只放「分期本金」，手續費另外存在 installmentDraft 裡 ——
   * 付款總額驗證是拿 totalPaid 跟應收比對，把手續費也算進 installment 的話，
   * 每一筆分期單都會被判定成溢收而送不出去。手續費是在送出時才加進應收的。
   */
  const installmentEnabled =
    hasFeature('installment') && can('installment:manage')
  const [installmentDraft, setInstallmentDraft] = useState(null)
  const [installmentModalOpen, setInstallmentModalOpen] = useState(false)

  // 所有 Hooks 必須在條件返回之前調用
  // 從 URL 參數讀取步驟，如果沒有則默認為 0
  const stepFromUrl = searchParams.get('step')
  const initialStep = useMemo(() => {
    return stepFromUrl ? parseInt(stepFromUrl, 10) : 0
  }, [stepFromUrl])
  const [currentStep, setCurrentStep] = useState(initialStep)
  const watchedStoredValue = Form.useWatch('storedValue', form) || 0
  const watchedCustomerName = Form.useWatch('customerName', form)
  // 優惠券是「付款方式」之一，手動改動時其餘付款方式必須重新分配
  //（目標：非優惠券付款總和 = 應收 − 優惠券 − 訂金），否則會出現已付 > 應收的溢收
  const watchedVoucherAmount = Number(Form.useWatch('voucher', form) || 0)

  // 當 URL 參數改變時，更新步驟（例如用戶直接修改 URL）
  useEffect(() => {
    const urlStep = stepFromUrl ? parseInt(stepFromUrl, 10) : 0
    if (urlStep !== currentStep && urlStep >= 0) {
      setCurrentStep(urlStep)
    }
  }, [stepFromUrl])

  // 當步驟改變時，更新 URL 參數
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams)
    if (currentStep > 0) {
      newSearchParams.set('step', currentStep.toString())
    } else {
      newSearchParams.delete('step')
    }
    const newUrl = `?${newSearchParams.toString()}`
    // 避免重複更新相同的 URL
    if (window.location.search !== newUrl) {
      navigate(newUrl, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep])

  // 平板/手機：為 body 加上 class，用於防止橫向滑動與版面跳動。
  // 門檻用 1024px —— iPad 直向 810–834、橫向 1024，原本的 768 把平板整個漏掉了。
  // 另外要監聽轉向，否則 iPad 一轉就失效（原本只在掛載當下判斷一次）。
  useEffect(() => {
    if (typeof window === 'undefined') return
    const sync = () => {
      document.body.classList.toggle(
        'checkout-page-mobile',
        window.innerWidth <= 1024
      )
    }
    sync()
    window.addEventListener('resize', sync)
    window.addEventListener('orientationchange', sync)
    return () => {
      window.removeEventListener('resize', sync)
      window.removeEventListener('orientationchange', sync)
      document.body.classList.remove('checkout-page-mobile')
    }
  }, [])

  // 若基礎資料遲遲未回傳，2.5 秒後仍顯示頁面，避免永遠卡在 loading（例如無服務/人員的店家）
  useEffect(() => {
    const t = setTimeout(() => setInitialLoadTimeout(true), 2500)
    return () => clearTimeout(t)
  }, [])

  const [customerMode, setCustomerMode] = useState('quick') // 'quick' or 'full'
  const [employees, setEmployees] = useState([])
  const [services, setServices] = useState([])
  // 預設帶入員工：登入者本人（若在員工清單中，employee._id === user.userId）優先，
  // 否則第一位（老闆等非員工帳號登入時維持原行為）
  const getDefaultEmployee = useCallback(() => {
    if (!employees || employees.length === 0) return null
    const self = employees.find(
      (e) => String(e._id) === String(user?.userId || '')
    )
    return self || employees[0]
  }, [employees, user?.userId])
  const [additionalServices, setAdditionalServices] = useState([]) // 加選服務（不在主服務列表，需單獨取得名稱）
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [savingDraft, setSavingDraft] = useState(false)
  const [selectedServices, setSelectedServices] = useState([])
  const [inventoryProducts, setInventoryProducts] = useState([]) // 庫存商品列表
  const [inventoryCategories, setInventoryCategories] = useState([]) // 庫存商品分類
  const [selectedProducts, setSelectedProducts] = useState([]) // 已選擇的商品
  // 展開成完整編輯卡片的耗材 key；其餘只顯示摘要列
  const [expandedConsumables, setExpandedConsumables] = useState([])
  // 已選擇要購買的堂票（每筆代表一種範本買 N 張）
  // 結構：{ key, templateId, templateName, price, quantity, totalCount,
  //        validityMode, validityDays, validUntil, validUntilDate }
  const [selectedClassTickets, setSelectedClassTickets] = useState([])
  // 可選的堂票範本列表（從後端載入）
  const [classTicketTemplates, setClassTicketTemplates] = useState([])
  /**
   * 堂票折抵。
   *
   * usableClassTickets：這位客人現在扣得動的票（未作廢、未過期、還有剩）。
   * ticketRedemptions：serviceKey -> { ticketId, ticketCode, templateName, count, prevPrice }
   *
   * 折抵的做法是把該筆服務的 price 直接設成 0，讓小計、應收、業績分配全部
   * 沿用既有邏輯，不用在十幾個地方各補一次判斷。prevPrice 留著是為了取消
   * 折抵時把價格還原。收入不重複認列的理由見後端 checkoutController 的註解。
   */
  const [usableClassTickets, setUsableClassTickets] = useState([])
  const [ticketRedemptions, setTicketRedemptions] = useState({})
  // 商品數量輸入暫存（productKey -> value），未 blur 時尚未寫回 selectedProducts，提交時需一併讀取
  const [productQuantityInputs, setProductQuantityInputs] = useState({})
  // 服務數量狀態（serviceKey -> quantity）
  const [serviceQuantities, setServiceQuantities] = useState({})
  // 預約人數
  const [participantCount, setParticipantCount] = useState(1)
  const [bookingData, setBookingData] = useState(null) // 預約數據
  const [loadingBooking, setLoadingBooking] = useState(false) // 載入預約數據中
  const [loadingOrder, setLoadingOrder] = useState(false) // 載入訂單詳情中（重新結帳用）
  const [loadingDraft, setLoadingDraft] = useState(false) // 載入草稿中（繼續結帳用）
  const [initialLoadTimeout, setInitialLoadTimeout] = useState(false) // 避免無資料時永遠卡在 loading
  const [recheckoutLoadedTotal, setRecheckoutLoadedTotal] = useState(null) // 重新結帳時由訂單載入的總額（state 未更新前用於顯示，與上方總計一致）
  const fetchedBookingIdRef = useRef(null) // 使用 ref 追蹤已獲取的 bookingId，避免重複調用
  const sectionServicesRef = useRef(null) // 選擇服務區塊，選完顧客後滾動至此
  const sectionDateTimeRef = useRef(null)
  const stepTwoContentShownRef = useRef(false) // 步驟二是否已顯示過（避免 remount 時重複 loading）
  // 客戶搜尋/自動帶入
  const [selectedCustomerId, setSelectedCustomerId] = useState(null)
  const [customerOptions, setCustomerOptions] = useState([])
  const [searching, setSearching] = useState(false)
  const [showCustomerList, setShowCustomerList] = useState(false)
  const [showPhoneCustomerList, setShowPhoneCustomerList] = useState(false)
  const [isComposing, setIsComposing] = useState(false)
  const [customerNameValue, setCustomerNameValue] = useState('')
  const [customerBalance, setCustomerBalance] = useState(null) // 客戶儲值金餘額
  const [customerBalanceInfo, setCustomerBalanceInfo] = useState(null) // 客戶儲值金詳細信息（包括業績折扣率）
  const [loadingBalance, setLoadingBalance] = useState(false) // 載入餘額中
  const [checkoutStoreCoupons, setCheckoutStoreCoupons] = useState([])
  const [loadingCheckoutCoupons, setLoadingCheckoutCoupons] = useState(false)
  const [appliedStoreCouponId, setAppliedStoreCouponId] = useState(null)
  const appliedStoreCouponIdRef = useRef(null)
  const [pendingCheckoutCouponId, setPendingCheckoutCouponId] = useState(null)
  const [checkoutCouponAudienceHint, setCheckoutCouponAudienceHint] =
    useState('')
  useEffect(() => {
    appliedStoreCouponIdRef.current = appliedStoreCouponId
  }, [appliedStoreCouponId])

  const [orderSuccess, setOrderSuccess] = useState(false) // 訂單成功狀態
  const [orderData, setOrderData] = useState(null) // 訂單數據
  const [topupModalVisible, setTopupModalVisible] = useState(false)
  const [topupConsent, setTopupConsent] = useState({ open: false, variables: {} })
  const [checkoutConsent, setCheckoutConsent] = useState({
    open: false,
    subjectRef: null,
    autoSent: false, // 後端是否已自動發送購買同意書給顧客
    classTicketTemplateIds: '', // 逗號串；同意書限定堂票時用來過濾
    variables: {}
  })
  const [topupAmount, setTopupAmount] = useState(0)
  const [selectedTopupBonus, setSelectedTopupBonus] = useState(null)
  const [topupBonusOptedOut, setTopupBonusOptedOut] = useState(false) // 使用者主動選擇不套用活動方案
  const [topupLoading, setTopupLoading] = useState(false)
  // 真正送出儲值請求期間為 true（同步可讀，不受 closure 影響），擋掉重複送出
  const topupSubmittingRef = useRef(false)
  const [topupSalesPersonnel, setTopupSalesPersonnel] = useState(null) // 銷售人員 ID
  // 開啟儲值視窗時，銷售人員預設帶入登入者本人（若在員工清單中；未選過才帶）
  useEffect(() => {
    if (!topupModalVisible || topupSalesPersonnel) return
    const self = employees.find(
      (e) => String(e._id) === String(user?.userId || '')
    )
    if (self) setTopupSalesPersonnel(self._id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topupModalVisible, employees])
  const [topupDiscount, setTopupDiscount] = useState(100) // 折扣百分比（預設 100 = 無折扣）
  const [topupPaymentMethod, setTopupPaymentMethod] = useState('由店務系統付費') // 付款方式
  const [topupPaymentDetails, setTopupPaymentDetails] = useState({
    cash: 0,
    bankTransfer: 0,
    accountsReceivable: 0,
    mobileCardSwipe: 0, // 向後相容：保留為 cardPayment+mobilePayment 的合計
    cardPayment: 0,
    mobilePayment: 0
  }) // 付款詳情
  const [topupDate, setTopupDate] = useState(tzNow()) // 儲值日期（預設今天）
  const [topupNote, setTopupNote] = useState('') // 操作備註
  const [topupSchemeKey, setTopupSchemeKey] = useState('') // 儲值活動名稱（選填，結帳時可指定從此儲值活動扣款）
  const [topupExpiryDate, setTopupExpiryDate] = useState(null) // 使用期限（選填）
  // 儲值金轉讓（目前僅前端 UI，實際轉讓需後端 API 支援）
  const [transferModalVisible, setTransferModalVisible] = useState(false)
  const [transferFromCustomerName, setTransferFromCustomerName] = useState('')
  const [transferFromCustomerId, setTransferFromCustomerId] = useState(null)
  const [transferFromBalance, setTransferFromBalance] = useState(0)
  const [transferAmount, setTransferAmount] = useState(null)
  const [transferDate, setTransferDate] = useState(tzNow()) // 轉讓日期（預設今天）
  const [transferLoading, setTransferLoading] = useState(false)
  const [transferSearchOptions, setTransferSearchOptions] = useState([])
  const [transferSearching, setTransferSearching] = useState(false)
  const transferSearchTimeoutRef = useRef(null)
  const searchTimeoutRef = useRef(null)
  const [discountAmount, setDiscountAmount] = useState(0) // 折扣金額（已套用）
  const [tempDiscountAmount, setTempDiscountAmount] = useState(0) // 臨時折扣金額（輸入中）
  const discountInputRef = useRef(null) // 折扣輸入框引用
  const [discountPercentage, setDiscountPercentage] = useState(0) // 折扣趴數（已套用）
  const [tempDiscountPercentage, setTempDiscountPercentage] = useState(0) // 臨時折扣趴數（輸入中）
  // 業績折扣相關狀態
  const [applyRevenueDiscount, setApplyRevenueDiscount] = useState(false) // 是否應用業績折扣
  const [revenueDiscountRate, setRevenueDiscountRate] = useState(100) // 業績折扣率（0-100，默認100表示無折扣）
  const [includeZeroYuanInRevenue, setIncludeZeroYuanInRevenue] = useState(true) // 0元服務是否計入應用業績（true=按原價×數量參與分配，false=該項業績0，整單業績只分給有收費項目）
  // 新增客戶相關狀態
  const [addCustomerModalVisible, setAddCustomerModalVisible] = useState(false)
  const [addCustomerForm] = Form.useForm()
  const [addCustomerLoading, setAddCustomerLoading] = useState(false)
  // 服務抽成規則相關狀態
  const [serviceCommissionRules, setServiceCommissionRules] = useState({}) // 存儲每個服務的抽成規則
  // 會員等級相關狀態
  const [membershipTiers, setMembershipTiers] = useState([]) // 會員等級列表
  // 金流手續費設定（結帳時用來自動計算手續費、以及是否向客人收服務費）
  // 一律經過 resolvePaymentFeeSettings，確保欄位齊全且加收服務費預設關閉
  const [paymentFeeSettings, setPaymentFeeSettings] = useState(() =>
    resolvePaymentFeeSettings({})
  )
  // 手續費是否由店家手動指定。用 ref 而非 state：它只在送出時讀取，
  // 不需要觸發重繪（每次重繪都會讓整個結帳表單重算，代價不小）。
  const paymentFeeOverriddenRef = useRef(false)
  const markPaymentFeeOverridden = useCallback((overridden) => {
    paymentFeeOverriddenRef.current = !!overridden
  }, [])
  const [customerMembershipTier, setCustomerMembershipTier] = useState(null) // 客戶會員等級
  const [customerTierAccumulated, setCustomerTierAccumulated] = useState(0) // 客戶累計實付金額（用於儲值頁判斷升等資格）
  const [membershipDiscountRate, setMembershipDiscountRate] = useState(100) // 會員折扣率（默認100，無折扣）
  const [applyMembershipDiscount, setApplyMembershipDiscount] = useState(true) // 是否應用會員折扣（默認應用）

  // 獲取預約 ID、訂單 ID、草稿 ID（從 URL 參數或 location.state）
  const bookingId =
    searchParams.get('bookingId') || location.state?.bookingId || null
  const orderIdFromUrl =
    searchParams.get('orderId') || location.state?.orderId || null
  const draftIdFromUrl = searchParams.get('draftId') || null
  const isRecheckout = searchParams.get('recheckout') === 'true'
  const checkoutMode = bookingId ? 'booking' : 'walkin' // 'booking' 預約結帳, 'walkin' 現場結帳

  // 預約已收的定金：這筆錢客人早就付過了，屬於「已付款項」。
  // 自動填款的 effect 必須保留它（先從應收扣掉再分配其餘金額），
  // 不能像其他付款方式一樣歸零，否則結帳會把定金重複跟客人收一次。
  const paidDepositAmount =
    checkoutMode === 'booking' &&
    bookingData?.depositStatus === 'paid' &&
    Number(bookingData?.depositAmount) > 0
      ? Number(bookingData.depositAmount)
      : 0
  const fetchedOrderIdRef = useRef(null) // 追蹤已載入的 orderId，避免重複請求
  const fetchedDraftIdRef = useRef(null) // 追蹤已載入的 draftId，避免重複請求

  // 商品銷售人員必選：未選時自動帶入登入者本人（非員工帳號則帶第一位）
  useEffect(() => {
    if (selectedProducts.length === 0 || employees.length === 0) return
    const firstPerson = getDefaultEmployee()
    if (!firstPerson || !firstPerson._id) return
    const needsUpdate = selectedProducts.some((p) => !p.productSalesPersonnelId)
    if (!needsUpdate) return
    setSelectedProducts((prev) =>
      prev.map((p) =>
        p.productSalesPersonnelId
          ? p
          : {
              ...p,
              productSalesPersonnelId: firstPerson._id,
              productSalesPersonnelName: firstPerson.name || null
            }
      )
    )
  }, [selectedProducts.length, employees])

  // 服務搭配的耗材自動帶入：偵測 selectedServices 變化，
  // 新增的服務 → 呼叫後端取得 consumables 並追加到 selectedProducts；
  // 移除的服務 → 從 selectedProducts 中刪掉對應的 consumable 行。
  // 用 ref 記住「已處理過的 serviceId」，這樣使用者後續手動編輯耗材列（改數量、勾選業績）
  // 都不會在下一次 effect 觸發時被覆蓋。
  const processedConsumableServiceIdsRef = useRef(new Set())
  useEffect(() => {
    if (!user?.businessId) return
    const currentServiceIds = (selectedServices || [])
      .map((s) => s.serviceId)
      .filter(Boolean)
    const currentSet = new Set(currentServiceIds.map(String))
    const processed = processedConsumableServiceIdsRef.current

    // 找出新增的（要 fetch consumables 的）服務
    const addedIds = currentServiceIds.filter((id) => !processed.has(String(id)))
    // 找出已被移除的服務 → 移除對應的耗材列
    const removedIds = []
    processed.forEach((id) => {
      if (!currentSet.has(id)) removedIds.push(id)
    })

    if (removedIds.length > 0) {
      setSelectedProducts((prev) =>
        prev.filter(
          (p) =>
            !(
              p.isConsumable &&
              p.sourceServiceId &&
              removedIds.includes(String(p.sourceServiceId))
            )
        )
      )
      removedIds.forEach((id) => processed.delete(id))
    }

    if (addedIds.length === 0) return

    let cancelled = false
    ;(async () => {
      try {
        const res = await resolveServiceConsumables(user.businessId, addedIds)
        if (cancelled) return
        const items = res?.data?.items || []
        if (items.length === 0) {
          addedIds.forEach((id) => processed.add(String(id)))
          return
        }
        const newRows = items
          .filter((it) => !it.isMissing && it.isActive)
          .map((it, idx) => {
            // 服務耗材支援小數量（例如 0.03、0.5），不可 Math.floor，
            // 否則 0.03 會被取整成 0、再 max 成 1，造成庫存與成本錯算。
            // 與後端 utils/inventoryQty.js 的 normalizeQty 一致：
            //   - 非數字 / NaN → 退回 1
            //   - 小於 0.01 → 拉到 0.01
            //   - 其他 → round 到 2 位小數，避免浮點累積誤差
            const rawQty = Number(it.quantity)
            const qty = Number.isFinite(rawQty)
              ? Math.round(Math.max(0.01, rawQty) * 100) / 100
              : 1
            const defaultCount = Boolean(it.defaultCountAsSales)
            // 不加入業績時：unit/total price 設 0（與後端 countAsSales=false 行為一致）
            const unitPrice = defaultCount
              ? Number(it.currentSuggestedPrice ?? it.currentPrice ?? 0)
              : 0
            return {
              key: `consumable-${it.serviceId}-${it.productId}-${Date.now()}-${idx}`,
              productId: it.productId,
              name: it.productName || '耗材',
              categoryId: null,
              categoryName: '-',
              unitPrice,
              quantity: qty,
              // round 到 2 位小數避免 0.03 * 290 之類產生 8.700000000001
              totalPrice: Math.round(unitPrice * qty * 100) / 100,
              manufacturer: undefined,
              capacity: undefined,
              stock: it.currentStock,
              lowStockThreshold: 10,
              unit: '件',
              productSalesPersonnelId: null,
              productSalesPersonnelName: null,
              // includeInRevenue 是現有 UI 控制業績計算的旗標；同步給 countAsSales
              includeInRevenue: defaultCount,
              // 新欄位：標示此列為「服務搭配的耗材」、來自哪個服務
              isConsumable: true,
              countAsSales: defaultCount,
              sourceServiceId: it.serviceId,
              sourceServiceName: it.serviceName,
              consumableNote: it.note || ''
            }
          })
        if (newRows.length > 0) {
          setSelectedProducts((prev) => [...prev, ...newRows])
        }
        addedIds.forEach((id) => processed.add(String(id)))
      } catch (err) {
        console.error('取得服務耗材失敗:', err)
      }
    })()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // 用 IDs 的 join 字串做依賴，避免每次 selectedServices 物件 reference 變動就重跑
    (selectedServices || []).map((s) => s.serviceId).join(','),
    user?.businessId
  ])

  // 計算服務總額（service.price 已經是總價，已考慮數量，不需要再乘以數量）
  const totalServiceAmount = selectedServices.reduce((sum, item) => {
    // service.price 已經是總價（已考慮數量），直接使用
    const servicePrice = Number(item.price) || 0
    return sum + servicePrice
  }, 0)

  const totalProductAmount = selectedProducts.reduce(
    (sum, item) => sum + (Number(item.totalPrice) || 0),
    0
  )
  const totalProductRevenueBasis = selectedProducts.reduce(
    (sum, item) =>
      sum +
      (item.includeInRevenue !== false ? Number(item.totalPrice) || 0 : 0),
    0
  )
  // 堂票購買金額（不分配為設計師業績；與商品總額並列為訂單品項之一）
  const totalClassTicketAmount = selectedClassTickets.reduce(
    (sum, t) => sum + (Number(t.price) || 0) * (Number(t.quantity) || 0),
    0
  )
  const totalRevenueBasis = totalServiceAmount + totalProductRevenueBasis
  const subtotalAmount =
    totalServiceAmount + totalProductAmount + totalClassTicketAmount // 小計 = 服務 + 商品 + 堂票
  // 計算會員折扣金額（僅用於顯示，實際折扣已應用在服務價格計算中）
  const membershipDiscountAmount =
    membershipDiscountRate < 100
      ? Math.round((subtotalAmount * (100 - membershipDiscountRate)) / 100)
      : 0
  // 如果有折扣趴數，優先使用折扣趴數計算折扣金額
  // 折扣趴數 = 「折抵幾 %」，與 App 端 calcDiscountAmount 的 percent 模式一致。
  // 例：輸入 10 → 折抵 10%（打 9 折）；折扣金額 = 原價 × 折扣趴數 / 100
  const calculatedDiscountFromPercentage =
    discountPercentage > 0 && discountPercentage <= 100
      ? Math.round((subtotalAmount * discountPercentage) / 100)
      : 0
  const finalDiscountAmount =
    discountPercentage > 0 && discountPercentage <= 100
      ? calculatedDiscountFromPercentage
      : discountAmount
  // 應收 = 小計 − 手動折扣（會員折扣已在服務價格計算中考慮）。
  // 優惠券視同現金、屬於付款方式，不從應收扣除；它與其餘付款方式一起湊滿應收。
  const totalAmount = Math.max(0, subtotalAmount - finalDiscountAmount)
  // 總付款金額（即應收金額）= 所有服務 + 所有商品的總和；重新結帳時 state 未更新前用 recheckoutLoadedTotal，否則用 totalAmount
  const formTotalPayment = Number(Form.useWatch('totalPayment', form)) || 0
  const displayTotalAmount =
    totalAmount > 0
      ? totalAmount
      : recheckoutLoadedTotal != null && Number.isFinite(recheckoutLoadedTotal)
        ? recheckoutLoadedTotal
        : Number.isFinite(formTotalPayment)
          ? formTotalPayment
          : 0
  const [paymentMethods, setPaymentMethods] = useState({
    storedValue: 0, // 儲值金
    receivable: totalAmount, // 應收款
    voucher: 0, // 優惠券
    cash: 0, // 現金
    transfer: 0, // 匯款
    deposit: 0, // 押金
    cardPayment: 0, // 刷卡（新增）
    mobilePayment: 0, // 行動支付（新增）
    paymentFee: 0 // 金流手續費（新增）
  })

  // 定義函數（需要在 useEffect 之前定義）
  const fetchEmployees = async () => {
    if (!user?.businessId) return
    try {
      const response = await api.get(`/api/employees/${user.businessId}`)
      setEmployees(response.data || [])
    } catch (error) {
      console.error('獲取員工列表失敗:', error)
    }
  }

  const fetchServices = async () => {
    if (!user?.businessId) return
    try {
      const response = await api.get(
        `/api/business_B/${user.businessId}/products`
      )
      setServices(response.data || [])
    } catch (error) {
      console.error('獲取服務列表失敗:', error)
    }
  }

  const fetchAdditionalServices = async () => {
    if (!user?.businessId) return
    try {
      const response = await api.get(
        `/api/business_B/${user.businessId}/additional-services`
      )
      setAdditionalServices(response.data?.data || response.data || [])
    } catch (error) {
      console.error('獲取加選服務列表失敗:', error)
      setAdditionalServices([])
    }
  }

  /** 依 ID 取得服務（主服務或加選服務），用於顯示名稱、價格等 */
  const getServiceOrAdditionalById = useCallback(
    (serviceId) => {
      if (!serviceId) return null
      const fromMain = services.find((s) => s._id === serviceId)
      if (fromMain) return fromMain
      return additionalServices.find((s) => s._id === serviceId) || null
    },
    [services, additionalServices]
  )

  const fetchCategories = async () => {
    if (!user?.businessId) return
    try {
      const response = await api.get(
        `/api/business_B/${user.businessId}/categories`
      )
      setCategories(response.data || [])
    } catch (error) {
      console.error('獲取類別列表失敗:', error)
    }
  }

  // 獲取服務抽成規則列表
  const fetchServiceCommissionRules = async () => {
    if (!user?.businessId) return
    try {
      const response = await api.get(
        `/api/businesses/${user.businessId}/services/commission-rules`
      )
      const rules = response.data?.data?.rules || response.data?.rules || []
      const map = {}
      rules.forEach((rule) => {
        if (!rule) return
        const key =
          rule.serviceId ||
          rule.service_id ||
          rule.service?._id ||
          rule.service?.serviceId ||
          rule._id
        if (key) {
          map[key] = {
            ...rule,
            supportDoseTypes:
              rule.supportDoseTypes ??
              (rule.doseCommissions?.length ? true : undefined)
          }
        } else {
          console.info('[DoseDebug][commission rule no key]', rule)
        }
      })
      setServiceCommissionRules(map)
    } catch (error) {
      console.error('取得服務抽成規則列表失敗', error)
      // 不阻斷主流程
    }
  }

  // 獲取庫存商品列表
  const fetchInventoryProducts = async () => {
    if (!user?.businessId) return
    try {
      const response = await getInventoryProducts({
        page: 1,
        pageSize: 1000,
        status: 'active' // 只獲取啟用的商品
      })
      const productsData =
        response.data?.data?.items || response.data?.items || []
      setInventoryProducts(productsData)
    } catch (error) {
      console.error('獲取庫存商品列表失敗:', error)
      setInventoryProducts([])
    }
  }

  // 獲取可購買的堂票範本
  const fetchClassTicketTemplates = async () => {
    if (!user?.businessId) return
    try {
      const response = await api.get(
        `/api/businesses/${user.businessId}/class-tickets/templates`,
        { params: { includeInactive: '0' } }
      )
      setClassTicketTemplates(response?.data?.data || [])
    } catch (error) {
      console.error('獲取堂票範本失敗:', error)
      setClassTicketTemplates([])
    }
  }

  // 載入這位客人「現在扣得動」的堂票（換客人就重抓，並清掉上一位的折抵）
  useEffect(() => {
    if (!user?.businessId || !selectedCustomerId) {
      setUsableClassTickets([])
      setTicketRedemptions({})
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await api.get(
          `/api/businesses/${user.businessId}/class-tickets/by-customer/${selectedCustomerId}`,
          { params: { usableOnly: 1 } }
        )
        if (!cancelled) setUsableClassTickets(res?.data?.data || [])
      } catch (error) {
        // 非品牌方案會回 403，屬正常情況，不要在畫面上報錯
        if (!cancelled) setUsableClassTickets([])
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.businessId, selectedCustomerId])

  // 服務被移除時，連同它的折抵一起清掉，避免送出孤兒的 classTicketUsages
  useEffect(() => {
    setTicketRedemptions((prev) => {
      const keys = new Set((selectedServices || []).map((s) => s.key))
      const next = {}
      let changed = false
      Object.entries(prev).forEach(([k, v]) => {
        if (keys.has(k)) next[k] = v
        else changed = true
      })
      return changed ? next : prev
    })
  }, [selectedServices])

  // 這張票適不適用這個服務：範本沒限定服務（空陣列）就是不限
  const isTicketApplicableToService = useCallback((ticket, serviceId) => {
    const scope = ticket?.applicableServiceIds || []
    if (!scope.length) return true
    return scope.map(String).includes(String(serviceId))
  }, [])

  /**
   * 勾選／取消堂票折抵。
   * ticketId 傳空值 = 取消，會把服務價格還原成折抵前的金額。
   */
  const applyTicketRedemption = useCallback(
    (serviceKey, ticketId) => {
      const existing = ticketRedemptions[serviceKey]
      if (!ticketId) {
        if (!existing) return
        setSelectedServices((list) =>
          list.map((s) =>
            s.key === serviceKey ? { ...s, price: existing.prevPrice } : s
          )
        )
        setTicketRedemptions((prev) => {
          const next = { ...prev }
          delete next[serviceKey]
          return next
        })
        return
      }
      const ticket = usableClassTickets.find(
        (t) => String(t._id) === String(ticketId)
      )
      const svc = (selectedServices || []).find((s) => s.key === serviceKey)
      if (!ticket || !svc) return
      // 換票時保留最早那筆 prevPrice，否則第二次換票會把 0 記成原價
      const prevPrice = existing
        ? existing.prevPrice
        : Number(svc.price) || 0
      setSelectedServices((list) =>
        list.map((s) => (s.key === serviceKey ? { ...s, price: 0 } : s))
      )
      setTicketRedemptions((prev) => ({
        ...prev,
        [serviceKey]: {
          ticketId: String(ticket._id),
          ticketCode: ticket.ticketCode,
          templateName: ticket.templateName,
          count: 1,
          prevPrice
        }
      }))
    },
    [ticketRedemptions, usableClassTickets, selectedServices]
  )

  // 獲取庫存商品分類
  const fetchInventoryCategories = async () => {
    if (!user?.businessId) return
    try {
      const response = await api.get('/api/store/categories')
      let categoriesData = []
      if (Array.isArray(response.data?.data)) {
        categoriesData = response.data.data
      } else if (Array.isArray(response.data)) {
        categoriesData = response.data
      }
      setInventoryCategories(categoriesData)
    } catch (error) {
      console.error('獲取庫存商品分類失敗:', error)
      setInventoryCategories([])
    }
  }

  // 獲取預約數據（需要在 useEffect 之前定義）
  // options.skipFormUpdate: 重新結帳時只設 bookingData，不覆寫表單（表單已由訂單帶入）
  const fetchBookingData = async (id, options = {}) => {
    if (!hasFeature('checkout') || !id || fetchedBookingIdRef.current === id)
      return // 如果沒有權限、沒有ID或已獲取該ID的數據，不執行
    setLoadingBooking(true)
    fetchedBookingIdRef.current = id // 標記為已獲取
    try {
      const response = await api.get(`/api/business/bookings/${id}`)
      // 處理不同的響應格式
      const rawBooking = response.data?.data || response.data
      const servicesList = normalizeBookingServices(rawBooking)
      const displayName = getBookingDisplayName(rawBooking)
      const booking = {
        ...rawBooking,
        services: servicesList,
        productId: servicesList[0]?.serviceId || rawBooking.productId,
        productName: displayName,
        displayName
      }
      setBookingData(booking)

      if (options.skipFormUpdate) {
        setLoadingBooking(false)
        return
      }

      // 自動填充預約人數
      if (booking.participantCount) {
        setParticipantCount(booking.participantCount)
        // 設置表單值
        setTimeout(() => {
          try {
            form.setFieldsValue({
              participantCount: booking.participantCount
            })
          } catch (error) {
            console.warn('Form not ready yet:', error)
          }
        }, 100)
      }

      // 自動填充服務數量
      if (booking.services && Array.isArray(booking.services)) {
        const initialQuantities = {}
        booking.services.forEach((service) => {
          const serviceId = service.serviceId || service._id
          const quantity = service.quantity || 1
          // 需要找到對應的服務key來設置數量
          // 這裡先設置，後續在設置selectedServices時會使用
          if (serviceId) {
            initialQuantities[serviceId] = quantity
          }
        })
        // 先設置初始數量，後續在設置selectedServices時會更新
        setServiceQuantities(initialQuantities)
      }

      // 自動帶入定金：預約已收定金時預填定金欄位。
      // 不依賴 booking.userName（沒有客戶姓名的預約一樣要帶），
      // 也不需要等表單其他欄位就緒；真正的分配由自動填款的 effect
      // 依 paidDepositAmount 負責，這裡只是讓欄位盡早顯示。
      if (booking.depositStatus === 'paid' && Number(booking.depositAmount) > 0) {
        try {
          form.setFieldsValue({ deposit: Number(booking.depositAmount) })
        } catch (error) {
          console.warn('帶入定金失敗:', error)
        }
      }

      // 自動填充客戶信息
      if (booking.userName) {
        setCustomerNameValue(booking.userName)
        // 使用 setTimeout 確保 Form 已經渲染完成
        // 增加延遲時間，確保 Form 已經完全初始化
        setTimeout(() => {
          try {
            form.setFieldsValue({
              customerName: booking.userName,
              phone: booking.phone || '',
              servicePersonnel: booking.stylistId || null,
              customerType: booking.customerType || 'existing',
              customerSource: booking.customerSource || 'none'
            })
          } catch (error) {
            // 如果 Form 還沒有準備好，忽略錯誤
            console.warn('Form not ready yet:', error)
          }
        }, 100)

        // 參考現場結帳的方式：通過搜索API獲取正確的客戶ID
        // 使用客戶姓名或電話搜索，確保獲取到正確的客戶ID
        const searchTerm = booking.userName || booking.phone || ''
        if (searchTerm) {
          try {
            const searchResponse = await api.get(
              `/api/business_B/${user.businessId}/customers/search`,
              { params: { searchTerm } }
            )
            if (
              searchResponse.data?.success &&
              searchResponse.data.data?.length > 0
            ) {
              // 找到匹配的客戶（優先匹配電話，如果電話相同則使用第一個）
              const customers = searchResponse.data.data || []
              let foundCustomer = null

              // 如果有電話，優先匹配電話
              if (booking.phone) {
                foundCustomer = customers.find(
                  (c) =>
                    (c.phone || c.userInfo?.phone) === booking.phone ||
                    (c.phone || c.userInfo?.phone)?.replace(/\s/g, '') ===
                      booking.phone?.replace(/\s/g, '')
                )
              }

              // 如果沒找到或沒有電話，使用第一個匹配的客戶
              if (!foundCustomer && customers.length > 0) {
                foundCustomer = customers[0]
              }

              if (foundCustomer) {
                // 使用與現場結帳相同的方式獲取客戶ID
                const customerId =
                  foundCustomer._id || foundCustomer.userInfo?._id || null
                if (customerId) {
                  setSelectedCustomerId(customerId)
                  // 獲取客戶儲值金餘額和會員等級
                  await fetchCustomerBalance(customerId)
                  if (membershipTiers.length > 0) {
                    await fetchCustomerMembershipTier(customerId)
                  }
                }
              }
            }
          } catch (error) {
            console.error('搜索客戶失敗:', error)
            // 如果搜索失敗，嘗試使用預約數據中的ID作為備用方案
            const fallbackCustomerId = booking.customerId || booking.userId
            if (fallbackCustomerId) {
              setSelectedCustomerId(fallbackCustomerId)
              await fetchCustomerBalance(fallbackCustomerId)
              if (membershipTiers.length > 0) {
                await fetchCustomerMembershipTier(fallbackCustomerId)
              }
            }
          }
        } else {
          // 如果沒有搜索條件，嘗試使用預約數據中的ID
          const fallbackCustomerId = booking.customerId || booking.userId
          if (fallbackCustomerId) {
            setSelectedCustomerId(fallbackCustomerId)
            await fetchCustomerBalance(fallbackCustomerId)
            if (membershipTiers.length > 0) {
              await fetchCustomerMembershipTier(fallbackCustomerId)
            }
          }
        }
      }

      // 自動填充服務信息
      let allServices = []

      if (
        booking.services &&
        Array.isArray(booking.services) &&
        booking.services.length > 0
      ) {
        const hasBookingAdditional =
          (booking.additionalServices?.length > 0) ||
          (Number(booking.additionalServicesPrice) > 0)
        const mainRowCount = booking.services.filter(
          (s) => s.category !== 'additional'
        ).length

        const bookingServices = booking.services.map((service, index) => {
          const fullService = services.find((s) => s._id === service.serviceId)
          const category = categories.find(
            (c) => c._id === service.category || c._id === fullService?.category
          )
          const personnel = employees.find((e) => e._id === booking.stylistId)

          // 原始價格：優先使用 basePrice，其次使用服務的 price
          const originalPrice = service.basePrice || fullService?.price || 0
          // 實際價格：如果有 actualPrice 使用 actualPrice，否則使用原始價格
          let actualPrice =
            service.actualPrice || service.basePrice || fullService?.price || 0
          // 後端／列表 state 曾把整單 discountedPrice 誤塞成主行 actualPrice，與加選並列時需還原為主服務份額
          if (
            hasBookingAdditional &&
            mainRowCount === 1 &&
            service.category !== 'additional' &&
            booking.discountedPrice != null
          ) {
            const base = originalPrice
            const rawAct =
              service.actualPrice != null
                ? Number(service.actualPrice)
                : Number(actualPrice)
            const disc = Number(booking.discountedPrice)
            if (
              base > 0 &&
              Math.round(rawAct) === Math.round(disc) &&
              Math.round(disc) !== Math.round(base)
            ) {
              const tor =
                Number(booking.totalOriginalPrice) ||
                base + (Number(booking.additionalServicesPrice) || 0)
              if (tor > 0 && Math.round(disc) !== Math.round(tor)) {
                actualPrice = Math.round((disc * base) / tor)
              } else {
                actualPrice = base
              }
            }
          }

          // 計算折扣價（僅用於判斷是否為手動更改）
          const discountedPrice =
            membershipDiscountRate < 100
              ? Math.round((originalPrice * membershipDiscountRate) / 100)
              : originalPrice

          // 判斷是否為手動更改的價格（actualPrice 與折扣價不同，且不等於原價）
          const isManualPrice =
            actualPrice > 0 &&
            actualPrice !== discountedPrice &&
            actualPrice !== originalPrice

          // 直接使用實際價格，不再自動應用折扣
          const finalPrice = actualPrice || originalPrice

          // 檢查服務是否支持第一劑/第二劑
          const commissionRule = serviceCommissionRules[service.serviceId]
          const supportDoseTypes = shouldEnableDose({
            service,
            fullService,
            rule: commissionRule
          })
          if (!supportDoseTypes) {
            // 偵錯用：預約服務沒有被判定為第一/第二劑，觀察來源
            console.info('[DoseDebug][booking service]', {
              serviceId: service.serviceId,
              serviceName: service.serviceName,
              supportDoseTypes: supportDoseTypes,
              serviceSupportDoseTypes: service.supportDoseTypes,
              fullServiceSupportDoseTypes: fullService?.supportDoseTypes,
              ruleSupportDoseTypes: commissionRule?.supportDoseTypes,
              hasDosePersonnel:
                !!service.firstDosePersonnelId ||
                !!service.secondDosePersonnelId
            })
          }

          // 獲取設計師和助理（從服務對象或預約層級）
          const designerId = service.designerId || booking.stylistId || null
          const assistantId = service.assistantId || null
          const designer = employees.find((e) => e._id === designerId)
          const assistant = employees.find((e) => e._id === assistantId)

          // 獲取第一劑/第二劑操作人員（如果支持）
          const firstDosePersonnelId = service.firstDosePersonnelId || null
          const secondDosePersonnelId = service.secondDosePersonnelId || null
          const firstDosePersonnel = employees.find(
            (e) => e._id === firstDosePersonnelId
          )
          const secondDosePersonnel = employees.find(
            (e) => e._id === secondDosePersonnelId
          )

          // 獲取服務數量（從預約數據中獲取）
          const serviceQuantity = service.quantity || 1
          // 表單的 price 慣例是「該行總價」；預約存的是單價 → ×數量
          const bookingLineTotal =
            Math.round(finalPrice * serviceQuantity * 100) / 100

          if (supportDoseTypes) {
            // 第一劑/第二劑模式
            return {
              key: `booking-${service.serviceId}-${Date.now()}-${index}`,
              serviceId: service.serviceId,
              categoryId: service.category || fullService?.category,
              supportDoseTypes: true,
              categoryName: category?.name || '-',
              name: service.serviceName || fullService?.name || '-',
              // 價格相關
              originalPrice: originalPrice,
              basePrice: originalPrice, // 基礎價格（單價）
              price: bookingLineTotal, // 實收價（該行總價 = 單價×數量，可能含折扣或手動調價）
              isManualPrice: isManualPrice, // 標記是否為手動更改的價格
              // 服務數量
              quantity: serviceQuantity,
              // 設計師ID（從服務對象或預約層級獲取，用於計算設計師抽成和業績）
              designerId: designerId,
              designerName: designer?.name || booking.stylistName || '待指派',
              personnelId: designerId, // 向後兼容
              personnelName: designer?.name || booking.stylistName || '待指派',
              // 第一劑/第二劑操作人員
              firstDosePersonnelId: firstDosePersonnelId,
              firstDosePersonnelName: firstDosePersonnel?.name || null,
              secondDosePersonnelId: secondDosePersonnelId,
              secondDosePersonnelName: secondDosePersonnel?.name || null,
              // 助理欄位設為 null（第一劑/第二劑模式不使用助理）
              assistantId: null,
              assistantName: null,
              assist: false
            }
          } else {
            // 一般抽成模式
            return {
              key: `booking-${service.serviceId}-${Date.now()}-${index}`,
              serviceId: service.serviceId,
              categoryId: service.category || fullService?.category,
              supportDoseTypes: false,
              categoryName: category?.name || '-',
              name: service.serviceName || fullService?.name || '-',
              // 價格相關
              originalPrice: originalPrice,
              basePrice: originalPrice, // 基礎價格（單價）
              price: bookingLineTotal, // 實收價（該行總價 = 單價×數量，可能含折扣或手動調價）
              isManualPrice: isManualPrice, // 標記是否為手動更改的價格
              // 服務數量
              quantity: serviceQuantity,
              // 員工相關
              designerId: designerId,
              designerName: designer?.name || booking.stylistName || '待指派',
              assistantId: assistantId,
              assistantName: assistant?.name || '-',
              // 向後兼容字段
              personnelId: designerId,
              personnelName: designer?.name || booking.stylistName || '待指派',
              assist: !!assistantId,
              // 第一劑/第二劑設為 null
              firstDosePersonnelId: null,
              firstDosePersonnelName: null,
              secondDosePersonnelId: null,
              secondDosePersonnelName: null
            }
          }
        })
        allServices = [...bookingServices]
      }

      if (
        booking.additionalServices &&
        Array.isArray(booking.additionalServices) &&
        booking.additionalServices.length > 0
      ) {
        const additionalServicesFromApi = additionalServices // 加選服務列表（用於解析名稱）
        const additionalServicesMapped = booking.additionalServices.map(
          (service, index) => {
            const fullService = services.find(
              (s) => s._id === service.serviceId
            )
            const additionalServiceMeta = additionalServicesFromApi.find(
              (a) => a._id === service.serviceId
            )
            const category = categories.find(
              (c) =>
                c._id === service.category || c._id === fullService?.category
            )
            const personnel = employees.find((e) => e._id === booking.stylistId)

            // 原始價格和實際價格
            // 加選服務不在主服務列表（fullService 為 null），需額外從 additionalServiceMeta 取得原價
            const originalPrice = service.basePrice || fullService?.price || additionalServiceMeta?.price || additionalServiceMeta?.basePrice || 0
            const actualPrice =
              service.actualPrice ||
              service.basePrice ||
              service.price ||
              fullService?.price ||
              0

            // 獲取設計師和助理
            const designerId = service.designerId || booking.stylistId || null
            const assistantId = service.assistantId || null
            const designer = employees.find((e) => e._id === designerId)
            const assistant = employees.find((e) => e._id === assistantId)

            // 判斷是否支持第一劑/第二劑（若預約資料已有第一/二劑人員也視為支持）
            const commissionRule = serviceCommissionRules[service.serviceId]
            const supportDoseTypes = shouldEnableDose({
              service,
              fullService,
              rule: commissionRule
            })
            if (!supportDoseTypes) {
              console.info('[DoseDebug][booking additional]', {
                serviceId: service.serviceId,
                serviceName: service.serviceName,
                supportDoseTypes: supportDoseTypes,
                serviceSupportDoseTypes: service.supportDoseTypes,
                fullServiceSupportDoseTypes: fullService?.supportDoseTypes,
                ruleSupportDoseTypes: commissionRule?.supportDoseTypes,
                hasDosePersonnel:
                  !!service.firstDosePersonnelId ||
                  !!service.secondDosePersonnelId
              })
            }

            // 計算折扣價（僅用於判斷是否為手動更改）
            const discountedPrice =
              membershipDiscountRate < 100
                ? Math.round((originalPrice * membershipDiscountRate) / 100)
                : originalPrice

            // 判斷是否為手動更改的價格（actualPrice 與折扣價不同，且不等於原價）
            const isManualPrice =
              actualPrice > 0 &&
              actualPrice !== discountedPrice &&
              actualPrice !== originalPrice

            // 直接使用實際價格，不再自動應用折扣
            const finalPrice = actualPrice || originalPrice

            // 獲取服務數量（從預約數據中獲取）
            const serviceQuantity = service.quantity || 1
            // 表單的 price 慣例是「該行總價」；預約存的是單價 → ×數量
            const bookingLineTotal =
              Math.round(finalPrice * serviceQuantity * 100) / 100

            const resolvedName =
              service.serviceName ||
              additionalServiceMeta?.name ||
              additionalServiceMeta?.serviceName ||
              fullService?.name ||
              '-'

            if (supportDoseTypes) {
              return {
                key: `additional-${service.serviceId}-${Date.now()}-${index}`,
                serviceId: service.serviceId,
                categoryId: service.category || fullService?.category,
                categoryName: category?.name || '-',
                // 服務數量
                quantity: serviceQuantity,
                name: resolvedName,
                // 價格相關
                originalPrice: originalPrice,
                price: bookingLineTotal, // 實收價（該行總價 = 單價×數量，可能含折扣或手動調價）
                isManualPrice: isManualPrice, // 標記是否為手動更改的價格
                // 第一劑/第二劑相關
                supportDoseTypes: true,
                firstDosePersonnelId: service.firstDosePersonnelId || null,
                secondDosePersonnelId: service.secondDosePersonnelId || null,
                firstDosePersonnelName:
                  employees.find((e) => e._id === service.firstDosePersonnelId)
                    ?.name || null,
                secondDosePersonnelName:
                  employees.find((e) => e._id === service.secondDosePersonnelId)
                    ?.name || null,
                // 傳統字段置為 null
                designerId: null,
                designerName: null,
                assistantId: null,
                assistantName: null,
                personnelId: null,
                personnelName: null,
                assist: false
              }
            }

            return {
              key: `additional-${service.serviceId}-${Date.now()}-${index}`,
              serviceId: service.serviceId,
              categoryId: service.category || fullService?.category,
              categoryName: category?.name || '-',
              name: resolvedName,
              // 價格相關
              originalPrice: originalPrice,
              basePrice: originalPrice, // 基礎價格（單價）
              price: bookingLineTotal, // 實收價（該行總價 = 單價×數量，可能含折扣或手動調價）
              isManualPrice: isManualPrice, // 標記是否為手動更改的價格
              // 服務數量
              quantity: serviceQuantity,
              // 員工相關
              designerId: designerId,
              designerName: designer?.name || booking.stylistName || '待指派',
              assistantId: assistantId,
              assistantName: assistant?.name || '-',
              // 向後兼容字段
              personnelId: designerId,
              personnelName: designer?.name || booking.stylistName || '待指派',
              assist: !!assistantId,
              // 第一劑/第二劑置為 null
              supportDoseTypes: false,
              firstDosePersonnelId: null,
              secondDosePersonnelId: null,
              firstDosePersonnelName: null,
              secondDosePersonnelName: null
            }
          }
        )
        // 只加入尚未在 allServices 中的加選項目，避免 API 同時放在 services 與 additionalServices 時重複
        const existingIds = new Set(allServices.map((s) => s.serviceId))
        const extra = additionalServicesMapped.filter(
          (s) => !existingIds.has(s.serviceId)
        )
        allServices = [...allServices, ...extra]
      } else if (booking.productId && allServices.length === 0) {
        const product = services.find((s) => s._id === booking.productId)
        if (product) {
          const category = categories.find((c) => c._id === product.category)
          const personnel = employees.find((e) => e._id === booking.stylistId)

          // 原始價格和實際價格
          const originalPrice = product.price || 0
          const hasBookingAdditional =
            (booking.additionalServices &&
              booking.additionalServices.length > 0) ||
            (Number(booking.additionalServicesPrice) > 0)
          // 有加選時 booking.discountedPrice 為整單實付，不可整筆塞進主服務行（會與加選行重複加總）
          const bookingPrice = hasBookingAdditional
            ? booking.originalPrice || originalPrice
            : booking.discountedPrice ||
              booking.originalPrice ||
              product.price

          // 應用會員折扣計算折後價
          const discountedPrice = Math.round(
            (originalPrice * membershipDiscountRate) / 100
          )
          const finalPrice = bookingPrice || discountedPrice

          // 獲取設計師和助理
          const designerId = booking.stylistId || null
          const assistantId = null
          const designer = employees.find((e) => e._id === designerId)

          const serviceItem = {
            key: `${product._id}-${Date.now()}`,
            serviceId: product._id,
            categoryId: product.category,
            categoryName: category?.name || '-',
            name: product.name,
            // 價格相關
            originalPrice: originalPrice,
            price: finalPrice, // 折後實收價（已應用會員折扣）
            // 員工相關
            designerId: designerId,
            designerName: designer?.name || '-',
            assistantId: assistantId,
            assistantName: '-',
            // 向後兼容字段
            personnelId: designerId,
            personnelName: designer?.name || '-',
            assist: false
          }
          allServices = [serviceItem]
        }
      }
      setSelectedServices(allServices)

      // 設置服務數量（從預約數據中獲取）
      const quantitiesFromBooking = {}
      allServices.forEach((service) => {
        if (service.key && service.quantity) {
          quantitiesFromBooking[service.key] = service.quantity
        }
      })
      if (Object.keys(quantitiesFromBooking).length > 0) {
        setServiceQuantities((prev) => ({
          ...prev,
          ...quantitiesFromBooking
        }))
      }

      // 計算並設置總金額
      const calculatedTotalServiceAmount = allServices.reduce(
        (sum, item) => sum + (Number(item.price) || 0),
        0
      )
      const calculatedTotalProductAmount = selectedProducts.reduce(
        (sum, item) => sum + (Number(item.totalPrice) || 0),
        0
      )
      const calculatedSubtotal =
        calculatedTotalServiceAmount + calculatedTotalProductAmount
      const calculatedTotal = Math.max(0, calculatedSubtotal - discountAmount)

      // 延遲設置總金額，確保表單已準備好
      setTimeout(() => {
        try {
          form.setFieldsValue({
            totalPayment: calculatedTotal
          })
        } catch (error) {
          console.warn('設置總金額失敗:', error)
        }
      }, 200)

      if (booking.date) {
        setTimeout(() => {
          try {
            form.setFieldsValue({
              serviceDate: dayjs(booking.date)
            })
          } catch (error) {
            console.warn('Form not ready yet:', error)
          }
        }, 100)
      }
      if (booking.time) {
        const [startTime, endTime] = booking.time.split('-') || [booking.time]
        setTimeout(() => {
          try {
            form.setFieldsValue({
              serviceStartTime: startTime.trim(),
              serviceEndTime: endTime?.trim() || undefined
            })
          } catch (error) {
            console.warn('Form not ready yet:', error)
          }
        }, 100)
      }

      if (booking.note) {
        setTimeout(() => {
          try {
            form.setFieldsValue({
              remarks: booking.note
            })
          } catch (error) {
            console.warn('Form not ready yet:', error)
          }
        }, 100)
      }
    } catch (error) {
      console.error('獲取預約數據失敗:', error)
      message.error('獲取預約信息失敗，請重試')
    } finally {
      setLoadingBooking(false)
    }
  }

  // 獲取訂單詳情並帶入結帳表單（重新結帳用，載入完整服務、商品、客戶、付款方式等）
  const fetchOrderData = async (id) => {
    if (!hasFeature('checkout') || !id || !user?.businessId) return
    if (fetchedOrderIdRef.current === id) return
    if (
      services.length === 0 ||
      categories.length === 0 ||
      employees.length === 0
    )
      return

    setLoadingOrder(true)
    fetchedOrderIdRef.current = id
    try {
      const response = await api.get(
        `/api/businesses/${user.businessId}/checkout/orders/${id}`
      )
      // 兼容多種回傳結構：response.data.data / response.data.data.order / response.data
      const raw = response.data?.data ?? response.data
      let order = raw
      if (raw?.data && (raw.data.amounts != null || raw.data.payment != null)) {
        order = raw.data
      } else if (
        raw?.order &&
        (raw.order.amounts != null || raw.order.payment != null)
      ) {
        order = raw.order
      }
      if (!order) {
        message.error('無法取得訂單詳情')
        return
      }

      // 支援多種 API 結構：services / items / serviceList；products / productList
      const orderServices =
        order.services ??
        order.items ??
        order.serviceList ??
        raw?.services ??
        []
      const orderProducts =
        order.products ?? order.productList ?? raw?.products ?? []
      const orderCustomer = order.customer || {}
      const orderPayment = order.payment || {}
      const orderTime = order.time || {}
      const orderAmounts = order.amounts || {}

      // 1.  mapping 服務
      const mappedServices = orderServices.map((s, idx) => {
        const fullService = services.find(
          (fs) => fs._id === (s.serviceId || s._id)
        )
        const category = categories.find(
          (c) => c._id === (s.categoryId || s.category || fullService?.category)
        )
        const personnelId =
          typeof s.personnelId === 'object' && s.personnelId !== null
            ? s.personnelId._id
            : s.personnelId
        const designerFromObj =
          typeof s.designer === 'object' && s.designer !== null
            ? s.designer._id || s.designer.id
            : null
        const designerIdFromField =
          typeof s.designerId === 'object' && s.designerId !== null
            ? s.designerId._id || s.designerId.id
            : s.designerId
        let designerId =
          designerIdFromField || personnelId || designerFromObj || null
        if (!designerId && employees.length > 0) {
          designerId = getDefaultEmployee()._id
        }
        const assistantId = s.assistantId || null
        const designer = employees.find((e) => e._id === designerId)
        const assistant = employees.find((e) => e._id === assistantId)
        const originalPrice =
          s.originalPrice || s.basePrice || fullService?.price || 0
        // 重新結帳時，若原訂單實收為 0（免費或誤打），以目錄原價重填，
        // 讓使用者直接看到正確金額；用 || 而非 ??，確保 0 也會 fallback
        const quantity = s.quantity ?? 1
        // API 的 price 為「單價」；表單的 price 慣例是「該行總價」→ ×數量
        const unitPriceFromOrder = s.price || s.actualPrice || originalPrice
        const price =
          Math.round(unitPriceFromOrder * (Number(quantity) || 1) * 100) / 100
        const supportDoseTypes =
          s.supportDoseTypes ??
          !!(
            s.firstDosePersonnelId ||
            s.secondDosePersonnelId ||
            serviceCommissionRules[s.serviceId || s._id]?.supportDoseTypes
          )

        if (supportDoseTypes) {
          let firstId = s.firstDosePersonnelId || null
          let secondId = s.secondDosePersonnelId || null
          if (
            typeof s.firstDosePersonnelId === 'object' &&
            s.firstDosePersonnelId !== null
          ) {
            firstId = s.firstDosePersonnelId._id || s.firstDosePersonnelId.id
          }
          if (
            typeof s.secondDosePersonnelId === 'object' &&
            s.secondDosePersonnelId !== null
          ) {
            secondId = s.secondDosePersonnelId._id || s.secondDosePersonnelId.id
          }
          if (!firstId && !secondId && employees.length > 0) {
            firstId = getDefaultEmployee()._id
          }
          const firstDosePersonnelName = firstId
            ? employees.find((e) => e._id === firstId)?.name ||
              s.firstDosePersonnelName ||
              null
            : null
          const secondDosePersonnelName = secondId
            ? employees.find((e) => e._id === secondId)?.name ||
              s.secondDosePersonnelName ||
              null
            : null
          return {
            key: `order-${s.serviceId || s._id}-${Date.now()}-${idx}`,
            serviceId: s.serviceId || s._id,
            categoryId: s.categoryId || s.category || fullService?.category,
            categoryName: category?.name || '-',
            name: s.name || s.serviceName || fullService?.name || '-',
            originalPrice,
            basePrice: originalPrice,
            price,
            quantity,
            supportDoseTypes: true,
            designerId: null,
            designerName: null,
            assistantId: null,
            assistantName: null,
            firstDosePersonnelId: firstId,
            firstDosePersonnelName,
            secondDosePersonnelId: secondId,
            secondDosePersonnelName,
            personnelId: null,
            personnelName: null,
            assist: false
          }
        }
        return {
          key: `order-${s.serviceId || s._id}-${Date.now()}-${idx}`,
          serviceId: s.serviceId || s._id,
          categoryId: s.categoryId || s.category || fullService?.category,
          categoryName: category?.name || '-',
          name: s.name || s.serviceName || fullService?.name || '-',
          originalPrice,
          basePrice: originalPrice,
          price,
          quantity,
          supportDoseTypes: false,
          designerId,
          designerName:
            designer?.name || s.designerName || s.personnelName || '-',
          assistantId,
          assistantName: assistant?.name || s.assistantName || '-',
          personnelId: designerId,
          personnelName:
            designer?.name || s.designerName || s.personnelName || '-',
          assist: !!assistantId,
          firstDosePersonnelId: null,
          firstDosePersonnelName: null,
          secondDosePersonnelId: null,
          secondDosePersonnelName: null
        }
      })
      setSelectedServices(mappedServices)

      const quantities = {}
      mappedServices.forEach((svc) => {
        if (svc.key && svc.quantity) quantities[svc.key] = svc.quantity
      })
      setServiceQuantities((prev) => ({ ...prev, ...quantities }))

      // 2. mapping 商品
      const mappedProducts = orderProducts.map((p, idx) => {
        const invProduct = inventoryProducts.find(
          (ip) => ip._id === (p.productId || p._id)
        )
        const unitPrice = p.price ?? p.unitPrice ?? invProduct?.price ?? 0
        const qty = p.quantity ?? 1
        const totalPrice = p.totalPrice ?? Number(unitPrice) * qty
        const salesPersonId =
          p.productSalesPersonnelId ||
          (typeof p.productSalesPersonnel === 'object' &&
          p.productSalesPersonnel
            ? p.productSalesPersonnel._id
            : p.productSalesPersonnel) ||
          null
        const salesPerson = salesPersonId
          ? employees.find((e) => e._id === salesPersonId)
          : null

        return {
          key: `order-product-${p.productId || p._id}-${Date.now()}-${idx}`,
          productId: p.productId || p._id,
          name: p.name || p.productName || invProduct?.name || '商品',
          categoryName: invProduct?.categoryId
            ? inventoryCategories.find((ic) => ic._id === invProduct.categoryId)
                ?.name || '-'
            : p.categoryName || '-',
          quantity: qty,
          unitPrice: Number(unitPrice),
          totalPrice: Number(totalPrice),
          productSalesPersonnelId:
            salesPersonId || (getDefaultEmployee()?._id ?? null),
          productSalesPersonnelName:
            salesPerson?.name ||
            p.productSalesPersonnelName ||
            getDefaultEmployee()?.name ||
            null,
          manufacturer: invProduct?.manufacturer || p.manufacturer,
          capacity: invProduct?.capacity || p.capacity,
          unit: invProduct?.unit || p.unit || '件',
          stock: invProduct?.stock
        }
      })
      setSelectedProducts(mappedProducts)

      // 3. 客戶（確保 customerId 為字串，後端可能回傳 populated 物件導致 /customers/[object Object]）
      const rawCustomerId =
        orderCustomer.customerId || orderCustomer._id || null
      const customerId =
        rawCustomerId != null && typeof rawCustomerId === 'object'
          ? (rawCustomerId._id ?? rawCustomerId.id ?? null)
          : rawCustomerId
      const customerIdStr =
        customerId != null && typeof customerId === 'string' ? customerId : null
      const customerName = orderCustomer.name || orderCustomer.userName || ''
      const customerPhone = orderCustomer.phone || ''
      setCustomerNameValue(customerName)
      setSelectedCustomerId(customerIdStr)
      if (customerIdStr) {
        await Promise.all([
          fetchCustomerBalance(customerIdStr),
          membershipTiers.length > 0
            ? fetchCustomerMembershipTier(customerIdStr)
            : Promise.resolve()
        ])
      }
      setTimeout(() => {
        form.setFieldsValue({
          customerName: customerName,
          phone: customerPhone,
          servicePersonnel: orderCustomer.servicePersonnel || null,
          customerType: orderCustomer.customerType || 'existing',
          customerSource: orderCustomer.customerSource || 'none',
          participantCount: order.participantCount ?? 1
        })
      }, 0)
      if (order.participantCount) setParticipantCount(order.participantCount)

      // 4. 付款方式（order.payment 與表單欄位對應，兼容多種後端結構並避免顯示 NT$ 非數值）
      const pay = orderPayment || {}
      const orderAmountsObj = order.amounts || {}
      const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0)
      // 讀取金額：支援 camelCase 與 snake_case
      const payFields = {
        storedValue: toNum(pay.storedValue ?? pay.stored_value),
        voucher: toNum(pay.voucher),
        cash: toNum(pay.cash),
        transfer: toNum(pay.transfer),
        deposit: toNum(pay.deposit),
        bonus: toNum(pay.bonus),
        debt: toNum(pay.debt),
        installment: toNum(pay.installment),
        // 刷卡／行動支付／手續費也要帶回來。少了這三個欄位，原本刷卡收的錢會被
        // 下面的「付款加總為 0 就預填」灌進現金，手續費也整筆消失。
        cardPayment: toNum(pay.cardPayment),
        mobilePayment: toNum(pay.mobilePayment),
        paymentFee: toNum(pay.paymentFee)
      }
      // 原訂單若是店家手動指定手續費，重新結帳沿用該狀態
      paymentFeeOverriddenRef.current = pay.paymentFeeOverridden === true
      // 由店務系統付費：明細可能在 paymentDetails（cash, bankTransfer, accountsReceivable, mobileCardSwipe）
      if (pay.paymentDetails && typeof pay.paymentDetails === 'object') {
        const pd = pay.paymentDetails
        const cashDetail = toNum(pd.cash)
        const bankDetail = toNum(pd.bankTransfer)
        const arDetail = toNum(pd.accountsReceivable)
        const mobileDetail = toNum(pd.mobileCardSwipe)
        if (
          cashDetail > 0 ||
          bankDetail > 0 ||
          arDetail > 0 ||
          mobileDetail > 0
        ) {
          payFields.cash = cashDetail
          payFields.transfer = bankDetail
          payFields.debt = arDetail
          if (mobileDetail > 0) payFields.cash = payFields.cash + mobileDetail
        }
      }
      let sumFromPayment =
        payFields.storedValue +
        payFields.voucher +
        payFields.cash +
        payFields.transfer +
        payFields.deposit +
        payFields.bonus +
        payFields.debt +
        payFields.installment +
        payFields.cardPayment +
        payFields.mobilePayment
      if (!Number.isFinite(sumFromPayment)) sumFromPayment = 0
      const apiPaid = toNum(orderAmountsObj.paid)
      const apiReceivable = toNum(orderAmountsObj.receivable)
      const payReceivable = toNum(pay.receivable)
      // 總付款金額（用於預填各支付方式）：優先使用 amounts.paid / amounts.receivable，否則用 payment 各項加總
      let totalPaid = sumFromPayment
      if (apiPaid > 0 || apiReceivable > 0 || payReceivable > 0) {
        const fromAmounts = apiPaid > 0 ? apiPaid : apiReceivable
        totalPaid = fromAmounts > 0 ? fromAmounts : payReceivable
      }
      // 應收金額不固定為訂單後端值，改為「目前所有項目的總和」計算（新增/刪除項目時會由 useEffect 同步）
      const orderDiscount =
        toNum(orderAmountsObj.discount) || toNum(pay.discount)
      const calcServiceTotal = mappedServices.reduce(
        (sum, s) => sum + (Number(s.price) || 0),
        0
      )
      const calcProductTotal = mappedProducts.reduce(
        (sum, p) => sum + (Number(p.totalPrice) || 0),
        0
      )
      const calculatedTotalFromItems = Math.max(
        0,
        calcServiceTotal + calcProductTotal - orderDiscount
      )
      // 立即寫入「重新結帳載入總額」，讓總付款金額與上方總計一致（不依賴表單或 state 時序）
      setRecheckoutLoadedTotal(calculatedTotalFromItems)
      // 若 payment 加總為 0 但後端有金額，將總額預填（優先對應 payment.receivable 來源：儲值金或現金）
      if (sumFromPayment === 0 && totalPaid > 0) {
        const fromStored = toNum(pay.storedValue ?? pay.stored_value)
        if (fromStored > 0) {
          payFields.storedValue = totalPaid
        } else {
          payFields.cash = totalPaid
        }
      }
      payFields.receivable = totalPaid
      setPaymentMethods((prev) => ({ ...prev, ...payFields }))
      // 立即寫入 totalPayment 與其餘付款欄位，讓「總付款金額」與上方總計一致（減少延遲）
      setTimeout(() => {
        form.setFieldsValue({
          totalPayment: calculatedTotalFromItems,
          storedValue: payFields.storedValue,
          voucher: payFields.voucher,
          cash: payFields.cash,
          transfer: payFields.transfer,
          deposit: payFields.deposit,
          bonus: payFields.bonus,
          debt: payFields.debt,
          installment: payFields.installment,
          cardPayment: payFields.cardPayment,
          mobilePayment: payFields.mobilePayment,
          paymentFee: payFields.paymentFee
        })
      }, 0)

      // 5. 折扣、備註、日期時間
      // 結帳單存的是 amounts.discountAmount（沒有 amounts.discount）。
      // 不能寫 Number(x) ?? …：Number(undefined) 是 NaN 不是 nullish，?? 接不住，
      // 應收會變成 NaN，送出時跳「應收金額（NT$ 非數值）不符」。
      const discountVal = toNum(
        orderAmounts.discountAmount ??
          orderAmounts.discount ??
          orderPayment?.discount
      )
      setDiscountAmount(discountVal)
      setTempDiscountAmount(discountVal)
      if (order.remarks) {
        form.setFieldsValue({ remarks: order.remarks })
      }
      if (orderTime.date) {
        form.setFieldsValue({
          serviceDate: dayjs(orderTime.date),
          date: dayjs(orderTime.date)
        })
      }
      if (orderTime.startTime || orderTime.endTime) {
        form.setFieldsValue({
          serviceStartTime: orderTime.startTime || undefined,
          serviceEndTime: orderTime.endTime || undefined
        })
      }

      message.success(
        '已帶入原訂單內容，可修改後重新結帳。請檢查設計師資訊是否正確。'
      )
    } catch (error) {
      console.error('獲取訂單詳情失敗:', error)
      message.error(
        error?.response?.data?.message || '無法載入訂單詳情，請稍後再試'
      )
      fetchedOrderIdRef.current = null
    } finally {
      setLoadingOrder(false)
    }
  }

  // 取得草稿並帶入表單（繼續結帳用）
  const fetchDraftData = async (draftId) => {
    if (!hasFeature('checkout') || !draftId || !user?.businessId) return
    if (fetchedDraftIdRef.current === draftId) return
    if (
      services.length === 0 ||
      categories.length === 0 ||
      employees.length === 0
    ) {
      return
    }
    setLoadingDraft(true)
    fetchedDraftIdRef.current = draftId
    try {
      const response = await api.get(
        `/api/businesses/${user.businessId}/checkout/drafts/${draftId}`
      )
      const draft = response.data?.data ?? response.data
      if (!draft) {
        message.error('無法取得草稿')
        return
      }
      const draftServices = draft.services ?? []
      const draftProducts = draft.products ?? []
      const draftCustomer = draft.customer || {}
      const draftPayment = draft.payment || {}
      const draftTime = draft.time || {}
      const draftTotals = draft.totals || {}
      const draftTicketUsages = draft.classTicketUsages ?? []

      // 1. 映射服務（與訂單結構類同，draft 為我們儲存的 payload）
      const mappedServices = draftServices.map((s, idx) => {
        const fullService = services.find(
          (fs) => fs._id === (s.serviceId || s._id)
        )
        const category = categories.find(
          (c) => c._id === (s.categoryId || s.category || fullService?.category)
        )
        const doseFromRule =
          s.firstDosePersonnelId ||
          s.secondDosePersonnelId ||
          serviceCommissionRules[s.serviceId || s._id]?.supportDoseTypes
        const supportDoseTypes = !!(s.supportDoseTypes ?? doseFromRule)
        const originalPrice =
          s.originalPrice || s.basePrice || fullService?.price || 0
        const price = s.price ?? originalPrice
        const quantity = s.quantity ?? 1

        if (supportDoseTypes) {
          const firstId = s.firstDosePersonnelId || null
          const secondId = s.secondDosePersonnelId || null
          const firstDosePersonnelName = firstId
            ? employees.find((e) => e._id === firstId)?.name ||
              s.firstDosePersonnelName ||
              null
            : null
          const secondDosePersonnelName = secondId
            ? employees.find((e) => e._id === secondId)?.name ||
              s.secondDosePersonnelName ||
              null
            : null
          return {
            key: `draft-${s.serviceId || s._id}-${Date.now()}-${idx}`,
            serviceId: s.serviceId || s._id,
            categoryId: s.categoryId || s.category || fullService?.category,
            categoryName: category?.name || '-',
            name: s.name || s.serviceName || fullService?.name || '-',
            originalPrice,
            basePrice: originalPrice,
            price,
            quantity,
            supportDoseTypes: true,
            designerId: null,
            designerName: null,
            assistantId: null,
            assistantName: null,
            firstDosePersonnelId: firstId,
            firstDosePersonnelName,
            secondDosePersonnelId: secondId,
            secondDosePersonnelName,
            personnelId: null,
            personnelName: null,
            assist: false
          }
        }
        const designerId = s.designerId || s.personnelId || null
        const assistantId = s.assistantId || null
        const designer = employees.find((e) => e._id === designerId)
        const assistant = employees.find((e) => e._id === assistantId)
        return {
          key: `draft-${s.serviceId || s._id}-${Date.now()}-${idx}`,
          serviceId: s.serviceId || s._id,
          categoryId: s.categoryId || s.category || fullService?.category,
          categoryName: category?.name || '-',
          name: s.name || s.serviceName || fullService?.name || '-',
          originalPrice,
          basePrice: originalPrice,
          price,
          quantity,
          supportDoseTypes: false,
          designerId,
          designerName:
            designer?.name || s.designerName || s.personnelName || '-',
          assistantId,
          assistantName: assistant?.name || s.assistantName || '-',
          personnelId: designerId,
          personnelName:
            designer?.name || s.designerName || s.personnelName || '-',
          assist: !!assistantId,
          firstDosePersonnelId: null,
          firstDosePersonnelName: null,
          secondDosePersonnelId: null,
          secondDosePersonnelName: null
        }
      })
      setSelectedServices(mappedServices)
      const quantities = {}
      mappedServices.forEach((svc) => {
        if (svc.key && svc.quantity) quantities[svc.key] = svc.quantity
      })
      setServiceQuantities((prev) => ({ ...prev, ...quantities }))

      /**
       * 1-1. 還原堂票折抵。
       *
       * 服務的 price 在存草稿時就已經是 0（折抵當下改的），這裡補回
       * 「是哪張票折的」，畫面才會顯示票號、也才送得出 classTicketUsages。
       * 先用 serviceIndex 對位，對不到才退回用 serviceId 找一列還沒被
       * 佔用的（同一個服務可能有兩列，各折各的票，不能重複指到同一列）。
       *
       * 票的有效性不在這裡擋 —— 存草稿到現在，票可能已經被別張單用完或
       * 過期了。後端結帳時本來就會驗歸屬、效期與適用範圍並擋下來，
       * 在這裡再擋一次只會多一套會過期的規則。
       */
      const restoredRedemptions = {}
      const takenKeys = new Set()
      draftTicketUsages.forEach((usage) => {
        if (!usage?.ticketId) return
        let svc =
          Number.isInteger(usage.serviceIndex) &&
          mappedServices[usage.serviceIndex]
            ? mappedServices[usage.serviceIndex]
            : null
        // 索引對到的那一列必須是同一個服務。草稿存起來之後如果中間有一列
        // 被刪掉，位置就會位移，硬套會把票折到別的服務上。
        if (
          svc &&
          usage.serviceId &&
          String(svc.serviceId) !== String(usage.serviceId)
        ) {
          svc = null
        }
        if (svc && takenKeys.has(svc.key)) svc = null
        if (!svc) {
          svc = mappedServices.find(
            (m) =>
              String(m.serviceId) === String(usage.serviceId) &&
              !takenKeys.has(m.key)
          )
        }
        if (!svc) return
        takenKeys.add(svc.key)
        restoredRedemptions[svc.key] = {
          ticketId: String(usage.ticketId),
          ticketCode: usage.ticketCode || '',
          templateName: usage.templateName || '',
          count: usage.count || 1,
          prevPrice:
            usage.prevPrice ?? svc.originalPrice ?? svc.basePrice ?? 0
        }
      })
      setTicketRedemptions(restoredRedemptions)

      // 2. 映射商品
      const mappedProducts = draftProducts.map((p, idx) => {
        const invProduct = inventoryProducts.find(
          (ip) => ip._id === (p.productId || p._id)
        )
        const unitPrice = p.unitPrice ?? invProduct?.price ?? 0
        const qty = p.quantity ?? 1
        const totalPrice = p.totalPrice ?? Number(unitPrice) * qty
        const salesPersonId = p.productSalesPersonnelId || null
        const salesPerson = salesPersonId
          ? employees.find((e) => e._id === salesPersonId)
          : null
        return {
          key: `draft-product-${p.productId || p._id}-${Date.now()}-${idx}`,
          productId: p.productId || p._id,
          name: p.name || p.productName || invProduct?.name || '商品',
          categoryName: invProduct?.categoryId
            ? inventoryCategories.find((ic) => ic._id === invProduct.categoryId)
                ?.name || '-'
            : p.categoryName || '-',
          quantity: qty,
          unitPrice: Number(unitPrice),
          totalPrice: Number(totalPrice),
          productSalesPersonnelId:
            salesPersonId || (getDefaultEmployee()?._id ?? null),
          productSalesPersonnelName:
            salesPerson?.name ||
            p.productSalesPersonnelName ||
            getDefaultEmployee()?.name ||
            null,
          manufacturer: invProduct?.manufacturer || p.manufacturer,
          capacity: invProduct?.capacity || p.capacity,
          unit: invProduct?.unit || p.unit || '件',
          stock: invProduct?.stock
        }
      })
      setSelectedProducts(mappedProducts)

      // 3. 客戶
      const rawCustomerId =
        draftCustomer.customerId || draftCustomer._id || null
      const customerIdStr =
        rawCustomerId != null && typeof rawCustomerId === 'object'
          ? (rawCustomerId._id ?? rawCustomerId.id ?? null)
          : rawCustomerId
      const customerName = draftCustomer.name || ''
      const customerPhone = draftCustomer.phone || ''
      setCustomerNameValue(customerName)
      setSelectedCustomerId(customerIdStr)
      if (customerIdStr) {
        await Promise.all([
          fetchCustomerBalance(customerIdStr),
          membershipTiers.length > 0
            ? fetchCustomerMembershipTier(customerIdStr)
            : Promise.resolve()
        ])
      }
      setTimeout(() => {
        form.setFieldsValue({
          customerName: customerName,
          phone: customerPhone,
          customerType: draftCustomer.customerType || 'existing',
          customerSource: draftCustomer.customerSource || 'none',
          participantCount: draft.participantCount ?? 1
        })
      }, 0)
      if (draft.participantCount) setParticipantCount(draft.participantCount)

      // 4. 付款與總額
      const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0)
      const payFields = {
        storedValue: toNum(draftPayment.storedValue),
        voucher: toNum(draftPayment.voucher),
        cash: toNum(draftPayment.cash),
        transfer: toNum(draftPayment.transfer),
        deposit: toNum(draftPayment.deposit),
        bonus: toNum(draftPayment.bonus),
        debt: toNum(draftPayment.debt),
        installment: toNum(draftPayment.installment),
        // 草稿存的刷卡／行動支付／手續費一併帶回，否則續結時金額會對不上、
        // 手續費也會從 0 重新開始
        cardPayment: toNum(draftPayment.cardPayment),
        mobilePayment: toNum(draftPayment.mobilePayment),
        paymentFee: toNum(draftPayment.paymentFee)
      }
      paymentFeeOverriddenRef.current =
        draftPayment.paymentFeeOverridden === true
      const grandTotal = toNum(draftTotals.grandTotal)
      setRecheckoutLoadedTotal(grandTotal > 0 ? grandTotal : null)
      setPaymentMethods((prev) => ({ ...prev, ...payFields }))
      setTimeout(() => {
        form.setFieldsValue({
          totalPayment: grandTotal,
          storedValue: payFields.storedValue,
          voucher: payFields.voucher,
          cash: payFields.cash,
          transfer: payFields.transfer,
          deposit: payFields.deposit,
          bonus: payFields.bonus,
          debt: payFields.debt,
          installment: payFields.installment,
          cardPayment: payFields.cardPayment,
          mobilePayment: payFields.mobilePayment,
          paymentFee: payFields.paymentFee
        })
      }, 0)

      // 5. 折扣、備註、日期時間
      const discountVal =
        toNum(draftTotals.discountAmount) ?? toNum(draftPayment?.discount) ?? 0
      setDiscountAmount(discountVal)
      setTempDiscountAmount(discountVal)
      if (draft.remarks) {
        form.setFieldsValue({ remarks: draft.remarks })
      }
      if (draftTime.date) {
        const dateVal =
          typeof draftTime.date === 'string'
            ? dayjs(draftTime.date, 'YYYY-MM-DD')
            : dayjs(draftTime.date)
        if (dateVal.isValid()) {
          form.setFieldsValue({
            serviceDate: dateVal,
            date: dateVal
          })
        }
      }
      if (draftTime.startTime || draftTime.endTime) {
        form.setFieldsValue({
          serviceStartTime: draftTime.startTime || undefined,
          serviceEndTime: draftTime.endTime || undefined
        })
      }

      // 6. 若草稿有關聯預約，寫入 URL 以便顯示預約結帳模式並載入預約資訊
      if (draft.bookingId) {
        const next = new URLSearchParams(searchParams)
        next.set('bookingId', draft.bookingId)
        setSearchParams(next)
      }

      if (draft.membershipLevel)
        setCustomerMembershipTier(draft.membershipLevel)
      if (draft.membershipDiscount != null)
        setMembershipDiscountRate(draft.membershipDiscount)

      message.success('已載入草稿，可繼續編輯並完成結帳')
    } catch (error) {
      console.error('載入草稿失敗:', error)
      message.error(
        error?.response?.data?.message || '無法載入草稿，請稍後再試'
      )
      fetchedDraftIdRef.current = null
    } finally {
      setLoadingDraft(false)
    }
  }

  // 所有 useEffect 必須在權限檢查之前
  useEffect(() => {
    if (!hasFeature('checkout')) return // 如果沒有權限，不執行
    fetchEmployees()
    fetchServices()
    fetchAdditionalServices()
    fetchCategories()
    fetchInventoryProducts()
    fetchInventoryCategories()
    fetchServiceCommissionRules()
    fetchMembershipTiers()
    fetchClassTicketTemplates()
    fetchPaymentFeeSettings()

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessPlan, user?.businessId])

  // 當會員等級列表加載完成且客戶已選擇時，獲取客戶會員等級
  useEffect(() => {
    if (membershipTiers.length > 0 && selectedCustomerId) {
      fetchCustomerMembershipTier(selectedCustomerId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membershipTiers, selectedCustomerId])

  // 現場結帳：選完顧客後平滑滾動至「選擇服務」區塊（僅在步驟一之後才渲染該區塊，故需 currentStep >= 1）
  useEffect(() => {
    if (checkoutMode !== 'walkin' || !selectedCustomerId) return
    if (currentStep < 1) return
    const timer = setTimeout(() => {
      sectionServicesRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }, 300)
    return () => clearTimeout(timer)
  }, [checkoutMode, selectedCustomerId, currentStep])

  // 現場結帳：從步驟一進入步驟二時，滾動至「選擇服務」區塊
  useEffect(() => {
    if (checkoutMode !== 'walkin' || currentStep !== 1) return
    const timer = setTimeout(() => {
      sectionServicesRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      })
    }, 150)
    return () => clearTimeout(timer)
  }, [checkoutMode, currentStep])

  // 不再自動計算折扣，由用戶在第一步驟手動選擇是否應用折扣

  // 當抽成規則或已選服務變化後，確保已選服務（包含預約帶入）會啟用第一/第二劑模式；一二劑時自動帶入登入者本人（非員工則第一順位設計師）
  useEffect(() => {
    if (!selectedServices.length) return
    const firstDesigner = getDefaultEmployee()
    let changed = false
    const updated = selectedServices.map((s) => {
      const rule = serviceCommissionRules[s.serviceId]
      const fullService = services.find((fs) => fs._id === s.serviceId)
      const shouldSupportDose = shouldEnableDose({
        service: s,
        fullService,
        rule
      })
      if (shouldSupportDose && s.supportDoseTypes !== true) {
        changed = true
        const needDefaultDesigner =
          firstDesigner?._id && !s.firstDosePersonnelId && !s.designerId
        return {
          ...s,
          supportDoseTypes: true,
          firstDosePersonnelId:
            s.firstDosePersonnelId ||
            (needDefaultDesigner ? firstDesigner._id : null),
          secondDosePersonnelId:
            s.secondDosePersonnelId ||
            (needDefaultDesigner ? firstDesigner._id : null),
          firstDosePersonnelName:
            s.firstDosePersonnelName ||
            (needDefaultDesigner ? firstDesigner.name : null),
          secondDosePersonnelName:
            s.secondDosePersonnelName ||
            (needDefaultDesigner ? firstDesigner.name : null),
          designerId:
            s.designerId ||
            s.personnelId ||
            (needDefaultDesigner ? firstDesigner._id : null),
          designerName:
            s.designerName ||
            s.personnelName ||
            (needDefaultDesigner ? firstDesigner.name : null),
          personnelId:
            s.designerId ||
            s.personnelId ||
            (needDefaultDesigner ? firstDesigner._id : null),
          personnelName:
            s.designerName ||
            s.personnelName ||
            (needDefaultDesigner ? firstDesigner.name : null),
          assistantId: null,
          assistantName: null,
          assist: false,
          doseDebug: {
            from: 'selectedServices useEffect',
            ruleSupportDoseTypes: rule?.supportDoseTypes,
            ruleDoseCommissions: rule?.doseCommissions?.length,
            fullServiceSupportDoseTypes: fullService?.supportDoseTypes,
            serviceSupportDoseTypes: s.supportDoseTypes
          }
        }
      }
      if (
        shouldSupportDose &&
        s.supportDoseTypes === true &&
        firstDesigner?._id
      ) {
        const needDefaultDesigner =
          !s.firstDosePersonnelId || !s.secondDosePersonnelId || !s.designerId
        if (needDefaultDesigner) {
          changed = true
          return {
            ...s,
            firstDosePersonnelId: s.firstDosePersonnelId || firstDesigner._id,
            firstDosePersonnelName:
              s.firstDosePersonnelName || firstDesigner.name || null,
            secondDosePersonnelId: s.secondDosePersonnelId || firstDesigner._id,
            secondDosePersonnelName:
              s.secondDosePersonnelName || firstDesigner.name || null,
            designerId: s.designerId || s.personnelId || firstDesigner._id,
            designerName:
              s.designerName || s.personnelName || firstDesigner.name || null,
            personnelId: s.designerId || s.personnelId || firstDesigner._id,
            personnelName:
              s.personnelName || s.designerName || firstDesigner.name || null
          }
        }
      }
      return s
    })
    if (changed) {
      setSelectedServices(updated)
    } else {
      // 偵錯：哪些服務仍未啟用第一/第二劑
      selectedServices
        .filter(
          (s) =>
            !shouldEnableDose({
              service: s,
              fullService: services.find((fs) => fs._id === s.serviceId),
              rule: serviceCommissionRules[s.serviceId]
            })
        )
        .forEach((s) => {
          const rule = serviceCommissionRules[s.serviceId]
          const fullService = services.find((fs) => fs._id === s.serviceId)
          console.info('[DoseDebug][selectedServices unchanged]', {
            serviceId: s.serviceId,
            serviceName: s.name,
            supportDoseTypes: s.supportDoseTypes,
            serviceSupportDoseTypes: s.supportDoseTypes,
            fullServiceSupportDoseTypes: fullService?.supportDoseTypes,
            ruleSupportDoseTypes: rule?.supportDoseTypes,
            hasDosePersonnel:
              !!s.firstDosePersonnelId || !!s.secondDosePersonnelId
          })
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceCommissionRules, selectedServices, employees])

  // 當 totalAmount 已由 state 正確計算（> 0），清除「重新結帳載入總額」，避免之後清空項目時仍顯示舊值
  useEffect(() => {
    if (totalAmount > 0 && recheckoutLoadedTotal != null) {
      setRecheckoutLoadedTotal(null)
    }
  }, [totalAmount, recheckoutLoadedTotal])

  // 當 orderId 存在（重新結帳）：從訂單詳情載入完整內容（服務、商品、客戶、付款方式等）
  useEffect(() => {
    if (!hasFeature('checkout') || !orderIdFromUrl || !isRecheckout) {
      setRecheckoutLoadedTotal(null)
      return
    }
    if (fetchedOrderIdRef.current === orderIdFromUrl) return
    // 重新結帳時立即顯示載入中，避免在等 services/employees 等資料時畫面無反應
    setLoadingOrder(true)
    if (
      services.length > 0 &&
      categories.length > 0 &&
      employees.length > 0 &&
      inventoryProducts.length >= 0
    ) {
      fetchOrderData(orderIdFromUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderIdFromUrl,
    isRecheckout,
    services.length,
    categories.length,
    employees.length,
    inventoryProducts.length
  ])

  // 當 draftId 存在（繼續結帳）：取得草稿並帶入表單與服務／商品
  useEffect(() => {
    if (!hasFeature('checkout') || !draftIdFromUrl) return
    if (orderIdFromUrl && isRecheckout) return // 重新結帳優先，不載入草稿
    if (fetchedDraftIdRef.current === draftIdFromUrl) return
    if (
      services.length > 0 &&
      categories.length > 0 &&
      employees.length > 0 &&
      inventoryProducts.length >= 0
    ) {
      fetchDraftData(draftIdFromUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    draftIdFromUrl,
    orderIdFromUrl,
    isRecheckout,
    services.length,
    categories.length,
    employees.length,
    inventoryProducts.length
  ])

  // 當 services 和 categories 加載完成後，若有 bookingId 則獲取預約（重新結帳時只設 bookingData 不覆寫表單）
  useEffect(() => {
    if (!hasFeature('checkout') || !bookingId) return
    if (fetchedBookingIdRef.current === bookingId) return
    if (services.length > 0 && categories.length > 0) {
      const skipFormUpdate = !!(orderIdFromUrl && isRecheckout)
      fetchBookingData(bookingId, { skipFormUpdate })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bookingId,
    orderIdFromUrl,
    isRecheckout,
    businessPlan,
    services.length,
    categories.length
  ])

  // 當服務或商品變化時，自動更新總金額與帶入金額到表單（總金額變動時，儲值金/現金要跟著變為新總額；重新結帳時加減項目後也會一併更新，業績金額才會含新項目）
  useEffect(() => {
    const isRecheckoutLoading =
      isRecheckout && orderIdFromUrl && totalAmount === 0

    const applyPaymentUpdate = () => {
      try {
        // 套用店家優惠券時由 applyCheckoutStoreCoupon 自行分配付款方式，這裡不介入
        if (appliedStoreCouponId) return
        if (!isRecheckoutLoading) {
          form.setFieldsValue({ totalPayment: totalAmount })
        }
        if (totalAmount <= 0) return

        const currentStoredValue =
          Number(form.getFieldValue('storedValue')) || 0
        const currentCash = Number(form.getFieldValue('cash')) || 0
        const currentTransfer = Number(form.getFieldValue('transfer')) || 0
        const currentDeposit = Number(form.getFieldValue('deposit')) || 0
        const currentVoucher = Number(form.getFieldValue('voucher')) || 0
        const currentBonus = Number(form.getFieldValue('bonus')) || 0
        const currentDebt = Number(form.getFieldValue('debt')) || 0
        const currentInstallment =
          Number(form.getFieldValue('installment')) || 0
        // 刷卡／行動支付也是已填的付款，漏算會讓下面判定成「還沒收款」，
        // 把現金重填成全額，跟已輸入的刷卡金額打架變成溢收
        const currentCardPayment =
          Number(form.getFieldValue('cardPayment')) || 0
        const currentMobilePayment =
          Number(form.getFieldValue('mobilePayment')) || 0
        const totalPaid =
          currentStoredValue +
          currentCash +
          currentTransfer +
          currentDeposit +
          currentVoucher +
          currentBonus +
          currentDebt +
          currentInstallment +
          currentCardPayment +
          currentMobilePayment

        // 預約已收的定金先保留下來，只分配「扣掉定金之後」的餘額
        const keepDeposit = Math.min(paidDepositAmount, totalAmount)
        // 優惠券同樣是「已經收到的付款」，必須連同定金一起先扣掉再分配，
        // 否則儲值金/現金會被填成全額應收，加上優惠券後變成 已付 > 應收（溢收）
        const keepVoucher = Math.min(
          currentVoucher,
          Math.max(0, totalAmount - keepDeposit)
        )
        // 刷卡／行動支付同樣是已經收到的款，要先扣掉再分配儲值金／現金，
        // 否則現金會被填成全額應收，加上刷卡金額就變成溢收
        const keepElectronic = Math.min(
          currentCardPayment + currentMobilePayment,
          Math.max(0, totalAmount - keepDeposit - keepVoucher)
        )
        const amountToAllocate = Math.max(
          0,
          totalAmount - keepDeposit - keepVoucher - keepElectronic
        )

        // 應依「餘額優先」：儲值金 = min(餘額, 待分配金額)，其餘現金；若目前儲值金未用滿餘額也需重填
        const idealStored =
          selectedCustomerId && customerBalance !== null && customerBalance > 0
            ? Math.min(customerBalance, amountToAllocate)
            : 0
        const mismatch =
          // 付款總額必須「剛好」等於應收：舊版容許 1 元誤差，正是
          // 「儲值金填滿應收 + 優惠券 1 元」這種溢收沒被修正的原因
          Math.abs(totalPaid - totalAmount) > 0.01 ||
          (totalPaid === 0 && totalAmount > 0) ||
          totalPaid > totalAmount ||
          (idealStored > 0 && Math.abs(currentStoredValue - idealStored) > 1) ||
          // 定金被其他流程清掉時也要補回來
          Math.abs(currentDeposit - keepDeposit) > 1 ||
          // 優惠券被手動改動時，其餘付款方式也要跟著重新分配
          Math.abs(currentVoucher - keepVoucher) > 0.01
        if (!mismatch) return

        let fillStored = idealStored
        let fillCash = Math.max(0, amountToAllocate - fillStored)

        form.setFieldsValue({
          storedValue: fillStored,
          cash: fillCash,
          transfer: 0,
          deposit: keepDeposit,
          // 不再無條件歸零：保留店家已輸入的優惠券金額（上限為應收 − 定金）
          voucher: keepVoucher,
          bonus: 0,
          debt: 0,
          installment: 0
        })
        // 強制更新欄位狀態，讓 Form.useWatch 的輸入框顯示正確金額
        form.setFields(
          [
            { name: 'storedValue', value: fillStored },
            { name: 'cash', value: fillCash },
            { name: 'deposit', value: keepDeposit },
            { name: 'voucher', value: keepVoucher }
          ],
          { validate: false }
        )
      } catch (error) {
        console.warn('更新總金額失敗:', error)
      }
    }

    // 延遲一幀執行，確保表單與 DOM 已就緒，input 能正確顯示
    const rafId = requestAnimationFrame(() => {
      applyPaymentUpdate()
    })
    return () => cancelAnimationFrame(rafId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedServices,
    selectedProducts,
    discountAmount,
    discountPercentage,
    totalAmount,
    selectedCustomerId,
    customerBalance,
    membershipDiscountRate, // 當會員折扣率變化時，重新自動填充
    appliedStoreCouponId,
    paidDepositAmount, // 預約定金載入後要重新分配
    watchedVoucherAmount // 手動輸入優惠券金額後，其餘付款方式要重新分配
  ])

  // 當客戶儲值金加載完成後，如果總金額已計算且支付方式都為0，自動填充
  // 或者當總金額變化（例如折扣變化）時，如果支付金額為0，重新自動填充
  useEffect(() => {
    // 套用店家優惠券時由 applyCheckoutStoreCoupon 自行分配付款方式，這裡不介入
    if (appliedStoreCouponId) return
    if (customerBalance !== null && selectedCustomerId && totalAmount > 0) {
      const timer = setTimeout(() => {
        try {
          if (appliedStoreCouponIdRef.current) return
          const currentStoredValue =
            Number(form.getFieldValue('storedValue')) || 0
          const currentCash = Number(form.getFieldValue('cash')) || 0
          const currentTransfer = Number(form.getFieldValue('transfer')) || 0
          const currentDeposit = Number(form.getFieldValue('deposit')) || 0
          const currentVoucher = Number(form.getFieldValue('voucher')) || 0

          // 計算所有支付方式的總和（優惠券視同現金，計入）
          const totalPaid =
            currentStoredValue +
            currentCash +
            currentTransfer +
            currentDeposit +
            currentVoucher

          // 預約已收的定金屬於已付款項，分配時先扣掉、且不可歸零
          const keepDeposit = Math.min(paidDepositAmount, totalAmount)
          // 優惠券也是已收到的付款，與定金同樣先扣掉再分配其餘付款方式
          const keepVoucher = Math.min(
            currentVoucher,
            Math.max(0, totalAmount - keepDeposit)
          )
          const allocTotal = Math.max(
            0,
            totalAmount - keepDeposit - keepVoucher
          )
          // 如果支付金额超过总金额，需要调整
          // 或者如果支付金额与总金额不匹配（第一步修改了价格），需要调整
          // 或者如果所有支付方式都為0，進行自動填充（第一次或折扣變化後）
          if (totalPaid > totalAmount) {
            // 如果儲值金超過「扣掉定金與優惠券後的待分配金額」，調整為待分配金額
            //（比對對象必須是 allocTotal 而非 totalAmount，否則
            // 「儲值金=應收 + 優惠券>0」這種溢收剛好落在判斷之外而不被修正）
            if (currentStoredValue > allocTotal) {
              const adjustedAmount = Math.min(customerBalance || 0, allocTotal)
              setTimeout(() => {
                if (appliedStoreCouponIdRef.current) return
                form.setFieldsValue({
                  storedValue: adjustedAmount,
                  cash: Math.max(0, allocTotal - adjustedAmount),
                  transfer: 0,
                  deposit: keepDeposit,
                  voucher: keepVoucher
                })
                form.setFields(
                  [
                    { name: 'storedValue', value: adjustedAmount },
                    {
                      name: 'cash',
                      value: Math.max(0, allocTotal - adjustedAmount)
                    },
                    { name: 'voucher', value: keepVoucher }
                  ],
                  { validate: false }
                )
              }, 100)
            }
          } else if (
            totalPaid < totalAmount ||
            (Math.abs(totalPaid - totalAmount) > 1 && totalPaid > 0)
          ) {
            // 總金額變動：有儲值金餘額時一律先填儲值金（不超過餘額），不夠再用現金補足；沒有儲值金則全部填現金
            if (customerBalance !== null && customerBalance > 0) {
              const fillStored = Math.min(customerBalance, allocTotal)
              setTimeout(() => {
                if (appliedStoreCouponIdRef.current) return
                form.setFieldsValue({
                  storedValue: fillStored,
                  cash: Math.max(0, allocTotal - fillStored),
                  transfer: 0,
                  deposit: keepDeposit,
                  voucher: keepVoucher
                })
              }, 50)
            } else {
              setTimeout(() => {
                if (appliedStoreCouponIdRef.current) return
                form.setFieldsValue({
                  storedValue: 0,
                  cash: allocTotal,
                  transfer: 0,
                  deposit: keepDeposit,
                  voucher: keepVoucher
                })
                form.setFields([{ name: 'cash', value: allocTotal }], {
                  validate: false
                })
              }, 50)
            }
          } else if (totalPaid === 0) {
            // 第一次或折扣後：有儲值金先填儲值金（不超過餘額），不夠再用現金補足；沒有儲值金則填現金
            if (customerBalance !== null && customerBalance > 0) {
              const fillAmount = Math.min(customerBalance, allocTotal)
              setTimeout(() => {
                if (appliedStoreCouponIdRef.current) return
                form.setFieldsValue({
                  storedValue: fillAmount,
                  cash: Math.max(0, allocTotal - fillAmount),
                  transfer: 0,
                  deposit: keepDeposit,
                  voucher: keepVoucher
                })
                form.setFields([{ name: 'storedValue', value: fillAmount }], {
                  validate: false
                })
              }, 50)
            } else {
              // 沒有儲值金，自動填充到cash（使用折扣後的總金額，包含會員折扣）
              // totalAmount 已經包含所有折扣（會員折扣已應用在服務價格中，手動折扣已減去）
              setTimeout(() => {
                if (appliedStoreCouponIdRef.current) return
                form.setFieldsValue({
                  cash: allocTotal
                })
                // 强制更新表单字段（使用 setFieldsValue 的第二个参数）
                form.setFields([{ name: 'cash', value: allocTotal }], {
                  validate: false
                })
              }, 100)
            }
          } else {
            // 總和已等於應收，但儲值金未依「餘額優先」：改為 儲值金 = min(餘額, 應收)，現金 = 應收 - 儲值金
            const idealStored = Math.min(customerBalance || 0, allocTotal)
            if (
              customerBalance !== null &&
              customerBalance > 0 &&
              Math.abs(currentStoredValue - idealStored) > 1
            ) {
              const fillStored = idealStored
              const fillCash = Math.max(0, allocTotal - fillStored)
              setTimeout(() => {
                if (appliedStoreCouponIdRef.current) return
                form.setFieldsValue({
                  storedValue: fillStored,
                  cash: fillCash,
                  transfer: 0,
                  deposit: keepDeposit,
                  voucher: keepVoucher
                })
                form.setFields(
                  [
                    { name: 'storedValue', value: fillStored },
                    { name: 'cash', value: fillCash },
                    { name: 'voucher', value: keepVoucher }
                  ],
                  { validate: false }
                )
              }, 50)
            }
          }
        } catch (error) {
          console.warn('❌ [自動填充儲值金失敗]', error)
        }
      }, 200)

      return () => clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    customerBalance,
    selectedCustomerId,
    totalAmount,
    discountAmount,
    discountPercentage,
    membershipDiscountRate, // 當會員折扣率變化時，重新自動填充
    appliedStoreCouponId,
    paidDepositAmount, // 預約定金載入後要重新分配
    watchedVoucherAmount // 手動輸入優惠券金額後，其餘付款方式要重新分配
  ])

  useEffect(() => {
    setAppliedStoreCouponId(null)
    setPendingCheckoutCouponId(null)
    setCheckoutCouponAudienceHint('')
    if (!user?.businessId) {
      setCheckoutStoreCoupons([])
      return
    }
    let cancelled = false
    ;(async () => {
      setLoadingCheckoutCoupons(true)
      try {
        const res = await api.get(
          `/api/businesses/${user.businessId}/checkout/store-coupons`
        )
        const list = res.data?.coupons || []
        if (!cancelled) {
          setCheckoutStoreCoupons(Array.isArray(list) ? list : [])
        }
      } catch {
        if (!cancelled) setCheckoutStoreCoupons([])
      } finally {
        if (!cancelled) setLoadingCheckoutCoupons(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [user?.businessId])

  const applyCheckoutStoreCoupon = async (storeCouponId) => {
    if (!user?.businessId) {
      message.error('店家資訊不完整')
      return
    }
    if (!storeCouponId) {
      message.error('請先選擇一張優惠券')
      return
    }
    const serviceProductIds = selectedServices
      .map((s) => s.serviceId)
      .filter(Boolean)
    const cartSubtotal = displayTotalAmount
    try {
      const res = await api.post(
        `/api/businesses/${user.businessId}/checkout/validate-store-coupon`,
        {
          storeCouponId,
          serviceProductIds,
          cartSubtotal
        }
      )
      const data = res.data
      if (!data?.success) {
        message.error(data?.message || '無法使用此優惠券')
        return
      }
      const deduction = Number(data.deduction) || 0
      // 優惠券視同現金：應收不變，改把其餘付款方式調降為「應收 − 優惠券」，
      // 兩者相加才會剛好等於應收，不會多收客人的錢。
      const currentCash = Number(form.getFieldValue('cash')) || 0
      const currentStoredValue = Number(form.getFieldValue('storedValue')) || 0
      const currentTransfer = Number(form.getFieldValue('transfer')) || 0
      const currentDeposit = Number(form.getFieldValue('deposit')) || 0
      const currentBonus = Number(form.getFieldValue('bonus')) || 0
      const currentDebt = Number(form.getFieldValue('debt')) || 0
      const currentInstallment = Number(form.getFieldValue('installment')) || 0
      const targetNonVoucher = Math.max(0, cartSubtotal - deduction)
      const updated = {
        cash: currentCash,
        storedValue: currentStoredValue,
        transfer: currentTransfer,
        deposit: currentDeposit,
        bonus: currentBonus,
        debt: currentDebt,
        installment: currentInstallment
      }
      const currentNonVoucher =
        updated.cash +
        updated.storedValue +
        updated.transfer +
        updated.deposit +
        updated.bonus +
        updated.debt +
        updated.installment
      if (currentNonVoucher > targetNonVoucher) {
        let excess = currentNonVoucher - targetNonVoucher
        const reduceOrder = [
          'cash',
          'storedValue',
          'transfer',
          'deposit',
          'bonus',
          'debt',
          'installment'
        ]
        reduceOrder.forEach((key) => {
          if (excess <= 0) return
          const reducible = Math.min(updated[key], excess)
          updated[key] -= reducible
          excess -= reducible
        })
      } else if (currentNonVoucher < targetNonVoucher) {
        updated.cash += targetNonVoucher - currentNonVoucher
      }
      form.setFieldsValue({
        voucher: deduction,
        cash: updated.cash,
        storedValue: updated.storedValue,
        transfer: updated.transfer,
        deposit: updated.deposit,
        bonus: updated.bonus,
        debt: updated.debt,
        installment: updated.installment
      })
      form.setFields(
        [
          { name: 'cash', value: updated.cash },
          { name: 'storedValue', value: updated.storedValue }
        ],
        { validate: false }
      )
      setAppliedStoreCouponId(storeCouponId)
      setPendingCheckoutCouponId(null)
      setCheckoutCouponAudienceHint(data.targetAudienceDescription || '')
      message.success(`已套用優惠券，折抵 NT$ ${deduction.toLocaleString()}`)
    } catch (err) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        '驗證失敗'
      message.error(msg)
    }
  }

  const renderCheckoutCouponPicker = () => {
    if (!user?.businessId) return null
    return (
      <div style={{ marginBottom: 12, fontSize: 13 }}>
        <div style={{ marginBottom: 6, fontWeight: 500 }}>
          店家方案優惠券
        </div>
        <div style={{ color: '#666', fontSize: 12, marginBottom: 8, lineHeight: 1.5 }}>
          顯示本店已設定之優惠券，客戶不必先於平台領取。結帳送出成功後會扣減該券在店家的剩餘張數。系統會檢查效期、最低消費與適用服務；若券面有設定「使用對象」，僅顯示提醒供參考，是否使用由店家自行決定。
        </div>
        <Space wrap align='start'>
          <ResponsiveSelect
            style={{ minWidth: 260 }}
            allowClear
            placeholder={
              loadingCheckoutCoupons ? '載入優惠券…' : '選擇優惠券'
            }
            loading={loadingCheckoutCoupons}
            value={pendingCheckoutCouponId}
            onChange={(v) => {
              setPendingCheckoutCouponId(v || null)
              if (!v) {
                setCheckoutCouponAudienceHint('')
                return
              }
              const row = checkoutStoreCoupons.find(
                (x) => String(x._id) === String(v)
              )
              setCheckoutCouponAudienceHint(
                row?.targetAudienceDescription || ''
              )
            }}
            options={checkoutStoreCoupons.map((c) => ({
              value: c._id,
              label: `${c.code || ''} · 到期 ${
                c.expiryDate ? dayjs(c.expiryDate).format('YYYY/MM/DD') : '—'
              }${
                c.remainingQuantity != null
                  ? ` · 剩 ${c.remainingQuantity}`
                  : ''
              }`
            }))}
          />
          <Button
            type='primary'
            size='small'
            onClick={() => applyCheckoutStoreCoupon(pendingCheckoutCouponId)}
            disabled={!pendingCheckoutCouponId}
          >
            套用驗證
          </Button>
          {appliedStoreCouponId ? (
            <Button
              type='link'
              size='small'
              onClick={() => {
                const currentVoucher = Number(form.getFieldValue('voucher')) || 0
                const currentCash = Number(form.getFieldValue('cash')) || 0
                const currentStoredValue =
                  Number(form.getFieldValue('storedValue')) || 0
                setAppliedStoreCouponId(null)
                setPendingCheckoutCouponId(null)
                setCheckoutCouponAudienceHint('')
                const restoreToStored =
                  currentStoredValue > 0 && currentCash <= 0
                form.setFieldsValue(
                  restoreToStored
                    ? {
                        voucher: 0,
                        storedValue: currentStoredValue + currentVoucher
                      }
                    : { voucher: 0, cash: currentCash + currentVoucher }
                )
                form.setFields(
                  restoreToStored
                    ? [
                        {
                          name: 'storedValue',
                          value: currentStoredValue + currentVoucher
                        }
                      ]
                    : [{ name: 'cash', value: currentCash + currentVoucher }],
                  { validate: false }
                )
              }}
            >
              清除已套用優惠券
            </Button>
          ) : null}
        </Space>
        {checkoutCouponAudienceHint ? (
          <div
            style={{
              marginTop: 10,
              padding: '8px 10px',
              background: '#fffbe6',
              border: '1px solid #ffe58f',
              borderRadius: 6,
              fontSize: 12,
              color: '#614700',
              lineHeight: 1.55
            }}
          >
            <span style={{ fontWeight: 600 }}>券面設定・使用對象：</span>
            {checkoutCouponAudienceHint}
            <div style={{ marginTop: 4, color: '#666', fontWeight: 400 }}>
              是否使用本券請由店家自行確認；系統不會依客戶身分擋下此券。
            </div>
          </div>
        ) : null}
      </div>
    )
  }

  // 頁面是否可顯示：基礎資料已載入（或逾時）且無正在載入的草稿/訂單/預約
  const baseDataReady =
    (services.length > 0 && categories.length > 0 && employees.length > 0) ||
    initialLoadTimeout
  const waitingForDraft = !!(draftIdFromUrl && loadingDraft)
  const waitingForOrder = !!(orderIdFromUrl && isRecheckout && loadingOrder)
  const waitingForBooking = !!(bookingId && loadingBooking)
  const pageReady =
    baseDataReady && !waitingForDraft && !waitingForOrder && !waitingForBooking

  // 權限檢查
  if (!hasFeature('checkout')) {
    return (
      <PageContainer>
        <Result
          status='403'
          title='功能限制'
          subTitle='此功能需要更高級別的會員方案才能使用，請升級您的會員方案。'
          extra={
            <Button
              type='primary'
              onClick={() => navigate('/settings/membership')}
            >
              查看會員方案
            </Button>
          }
        />
      </PageContainer>
    )
  }

  // 客戶搜尋（姓名或電話）
  const searchCustomers = async (searchTerm, source = 'name') => {
    if (!searchTerm || searchTerm.length < 2) {
      setCustomerOptions([])
      setShowCustomerList(false)
      setShowPhoneCustomerList(false)
      return
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setSearching(true)
      try {
        const response = await api.get(
          `/api/business_B/${user.businessId}/customers/search`,
          { params: { searchTerm } }
        )
        if (response.data?.success) {
          const customers = response.data.data || []
          const list = customers.map((c) => {
            const name = c.name || c.userInfo?.name || ''
            const phone = c.phone || c.userInfo?.phone || ''
            const email = c.email || c.userInfo?.email || ''
            return {
              value: c._id || c.userInfo?._id,
              label: `${name} ${phone}`.trim() || '未命名客戶',
              customer: c,
              name,
              phone,
              email
            }
          })
          setCustomerOptions(list)
          if (source === 'name') {
            setShowCustomerList(list.length > 0)
            setShowPhoneCustomerList(false)
          } else {
            setShowPhoneCustomerList(list.length > 0)
            setShowCustomerList(false)
          }
        } else {
          setCustomerOptions([])
          setShowCustomerList(false)
          setShowPhoneCustomerList(false)
        }
      } catch (e) {
        setCustomerOptions([])
        setShowCustomerList(false)
        setShowPhoneCustomerList(false)
      } finally {
        setSearching(false)
      }
    }, 300)
  }

  // 轉讓客戶搜尋（僅用於轉出方輸入框）
  const searchTransferCustomers = async (keyword) => {
    const searchTerm = keyword?.trim() || ''
    setTransferFromCustomerName(searchTerm)
    setTransferFromCustomerId(null)
    setTransferFromBalance(0)

    if (!searchTerm || searchTerm.length < 2) {
      setTransferSearchOptions([])
      return
    }

    if (transferSearchTimeoutRef.current) {
      clearTimeout(transferSearchTimeoutRef.current)
    }

    transferSearchTimeoutRef.current = setTimeout(async () => {
      setTransferSearching(true)
      try {
        const response = await api.get(
          `/api/business_B/${user.businessId}/customers/search`,
          { params: { searchTerm } }
        )
        if (response.data?.success) {
          const customers = response.data.data || []
          const list = customers.map((c) => ({
            id: c._id || c.userInfo?._id,
            name: c.name || c.userInfo?.name || '',
            phone: c.phone || c.userInfo?.phone || ''
          }))
          setTransferSearchOptions(list)
        } else {
          setTransferSearchOptions([])
        }
      } catch (e) {
        setTransferSearchOptions([])
      } finally {
        setTransferSearching(false)
      }
    }, 300)
  }

  const clearTransferSearch = () => {
    setTransferSearchOptions([])
    if (transferSearchTimeoutRef.current) {
      clearTimeout(transferSearchTimeoutRef.current)
    }
  }

  const handleSelectTransferCustomer = async (option) => {
    if (!option) return
    clearTransferSearch()
    setTransferFromCustomerName(option.name || '')
    setTransferFromCustomerId(option.id || null)

    if (!option.id || !user?.businessId) {
      setTransferFromBalance(0)
      return
    }

    try {
      // 取得轉出方儲值金餘額，顯示在提示文字
      const response = await api.get(
        `/api/businesses/${user.businessId}/customers/${option.id}/balance`
      )

      let balance = null
      if (response.data?.data?.balance !== undefined) {
        balance = response.data.data.balance
      } else if (response.data?.balance !== undefined) {
        balance = response.data.balance
      } else if (response.data?.data?.data?.balance !== undefined) {
        balance = response.data.data.data.balance
      }

      setTransferFromBalance(
        balance !== null && balance !== undefined ? balance : 0
      )
    } catch (error) {
      console.error('獲取轉出方餘額失敗:', error)
      setTransferFromBalance(0)
    }
  }

  // 獲取客戶儲值金餘額及詳細信息（包括業績折扣率）
  const fetchCustomerBalance = async (customerId) => {
    if (!customerId || !user?.businessId) {
      setCustomerBalance(null)
      setCustomerBalanceInfo(null)
      return
    }

    try {
      setLoadingBalance(true)
      const response = await api.get(
        `/api/businesses/${user.businessId}/customers/${customerId}/balance`
      )

      // 處理不同的響應格式
      let balance = null
      let balanceInfo = null

      if (response.data?.data) {
        // 優先使用 data.data 格式
        balance = response.data.data.balance
        balanceInfo = response.data.data // 保存完整信息
      } else if (response.data?.balance !== undefined) {
        balance = response.data.balance
        balanceInfo = response.data
      } else if (response.data?.data?.data) {
        balance = response.data.data.data.balance
        balanceInfo = response.data.data.data
      }

      const finalBalance =
        balance !== null && balance !== undefined ? balance : null
      setCustomerBalance(finalBalance)
      setCustomerBalanceInfo(balanceInfo)
    } catch (error) {
      console.error('獲取客戶餘額失敗:', error)
      setCustomerBalance(null)
      setCustomerBalanceInfo(null)
    } finally {
      setLoadingBalance(false)
    }
  }

  // 新增儲值金
  const handleTopupSubmit = async (consentSignatureId = null, consentResolved = false) => {
    const sigId = typeof consentSignatureId === 'string' ? consentSignatureId : null
    if (!selectedCustomerId) {
      message.error('請先選擇客戶再進行儲值')
      return
    }
    if (!topupAmount || topupAmount <= 0) {
      message.error('請輸入大於 0 的儲值金額')
      return
    }
    if (!topupSalesPersonnel) {
      message.error('請選擇銷售人員')
      return
    }
    if (!topupDate) {
      message.error('請選擇儲值日期')
      return
    }
    if (topupDiscount < 0 || topupDiscount > 100) {
      message.error('折扣必須在 0-100 之間')
      return
    }
    if (!topupPaymentMethod) {
      message.error('請選擇付款方式')
      return
    }
    const paymentSum =
      topupPaymentDetails.cash +
      topupPaymentDetails.bankTransfer +
      topupPaymentDetails.accountsReceivable +
      topupPaymentDetails.mobileCardSwipe
    if (paymentSum <= 0) {
      message.error('請至少輸入一筆付款金額')
      return
    }

    // 只有「真正送出儲值請求」時才進入送出中狀態。
    // 以前 setTopupLoading(true) 放在 try 一開頭，但第一次呼叫會先進購買同意書分支 return，
    // finally 又把 loading 設回 false；等同意書回呼真正送出時，反而沒有任何防重複送出的保護。
    let sending = false
    try {
      // 檢查是否有匹配的等級儲值回饋（搜尋所有啟用的等級）
      let bonusAmount = 0
      let matchedTier = null

      if (topupBonusOptedOut) {
        // 使用者主動選擇不套用活動方案，bonusAmount 維持 0
      } else if (selectedTopupBonus) {
        bonusAmount = Number(selectedTopupBonus.bonus) || 0
      } else {
        membershipTiers.forEach((tier) => {
          if (tier.isEnabled && tier.topupBonuses) {
            const matchedBonus = tier.topupBonuses.find(
              (bonus) => Number(bonus.amount) === Number(topupAmount)
            )
            if (matchedBonus) {
              // 如果匹配到多個等級（可能性較低），以等級更高的為準（假設 order 越小等級越高，或者簡單取最高 bonus）
              if (matchedBonus.bonus >= bonusAmount) {
                bonusAmount = Number(matchedBonus.bonus) || 0
                matchedTier = tier
              }
            }
          }
        })
      }

      // 儲值：先走購買同意書（無綁定則直接儲值；現場簽回傳 signatureId）
      if (!consentResolved) {
        setTopupConsent({
          open: true,
          variables: {
            planName: (topupSchemeKey && topupSchemeKey.trim()) || '儲值',
            amount: topupAmount || 0,
            price: topupAmount || 0,
            expiryDate: topupExpiryDate
              ? dayjs(topupExpiryDate).format('YYYY/MM/DD')
              : '',
            customerName: ''
          }
        })
        return
      }

      // 員工選的等級名稱（後端會嚴格驗證資格才升等；不符合條件就不升）
      const finalSelectedTierName = selectedTopupBonus?.tierName || null

      // 構建請求數據
      const requestData = {
        amount: topupAmount,
        // 如果有匹配的贈送金額，一併提交給後端
        ...(bonusAmount > 0 ? { bonusAmount: bonusAmount } : {}),
        // 使用者主動選擇不套用活動方案時，通知後端跳過 bonus 自動計算
        ...(topupBonusOptedOut ? { bonusOptedOut: true } : {}),
        // 員工最終選擇的會員等級（原本選擇或經提醒升等後的較高等級）
        ...(finalSelectedTierName
          ? { selectedTierName: finalSelectedTierName }
          : {}),
        // 備註為選填，僅在有輸入時傳遞
        ...(topupNote && topupNote.trim() ? { note: topupNote.trim() } : {}),
        // 儲值活動與使用期限（選填；之後結帳可指定從此儲值活動扣款）
        ...(topupSchemeKey && topupSchemeKey.trim()
          ? { schemeKey: topupSchemeKey.trim() }
          : {}),
        ...(topupExpiryDate
          ? { expiryDate: topupExpiryDate.format('YYYY-MM-DD') }
          : {})
      }

      // 取得銷售人員名稱（必填）
      const salesPerson = employees.find((e) => e._id === topupSalesPersonnel)
      if (!salesPerson) {
        message.error('找不到選擇的銷售人員')
        return
      }

      const totalPayment = Math.round(topupAmount * (topupDiscount / 100))

      requestData.salesPersonnelId = topupSalesPersonnel
      requestData.salesPersonnel = salesPerson.name
      requestData.discount = topupDiscount
      if (topupPaymentMethod) {
        requestData.paymentMethod = topupPaymentMethod
      }
      const paymentSum =
        topupPaymentDetails.cash +
        topupPaymentDetails.bankTransfer +
        topupPaymentDetails.accountsReceivable +
        (topupPaymentDetails.cardPayment || 0) +
        (topupPaymentDetails.mobilePayment || 0)
      if (paymentSum > 0) {
        const cardVal = topupPaymentDetails.cardPayment || 0
        const mobileVal = topupPaymentDetails.mobilePayment || 0
        requestData.paymentDetails = {
          cash: topupPaymentDetails.cash,
          bankTransfer: topupPaymentDetails.bankTransfer,
          accountsReceivable: topupPaymentDetails.accountsReceivable,
          // 新版分開兩欄
          cardPayment: cardVal,
          mobilePayment: mobileVal,
          // 雙寫舊欄位（向後相容）
          mobileCardSwipe: cardVal + mobileVal
        }
        // 金流手續費（自動算）+ 費率快照
        const cardFee = (cardVal * paymentFeeSettings.cardFeeRate) / 100
        const mobileFee =
          (mobileVal * paymentFeeSettings.mobilePaymentFeeRate) / 100
        requestData.paymentFee =
          Math.round((cardFee + mobileFee) * 100) / 100
        requestData.cardFeeRate = paymentFeeSettings.cardFeeRate
        requestData.mobilePaymentFeeRate =
          paymentFeeSettings.mobilePaymentFeeRate
      }
      requestData.date = topupDate.format('YYYY-MM-DD')
      if (sigId) requestData.consentSignatureId = sigId

      // 送出中不接受第二次（ref 同步可讀，連點或同意書重複回呼都擋得住）
      if (topupSubmittingRef.current) return
      topupSubmittingRef.current = true
      sending = true
      setTopupLoading(true)

      // 遇到「可能重複儲值」時先請店家確認；取消就保留視窗讓他檢查
      const response = await postTopupWithDuplicateConfirm(
        api,
        `/api/businesses/${user.businessId}/customers/${selectedCustomerId}/balance/topup`,
        requestData
      )
      if (!response) return

      message.success(response.data.message || '儲值成功')
      setTopupModalVisible(false)
      setTopupAmount(0)
      setSelectedTopupBonus(null)
      setTopupBonusOptedOut(false)
      setTopupSalesPersonnel(null)
      setTopupDiscount(100)
      setTopupPaymentMethod(null)
      setTopupPaymentDetails({
        cash: 0,
        bankTransfer: 0,
        accountsReceivable: 0,
        mobileCardSwipe: 0,
        cardPayment: 0,
        mobilePayment: 0
      })
      setTopupDate(tzNow())
      setTopupNote('')
      setTopupSchemeKey('')
      setTopupExpiryDate(null)
      await fetchCustomerBalance(selectedCustomerId)
      await fetchCustomerMembershipTier(selectedCustomerId)
    } catch (error) {
      console.error('儲值失敗:', error)
      message.error(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          '儲值失敗，請稍後再試'
      )
    } finally {
      if (sending) {
        topupSubmittingRef.current = false
        setTopupLoading(false)
      }
    }
  }

  // 儲值金轉讓
  const handleTransferSubmit = async () => {
    if (!selectedCustomerId) {
      message.error('請先選擇目前結帳的客戶（轉入方）')
      return
    }
    if (!transferFromCustomerName.trim()) {
      message.error('請輸入轉讓客戶名稱')
      return
    }
    if (!transferAmount || transferAmount <= 0) {
      message.error('請輸入大於 0 的轉入金額')
      return
    }

    try {
      setTransferLoading(true)

      let fromCustomerId = transferFromCustomerId

      // 若尚未透過清單選擇客戶，最後再以名稱搜尋一次嘗試匹配
      if (!fromCustomerId) {
        const searchTerm = transferFromCustomerName.trim()
        const searchRes = await api.get(
          `/api/business_B/${user.businessId}/customers/search`,
          { params: { searchTerm } }
        )

        const customers = searchRes.data?.data || []
        if (!customers.length) {
          message.error('找不到對應的轉讓客戶，請確認名稱是否正確')
          return
        }

        // 嘗試以姓名做精確比對（優先）
        const normalized = customers.map((c) => ({
          id: c._id || c.userInfo?._id,
          name: c.name || c.userInfo?.name || ''
        }))
        const exactMatches = normalized.filter(
          (c) => c.name && c.name.trim() === searchTerm
        )

        if (exactMatches.length === 1) {
          fromCustomerId = exactMatches[0].id
        } else if (!exactMatches.length && normalized.length === 1) {
          // 只有一筆搜尋結果時，直接使用
          fromCustomerId = normalized[0].id
        } else {
          message.error(
            '找到多位相同或相似的客戶，請透過下方清單點選確認正確客戶'
          )
          return
        }
      }

      if (!fromCustomerId) {
        message.error('無法取得轉讓客戶 ID，請稍後再試')
        return
      }

      if (fromCustomerId === selectedCustomerId) {
        message.error('轉出方與轉入方不可為同一位客戶')
        return
      }

      // 2. 呼叫後端轉讓 API
      await api.post(
        `/api/businesses/${user.businessId}/customers/balance/transfer`,
        {
          fromCustomerId,
          toCustomerId: selectedCustomerId,
          amount: transferAmount,
          transferDate: transferDate
            ? transferDate.format('YYYY-MM-DD')
            : tzNow().format('YYYY-MM-DD'),
          note: '結帳頁儲值金轉讓'
        }
      )

      message.success('儲值金轉讓成功')

      // 3. 重新取得轉入方（目前結帳客戶）的儲值金餘額
      await fetchCustomerBalance(selectedCustomerId)

      // 4. 關閉視窗並重置表單
      setTransferModalVisible(false)
      setTransferAmount(null)
      setTransferFromCustomerName('')
      setTransferFromBalance(0)
      setTransferDate(tzNow()) // 重置為今天
    } catch (error) {
      console.error('儲值金轉讓失敗:', error)
      const errMsg =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        '儲值金轉讓失敗，請稍後再試'
      message.error(errMsg)
    } finally {
      setTransferLoading(false)
    }
  }

  // 獲取會員等級列表
  const fetchMembershipTiers = async () => {
    if (!user?.businessId) return

    try {
      const response = await api.get(
        `/api/business_B/${user.businessId}/membership-tiers`
      )
      if (response.data.success) {
        const tiersData = response.data.data?.tiers || []
        const enabledTiers = tiersData.filter((t) => t.isEnabled !== false)
        setMembershipTiers(enabledTiers)
      }
    } catch (error) {
      console.error('獲取會員等級列表失敗:', error)
    }
  }

  // 獲取金流手續費設定
  const fetchPaymentFeeSettings = async () => {
    if (!user?.businessId) return
    try {
      const response = await api.get(
        `/api/business_B/${user.businessId}/payment-fee-settings`
      )
      if (response?.data?.success && response.data.data) {
        // API 回傳的已是正規化結果，這裡再跑一次是冪等的，
        // 但可以擋掉舊版後端只回三個欄位的情況（缺的欄位會補成安全預設）
        setPaymentFeeSettings(
          resolvePaymentFeeSettings({ paymentFeeSettings: response.data.data })
        )
      }
    } catch (error) {
      // 若 API 尚未部署，直接使用預設值，不阻擋結帳流程
      console.warn('獲取金流手續費設定失敗，使用預設值:', error?.message)
    }
  }

  // 獲取客戶會員等級和折扣率
  const fetchCustomerMembershipTier = async (customerId) => {
    if (!customerId || !user?.businessId) {
      setCustomerMembershipTier(null)
      setCustomerTierAccumulated(0)
      setMembershipDiscountRate(100)
      setApplyMembershipDiscount(true) // 重置為默認應用
      return
    }

    try {
      const response = await api.get(
        `/api/business_B/${user.businessId}/customers/${customerId}`
      )
      if (response.data.success) {
        const customerData = response.data.customer || response.data.data
        const rawTierName =
          customerData.businessSpecificData?.membershipLevel ||
          customerData.businessSpecificData?.membershipTier ||
          customerData.businessRelations?.[0]?.businessSpecificData
            ?.membershipLevel ||
          customerData.businessRelations?.[0]?.businessSpecificData
            ?.membershipTier ||
          null

        // 舊英文預設值對應到中文（向下相容）
        const legacyMap = {
          regular: '普通會員',
          silver: '銀卡會員',
          gold: '金卡會員',
          diamond: '鑽石會員',
          vip: 'VIP會員'
        }
        const lower =
          typeof rawTierName === 'string' ? rawTierName.toLowerCase() : null
        const isLegacyRegular = lower === 'regular'

        // 1) 直接命中商家現有等級 → 用 raw
        // 2) 舊英文（含 Regular）但商家有同名中文 → 對應過去
        // 3) 舊「Regular」且找不到對應 → 視為「未設置」，不顯示徽章（避免顯示英文）
        let membershipTierName = null
        if (rawTierName) {
          const direct = membershipTiers.find((t) => t.name === rawTierName)
          if (direct) {
            membershipTierName = rawTierName
          } else if (lower && legacyMap[lower]) {
            const cn = legacyMap[lower]
            const matched = membershipTiers.find((t) => t.name === cn)
            membershipTierName = matched ? matched.name : null
          } else {
            membershipTierName = rawTierName // 自定義名稱，雖找不到 tier 仍顯示
          }
          // Regular 找不到對應就不顯示
          if (isLegacyRegular && !membershipTierName) {
            membershipTierName = null
          }
        }

        setCustomerMembershipTier(membershipTierName)

        // 一併抓「累計實付金額」供儲值頁判斷升等資格
        const accumulated = Number(
          customerData.businessSpecificData?.tierAccumulatedAmount ??
            customerData.businessRelations?.[0]?.businessSpecificData
              ?.tierAccumulatedAmount ??
            0
        )
        setCustomerTierAccumulated(Number.isFinite(accumulated) ? accumulated : 0)

        // 查找對應的折扣率
        if (membershipTierName) {
          const tier = membershipTiers.find(
            (t) => t.name === membershipTierName
          )
          setMembershipDiscountRate(tier ? tier.discountRate : 100)
          setApplyMembershipDiscount(true) // 有會員等級時，默認應用折扣
        } else {
          setMembershipDiscountRate(100)
          setApplyMembershipDiscount(true) // 重置為默認應用
        }
      }
    } catch (error) {
      console.error('獲取客戶會員等級失敗:', error)
      setCustomerMembershipTier(null)
      setCustomerTierAccumulated(0)
      setMembershipDiscountRate(100)
      setApplyMembershipDiscount(true) // 重置為默認應用
    }
  }

  const handleCustomerSelect = async (value, option) => {
    const c = option?.customer
    if (!c) return
    const name = c.name || c.userInfo?.name || ''
    const phone = c.phone || c.userInfo?.phone || ''
    const customerId = c._id || c.userInfo?._id || null
    setSelectedCustomerId(customerId)
    setCustomerNameValue(name)
    // 選擇現有客戶時，自動設置為舊客戶
    form.setFieldsValue({
      customerName: name,
      phone: phone,
      customerType: 'existing'
    })
    setCustomerOptions([])
    setShowCustomerList(false)
    setShowPhoneCustomerList(false)

    // 獲取客戶儲值金餘額和會員等級
    if (customerId) {
      await fetchCustomerBalance(customerId)
      await fetchCustomerMembershipTier(customerId)
    } else {
      setCustomerBalance(null)
      setCustomerMembershipTier(null)
      setMembershipDiscountRate(100)
      setApplyMembershipDiscount(true) // 重置為默認應用
    }
  }

  // 新增客戶
  const handleAddCustomer = async (values) => {
    try {
      setAddCustomerLoading(true)
      const response = await api.post(
        `/api/business_B/${user.businessId}/customers`,
        {
          ...values,
          birthdate: values.birthdate?.format('YYYY-MM-DD')
        }
      )

      if (response.data.success) {
        message.success('顧客新增成功')
        setAddCustomerModalVisible(false)
        addCustomerForm.resetFields()

        // 新增成功後，自動搜索並選擇新客戶
        const newCustomer = response.data.data
        const customerId = newCustomer._id || newCustomer.id
        const customerName =
          newCustomer.name || newCustomer.userInfo?.name || ''
        const customerPhone =
          newCustomer.phone || newCustomer.userInfo?.phone || ''

        if (customerId) {
          // 設置選中的客戶
          setSelectedCustomerId(customerId)
          setCustomerNameValue(customerName)
          form.setFieldsValue({
            customerName: customerName,
            phone: customerPhone,
            customerType: 'new',
            customerSource: 'none'
          })

          // 獲取客戶儲值金餘額
          await fetchCustomerBalance(customerId)

          // 清空搜索結果
          setCustomerOptions([])
          setShowCustomerList(false)
          setShowPhoneCustomerList(false)
        }
      }
    } catch (error) {
      console.error('新增客戶失敗:', error)
      const errorMessage = error.response?.data?.error || '新增顧客失敗'
      message.error(errorMessage)
    } finally {
      setAddCustomerLoading(false)
    }
  }

  const handlePhoneSearch = async (phone) => {
    if (!phone || phone.length < 4) {
      setShowPhoneCustomerList(false)
      setCustomerOptions([])
      return
    }
    await searchCustomers(phone, 'phone')
  }

  // 套用折扣金額
  const handleApplyDiscount = () => {
    const discount = tempDiscountAmount || 0
    if (discount < 0) {
      message.error('折扣金額不能為負數')
      setTempDiscountAmount(0)
      return
    }
    if (discount > subtotalAmount) {
      message.error('折扣金額不能超過小計金額')
      setTempDiscountAmount(subtotalAmount)
      return
    }

    // 記錄套用折扣前的總金額和現金值
    const previousTotalAmount = totalAmount
    const currentCash = Number(form.getFieldValue('cash')) || 0
    const isAutoFilledCash =
      (customerBalance === null ||
        customerBalance === 0 ||
        customerBalance <= 0) &&
      currentCash > 0 &&
      Math.abs(currentCash - previousTotalAmount) < 1

    setDiscountAmount(discount)
    // 清除折扣趴數（因為現在使用折扣金額）
    setDiscountPercentage(0)
    setTempDiscountPercentage(0)

    // 如果用戶沒有儲值金，且現金是自動填充的（等於之前的總金額），則更新現金
    if (isAutoFilledCash) {
      // 直接計算新的總金額
      const newTotalAmount = Math.max(0, subtotalAmount - discount)
      setTimeout(() => {
        form.setFieldsValue({
          cash: newTotalAmount
        })
        form.setFields([{ name: 'cash', value: newTotalAmount }], {
          validate: false
        })
      }, 100)
    }

    message.success('折扣已套用')
  }

  const handleApplyDiscountPercentage = () => {
    const percentage = tempDiscountPercentage || 0
    if (percentage < 0) {
      message.error('折扣趴數不能為負數')
      setTempDiscountPercentage(0)
      return
    }
    if (percentage > 100) {
      message.error('折扣趴數不能超過100%')
      setTempDiscountPercentage(100)
      return
    }

    // 記錄套用折扣前的總金額和現金值
    const previousTotalAmount = totalAmount
    const currentCash = Number(form.getFieldValue('cash')) || 0
    const isAutoFilledCash =
      (customerBalance === null ||
        customerBalance === 0 ||
        customerBalance <= 0) &&
      currentCash > 0 &&
      Math.abs(currentCash - previousTotalAmount) < 1

    // 折扣趴數 = 「折抵幾 %」，與 App 端 calcDiscountAmount 的 percent 模式一致。
    // 例：輸入 10 → 折抵 10%（打 9 折）；折扣金額 = 原價 × 折扣趴數 / 100
    const calculatedDiscount = Math.round((subtotalAmount * percentage) / 100)

    setDiscountPercentage(percentage)
    setDiscountAmount(calculatedDiscount)
    setTempDiscountAmount(calculatedDiscount)

    // 如果用戶沒有儲值金，且現金是自動填充的（等於之前的總金額），則更新現金
    if (isAutoFilledCash) {
      // 直接計算新的總金額
      const newTotalAmount = Math.max(0, subtotalAmount - calculatedDiscount)
      setTimeout(() => {
        form.setFieldsValue({
          cash: newTotalAmount
        })
        form.setFields([{ name: 'cash', value: newTotalAmount }], {
          validate: false
        })
      }, 100)
    }
    setTempDiscountAmount(calculatedDiscount)
    message.success('折扣已套用')
  }

  const handleNext = () => {
    // 根據結帳模式決定需要驗證的字段
    const fieldsToValidate =
      checkoutMode === 'booking'
        ? [] // 預約結帳模式，步驟一不需要驗證（因為沒有客戶資訊步驟）
        : ['customerName', 'phone'] // 現場結帳模式，需要驗證客戶資訊

    // 檢查服務數量是否為0或缺失
    const validateServiceQuantities = () => {
      const missingServices = []
      for (const service of selectedServices) {
        const quantity = serviceQuantities[service.key] ?? service.quantity
        if (quantity === undefined || quantity === null || quantity === 0) {
          missingServices.push(service.name || '未命名服務')
        }
      }
      return missingServices
    }

    const proceedToNextStep = () => {
      // 檢查服務數量
      if (selectedServices.length > 0) {
        const missingServices = validateServiceQuantities()
        if (missingServices.length > 0) {
          Modal.warning({
            title: '請填寫服務數量',
            content: `以下服務的數量為0或未填寫，請先填寫數量：\n${missingServices.join(
              '\n'
            )}`,
            okText: '確定'
          })
          return
        }
      }

      // 檢查每個服務都必須有設計師（或第一劑/第二劑操作人員），否則不給進行下一步
      if (selectedServices.length > 0) {
        const servicesMissingDesigner = []
        for (const service of selectedServices) {
          const commissionRule = serviceCommissionRules[service.serviceId]
          const supportDoseTypes =
            service.supportDoseTypes !== undefined
              ? service.supportDoseTypes
              : commissionRule?.supportDoseTypes || false
          if (supportDoseTypes) {
            const missing = []
            if (!service.firstDosePersonnelId) missing.push('第一劑操作人員')
            if (!service.secondDosePersonnelId) missing.push('第二劑操作人員')
            if (!service.designerId && !service.personnelId)
              missing.push('設計師')
            if (missing.length > 0) {
              servicesMissingDesigner.push(
                `${service.name || service.serviceName}（須選擇：${missing.join(
                  '、'
                )}）`
              )
            }
          } else {
            if (!service.designerId && !service.personnelId) {
              servicesMissingDesigner.push(
                service.name || service.serviceName || '未命名服務'
              )
            }
          }
        }
        if (servicesMissingDesigner.length > 0) {
          Modal.warning({
            title: '請為每個服務選擇設計師',
            content: (
              <div>
                <p style={{ marginBottom: 8 }}>
                  以下服務尚未選擇設計師，請先選擇後再進行下一步：
                </p>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {servicesMissingDesigner.map((name, i) => (
                    <li key={i}>{name}</li>
                  ))}
                </ul>
              </div>
            ),
            okText: '確定'
          })
          return
        }
      }

      // 進入下一步前，檢查並調整支付方式以匹配新的總付款金額
      // 如果第一步修改了價格，總付款金額會變化，需要調整支付方式
      if (totalAmount > 0 && selectedCustomerId) {
        const currentStoredValue =
          Number(form.getFieldValue('storedValue')) || 0
        const currentCash = Number(form.getFieldValue('cash')) || 0
        const currentTransfer = Number(form.getFieldValue('transfer')) || 0
        const currentDeposit = Number(form.getFieldValue('deposit')) || 0
        const currentVoucher = Number(form.getFieldValue('voucher')) || 0
        // 新增：將刷卡、行動支付納入「已填付款」計算，避免現金被重複 auto-fill
        const currentCardPayment =
          Number(form.getFieldValue('cardPayment')) || 0
        const currentMobilePayment =
          Number(form.getFieldValue('mobilePayment')) || 0

        const totalPaid =
          currentStoredValue +
          currentCash +
          currentTransfer +
          currentDeposit +
          currentVoucher +
          currentCardPayment +
          currentMobilePayment

        // 如果支付總額與新的總付款金額不匹配，需要調整
        if (Math.abs(totalPaid - totalAmount) > 1) {
          if (appliedStoreCouponId) {
            const nonCashPaid =
              currentStoredValue +
              currentTransfer +
              currentDeposit +
              currentVoucher +
              currentCardPayment +
              currentMobilePayment
            const nextCash = Math.max(0, totalAmount - nonCashPaid)
            setTimeout(() => {
              if (appliedStoreCouponIdRef.current) return
              form.setFieldsValue({ cash: nextCash })
              form.setFields([{ name: 'cash', value: nextCash }], {
                validate: false
              })
            }, 100)
            return
          }
          // 優惠券與定金都是「已收到的付款」，重填儲值金/現金時必須先扣掉，
          // 否則切換步驟時儲值金會被填成全額應收，加上優惠券後變成已付 > 應收
          const keepVoucher = Math.min(currentVoucher, totalAmount)
          const keepDeposit = Math.min(
            currentDeposit,
            Math.max(0, totalAmount - keepVoucher)
          )
          const allocTotal = Math.max(
            0,
            totalAmount - keepVoucher - keepDeposit
          )
          // 如果有儲值金，優先使用儲值金（不超過餘額和總金額）
          if (customerBalance !== null && customerBalance > 0) {
            // 保留既有刷卡/行動支付，避免切回步驟四時現金被重複填滿
            const existingCard = currentCardPayment || 0
            const existingMobile = currentMobilePayment || 0
            const fillAmount = Math.min(
              customerBalance,
              Math.max(0, allocTotal - existingCard - existingMobile)
            )
            setTimeout(() => {
              form.setFieldsValue({
                storedValue: fillAmount,
                cash: Math.max(
                  0,
                  allocTotal - fillAmount - existingCard - existingMobile
                )
              })
            }, 100)
          } else {
            // 沒有儲值金，自動填充到cash（扣除既有的刷卡/行動支付）
            const existingCard = currentCardPayment || 0
            const existingMobile = currentMobilePayment || 0
            const nextCash = Math.max(
              0,
              allocTotal - existingCard - existingMobile
            )
            setTimeout(() => {
              form.setFieldsValue({
                cash: nextCash
              })
              form.setFields([{ name: 'cash', value: nextCash }], {
                validate: false
              })
            }, 100)
          }
        }
      }

      setCurrentStep(currentStep + 1)
    }

    if (fieldsToValidate.length > 0) {
      form.validateFields(fieldsToValidate).then(() => {
        proceedToNextStep()
      })
    } else {
      proceedToNextStep()
    }
  }

  const handlePrev = () => {
    setCurrentStep(currentStep - 1)
  }

  async function handleSaveDraft() {
    try {
      setSavingDraft(true)
      const allValues = form.getFieldsValue()
      const draftPayload = {
        customer: {
          customerId: selectedCustomerId || null,
          name: customerNameValue || allValues.customerName || '',
          phone: allValues.phone || '',
          customerType: allValues.customerType,
          customerSource: allValues.customerSource
        },
        bookingId: bookingId || null,
        services: selectedServices.map((s) => ({
          serviceId: s.serviceId,
          categoryId: s.categoryId,
          originalPrice: s.originalPrice ?? s.basePrice ?? (s.price || 0),
          price: s.price ?? s.originalPrice ?? s.basePrice ?? 0,
          quantity: serviceQuantities[s.key] ?? s.quantity ?? 1,
          designerId: s.designerId || s.personnelId || null,
          personnelId: s.designerId || s.personnelId || null,
          assistantId: s.assistantId || null,
          supportDoseTypes: s.supportDoseTypes || false,
          firstDosePersonnelId: s.firstDosePersonnelId || null,
          secondDosePersonnelId: s.secondDosePersonnelId || null,
          ...s
        })),
        products: selectedProducts.map((p) => ({
          productId: p.productId,
          quantity:
            productQuantityInputs[p.key] !== undefined
              ? productQuantityInputs[p.key]
              : p.quantity,
          unitPrice: p.unitPrice,
          totalPrice: p.totalPrice,
          productSalesPersonnelId: p.productSalesPersonnelId || null,
          ...p
        })),
        time: {
          date: allValues.serviceDate?.format?.('YYYY-MM-DD') || null,
          startTime: allValues.serviceStartTime || null,
          endTime: allValues.serviceEndTime || null
        },
        remarks: allValues.remarks || '',
        payment: {
          storedValue: Number(allValues.storedValue) || 0,
          cash: Number(allValues.cash) || 0,
          transfer: Number(allValues.transfer) || 0,
          deposit: Number(allValues.deposit) || 0,
          bonus: Number(allValues.bonus) || 0,
          debt: Number(allValues.debt) || 0,
          installment: Number(allValues.installment) || 0,
          voucher: Number(allValues.voucher) || 0,
          // 新增：刷卡 / 行動支付 / 手續費
          cardPayment: Number(allValues.cardPayment) || 0,
          mobilePayment: Number(allValues.mobilePayment) || 0,
          paymentFee: Number(allValues.paymentFee) || 0,
          paymentFeeOverridden: paymentFeeOverriddenRef.current
        },
        totals: {
          grandTotal: totalAmount,
          subtotalAmount,
          discountAmount: finalDiscountAmount
        },
        /**
         * 堂票折抵。草稿不會扣張（扣張只在正式結帳），這裡存的是勾選狀態，
         * 好讓草稿叫回來時能還原。少了它，被折抵的服務會以 price=0 存進草稿
         * 卻沒有票，接著結帳就變成免費做、也沒扣到票。
         *
         * 用 serviceIndex 而不是 serviceKey 對位 —— key 在載入草稿時會重新
         * 產生（draft-xxx-時間戳），存舊的 key 回來對不到任何一列。
         */
        classTicketUsages: Object.entries(ticketRedemptions)
          .map(([serviceKey, redemption]) => {
            const idx = (selectedServices || []).findIndex(
              (s) => s.key === serviceKey
            )
            if (idx < 0) return null
            const svc = selectedServices[idx]
            return {
              serviceIndex: idx,
              serviceId: svc.serviceId,
              serviceName: svc.serviceName || svc.name || '',
              ticketId: redemption.ticketId,
              ticketCode: redemption.ticketCode || '',
              templateName: redemption.templateName || '',
              count: redemption.count || 1,
              // 取消折抵時要還原的價格；沒有就退回這列的原價
              prevPrice:
                redemption.prevPrice ?? svc.originalPrice ?? svc.basePrice ?? 0
            }
          })
          .filter(Boolean),
        membershipLevel: customerMembershipTier || null,
        membershipDiscount: membershipDiscountRate,
        participantCount: participantCount || 1,
        checkoutMode: bookingId ? 'booking' : 'walkin'
      }
      if (draftIdFromUrl) {
        await api.patch(
          `/api/businesses/${user.businessId}/checkout/drafts/${draftIdFromUrl}`,
          draftPayload
        )
        message.success('草稿已更新')
      } else {
        const res = await api.post(
          `/api/businesses/${user.businessId}/checkout/drafts`,
          draftPayload
        )
        message.success('草稿已儲存')
        const id = res.data?._id || res.data?.id
        if (id) {
          const next = new URLSearchParams(searchParams)
          next.set('draftId', id)
          setSearchParams(next)
        }
      }
    } catch (error) {
      console.error('儲存草稿失敗:', error)
      const msg = error.response?.data?.message || error.response?.data?.error
      if (error.response?.status === 404 || error.response?.status === 501) {
        message.info(
          '草稿功能需後端 API 完成後啟用，請見 docs/結帳草稿功能-後端API文檔.md'
        )
      } else {
        message.error(msg || '儲存草稿失敗，請稍後再試')
      }
    } finally {
      setSavingDraft(false)
    }
  }

  const handleSubmit = async () => {
    // ── 防重複送出：若正在處理中則直接 return ────────────────────────────────
    if (loading) return
    try {
      setLoading(true)

      // 獲取所有表單值
      const allValues = form.getFieldsValue()

      // 檢查是否有選擇服務、商品或堂票（堂票需有有效範本）
      const hasServices = selectedServices.length > 0
      const hasProducts = selectedProducts.length > 0
      const hasClassTickets = selectedClassTickets.some(
        (t) =>
          t.templateId &&
          (Number(t.quantity) || 0) > 0
      )
      if (!hasServices && !hasProducts && !hasClassTickets) {
        message.error('請至少選擇一項服務、商品或堂票')
        setLoading(false)
        return
      }

      // 根據結帳模式和是否有服務決定需要驗證的字段
      // 日期和開始時間總是需要驗證（不管是服務還是商品），結束時間只在有服務時驗證
      const fieldsToValidate =
        checkoutMode === 'booking'
          ? hasServices
            ? ['serviceDate', 'serviceStartTime', 'serviceEndTime'] // 預約結帳且有服務：需要驗證日期和時間
            : ['serviceDate', 'serviceStartTime'] // 預約結帳但只有商品：需要驗證日期和開始時間
          : hasServices
            ? [
                'customerName',
                'phone',
                'serviceDate',
                'serviceStartTime',
                'serviceEndTime'
              ] // 現場結帳且有服務：需要驗證所有字段
            : ['customerName', 'phone', 'serviceDate', 'serviceStartTime'] // 現場結帳但只有商品：需要驗證客戶信息、日期和開始時間

      // 驗證必填字段
      const values = await form.validateFields(fieldsToValidate)

      // 檢查服務數量是否為0或缺失
      if (hasServices) {
        const missingServices = []
        const zeroQuantityServices = []
        for (const service of selectedServices) {
          const quantity = serviceQuantities[service.key] ?? service.quantity
          if (quantity === undefined || quantity === null) {
            missingServices.push(service.name || '未命名服務')
          } else if (quantity === 0) {
            zeroQuantityServices.push(service.name || '未命名服務')
          }
        }

        if (missingServices.length > 0 || zeroQuantityServices.length > 0) {
          const allIssues = [...missingServices, ...zeroQuantityServices]
          Modal.warning({
            title: '請填寫服務數量',
            content: `以下服務的數量為0或未填寫，請先填寫數量：\n${allIssues.join(
              '\n'
            )}`,
            okText: '確定'
          })
          setLoading(false)
          return
        }
      }

      // 驗證每個服務都必須有設計師（所有結帳模式都需要）
      if (hasServices) {
        for (const service of selectedServices) {
          // 檢查服務是否支持第一劑/第二劑
          const commissionRule = serviceCommissionRules[service.serviceId]
          const supportDoseTypes =
            service.supportDoseTypes !== undefined
              ? service.supportDoseTypes
              : commissionRule?.supportDoseTypes || false

          if (supportDoseTypes) {
            const missing = []
            if (!service.firstDosePersonnelId) missing.push('第一劑操作人員')
            if (!service.secondDosePersonnelId) missing.push('第二劑操作人員')
            if (!service.designerId && !service.personnelId)
              missing.push('設計師')
            if (missing.length > 0) {
              message.error(
                `服務「${
                  service.name || service.serviceName
                }」必須選擇：${missing.join('、')}`
              )
              setLoading(false)
              return
            }
          } else {
            // 一般抽成模式：必須有設計師
            if (!service.designerId && !service.personnelId) {
              message.error(
                `服務「${service.name || service.serviceName}」必須選擇設計師`
              )
              setLoading(false)
              return
            }
          }
        }
      }

      // 驗證每個商品都必須有銷售人員（必選）
      if (selectedProducts.length > 0) {
        const missingSalesPersonnel = selectedProducts.filter(
          (p) => !p.productSalesPersonnelId
        )
        if (missingSalesPersonnel.length > 0) {
          message.error('請為每個商品選擇銷售人員')
          setLoading(false)
          return
        }
      }

      // 堂票重複扣堂提醒：同一張票在「服務日期」當天已在堂票頁手動核銷過，
      // 再用結帳折抵就是同一次來店扣兩堂（例：當天手動扣、之後補結帳）。
      // 查詢失敗不擋結帳。
      const redeemTicketIds = [
        ...new Set(
          Object.values(ticketRedemptions || {})
            .map((r) => r?.ticketId)
            .filter(Boolean)
        )
      ]
      if (redeemTicketIds.length > 0 && allValues.serviceDate) {
        const redeemDate = dayjs.isDayjs(allValues.serviceDate)
          ? allValues.serviceDate.format('YYYY-MM-DD')
          : String(allValues.serviceDate).slice(0, 10)
        let manualRedeems = []
        try {
          const res = await api.get(
            `/api/businesses/${user.businessId}/class-tickets/manual-redeems`,
            { params: { ticketIds: redeemTicketIds.join(','), date: redeemDate } }
          )
          manualRedeems = res?.data?.data || []
        } catch (e) {
          manualRedeems = []
        }
        if (manualRedeems.length > 0) {
          const proceed = await new Promise((resolve) => {
            Modal.confirm({
              title: '這張堂票當天已手動核銷過，確定要再扣嗎？',
              content: (
                <div>
                  <div style={{ marginBottom: 8 }}>
                    {redeemDate} 已在堂票頁手動核銷：
                  </div>
                  {manualRedeems.map((m) => (
                    <div key={m.usageId}>
                      ・{m.ticketCode} {m.templateName}　扣 {m.count} 堂
                      {m.operatorName ? `（${m.operatorName}）` : ''}
                    </div>
                  ))}
                  <div style={{ marginTop: 8, color: '#8c8c8c' }}>
                    如果是同一次來店，請取消這裡的堂票折抵，避免重複扣堂。
                  </div>
                </div>
              ),
              okText: '確定再扣',
              cancelText: '取消，回去修改',
              onOk: () => resolve(true),
              onCancel: () => resolve(false)
            })
          })
          if (!proceed) {
            setLoading(false)
            return
          }
        }
      }

      // 在驗證後，再次獲取所有表單值（包括支付方式），確保獲取最新值
      // 注意：必須在驗證之後獲取，因為驗證會觸發表單值的更新
      const latestFormValues = form.getFieldsValue()

      // 處理日期：可能是 dayjs 對象或字符串
      let serviceDate = null
      if (allValues.serviceDate) {
        if (dayjs.isDayjs(allValues.serviceDate)) {
          serviceDate = allValues.serviceDate.format('YYYY-MM-DD')
        } else if (typeof allValues.serviceDate === 'string') {
          serviceDate = allValues.serviceDate
        } else {
          serviceDate = dayjs(allValues.serviceDate).format('YYYY-MM-DD')
        }
      }

      // 獲取客戶信息（預約結帳或重新結帳且已有預約：用預約客戶送 payload；現場結帳：從表單獲取）
      let customerName = ''
      let phone = ''
      let customerType = 'new'
      let customerSource = 'none'
      let submitCustomerId = selectedCustomerId

      const useBookingCustomer =
        (checkoutMode === 'booking' && bookingData) ||
        (orderIdFromUrl && isRecheckout && bookingData)
      if (useBookingCustomer) {
        customerName = bookingData.userName || ''
        phone = bookingData.phone || ''
        customerType = bookingData.customerType || 'existing'
        customerSource = bookingData.customerSource || 'none'
        // 以預約上的顧客檔 ID 為準；userId 是客人當時登入的平台帳號，
        // 客人換過帳號時會對不到顧客檔（後端會回「客戶不存在」）
        submitCustomerId =
          bookingData.customerId || bookingData.userId || selectedCustomerId
      } else {
        customerName =
          values.customerName ||
          allValues.customerName ||
          customerNameValue ||
          ''
        phone = values.phone || allValues.phone || ''
        customerType = values.customerType || allValues.customerType || 'new'
        customerSource =
          values.customerSource || allValues.customerSource || 'none'
      }

      // 驗證客戶信息（現場結帳模式才需要）
      if (checkoutMode === 'walkin' && !customerName) {
        message.error('客戶姓名為必填')
        setLoading(false)
        return
      }

      // 驗證業績折扣率（如果啟用了業績折扣）
      if (applyRevenueDiscount) {
        if (revenueDiscountRate === null || revenueDiscountRate === undefined) {
          message.error('請輸入折扣率')
          setLoading(false)
          return
        }
        if (revenueDiscountRate < 0 || revenueDiscountRate > 100) {
          message.error('折扣率必須在 0-100 之間')
          setLoading(false)
          return
        }
      }

      // 驗證支付金額：確保所有支付方式的總和等於應收金額
      // 嘗試多種方式獲取表單值，確保能正確獲取到用戶輸入的值
      const getPaymentValue = (fieldName) => {
        // 優先從驗證後的最新表單值獲取（最可靠）
        let value = latestFormValues[fieldName]

        // 如果 latestFormValues 中沒有值，才嘗試其他方式
        // 注意：不要同時從多個來源獲取，避免重複計算
        if (value === undefined || value === null || value === '') {
          // 方法2: 嘗試使用 form.getFieldValue
          const formValue = form.getFieldValue(fieldName)
          if (
            formValue !== undefined &&
            formValue !== null &&
            formValue !== ''
          ) {
            value = formValue
          } else {
            // 方法3: 最後才嘗試從 DOM 直接獲取（作為備選）
            const inputElement = document.getElementById(fieldName)
            if (
              inputElement &&
              inputElement.value !== '' &&
              inputElement.value !== null &&
              inputElement.value !== undefined
            ) {
              value = inputElement.value
            }
          }
        }

        // 轉換為數字
        if (value === null || value === undefined || value === '') {
          return 0
        }
        const numValue = Number(value)
        return isNaN(numValue) ? 0 : numValue
      }

      const storedValueAmount = getPaymentValue('storedValue')
      const voucherAmount = getPaymentValue('voucher')
      const cashAmount = getPaymentValue('cash')
      const transferAmount = getPaymentValue('transfer')
      const depositAmount = getPaymentValue('deposit')
      const bonusAmount = getPaymentValue('bonus')
      const debtAmount = getPaymentValue('debt')
      const installmentAmount = getPaymentValue('installment')
      // 新增：刷卡 / 行動支付 / 手續費
      const cardPaymentAmount = getPaymentValue('cardPayment')
      const mobilePaymentAmount = getPaymentValue('mobilePayment')
      const paymentFeeAmount = getPaymentValue('paymentFee')

      // 優惠券視同現金，屬於付款方式，計入已付
      const totalPaid =
        storedValueAmount +
        voucherAmount +
        cashAmount +
        transferAmount +
        depositAmount +
        bonusAmount +
        debtAmount +
        installmentAmount +
        cardPaymentAmount +
        mobilePaymentAmount

      // 付款總額必須等於應收金額。
      // 舊版容許 1 元誤差，導致剛好差 1 元的溢收被放行（客戶儲值金被多扣），
      // 改為兩邊先四捨五入後嚴格比對。
      const roundedTotalPaid = Math.round(totalPaid)
      const roundedTotalAmount = Math.round(totalAmount)
      if (roundedTotalPaid !== roundedTotalAmount) {
        const gap = roundedTotalPaid - roundedTotalAmount
        message.error(
          `支付金額總和（NT$ ${roundedTotalPaid.toLocaleString()}）與應收金額（NT$ ${roundedTotalAmount.toLocaleString()}）不符（${
            gap > 0 ? '超收' : '短收'
          } NT$ ${Math.abs(gap).toLocaleString()}），請檢查支付方式${
            voucherAmount > 0
              ? '；使用優惠券時，其餘付款方式應為「應收 − 優惠券」'
              : ''
          }`
        )
        setLoading(false)
        return
      }

      // 驗證儲值金餘額（如果使用儲值金支付）
      if (storedValueAmount > 0 && selectedCustomerId) {
        const schemeKey = allValues.storedValueSchemeKey
        if (schemeKey && customerBalanceInfo?.balanceByScheme?.length > 0) {
          const scheme = customerBalanceInfo.balanceByScheme.find(
            (s) => s.schemeKey === schemeKey
          )
          if (scheme && Number(scheme.balance || 0) < storedValueAmount) {
            message.error(
              `儲值活動「${schemeKey}」餘額不足：NT$ ${Number(scheme.balance || 0).toLocaleString()}，需要 NT$ ${storedValueAmount.toLocaleString()}`
            )
            setLoading(false)
            return
          }
        } else if (customerBalance === null || customerBalance === undefined) {
          message.warning('無法獲取客戶儲值金餘額，請確認客戶信息')
        } else if (storedValueAmount > customerBalance) {
          message.error(
            `儲值金餘額不足：餘額 NT$ ${customerBalance.toLocaleString()}，支付金額 NT$ ${storedValueAmount.toLocaleString()}`
          )
          setLoading(false)
          return
        }
      }

      // 處理電話：如果沒有電話，自動補上虛擬號碼（00開頭）
      if (!phone || !phone.trim()) {
        // 生成虛擬號碼：00 + 8位隨機數字
        const randomDigits = Math.floor(10000000 + Math.random() * 90000000)
        phone = `00${randomDigits}`
      }

      // 預約結帳模式：確保有預約數據（重新結帳時已從訂單帶入，不要求預約數據）
      if (
        checkoutMode === 'booking' &&
        !bookingData &&
        !(orderIdFromUrl && isRecheckout)
      ) {
        message.error('預約信息不存在，請重新選擇')
        setLoading(false)
        return
      }

      // 如果是現場結帳模式且有選擇服務，需要先創建預約
      // 如果只選擇商品，不需要創建預約
      let finalBookingId = bookingId
      if (checkoutMode === 'walkin' && hasServices) {
        try {
          // 獲取第一個服務的信息來創建預約
          const firstService = selectedServices[0]
          if (!firstService) {
            message.error('請至少選擇一項服務')
            setLoading(false)
            return
          }

          const serviceProduct = getServiceOrAdditionalById(firstService.serviceId)
          if (!serviceProduct) {
            message.error('服務信息不存在')
            setLoading(false)
            return
          }

          // 檢查服務是否支持第一劑/第二劑
          const commissionRule = serviceCommissionRules[firstService.serviceId]
          const supportDoseTypes =
            firstService.supportDoseTypes !== undefined
              ? firstService.supportDoseTypes
              : commissionRule?.supportDoseTypes || false

          // 構建預約數據
          const bookingData = {
            businessId: user.businessId,
            status: 'confirmed',
            productId: firstService.serviceId,
            productName: serviceProduct.name,
            duration: serviceProduct.duration || 60,
            depositStatus: 'not_required',
            date: serviceDate,
            time: allValues.serviceStartTime || '',
            userName: customerName,
            phone: phone,
            email: allValues.email || '',
            customerId: selectedCustomerId || null,
            originalPrice: firstService.price,
            discountedPrice: firstService.price,
            note: allValues.remarks || '',
            bookingSource: 'business',
            participantCount:
              allValues.participantCount || participantCount || 1,
            // 現場結帳自動建立的預約紀錄：靜音模式。
            // 客人就在現場，不應收到預約確認通知或施作同意書，
            // 美容師也不需要「新預約」推播
            notifyCustomer: false,
            suppressNotifications: true
          }

          // 根據抽成規則設置操作人員
          if (supportDoseTypes) {
            // 第一劑/第二劑模式
            // 使用第一劑操作人員作為主要設計師（用於預約列表顯示）
            const firstDosePersonnel = employees.find(
              (e) => e._id === firstService.firstDosePersonnelId
            )
            bookingData.stylistId = firstService.firstDosePersonnelId || null
            bookingData.stylistName = firstDosePersonnel?.name || ''
            // 設置服務數組，包含第一劑/第二劑操作人員
            bookingData.services = selectedServices.map((s) => {
              const sCommissionRule = serviceCommissionRules[s.serviceId]
              const sSupportDoseTypes =
                s.supportDoseTypes !== undefined
                  ? s.supportDoseTypes
                  : sCommissionRule?.supportDoseTypes || false

              if (sSupportDoseTypes) {
                return {
                  serviceId: s.serviceId,
                  serviceName: s.name,
                  basePrice: s.originalPrice || s.price || 0,
                  actualPrice: s.price || 0,
                  designerId: s.designerId || s.personnelId || null, // 設計師ID（用於計算設計師抽成和業績）
                  firstDosePersonnelId: s.firstDosePersonnelId || null,
                  secondDosePersonnelId: s.secondDosePersonnelId || null,
                  assistantId: null
                }
              } else {
                return {
                  serviceId: s.serviceId,
                  serviceName: s.name,
                  basePrice: s.originalPrice || s.price || 0,
                  actualPrice: s.price || 0,
                  designerId: s.designerId || s.personnelId || null,
                  assistantId: s.assistantId || null
                }
              }
            })
          } else {
            // 一般抽成模式
            bookingData.stylistId =
              firstService.designerId ||
              firstService.personnelId ||
              allValues.servicePersonnel ||
              null
            bookingData.stylistName =
              employees.find(
                (e) =>
                  e._id ===
                  (firstService.designerId ||
                    firstService.personnelId ||
                    allValues.servicePersonnel)
              )?.name || ''
            // 設置服務數組，包含設計師和助理
            bookingData.services = selectedServices.map((s) => ({
              serviceId: s.serviceId,
              serviceName: s.name,
              basePrice: s.originalPrice || s.price || 0,
              actualPrice: s.price || 0,
              quantity: serviceQuantities[s.key] || s.quantity || 1,
              designerId: s.designerId || s.personnelId || null,
              assistantId: s.assistantId || null
            }))
          }

          const bookingResponse = await api.post(
            '/api/business/bookings',
            bookingData
          )
          finalBookingId = bookingResponse.data._id || bookingResponse.data.id
          message.success('預約已創建')
        } catch (error) {
          console.error('創建預約失敗:', error)
          message.error('創建預約失敗，請重試')
          setLoading(false)
          return
        }
      }

      // 計算總金額（會員折扣已應用在服務價格中，這裡只需要減去手動折扣）
      // 計算最終折扣金額（優先使用折扣趴數）
      // 折扣趴數 = 「折抵幾 %」，與 App 端一致；折扣金額 = 原價 × 折扣趴數 / 100
      const calculatedFinalDiscount =
        discountPercentage > 0 && discountPercentage <= 100
          ? Math.round((subtotalAmount * discountPercentage) / 100)
          : discountAmount
      const finalTotalWithMembershipDiscount = Math.max(
        0,
        subtotalAmount - calculatedFinalDiscount
      )

      // 實際應收 = 訂單總額。送給後端的 amounts.receivable / payment.receivable 必須為此值，不論以現金/儲值等何種方式支付（例如現場改現金 450 也傳 450，不傳 0）
      // 使用 displayTotalAmount：重新結帳且項目 state 尚未帶入價格時，
      // 會退回訂單載入的總額（recheckoutLoadedTotal），避免送出 receivable=0
      // 造成後端「應收金額差異：前端提交 0」與報表金額錯誤
      const actualReceivable = Number.isFinite(displayTotalAmount)
        ? Number(displayTotalAmount)
        : Number.isFinite(totalAmount)
          ? Number(totalAmount)
          : 0

      /**
       * 服務費（金流客人加收服務費）—— 只在送出的最後一刻併上去。
       *
       * 店家在刷卡／行動支付欄位填的是「這筆要刷多少貨款」，服務費不進表單：
       * 整個結帳頁的自動分配與溢收防護都建立在「付款欄位總和 = 應收」這個等式上，
       * 把服務費塞進欄位會讓那套邏輯全面誤判成溢收。
       *
       * 所以在這裡才組裝成後端要的形狀：
       *   刷卡金額 = 貨款 + 該筆服務費   ← 這才是刷卡機實際會刷的數字，
       *                                    收單行的手續費也是對這個金額抽的
       *   應收     = 原應收 + 服務費總額
       *
       * surchargeBase 是「未含服務費」的基數，後端拿它用同一支公式
       * （utils/paymentSurcharge.js）重算驗證，不必從總額反推。
       *
       * 沒開加收服務費時 surcharge 全部為 0，下面每個值都等同改版前。
       */
      const surcharge = calcCustomerSurcharge({
        cardBase: cardPaymentAmount,
        mobileBase: mobilePaymentAmount,
        settings: paymentFeeSettings
      })
      /**
       * 分期手續費：跟服務費同樣是「客人這次要多付的錢」，所以要加進應收。
       *
       * 但兩者性質不同 —— 服務費是原封轉付給收單行的過路財，
       * 分期手續費是店家自己賺的；相同的是兩者都不該進設計師的業績基數，
       * 後端 utils/commissionService.js 會一起排除。
       */
      const installmentFeeAmount = installmentDraft
        ? Math.max(0, Math.round(Number(installmentDraft.feeAmount) || 0))
        : 0
      const receivableWithSurcharge =
        actualReceivable + surcharge.total + installmentFeeAmount
      const cardPaymentWithSurcharge = cardPaymentAmount + surcharge.card
      const mobilePaymentWithSurcharge = mobilePaymentAmount + surcharge.mobile

      /**
       * 手續費要對「含服務費的刷卡金額」算 —— 收單行是對客人實際刷的總額抽成。
       * 畫面上的 paymentFee 欄位是依貨款自動帶入的，開啟加收服務費後會比實際少一點，
       * 這裡補正。店家自己改過（overridden）就尊重店家的數字，不動它。
       */
      const paymentFeeToSubmit =
        surcharge.total > 0 &&
        paymentFeeSettings.autoCalculate &&
        !paymentFeeOverriddenRef.current
          ? Math.round(
              ((cardPaymentWithSurcharge *
                (Number(paymentFeeSettings.cardFeeRate) || 0)) /
                100 +
                (mobilePaymentWithSurcharge *
                  (Number(paymentFeeSettings.mobilePaymentFeeRate) || 0)) /
                  100) *
                100
            ) / 100
          : paymentFeeAmount

      // 先為每筆服務算好「業績權重」與數量（單一來源），再算業績基準總和與應用業績總額
      // 業績權重 = 單價×數量；0 元時：若 includeZeroYuanInRevenue 則用原價×數量（如 2500×2=5000），否則權重=0
      const serviceAllocationRows = selectedServices.map((s) => {
        const originalPrice = s.originalPrice ?? s.basePrice ?? (s.price || 0)
        const serviceQuantity = serviceQuantities[s.key] ?? s.quantity ?? 1
        const rawPrice = s.price ?? originalPrice
        // 表單慣例：s.price 一律為「該行總價」（見 totalServiceAmount 註解，含手動調價）；
        // API 慣例為「單價」→ 一律除以數量換算成單價提交。
        // 舊邏輯只在 price≈原價×數量時才換算，手動調價（如原價2800 x2 手動把總價改回2800）
        // 會被誤判成單價送出，後端存成 單價2800×數量2，明細就顯示 2,800 x2 = 5,600
        const actualPrice =
          rawPrice !== undefined && rawPrice !== null
            ? serviceQuantity > 0
              ? Math.round((Number(rawPrice) / serviceQuantity) * 100) / 100
              : Number(rawPrice)
            : originalPrice
        const isZeroYuan = actualPrice === 0
        const lineTotalForAllocation =
          isZeroYuan && !includeZeroYuanInRevenue
            ? 0
            : (actualPrice > 0 ? actualPrice : originalPrice) * serviceQuantity
        return {
          ...s,
          originalPrice,
          actualPrice,
          serviceQuantity,
          lineTotalForAllocation
        }
      })
      const totalServiceRevenueBasisForAllocation =
        serviceAllocationRows.reduce(
          (acc, row) => acc + row.lineTotalForAllocation,
          0
        )
      const totalRevenueBasisForAllocation =
        totalServiceRevenueBasisForAllocation + totalProductRevenueBasis

      // 應用業績總額：自動區分付款組合——
      //   有用儲值金付款時：折扣率只套用「儲值金支付的部分」，其餘付款（現金/刷卡等）全額計入
      //     例：總額 3,800、現金 400 ＋ 儲值 3,400、折扣率 90% → 3,400×0.9 + 400 = 3,460
      //   沒用儲值金時：維持整單 × 折扣率（原行為）
      const validRevenueDiscountRate =
        revenueDiscountRate !== null &&
        revenueDiscountRate !== undefined &&
        revenueDiscountRate >= 0 &&
        revenueDiscountRate <= 100
          ? revenueDiscountRate
          : 100
      const storedValuePaidForRevenue = Math.max(
        0,
        Number(allValues.storedValue) || 0
      )
      let totalRevenueAmount = null
      if (applyRevenueDiscount && totalRevenueBasisForAllocation >= 0) {
        if (storedValuePaidForRevenue > 0) {
          // 折扣部位以業績基準為上限（儲值金可能付了不計業績的品項）
          const discountedPortion = Math.min(
            storedValuePaidForRevenue,
            totalRevenueBasisForAllocation
          )
          const fullPortion =
            totalRevenueBasisForAllocation - discountedPortion
          totalRevenueAmount = Math.round(
            (discountedPortion * validRevenueDiscountRate) / 100 + fullPortion
          )
        } else {
          totalRevenueAmount = Math.round(
            (totalRevenueBasisForAllocation * validRevenueDiscountRate) / 100
          )
        }
      }

      const orderData = {
        bookingId: finalBookingId, // 關聯預約 ID（重新結帳時保留以通過「該時段已有預約」驗證）
        participantCount: allValues.participantCount || participantCount || 1, // 預約人數
        customer: {
          customerId: submitCustomerId || null,
          name: customerName,
          phone: phone,
          servicePersonnel: allValues.servicePersonnel || null,
          customerType,
          customerSource
        },
        membershipLevel: customerMembershipTier || null, // 本次使用的會員等級
        membershipDiscount: membershipDiscountRate, // 會員折扣率（實際應用情況由各服務的discountRate決定）
        services: serviceAllocationRows.map((s) => {
          const {
            originalPrice,
            actualPrice,
            serviceQuantity,
            lineTotalForAllocation
          } = s
          // 檢查服務是否支持第一劑/第二劑
          const commissionRule = serviceCommissionRules[s.serviceId]
          const supportDoseTypes =
            s.supportDoseTypes !== undefined
              ? s.supportDoseTypes
              : commissionRule?.supportDoseTypes || false

          // 計算折扣價（用於判斷是否應用了折扣）
          const discountedPrice =
            membershipDiscountRate < 100
              ? Math.round((originalPrice * membershipDiscountRate) / 100)
              : originalPrice

          // 判斷是否應用了折扣（實際價格等於折扣價）
          const isDiscountApplied =
            actualPrice === discountedPrice && actualPrice !== originalPrice

          // 業績金額：傳「整筆業績」（該行總額），權重以 單價×數量 計算（0 元時 = 原價×數量，例如 2500×2=5000）
          let revenueAmount = null
          if (
            applyRevenueDiscount &&
            totalRevenueAmount !== null &&
            totalRevenueBasisForAllocation > 0
          ) {
            const lineRevenueTotal = Math.round(
              (totalRevenueAmount * lineTotalForAllocation) /
                totalRevenueBasisForAllocation
            )
            revenueAmount = lineRevenueTotal
          }

          if (supportDoseTypes) {
            // 第一劑/第二劑模式
            return {
              serviceId: s.serviceId,
              categoryId: s.categoryId,
              originalPrice: originalPrice, // 原始價格
              price: actualPrice, // 實收價（可能是折扣價或手動更改價）
              quantity: serviceQuantity, // 服務數量
              discountRate: isDiscountApplied ? membershipDiscountRate : 100, // 應用的折扣率（如果未應用則為100）
              revenueAmount: revenueAmount, // 應用業績（整筆），後端會依 quantity 換算單價存
              supportDoseTypes: true,
              // 設計師ID（用於計算設計師抽成和業績）
              designerId: s.designerId || s.personnelId || null,
              designerName: s.designerName || s.personnelName || null,
              // 第一劑/第二劑操作人員
              firstDosePersonnelId: s.firstDosePersonnelId || null,
              firstDosePersonnelName: s.firstDosePersonnelName || null,
              secondDosePersonnelId: s.secondDosePersonnelId || null,
              secondDosePersonnelName: s.secondDosePersonnelName || null,
              // 向後兼容字段
              personnelId: s.designerId || s.personnelId || null,
              assistantId: null,
              assist: false
            }
          } else {
            // 一般抽成模式（須一併送出 quantity，否則後端會預設為 1）
            return {
              serviceId: s.serviceId,
              categoryId: s.categoryId,
              originalPrice: originalPrice, // 原始價格
              price: actualPrice, // 實收價（可能是折扣價或手動更改價）
              quantity: serviceQuantity, // 服務數量
              discountRate: isDiscountApplied ? membershipDiscountRate : 100, // 應用的折扣率（如果未應用則為100）
              revenueAmount: revenueAmount, // 應用業績（整筆），後端會依 quantity 換算單價存
              supportDoseTypes: false,
              designerId: s.designerId || s.personnelId || null, // 設計師ID
              assistantId: s.assistantId || null, // 助理ID
              // 向後兼容字段
              personnelId: s.designerId || s.personnelId || null,
              assist: !!s.assistantId || !!s.assist,
              // 第一劑/第二劑設為 null
              firstDosePersonnelId: null,
              firstDosePersonnelName: null,
              secondDosePersonnelId: null,
              secondDosePersonnelName: null
            }
          }
        }),
        products: selectedProducts.map((p) => {
          // 商品數量：優先使用輸入框暫存值（未 blur 時尚未寫回 selectedProducts），確保提交當下數量正確
          const rawProductQty =
            productQuantityInputs[p.key] !== undefined
              ? productQuantityInputs[p.key]
              : p.quantity
          // 服務搭配耗材支援小數量（0.03、0.5 等），不可 Math.floor，
          // 與後端 utils/inventoryQty.js 的 normalizeQty 一致：
          //   - 非數字 / NaN → 退回 1
          //   - 小於 0.01 → 拉到 0.01
          //   - 其他 → round 到 2 位小數
          const productQuantity = (() => {
            if (
              rawProductQty === null ||
              rawProductQty === undefined ||
              rawProductQty === ''
            ) {
              return 1
            }
            const n = Number(rawProductQty)
            if (!Number.isFinite(n)) return 1
            return Math.round(Math.max(0.01, n) * 100) / 100
          })()
          const productUnitPrice = Number(p.unitPrice) || 0
          // round 到 2 位避免 0.03 * 290 之類產生浮點誤差
          const productTotalPrice =
            Math.round(productQuantity * productUnitPrice * 100) / 100

          // 商品業績：後端設計 revenueAmount = 應用業績「單價」，計薪為 revenueAmount × quantity
          // 先算該項應用業績總額，再除以數量得到單價傳給後端（絕不傳該行總額）
          let productRevenueAmount = null
          if (p.includeInRevenue !== false) {
            if (
              applyRevenueDiscount &&
              totalRevenueAmount !== null &&
              totalRevenueBasisForAllocation > 0
            ) {
              const lineRevenueTotal = Math.round(
                (totalRevenueAmount * productTotalPrice) /
                  totalRevenueBasisForAllocation
              )
              // 一律傳單價：應用業績總額 / quantity，後端會算 revenueAmount × quantity
              productRevenueAmount =
                productQuantity > 0
                  ? Math.round(lineRevenueTotal / productQuantity)
                  : 0
            } else {
              // 不應用業績折扣，則為全額（單價 = 原單價）
              productRevenueAmount = productUnitPrice
            }
          } else {
            // 不計入業績
            productRevenueAmount = 0
          }

          // 銷售人員必選：未選時帶入登入者本人（非員工帳號則第一位）
          const salesPersonId =
            p.productSalesPersonnelId || (getDefaultEmployee()?._id ?? null)
          const salesPersonName =
            p.productSalesPersonnelName || (getDefaultEmployee()?.name ?? null)

          // 服務搭配耗材的新旗標：
          // - isConsumable：是否為從服務 consumables 自動帶入
          // - countAsSales：本筆是否計入商品銷售業績（沿用 includeInRevenue 的決定）
          // - sourceServiceId：耗材所屬的服務 ID（追溯用）
          const lineCountAsSales = p.includeInRevenue !== false
          return {
            productId: p.productId,
            quantity: productQuantity,
            price: productUnitPrice,
            totalPrice: productTotalPrice,
            productSalesPersonnelId: lineCountAsSales ? salesPersonId : null,
            productSalesPersonnelName: lineCountAsSales ? salesPersonName : null,
            revenueAmount: productRevenueAmount,
            isConsumable: Boolean(p.isConsumable),
            countAsSales: lineCountAsSales,
            sourceServiceId: p.sourceServiceId || null
          }
        }),
        // 堂票購買：每個 purchase = 一種範本買 quantity 張獨立堂票
        courses: {
          purchases: selectedClassTickets
            .filter((t) => t.templateId)
            .map((t) => ({
              courseId: t.templateId,
              courseName: t.templateName,
              price: Number(t.price) || 0,
              quantity: Math.max(1, parseInt(t.quantity, 10) || 1),
              // 買了就在這張單先用掉幾堂；後端會扣張並把對應服務金額歸零
              redeemCount: Math.max(0, parseInt(t.redeemCount, 10) || 0),
              salesPersonnelId: t.salesPersonnelId || null,
              salesPersonnelName: t.salesPersonnelName || ''
            })),
          usages: []
        },
        /**
         * 堂票折抵。服務的 price 在勾選當下就已經被設成 0，這裡只送
         * 「哪張票折抵了哪個服務」，後端會驗證票的歸屬、效期與適用範圍，
         * 再做扣張並寫入 ClassTicketUsage。
         */
        classTicketUsages: Object.entries(ticketRedemptions)
          .map(([serviceKey, redemption]) => {
            const svc = (selectedServices || []).find(
              (s) => s.key === serviceKey
            )
            if (!svc) return null
            return {
              ticketId: redemption.ticketId,
              serviceId: svc.serviceId,
              serviceName: svc.serviceName || svc.name || '',
              count: redemption.count || 1
            }
          })
          .filter(Boolean),
        totals: {
          subtotal: subtotalAmount, // 原價小計（已包含會員折扣後的價格）
          discountAmount: finalDiscountAmount, // 手動折扣金額（會員折扣已應用在服務價格中）
          finalAmount: finalTotalWithMembershipDiscount, // 實收金額
          servicesAmount: totalServiceAmount,
          productsAmount: totalProductAmount,
          grandTotal: finalTotalWithMembershipDiscount // 總金額（扣除手動折扣後）
        },
        // 後端驗證必填：amounts.receivable = 實際應收（訂單總額）。不論現場改為現金/儲值等何種方式支付，均須傳此值，不可傳 0 或未付餘額。
        // 含服務費（沒開加收服務費時等同原值）
        amounts: {
          receivable: receivableWithSurcharge,
          surchargeAmount: surcharge.total,
          installmentFeeAmount
        },
        // 店內自行分期：後端會在訂單建好後依這份設定建立分期計畫
        installmentPlan: installmentDraft
          ? {
              principal: installmentDraft.principal,
              feeAmount: installmentDraft.feeAmount,
              feeRate: installmentDraft.feeRate,
              periods: installmentDraft.periods,
              frequency: installmentDraft.frequency,
              firstDueDate: installmentDraft.firstDueDate,
              graceDays: installmentDraft.graceDays,
              remarks: installmentDraft.remarks,
              // 頭期款走分期帳本（後端會建一筆收款），不進訂單的現金欄位 ——
              // 兩邊都記會讓現金流把同一筆錢算兩次
              downPayment: installmentDraft.downPayment || 0,
              downPaymentMethod: installmentDraft.downPaymentMethod || 'cash',
              schedule: installmentDraft.schedule
            }
          : null,
        time: {
          date: serviceDate,
          startTime: allValues.serviceStartTime || null,
          endTime: allValues.serviceEndTime || null,
          timeRange:
            allValues.serviceStartTime && allValues.serviceEndTime
              ? `${allValues.serviceStartTime}-${allValues.serviceEndTime}`
              : null
        },
        remarks: allValues.remarks || '',
        payment: {
          // 儲值金支付：客戶實際使用儲值金支付的金額（會觸發儲值金扣款）
          // 使用驗證時獲取的值，確保與驗證邏輯一致
          storedValue: storedValueAmount,
          // 從哪一儲值活動扣款（選填；有帶時後端從該儲值活動餘額扣款）
          ...(storedValueAmount > 0 && allValues.storedValueSchemeKey
            ? { storedValueSchemeKey: allValues.storedValueSchemeKey }
            : {}),
          // 優惠券支付
          voucher: voucherAmount,
          ...(appliedStoreCouponId
            ? { storeCouponId: appliedStoreCouponId }
            : {}),
          // 應收金額：必須為實際應收（訂單總額）。就算現場改成現金 450，也傳 450，不傳 0
          // 開啟加收服務費時，這裡是「含服務費」的總額 —— 客人實際要付的錢
          receivable: receivableWithSurcharge,
          // 折扣金額
          discount: finalDiscountAmount,
          // 現金支付
          cash: cashAmount,
          // 轉帳支付
          transfer: transferAmount,
          // 定金支付
          deposit: depositAmount,
          // 獎金支付
          bonus: bonusAmount,
          // 欠款
          debt: debtAmount,
          // 分期付款
          installment: installmentAmount,
          // 新增：刷卡（信用卡/簽帳卡）—— 含服務費，即刷卡機實際刷的金額
          cardPayment: cardPaymentWithSurcharge,
          // 新增：行動支付（Line Pay、街口、Apple Pay 等）—— 同樣含服務費
          mobilePayment: mobilePaymentWithSurcharge,
          // 新增：金流手續費（店家支出，不計入客戶已付金額；會計上為「銷售費用－金流服務費」）
          paymentFee: paymentFeeToSubmit,
          // 向客人加收的服務費，以及它的計算基數（供後端用同一公式重算驗證）。
          // 沒開加收服務費時為 0 / 0，後端會直接跳過整段驗證。
          surchargeAmount: surcharge.total,
          surchargeBase: {
            card: cardPaymentAmount,
            mobile: mobilePaymentAmount
          },
          // 店家有沒有自己動過手續費欄位。false 時後端會依費率快照重算，
          // 空白欄位送出的 0 才不會被當成「店家決定不收手續費」。
          paymentFeeOverridden: paymentFeeOverriddenRef.current
        }
        // 注意：業績金額現在在每個服務的 revenueAmount 字段中，不再需要訂單級別的 revenue 字段
      }

      // 調試：打印提交的業績金額和完整訂單數據
      console.log('=== 提交訂單數據 ===')
      console.log('總業績金額 (totalRevenueAmount):', totalRevenueAmount)
      console.log('總服務金額 (totalServiceAmount):', totalServiceAmount)
      console.log(
        '總商品業績基準 (totalProductRevenueBasis):',
        totalProductRevenueBasis
      )
      console.log('總業績基準金額 (totalRevenueBasis):', totalRevenueBasis)
      console.log('是否應用業績折扣:', applyRevenueDiscount)
      console.log('業績折扣率:', revenueDiscountRate)
      console.log('儲值金金額:', storedValueAmount)
      console.log(
        '非儲值金付款:',
        cashAmount +
          transferAmount +
          depositAmount +
          bonusAmount +
          debtAmount +
          installmentAmount +
          voucherAmount
      )
      console.log(
        '每個服務的業績金額與數量（供與後端對帳）:',
        orderData.services?.map((s) => ({
          serviceId: s.serviceId,
          quantity: s.quantity,
          originalPrice: s.originalPrice,
          price: s.price,
          revenueAmount: s.revenueAmount
        }))
      )
      console.log(
        '每個商品的業績金額:',
        orderData.products?.map((p) => ({
          productId: p.productId,
          productName: p.name,
          totalPrice: p.totalPrice,
          revenueAmount: p.revenueAmount
        }))
      )
      console.log('完整 orderData JSON:', JSON.stringify(orderData, null, 2))

      const response = await api.post(
        `/api/businesses/${user.businessId}/checkout/orders`,
        orderData
      )

      if (response.status >= 200 && response.status < 300) {
        message.success('訂單創建成功')

        // 若為預約結帳，更新原預約紀錄：以實際結帳項目與金額為主，並標記為已完成
        if (checkoutMode === 'booking' && finalBookingId) {
          try {
            await api.put(`/api/business/bookings/${finalBookingId}`, {
              services: selectedServices.map((s) => {
                // 檢查服務是否支持第一劑/第二劑
                const commissionRule = serviceCommissionRules[s.serviceId]
                const supportDoseTypes =
                  s.supportDoseTypes !== undefined
                    ? s.supportDoseTypes
                    : commissionRule?.supportDoseTypes || false

                if (supportDoseTypes) {
                  // 第一劑/第二劑模式
                  return {
                    serviceId: s.serviceId,
                    serviceName: s.name,
                    basePrice: s.originalPrice || s.price || 0,
                    actualPrice: s.price || 0,
                    designerId: s.designerId || s.personnelId || null, // 設計師ID（用於計算設計師抽成和業績）
                    firstDosePersonnelId: s.firstDosePersonnelId || null,
                    secondDosePersonnelId: s.secondDosePersonnelId || null,
                    assistantId: null
                  }
                } else {
                  // 一般抽成模式
                  return {
                    serviceId: s.serviceId,
                    serviceName: s.name,
                    basePrice: s.originalPrice || s.price || 0,
                    actualPrice: s.price || 0,
                    designerId: s.designerId || s.personnelId || null,
                    assistantId: s.assistantId || null
                  }
                }
              }),
              additionalServices: [],
              originalPrice: totalServiceAmount,
              discountedPrice: totalAmount,
              status: 'completed'
            })
          } catch (e) {
            console.error('更新預約紀錄失敗:', e)
          }
        }

        // 保存訂單數據並顯示成功頁面
        const orderResponse = response.data?.data || response.data || {}

        // 購買同意書提醒：訂單含堂票購買時，提醒店家可請客人簽署（不阻擋結帳）
        // 後端已於結帳時自動發送同意書給顧客（classTicketConsentSent），
        // Modal 顯示於成功頁，供現場補簽；autoSent 時隱藏「傳送給客人」避免重複發送
        if (selectedClassTickets.length > 0) {
          const first = selectedClassTickets[0]
          setCheckoutConsent({
            open: true,
            subjectRef: orderResponse._id || null,
            autoSent: !!orderResponse.classTicketConsentSent,
            // 這張單買了哪幾種堂票；同意書若有限定堂票，只有對得上的才會跳出
            classTicketTemplateIds: [
              ...new Set(
                selectedClassTickets
                  .map((t) => t.templateId && String(t.templateId))
                  .filter(Boolean)
              )
            ].join(','),
            variables: {
              planName: first?.templateName || '堂票方案',
              // 指定到期日的範本直接用那一天，天數制才從今天起算
              expiryDate: formatTemplateExpiry(first) || '',
              price: first?.price || 0,
              amount: first?.price || 0,
              customerName: customerName || ''
            }
          })
        }
        setOrderData({
          orderId: orderResponse.orderId || orderResponse._id || 'N/A',
          customer: {
            name: customerName,
            phone: phone,
            servicePersonnel: allValues.servicePersonnel || null,
            customerType,
            customerSource
          },
          services: selectedServices,
          products: selectedProducts,
          amounts: orderResponse.amounts || {
            servicesAmount: totalServiceAmount,
            productsAmount: totalProductAmount,
            discountAmount: discountAmount,
            grandTotal: totalAmount,
            receivable: totalAmount,
            paid: totalPaid,
            unpaid: totalAmount - totalPaid
          },
          time: {
            date: serviceDate,
            startTime: allValues.serviceStartTime || null,
            endTime: allValues.serviceEndTime || null,
            timeRange:
              allValues.serviceStartTime && allValues.serviceEndTime
                ? `${allValues.serviceStartTime}-${allValues.serviceEndTime}`
                : null
          },
          remarks: allValues.remarks || '',
          payment: {
            // 使用驗證時獲取的值，確保與驗證邏輯一致
            storedValue: storedValueAmount,
            voucher: voucherAmount,
            cash: cashAmount,
            transfer: transferAmount,
            deposit: depositAmount,
            bonus: bonusAmount,
            debt: debtAmount,
            installment: installmentAmount,
            cardPayment: cardPaymentAmount,
            mobilePayment: mobilePaymentAmount,
            paymentFee: paymentFeeAmount
          },
          createdAt: orderResponse.createdAt || new Date().toISOString()
        })

        /**
         * 施作照片：訂單已經建好了，這時才有 finalBookingId 可以掛。
         *
         * 刻意不 await 進主流程的成敗判斷 —— 訂單是錢，照片不是；
         * 照片傳失敗不該讓店家以為結帳沒成功。失敗只提示一次，
         * 並告訴店家可以到預約詳情補傳。純賣商品（沒有預約）時直接跳過。
         */
        if (servicePhotoEnabled && servicePhotoFiles.length > 0) {
          if (!finalBookingId) {
            message.warning('這筆沒有關聯預約，施作照片未上傳')
          } else {
            const filesToUpload = servicePhotoFiles
              .map((f) => f.originFileObj || f)
              .filter(Boolean)
            ;(async () => {
              try {
                // 後端一次最多 5 張，超過自動分批
                for (let i = 0; i < filesToUpload.length; i += 5) {
                  await uploadServicePhotos(
                    user.businessId,
                    finalBookingId,
                    filesToUpload.slice(i, i + 5),
                    { source: 'checkout' }
                  )
                }
                message.success('施作照片已上傳，客人在預約紀錄看得到')
              } catch (photoError) {
                console.error('施作照片上傳失敗:', photoError)
                message.warning(
                  '訂單已建立，但施作照片上傳失敗，可到預約詳情重新上傳'
                )
              } finally {
                // 不論成敗都要清掉：留著的話店家接著結下一筆時，
                // 這批照片會跟著被送到下一筆預約上。
                // 傳失敗的可以到預約詳情重傳，那裡才是正確的補救位置。
                servicePhotoFiles.forEach((f) => {
                  if (f.thumbUrl?.startsWith('blob:')) {
                    URL.revokeObjectURL(f.thumbUrl)
                  }
                })
                setServicePhotoFiles([])
              }
            })()
          }
        }

        // 分期設定用完就清掉：留著的話店家接著結下一筆時會被帶到下一張單上
        setInstallmentDraft(null)

        setOrderSuccess(true)
        // 若為從草稿繼續結帳，結帳成功後刪除該草稿，避免草稿列表重複
        if (draftIdFromUrl && user?.businessId) {
          api
            .delete(
              `/api/businesses/${user.businessId}/checkout/drafts/${draftIdFromUrl}`
            )
            .catch((err) => {
              console.warn('刪除草稿失敗（不影響訂單）:', err)
            })
        }
      } else {
        const errorMsg =
          response.data?.message || response.data?.error || '訂單創建失敗'
        message.error(errorMsg)
      }
    } catch (error) {
      console.error('提交失敗:', error)

      // 如果是表單驗證錯誤，不顯示錯誤信息（Ant Design 會自動顯示）
      if (error.errorFields && error.errorFields.length > 0) {
        // 表單驗證錯誤，Ant Design 會自動顯示錯誤信息
        setLoading(false)
        return
      }

      // API 錯誤
      const errorMsg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        '訂單創建失敗，請稍後再試'
      message.error(errorMsg)
    } finally {
      // 無論成功、失敗、或未預期例外，確保 loading 一定被重置
      setLoading(false)
    }
  }

  // 渲染預約信息卡片
  const renderBookingInfo = () => {
    if (checkoutMode !== 'booking' || !bookingData) return null

    return (
      <BookingInfoCard
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>預約資訊</span>
            <span style={{ fontSize: 12, opacity: 0.9 }}>
              （正在為此預約進行結帳）
            </span>
          </div>
        }
      >
        <BookingInfoRow>
          <span className='label'>客戶姓名：</span>
          <span className='value'>
            <CustomerNameLink
              customerId={selectedCustomerId || bookingData.customerId}
              newTab
            >
              {bookingData.userName || '--'}
            </CustomerNameLink>
          </span>
        </BookingInfoRow>
        <BookingInfoRow>
          <span className='label'>聯絡電話：</span>
          <span className='value'>{bookingData.phone || '--'}</span>
        </BookingInfoRow>
        <BookingInfoRow>
          <span className='label'>預約服務：</span>
          <div className='value'>
            {selectedServices && selectedServices.length > 0 ? (
              selectedServices.map((service, index) => (
                <ServiceTag key={service.key || index}>
                  {service.name || service.serviceName || '未知服務'}
                </ServiceTag>
              ))
            ) : bookingData.services &&
              Array.isArray(bookingData.services) &&
              bookingData.services.length > 0 ? (
              bookingData.services.map((service, index) => (
                <ServiceTag key={index}>
                  {service.serviceName || '未知服務'}
                </ServiceTag>
              ))
            ) : bookingData.productName ? (
              <ServiceTag>{bookingData.productName}</ServiceTag>
            ) : null}
          </div>
        </BookingInfoRow>
        <BookingInfoRow>
          <span className='label'>服務設計師：</span>
          <span className='value'>
            {(() => {
              // 優先從 selectedServices 中獲取設計師信息（第一劑操作人員或設計師）
              if (selectedServices.length > 0) {
                const firstService = selectedServices[0]
                // 檢查是否支持第一劑/第二劑
                const commissionRule =
                  serviceCommissionRules[firstService.serviceId]
                const supportDoseTypes =
                  firstService.supportDoseTypes !== undefined
                    ? firstService.supportDoseTypes
                    : commissionRule?.supportDoseTypes || false

                if (supportDoseTypes) {
                  // 第一劑/第二劑模式：使用第一劑操作人員
                  const firstDosePersonnel = employees.find(
                    (e) => e._id === firstService.firstDosePersonnelId
                  )
                  return (
                    firstService.firstDosePersonnelName ||
                    firstDosePersonnel?.name ||
                    bookingData.stylistName ||
                    '未指定'
                  )
                } else {
                  // 一般抽成模式：使用設計師
                  const designer = employees.find(
                    (e) =>
                      e._id ===
                      (firstService.designerId || firstService.personnelId)
                  )
                  return (
                    firstService.designerName ||
                    firstService.personnelName ||
                    designer?.name ||
                    bookingData.stylistName ||
                    '未指定'
                  )
                }
              }

              // 如果 selectedServices 為空，嘗試從 bookingData.services 中獲取
              if (
                bookingData.services &&
                Array.isArray(bookingData.services) &&
                bookingData.services.length > 0
              ) {
                const firstService = bookingData.services[0]
                // 檢查是否支持第一劑/第二劑
                const commissionRule =
                  serviceCommissionRules[firstService.serviceId]
                const supportDoseTypes =
                  commissionRule?.supportDoseTypes || false

                if (supportDoseTypes && firstService.firstDosePersonnelId) {
                  // 第一劑/第二劑模式：使用第一劑操作人員
                  const firstDosePersonnel = employees.find(
                    (e) => e._id === firstService.firstDosePersonnelId
                  )
                  return (
                    firstService.firstDosePersonnelName ||
                    firstDosePersonnel?.name ||
                    bookingData.stylistName ||
                    '未指定'
                  )
                } else if (
                  firstService.designerId ||
                  firstService.personnelId
                ) {
                  // 一般抽成模式：使用設計師
                  const designer = employees.find(
                    (e) =>
                      e._id ===
                      (firstService.designerId || firstService.personnelId)
                  )
                  return (
                    firstService.designerName ||
                    firstService.personnelName ||
                    designer?.name ||
                    bookingData.stylistName ||
                    '未指定'
                  )
                }
              }

              // 最後使用 bookingData.stylistName
              return bookingData.stylistName || '未指定'
            })()}
          </span>
        </BookingInfoRow>
        {bookingData.date && (
          <BookingInfoRow>
            <span className='label'>預約日期：</span>
            <span className='value'>
              {dayjs(bookingData.date).format('YYYY年MM月DD日')}
            </span>
          </BookingInfoRow>
        )}
        {bookingData.time && (
          <BookingInfoRow>
            <span className='label'>預約時間：</span>
            <span className='value'>{bookingData.time}</span>
          </BookingInfoRow>
        )}
        {bookingData.note && (
          <BookingInfoRow>
            <span className='label'>備註：</span>
            <span className='value' style={{ color: '#666', fontWeight: 400 }}>
              {bookingData.note}
            </span>
          </BookingInfoRow>
        )}
      </BookingInfoCard>
    )
  }

  const renderStepContent = () => {
    return (
      <>
        {checkoutMode === 'walkin' && (
          <FormCard title='步驟一 客戶資訊' id='checkout-section-customer'>
            <Form form={form} layout='vertical'>
              <FormRow>
                <Form.Item
                  name='customerName'
                  label={
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <span>客戶名稱</span>
                      {(() => {
                        if (!customerMembershipTier) return null
                        return (
                          <span
                            style={{
                              fontSize: 12,
                              padding: '2px 8px',
                              background: '#f0f9ff',
                              color: '#0369a1',
                              borderRadius: 4,
                              border: '1px solid #bae6fd'
                            }}
                          >
                            {customerMembershipTier}
                          </span>
                        )
                      })()}
                    </div>
                  }
                  rules={[{ required: true, message: '請輸入客戶名稱' }]}
                >
                  <div
                    className='customer-search-container'
                    style={{ position: 'relative' }}
                  >
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Input
                        id='customerName'
                        value={customerNameValue}
                        onChange={(e) => {
                          const value = e.target.value
                          setCustomerNameValue(value)
                          form.setFieldsValue({ customerName: value })
                          // 只有在輸入法組合結束後才觸發搜尋
                          if (!isComposing) {
                            searchCustomers(value, 'name')
                          }
                        }}
                        onCompositionStart={() => {
                          setIsComposing(true)
                        }}
                        onCompositionEnd={(e) => {
                          setIsComposing(false)
                          // 輸入法組合結束後觸發搜尋
                          const value = e.target.value
                          searchCustomers(value, 'name')
                        }}
                        placeholder='輸入姓名或電話搜尋（至少2字）'
                        size='large'
                        allowClear
                        style={{ flex: 1 }}
                      />
                      <Button
                        type='default'
                        icon={<UserAddOutlined />}
                        size='large'
                        onClick={() => setAddCustomerModalVisible(true)}
                        style={{
                          whiteSpace: 'nowrap',
                          flexShrink: 0
                        }}
                      >
                        新增客戶
                      </Button>
                    </div>
                    {showCustomerList && customerOptions.length > 0 && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                          background: '#fff',
                          border: '1px solid #d9d9d9',
                          borderRadius: '6px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          // 捲到底不要把動量交給整頁
                          overscrollBehavior: 'contain',
                          WebkitOverflowScrolling: 'touch',
                          marginTop: '4px'
                        }}
                      >
                        {customerOptions.map((option) => (
                          <div
                            key={option.value}
                            onClick={() =>
                              handleCustomerSelect(option.value, option)
                            }
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#f5f5f5'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#fff'
                            }}
                          >
                            <div style={{ fontWeight: 500, marginBottom: 4 }}>
                              {option.name || '未命名客戶'}
                            </div>
                            <div style={{ fontSize: 12, color: '#6c757d' }}>
                              {option.phone}
                              {option.email && ` • ${option.email}`}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {searching && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                          background: '#fff',
                          border: '1px solid #d9d9d9',
                          borderRadius: '6px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          padding: '12px',
                          textAlign: 'center',
                          color: '#666',
                          marginTop: '4px'
                        }}
                      >
                        搜尋中...
                      </div>
                    )}
                  </div>
                </Form.Item>

                <Form.Item name='phone' label='電話'>
                  <div
                    className='phone-search-container'
                    style={{ position: 'relative' }}
                  >
                    <Input
                      id='phone'
                      value={form.getFieldValue('phone') || ''}
                      onChange={(e) => {
                        form.setFieldsValue({ phone: e.target.value })
                        handlePhoneSearch(e.target.value)
                      }}
                      placeholder='請輸入電話號碼'
                      size='large'
                      allowClear
                    />
                    {showPhoneCustomerList && customerOptions.length > 0 && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                          background: '#fff',
                          border: '1px solid #d9d9d9',
                          borderRadius: '6px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          // 捲到底不要把動量交給整頁
                          overscrollBehavior: 'contain',
                          WebkitOverflowScrolling: 'touch',
                          marginTop: '4px'
                        }}
                      >
                        {customerOptions.map((option) => (
                          <div
                            key={option.value}
                            onClick={() =>
                              handleCustomerSelect(option.value, option)
                            }
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              transition: 'background 0.2s'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#f5f5f5'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#fff'
                            }}
                          >
                            <div style={{ fontWeight: 500, marginBottom: 4 }}>
                              {option.name || '未命名客戶'}
                            </div>
                            <div style={{ fontSize: 12, color: '#6c757d' }}>
                              {option.phone}
                              {option.email && ` • ${option.email}`}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {searching && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          zIndex: 1000,
                          background: '#fff',
                          border: '1px solid #d9d9d9',
                          borderRadius: '6px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          padding: '12px',
                          textAlign: 'center',
                          color: '#666',
                          marginTop: '4px'
                        }}
                      >
                        搜尋中...
                      </div>
                    )}
                  </div>
                </Form.Item>
              </FormRow>

              <FormRow>
                <Form.Item
                  name='customerType'
                  label='客戶類型'
                  rules={[{ required: true, message: '請選擇客戶類型' }]}
                  initialValue='new'
                >
                  <ResponsiveSelect size='large'>
                    <Option value='new'>新客戶</Option>
                    <Option value='existing'>舊客戶</Option>
                  </ResponsiveSelect>
                </Form.Item>

                <Form.Item
                  name='customerSource'
                  label='客戶來源'
                  rules={[{ required: true, message: '請選擇客戶來源' }]}
                  initialValue='none'
                >
                  <ResponsiveSelect size='large'>
                    <Option value='none'>無</Option>
                    <Option value='online'>網路</Option>
                    <Option value='referral'>轉介</Option>
                    <Option value='walk-in'>路過</Option>
                  </ResponsiveSelect>
                </Form.Item>
              </FormRow>
            </Form>
            <div style={{ marginTop: 24, display: 'flex', width: '100%' }}>
              <Button
                type='primary'
                size='large'
                onClick={handleNext}
                style={{ width: '100%', background: '#F2B3C0' }}
              >
                下一步
              </Button>
            </div>
          </FormCard>
        )}
        {/* 現場結帳步驟一時不渲染下方區塊，避免輸入客戶資料時下面閃動；進到下一步再顯示 */}
        {(checkoutMode !== 'walkin' || currentStep >= 1) && (
          <>
            <div ref={sectionServicesRef} id='checkout-section-services'>
              <StepThreeSelectServices
                stepTwoContentShownRef={stepTwoContentShownRef}
              />
            </div>
            <div ref={sectionDateTimeRef} id='checkout-section-datetime'>
              <StepFourDateTimeSection
                servicePhotoEnabled={servicePhotoEnabled}
                servicePhotoFiles={servicePhotoFiles}
                setServicePhotoFiles={setServicePhotoFiles}
                installmentEnabled={installmentEnabled}
                installmentDraft={installmentDraft}
                setInstallmentDraft={setInstallmentDraft}
                installmentModalOpen={installmentModalOpen}
                setInstallmentModalOpen={setInstallmentModalOpen}
                form={form}
                checkoutMode={checkoutMode}
                bookingData={bookingData}
                subtotalAmount={subtotalAmount}
                discountAmount={discountAmount}
                tempDiscountAmount={tempDiscountAmount}
                setTempDiscountAmount={setTempDiscountAmount}
                handleApplyDiscount={handleApplyDiscount}
                totalAmount={totalAmount}
                displayTotalAmount={displayTotalAmount}
                recheckoutLoadedTotal={recheckoutLoadedTotal}
                loadingBalance={loadingBalance}
                selectedCustomerId={selectedCustomerId}
                customerBalance={customerBalance}
                fetchCustomerBalance={fetchCustomerBalance}
                setTopupModalVisible={setTopupModalVisible}
                topupModalVisible={topupModalVisible}
                topupAmount={topupAmount}
                setTopupAmount={setTopupAmount}
                selectedTopupBonus={selectedTopupBonus}
                setSelectedTopupBonus={setSelectedTopupBonus}
                topupLoading={topupLoading}
                handleTopupSubmit={handleTopupSubmit}
                selectedServices={selectedServices}
                selectedProducts={selectedProducts}
                employees={employees}
                serviceCommissionRules={serviceCommissionRules}
                customerMembershipTier={customerMembershipTier}
                customerTierAccumulated={customerTierAccumulated}
                membershipTiers={membershipTiers}
                membershipDiscountRate={membershipDiscountRate}
                customerBalanceInfo={customerBalanceInfo}
                applyRevenueDiscount={applyRevenueDiscount}
                setApplyRevenueDiscount={setApplyRevenueDiscount}
                revenueDiscountRate={revenueDiscountRate}
                setRevenueDiscountRate={setRevenueDiscountRate}
                includeZeroYuanInRevenue={includeZeroYuanInRevenue}
                setIncludeZeroYuanInRevenue={setIncludeZeroYuanInRevenue}
                serviceQuantities={serviceQuantities}
                onOpenTransferModal={() => {
                  // 打開儲值金轉讓 Modal 前，重置表單狀態
                  setTransferFromCustomerName('')
                  setTransferFromCustomerId(null)
                  setTransferFromBalance(0) // 實際餘額待後端 API 支援
                  setTransferAmount(null)
                  clearTransferSearch()
                  setTransferModalVisible(true)
                }}
                transferModalVisible={transferModalVisible}
                setTransferModalVisible={setTransferModalVisible}
                transferFromCustomerName={transferFromCustomerName}
                setTransferFromCustomerName={setTransferFromCustomerName}
                transferFromBalance={transferFromBalance}
                setTransferFromBalance={setTransferFromBalance}
                transferAmount={transferAmount}
                setTransferAmount={setTransferAmount}
                transferDate={transferDate}
                setTransferDate={setTransferDate}
                transferLoading={transferLoading}
                handleTransferSubmit={handleTransferSubmit}
                transferSearchOptions={transferSearchOptions}
                transferSearching={transferSearching}
                onTransferSearch={searchTransferCustomers}
                onSelectTransferCustomer={handleSelectTransferCustomer}
                onClearTransferSearch={clearTransferSearch}
                topupSalesPersonnel={topupSalesPersonnel}
                setTopupSalesPersonnel={setTopupSalesPersonnel}
                topupDiscount={topupDiscount}
                setTopupDiscount={setTopupDiscount}
                topupPaymentMethod={topupPaymentMethod}
                setTopupPaymentMethod={setTopupPaymentMethod}
                topupPaymentDetails={topupPaymentDetails}
                setTopupPaymentDetails={setTopupPaymentDetails}
                topupDate={topupDate}
                setTopupDate={setTopupDate}
                topupNote={topupNote}
                setTopupNote={setTopupNote}
                topupSchemeKey={topupSchemeKey}
                setTopupSchemeKey={setTopupSchemeKey}
                topupExpiryDate={topupExpiryDate}
                setTopupExpiryDate={setTopupExpiryDate}
                discountPercentage={discountPercentage}
                tempDiscountPercentage={tempDiscountPercentage}
                setTempDiscountPercentage={setTempDiscountPercentage}
                handleApplyDiscountPercentage={handleApplyDiscountPercentage}
                finalDiscountAmount={finalDiscountAmount}
                loadingBooking={loadingBooking}
                customerNameValue={customerNameValue}
                getServiceOrAdditionalById={getServiceOrAdditionalById}
                businessId={user?.businessId}
                renderCheckoutCouponPicker={renderCheckoutCouponPicker}
                couponLocked={!!appliedStoreCouponId}
                paymentFeeSettings={paymentFeeSettings}
                markPaymentFeeOverridden={markPaymentFeeOverridden}
                selectedClassTickets={selectedClassTickets}
                topupBonusOptedOut={topupBonusOptedOut}
                setTopupBonusOptedOut={setTopupBonusOptedOut}
              />
            </div>
          </>
        )}

        {/* 購買同意書（儲值前簽署） */}
        <PurchaseConsentModal
          open={topupConsent.open}
          onClose={() => setTopupConsent((c) => ({ ...c, open: false }))}
          businessId={user?.businessId}
          customerId={selectedCustomerId}
          customerName={topupConsent.variables?.customerName}
          target='balance'
          variables={topupConsent.variables}
          onNoForm={() => {
            setTopupConsent((c) => ({ ...c, open: false }))
            handleTopupSubmit(null, true)
          }}
          onSkip={() => {
            setTopupConsent((c) => ({ ...c, open: false }))
            handleTopupSubmit(null, true)
          }}
          onSigned={(sigId) => {
            setTopupConsent((c) => ({ ...c, open: false }))
            handleTopupSubmit(sigId, true)
          }}
          onSentRemote={() => {
            setTopupConsent((c) => ({ ...c, open: false }))
            handleTopupSubmit(null, true)
          }}
        />

      </>
    )
  }

  // 步驟二：返店提醒設定
  const StepTwoReturnReminder = () => {
    return (
      <FormCard title='步驟二 返店提醒設定'>
        <Form form={form} layout='vertical'>
          <Form.Item name='enableReminder' valuePropName='checked'>
            <Checkbox id='enableReminder'>
              於{' '}
              <Form.Item
                name='reminderDate'
                noStyle
                initialValue={tzNow().add(30, 'day')}
              >
                <DatePicker id='reminderDate' format='YYYY-MM-DD' />
              </Form.Item>
              透過設計師 APP 或是{' '}
              <span style={{ color: '#10b981' }}>Google 行事曆</span>、{' '}
              <span style={{ color: '#10b981' }}>Line</span> 提醒店家
            </Checkbox>
          </Form.Item>
        </Form>
      </FormCard>
    )
  }

  // 步驟三：選擇服務或商品
  const StepThreeSelectServices = ({ stepTwoContentShownRef }) => {
    const [newServiceCards, setNewServiceCards] = useState([])
    const [newProductCards, setNewProductCards] = useState([])
    const cardFormsRef = useRef({})
    const productCardFormsRef = useRef({})
    const [filteredServiceOptions, setFilteredServiceOptions] = useState({})
    const [selectedCategoryIds, setSelectedCategoryIds] = useState({})
    const [selectedProductCategoryIds, setSelectedProductCategoryIds] =
      useState({})
    const hasInitialized = useRef(false)
    // 用於暫存實際收費的輸入值，避免每次輸入都觸發重新渲染
    const priceInputRefs = useRef({})
    // 延遲顯示表單，等區塊內 state 穩定後再渲染 input，避免閃爍；若父層 ref 已標記顯示過（remount 情境）則直接就緒
    const [sectionReady, setSectionReady] = useState(
      () => stepTwoContentShownRef?.current ?? false
    )
    const servicesOptionsReady = services.length > 0 && categories.length > 0

    useEffect(() => {
      if (!servicesOptionsReady) {
        setSectionReady(false)
        return
      }
      if (stepTwoContentShownRef?.current) {
        setSectionReady(true)
        return
      }
      const t = setTimeout(() => {
        setSectionReady(true)
        if (stepTwoContentShownRef) stepTwoContentShownRef.current = true
      }, 280)
      return () => clearTimeout(t)
    }, [servicesOptionsReady, stepTwoContentShownRef])

    // 進入步驟時自動添加一張卡片（僅現場結帳模式）
    useEffect(() => {
      // 預約結帳模式：如果已經有預約服務，不需要自動添加卡片
      if (checkoutMode === 'booking' && selectedServices.length > 0) {
        hasInitialized.current = true
        return
      }

      // 現場結帳模式：只有「還沒加過任何服務」時才自動開第一張卡。
      // 少了 selectedServices.length === 0 這個條件，店員每加完一個服務
      // 就會再冒出一張空卡（此元件重新掛載時 hasInitialized 會歸零），
      // 之後要不要再加應該由「加選服務」按鈕決定。
      if (
        !hasInitialized.current &&
        newServiceCards.length === 0 &&
        selectedServices.length === 0 &&
        checkoutMode === 'walkin'
      ) {
        const newKey = `new-${Date.now()}`
        setNewServiceCards([newKey])
        hasInitialized.current = true
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkoutMode, selectedServices.length])

    const handleAddNewCard = () => {
      const newKey = `new-${Date.now()}`
      setNewServiceCards([...newServiceCards, newKey])
    }

    const handleSaveCard = (cardKey) => {
      const form = cardFormsRef.current[cardKey]
      if (!form) return

      form
        .validateFields()
        .then((values) => {
          // 檢查數量是否為0或未填寫，獲取數量（最小為1）
          const quantity = Math.max(1, values.quantity ?? 1)
          if (quantity === 0 || quantity === null || quantity === undefined) {
            const serviceName =
              getServiceOrAdditionalById(values.serviceId)?.name ||
              services.find((s) => s._id === values.serviceId)?.name ||
              '未命名服務'
            Modal.warning({
              title: '請填寫服務數量',
              content: `服務「${serviceName}」的數量為0或未填寫，請先填寫數量。`,
              okText: '確定'
            })
            return Promise.reject(new Error('數量不能為0'))
          }

          const selectedService =
            getServiceOrAdditionalById(values.serviceId) ||
            services.find((s) => s._id === values.serviceId)
          const isAdditional =
            values.categoryId === ADDITIONAL_SERVICES_CATEGORY_ID
          const category = isAdditional
            ? null
            : categories.find((c) => c._id === values.categoryId)
          const originalPrice = Number(selectedService?.price || 0)
          // 計算單價（原價）
          const unitPrice = originalPrice
          // 用戶輸入的實際收費（如果沒有輸入，使用單價）
          const userInputPrice = Number(values.price) || unitPrice
          // 如果實際收費等於單價，說明用戶還沒手動修改，自動計算：單價 * 數量
          // 如果實際收費不等於單價，說明用戶手動修改過，保持用戶輸入的值
          const finalPrice =
            userInputPrice === unitPrice ? unitPrice * quantity : userInputPrice

          // 計算折扣價（僅用於判斷是否為手動更改）
          const discountedPrice =
            membershipDiscountRate < 100
              ? Math.round((originalPrice * membershipDiscountRate) / 100)
              : originalPrice

          // 判斷是否為手動更改（用戶輸入的價格與折扣價不同，且不等於原價）
          const isManualPrice =
            finalPrice !== discountedPrice && finalPrice !== originalPrice

          // 檢查服務是否支持第一劑/第二劑
          const commissionRule = serviceCommissionRules[values.serviceId]
          const supportDoseTypes = commissionRule?.supportDoseTypes || false

          let item = {
            key: `${values.serviceId}-${Date.now()}`,
            serviceId: values.serviceId,
            categoryId: isAdditional
              ? selectedService?.category || selectedService?.categoryId || null
              : values.categoryId,
            categoryName: category?.name || (isAdditional ? '加選服務' : '-'),
            name: selectedService?.name || selectedService?.serviceName || '-',
            // 價格相關
            originalPrice: originalPrice, // 原始價格（單價）
            basePrice: originalPrice, // 基礎價格（單價）
            price: finalPrice, // 實收價（已考慮數量，可能是折扣價或手動更改價）
            isManualPrice: isManualPrice, // 標記是否為手動更改的價格
            quantity, // 服務數量
            // 抽成規則相關
            supportDoseTypes: supportDoseTypes
          }

          if (supportDoseTypes) {
            // 第一劑/第二劑模式
            const firstDosePersonnel = employees.find(
              (e) => e._id === values.firstDosePersonnelId
            )
            const secondDosePersonnel = employees.find(
              (e) => e._id === values.secondDosePersonnelId
            )
            item = {
              ...item,
              // 第一劑/第二劑操作人員
              firstDosePersonnelId: values.firstDosePersonnelId || null,
              firstDosePersonnelName: firstDosePersonnel?.name || null,
              secondDosePersonnelId: values.secondDosePersonnelId || null,
              secondDosePersonnelName: secondDosePersonnel?.name || null,
              // 向後兼容：設計師和助理設為 null
              designerId: null,
              designerName: null,
              assistantId: null,
              assistantName: null,
              personnelId: null,
              personnelName: null,
              assist: false
            }
          } else {
            // 一般抽成模式
            const designer = employees.find((e) => e._id === values.designerId)
            const assistant = employees.find(
              (e) => e._id === values.assistantId
            )
            item = {
              ...item,
              // 員工相關（分離設計師和助理）
              designerId: values.designerId || null,
              designerName: designer?.name || '-',
              assistantId: values.assistantId || null,
              assistantName: assistant?.name || '-',
              // 向後兼容字段
              personnelId: values.designerId || null,
              personnelName: designer?.name || '-',
              assist: !!values.assistantId,
              // 第一劑/第二劑設為 null
              firstDosePersonnelId: null,
              firstDosePersonnelName: null,
              secondDosePersonnelId: null,
              secondDosePersonnelName: null
            }
          }

          setSelectedServices([...selectedServices, item])
          // 初始化服務數量
          const serviceQuantity = quantity
          setServiceQuantities((prev) => ({
            ...prev,
            [item.key]: serviceQuantity
          }))
          setNewServiceCards(newServiceCards.filter((k) => k !== cardKey))
          delete cardFormsRef.current[cardKey]
          delete filteredServiceOptions[cardKey]
          setFilteredServiceOptions((prev) => {
            const newOptions = { ...prev }
            delete newOptions[cardKey]
            return newOptions
          })
        })
        .catch(() => {
          // validation failed
        })
    }

    const handleCancelCard = (cardKey) => {
      setNewServiceCards(newServiceCards.filter((k) => k !== cardKey))
      delete cardFormsRef.current[cardKey]
      delete filteredServiceOptions[cardKey]
      setFilteredServiceOptions((prev) => {
        const newOptions = { ...prev }
        delete newOptions[cardKey]
        return newOptions
      })
      setSelectedCategoryIds((prev) => {
        const newIds = { ...prev }
        delete newIds[cardKey]
        return newIds
      })
    }

    const ADDITIONAL_SERVICES_CATEGORY_ID = '__additional_services__'

    const ServiceCardForm = ({ cardKey }) => {
      const [form] = Form.useForm()
      const selectedCategoryId = selectedCategoryIds[cardKey] || null
      const formCategoryId = Form.useWatch('categoryId', form)
      const selectedServiceId = Form.useWatch('serviceId', form)
      const quantity = Form.useWatch('quantity', form) || 1
      const price = Form.useWatch('price', form)
      const originalPrice = Form.useWatch('originalPrice', form)
      // 使用 ref 記錄舊數量和是否手動修改過價格
      const prevQuantityRef = useRef(1)
      const isManualPriceRef = useRef(false)

      // 使用 useRef 存儲 form，避免在 useEffect 中設置狀態導致無限循環
      // 在每次渲染時更新 ref，但不在 useEffect 中設置狀態
      cardFormsRef.current[cardKey] = form

      useEffect(() => {
        return () => {
          delete cardFormsRef.current[cardKey]
        }
      }, [cardKey])

      // 同步 selectedCategoryId 到 form（如果 form 的值不同步，例如從外部更新）
      useEffect(() => {
        if (selectedCategoryId && formCategoryId !== selectedCategoryId) {
          form.setFieldsValue({ categoryId: selectedCategoryId })
        }
      }, [selectedCategoryId, formCategoryId, form])

      // 現場結帳模式：自動帶入登入者本人（非員工帳號則第一位設計師）
      useEffect(() => {
        if (checkoutMode === 'walkin' && employees.length > 0) {
          const firstDesigner = getDefaultEmployee()
          if (firstDesigner && firstDesigner._id) {
            // 檢查服務是否支持第一劑/第二劑
            const commissionRule = selectedServiceId
              ? serviceCommissionRules[selectedServiceId]
              : null
            const supportDoseTypes = commissionRule?.supportDoseTypes || false

            if (supportDoseTypes) {
              // 第一劑/第二劑模式：第一劑、第二劑、設計師都自動帶入第一順位
              const currentFirst = form.getFieldValue('firstDosePersonnelId')
              const currentSecond = form.getFieldValue('secondDosePersonnelId')
              const currentDesigner = form.getFieldValue('designerId')
              if (!currentFirst || !currentSecond || !currentDesigner) {
                form.setFieldsValue({
                  firstDosePersonnelId: currentFirst || firstDesigner._id,
                  secondDosePersonnelId: currentSecond || firstDesigner._id,
                  designerId: currentDesigner || firstDesigner._id
                })
              }
            } else {
              // 一般抽成模式：自動帶入第一位設計師
              const currentDesigner = form.getFieldValue('designerId')
              if (!currentDesigner) {
                form.setFieldsValue({
                  designerId: firstDesigner._id
                })
              }
            }
          }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [checkoutMode, employees, selectedServiceId, form])

      // 當數量變化時，自動計算實際收費
      useEffect(() => {
        if (originalPrice && quantity) {
          const unitPrice = originalPrice
          const currentPrice = price || unitPrice

          // 計算預期的總價（單價 * 數量）
          const expectedTotalPrice = unitPrice * quantity

          // 如果實際收費等於單價（說明用戶還沒手動修改，數量為1），自動計算：單價 * 數量
          // 如果實際收費等於預期總價（說明已經按數量計算過），保持比例：新價格 = 原價格 * (新數量 / 舊數量)
          // 如果實際收費不等於單價也不等於預期總價，說明用戶手動修改過，按比例調整
          let newPrice

          // 獲取舊數量（從表單中獲取，如果沒有則為1）
          const oldQuantity = form.getFieldValue('quantity') || 1

          if (currentPrice === unitPrice || currentPrice === 0) {
            // 實際收費等於單價，說明用戶還沒手動修改，自動計算：單價 * 數量
            newPrice = unitPrice * quantity
          } else if (currentPrice === unitPrice * oldQuantity) {
            // 實際收費等於單價 * 舊數量，說明已經按數量計算過，重新計算：單價 * 新數量
            newPrice = unitPrice * quantity
          } else {
            // 用戶手動修改過，按比例調整：新價格 = 原價格 * (新數量 / 舊數量)
            newPrice = Math.round((currentPrice * quantity) / oldQuantity)
          }

          // 只有當計算出的價格與當前價格不同時才更新，避免無限循環
          if (newPrice !== price && newPrice > 0) {
            form.setFieldsValue({ price: newPrice })
          }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [quantity, originalPrice])

      // 檢查服務是否支持第一劑/第二劑
      const commissionRule = selectedServiceId
        ? serviceCommissionRules[selectedServiceId]
        : null
      const supportDoseTypes = commissionRule?.supportDoseTypes || false

      // 根據選中的類別過濾服務選項；選「加選服務」時顯示 additionalServices
      const filteredServices =
        selectedCategoryId === ADDITIONAL_SERVICES_CATEGORY_ID
          ? additionalServices
          : selectedCategoryId
            ? services.filter((s) => s.category === selectedCategoryId)
            : []

      return (
        <NewServiceCard>
          <Form form={form} layout='vertical'>
            <Form.Item
              name='categoryId'
              label='服務類別'
              rules={[{ required: true, message: '請選擇服務類別' }]}
            >
              <FieldSelect
                id={`categoryId-${cardKey}`}
                placeholder='選擇類別'
                getPopupContainer={() => document.body}
                dropdownStyle={{ zIndex: 1050 }}
                onChange={(val) => {
                  setSelectedCategoryIds((prev) => ({
                    ...prev,
                    [cardKey]: val
                  }))
                  form.setFieldsValue({
                    categoryId: val,
                    serviceId: undefined,
                    price: undefined
                  })
                  form.resetFields(['serviceId', 'price'])
                  const opts =
                    val === ADDITIONAL_SERVICES_CATEGORY_ID
                      ? additionalServices
                      : services.filter((s) => s.category === val)
                  setFilteredServiceOptions((prev) => ({
                    ...prev,
                    [cardKey]: opts
                  }))
                }}
              >
                {categories.length > 0 ? (
                  <>
                    {categories.map((c) => (
                      <Option key={c._id} value={c._id}>
                        {c.name}
                      </Option>
                    ))}
                    {additionalServices.length > 0 && (
                      <Option
                        key={ADDITIONAL_SERVICES_CATEGORY_ID}
                        value={ADDITIONAL_SERVICES_CATEGORY_ID}
                      >
                        加選服務
                      </Option>
                    )}
                  </>
                ) : additionalServices.length > 0 ? (
                  <>
                    <Option
                      key={ADDITIONAL_SERVICES_CATEGORY_ID}
                      value={ADDITIONAL_SERVICES_CATEGORY_ID}
                    >
                      加選服務
                    </Option>
                  </>
                ) : (
                  <Option disabled value=''>
                    無可用類別
                  </Option>
                )}
              </FieldSelect>
            </Form.Item>

            <Form.Item
              name='serviceId'
              label='服務項目'
              rules={[{ required: true, message: '請選擇服務項目' }]}
            >
              <FieldSelect
                id={`serviceId-${cardKey}`}
                key={`service-${selectedCategoryId || 'none'}`}
                placeholder='選擇服務'
                getPopupContainer={() => document.body}
                dropdownStyle={{ zIndex: 1050 }}
                disabled={!selectedCategoryId}
                onChange={(val) => {
                  const svc =
                    getServiceOrAdditionalById(val) ||
                    services.find((s) => s._id === val)
                  if (svc) {
                    const currentQuantity = form.getFieldValue('quantity') || 1
                    const unitPrice = Number(svc.price || 0)
                    form.setFieldsValue({
                      originalPrice: unitPrice,
                      price: unitPrice * currentQuantity
                    })
                  }
                }}
                showSearch
                optionFilterProp='children'
                allowClear
              >
                {filteredServices.length > 0 ? (
                  filteredServices.map((s) => (
                    <Option key={s._id} value={s._id}>
                      {s.name || s.serviceName} - NT${' '}
                      {Number(s.price || 0).toLocaleString()}
                    </Option>
                  ))
                ) : (
                  <Option disabled value=''>
                    {selectedCategoryId ? '該類別暫無服務' : '請先選擇服務類別'}
                  </Option>
                )}
              </FieldSelect>
            </Form.Item>

            {/* 原始價格顯示（只讀） */}
            <Form.Item name='originalPrice' label='原始價格'>
              <FieldNumberInput
                id={`originalPrice-${cardKey}`}
                prefix='NT$'
                disabled
                readOnly
              />
            </Form.Item>

            {/* 現場結帳模式：無論什麼選項都要顯示設計師字段 */}
            {checkoutMode === 'walkin' ? (
              // 現場結帳模式：總是顯示設計師字段
              supportDoseTypes ? (
                // 第一劑/第二劑模式：顯示第一劑、第二劑操作人員和設計師
                <>
                  <FormRow>
                    <Form.Item
                      name='firstDosePersonnelId'
                      label='第一劑操作人員'
                      rules={[
                        {
                          required: true,
                          message: '請選擇第一劑操作人員'
                        }
                      ]}
                    >
                      <FieldSelect
                        id={`firstDosePersonnelId-${cardKey}`}
                        placeholder='選擇第一劑操作人員'
                        getPopupContainer={() => document.body}
                        dropdownStyle={{ zIndex: 1050 }}
                        allowClear={false}
                      >
                        {employees.map((e) => (
                          <Option key={e._id} value={e._id}>
                            {e.name}
                          </Option>
                        ))}
                      </FieldSelect>
                    </Form.Item>

                    <Form.Item
                      name='secondDosePersonnelId'
                      label='第二劑操作人員'
                      rules={[
                        {
                          required: true,
                          message: '請選擇第二劑操作人員'
                        }
                      ]}
                    >
                      <FieldSelect
                        id={`secondDosePersonnelId-${cardKey}`}
                        placeholder='選擇第二劑操作人員'
                        getPopupContainer={() => document.body}
                        dropdownStyle={{ zIndex: 1050 }}
                        allowClear={false}
                      >
                        {employees.map((e) => (
                          <Option key={e._id} value={e._id}>
                            {e.name}
                          </Option>
                        ))}
                      </FieldSelect>
                    </Form.Item>
                  </FormRow>
                  <FormRow>
                    <Form.Item
                      name='designerId'
                      label='設計師'
                      rules={[{ required: true, message: '請選擇設計師' }]}
                    >
                      <FieldSelect
                        id={`designerId-${cardKey}`}
                        placeholder='選擇設計師'
                        getPopupContainer={() => document.body}
                        dropdownStyle={{ zIndex: 1050 }}
                        allowClear={false}
                      >
                        {employees.map((e) => (
                          <Option key={e._id} value={e._id}>
                            {e.name}
                          </Option>
                        ))}
                      </FieldSelect>
                    </Form.Item>
                  </FormRow>
                </>
              ) : (
                // 一般抽成模式：顯示設計師字段（必填）
                <FormRow>
                  <Form.Item
                    name='designerId'
                    label='設計師'
                    rules={[{ required: true, message: '請選擇設計師' }]}
                  >
                    <FieldSelect
                      id={`designerId-${cardKey}`}
                      placeholder='選擇設計師'
                      getPopupContainer={() => document.body}
                      dropdownStyle={{ zIndex: 1050 }}
                      allowClear={false}
                    >
                      {employees.map((e) => (
                        <Option key={e._id} value={e._id}>
                          {e.name}
                        </Option>
                      ))}
                    </FieldSelect>
                  </Form.Item>

                  <Form.Item name='assistantId' label='助理'>
                    <FieldSelect
                      id={`assistantId-${cardKey}`}
                      placeholder='選擇助理（可選）'
                      getPopupContainer={() => document.body}
                      dropdownStyle={{ zIndex: 1050 }}
                      allowClear
                    >
                      {employees.map((e) => (
                        <Option key={e._id} value={e._id}>
                          {e.name}
                        </Option>
                      ))}
                    </FieldSelect>
                  </Form.Item>
                </FormRow>
              )
            ) : supportDoseTypes ? (
              // 預約結帳模式：第一劑/第二劑模式（第一劑、第二劑、設計師皆必填）
              <>
                <FormRow>
                  <Form.Item
                    name='firstDosePersonnelId'
                    label='第一劑操作人員'
                    rules={[
                      { required: true, message: '請選擇第一劑操作人員' }
                    ]}
                  >
                    <FieldSelect
                      id={`firstDosePersonnelId-${cardKey}`}
                      placeholder='選擇第一劑操作人員'
                      getPopupContainer={() => document.body}
                      dropdownStyle={{ zIndex: 1050 }}
                      allowClear={false}
                    >
                      {employees.map((e) => (
                        <Option key={e._id} value={e._id}>
                          {e.name}
                        </Option>
                      ))}
                    </FieldSelect>
                  </Form.Item>

                  <Form.Item
                    name='secondDosePersonnelId'
                    label='第二劑操作人員'
                    rules={[
                      { required: true, message: '請選擇第二劑操作人員' }
                    ]}
                  >
                    <FieldSelect
                      id={`secondDosePersonnelId-${cardKey}`}
                      placeholder='選擇第二劑操作人員'
                      getPopupContainer={() => document.body}
                      dropdownStyle={{ zIndex: 1050 }}
                      allowClear={false}
                    >
                      {employees.map((e) => (
                        <Option key={e._id} value={e._id}>
                          {e.name}
                        </Option>
                      ))}
                    </FieldSelect>
                  </Form.Item>
                </FormRow>
                <FormRow>
                  <Form.Item
                    name='designerId'
                    label='設計師'
                    rules={[{ required: true, message: '請選擇設計師' }]}
                  >
                    <FieldSelect
                      id={`designerId-${cardKey}`}
                      placeholder='選擇設計師'
                      getPopupContainer={() => document.body}
                      dropdownStyle={{ zIndex: 1050 }}
                      allowClear={false}
                    >
                      {employees.map((e) => (
                        <Option key={e._id} value={e._id}>
                          {e.name}
                        </Option>
                      ))}
                    </FieldSelect>
                  </Form.Item>
                </FormRow>
              </>
            ) : (
              // 預約結帳模式：一般抽成模式（設計師必填）
              <FormRow>
                <Form.Item
                  name='designerId'
                  label='設計師'
                  rules={[{ required: true, message: '請選擇設計師' }]}
                >
                  <FieldSelect
                    id={`designerId-${cardKey}`}
                    placeholder='選擇設計師'
                    getPopupContainer={() => document.body}
                    dropdownStyle={{ zIndex: 1050 }}
                    allowClear={false}
                  >
                    {employees.map((e) => (
                      <Option key={e._id} value={e._id}>
                        {e.name}
                      </Option>
                    ))}
                  </FieldSelect>
                </Form.Item>

                <Form.Item name='assistantId' label='助理'>
                  <FieldSelect
                    id={`assistantId-${cardKey}`}
                    placeholder='選擇助理（可選）'
                    getPopupContainer={() => document.body}
                    dropdownStyle={{ zIndex: 1050 }}
                    allowClear
                  >
                    {employees.map((e) => (
                      <Option key={e._id} value={e._id}>
                        {e.name}
                      </Option>
                    ))}
                  </FieldSelect>
                </Form.Item>
              </FormRow>
            )}

            {/* 實際收費2 */}
            <FormRow>
              <Form.Item
                name='price'
                label='實際收費'
                rules={[{ required: true, message: '請輸入實際收費' }]}
              >
                <InputNumber
                  id={`price-${cardKey}`}
                  style={{
                    width: '100%',
                    height: 44,
                    borderRadius: 10,
                    border: `1px solid ${theme.border}`,
                    padding: '0 12px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  min={0}
                  onWheel={(e) => e?.stopPropagation?.()}
                  onKeyDown={(e) => {
                    if (!e) return
                    const blockKeys = [
                      'ArrowUp',
                      'ArrowDown',
                      'PageUp',
                      'PageDown',
                      'Home',
                      'End'
                    ]
                    if (blockKeys.includes(e.key)) {
                      e.preventDefault?.()
                      e.stopPropagation?.()
                    }
                    if (e.key === 'Enter') {
                      e.preventDefault?.()
                    }
                  }}
                  onChange={(value) => {
                    // 當用戶手動修改實際收費時，標記為手動修改
                    if (value !== null && value !== undefined) {
                      isManualPriceRef.current = true
                    }
                  }}
                  className='actual-price-input'
                />
              </Form.Item>
              {/* 數量2 */}
              <Form.Item
                name='quantity'
                label='數量'
                rules={[
                  { required: true, message: '請輸入數量' },
                  { type: 'number', min: 1, message: '數量不能小於1' }
                ]}
                initialValue={1}
              >
                <InputNumber
                  min={1}
                  precision={0}
                  style={{
                    width: '100%',
                    height: 44,
                    borderRadius: 10,
                    border: `1px solid ${theme.border}`,
                    padding: '0 12px'
                  }}
                  placeholder='數量'
                  className='quantity-input'
                />
              </Form.Item>
            </FormRow>

            {/* 會員折扣信息 */}
            {(() => {
              if (!customerMembershipTier || membershipDiscountRate >= 100) {
                return null
              }

              const formValues = form.getFieldsValue()
              const originalPrice = formValues.originalPrice || 0
              if (!originalPrice || originalPrice === 0) {
                return null
              }

              const discountedPrice = Math.round(
                (originalPrice * membershipDiscountRate) / 100
              )
              const currentPrice = formValues.price || originalPrice
              const isApplied = currentPrice === discountedPrice

              return (
                <div
                  style={{
                    marginTop: 12,
                    padding: '16px',
                    background: '#f5f5f7',
                    borderRadius: 12,
                    border: 'none'
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      color: '#6e6e73',
                      lineHeight: '1.4',
                      marginBottom: 12
                    }}
                  >
                    此會員為{' '}
                    <span style={{ fontWeight: 600, color: '#1d1d1f' }}>
                      {customerMembershipTier}
                    </span>
                    ，折扣後價格為{' '}
                    <span style={{ fontWeight: 600, color: '#1d1d1f' }}>
                      NT$ {discountedPrice.toLocaleString()}
                    </span>
                  </div>
                  <Button
                    type={isApplied ? 'default' : 'primary'}
                    size='small'
                    onClick={() => {
                      form.setFieldsValue({ price: discountedPrice })
                    }}
                    style={{
                      fontSize: 13,
                      height: 32,
                      borderRadius: 8,
                      fontWeight: 500,
                      border: isApplied ? '1px solid #d2d2d7' : 'none',
                      background: isApplied
                        ? '#ffffff'
                        : 'rgba(0, 122, 255, 1)',
                      color: isApplied ? '#1d1d1f' : '#ffffff',
                      boxShadow: isApplied
                        ? 'none'
                        : '0 1px 3px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      if (!isApplied) {
                        e.currentTarget.style.background =
                          'rgba(0, 122, 255, 0.9)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isApplied) {
                        e.currentTarget.style.background =
                          'rgba(0, 122, 255, 1)'
                      }
                    }}
                  >
                    {isApplied ? '已套用' : '套用折扣'}
                  </Button>
                </div>
              )
            })()}

            <div
              style={{
                width: '100%',
                display: 'flex',
                gap: '12px',
                marginTop: '16px'
              }}
            >
              <Button
                onClick={() => handleCancelCard(cardKey)}
                style={{
                  flex: 1,
                  height: '48px',
                  fontSize: '15px',
                  fontWeight: '500',
                  borderRadius: '8px',
                  borderColor: '#D4D0C9',
                  color: '#2C2C2C'
                }}
              >
                取消
              </Button>
              <Button
                type='primary'
                onClick={() => handleSaveCard(cardKey)}
                style={{
                  flex: 1,
                  height: '48px',
                  fontSize: '15px',
                  fontWeight: '500',
                  borderRadius: '8px',
                  background: theme.primary,
                  borderColor: theme.primary
                }}
              >
                加入
              </Button>
            </div>
          </Form>
        </NewServiceCard>
      )
    }

    // 判斷服務是否為預約服務（預約結帳模式下，匹配預約的服務ID）
    const isBookingService = (service) => {
      if (checkoutMode !== 'booking' || !bookingData) return false

      // 通過 key 判斷（如果 key 以 booking- 或 additional- 開頭，則是預約服務）
      if (
        service.key &&
        (service.key.startsWith('booking-') ||
          service.key.startsWith('additional-'))
      ) {
        return true
      }

      // 檢查服務ID是否匹配預約的 services 數組中的服務
      if (bookingData.services && Array.isArray(bookingData.services)) {
        const isMainService = bookingData.services.some(
          (bs) => bs.serviceId === service.serviceId
        )
        if (isMainService) return true
      }

      // 檢查服務ID是否匹配預約的 additionalServices 數組中的服務
      if (
        bookingData.additionalServices &&
        Array.isArray(bookingData.additionalServices)
      ) {
        const isAdditionalService = bookingData.additionalServices.some(
          (as) => as.serviceId === service.serviceId
        )
        if (isAdditionalService) return true
      }

      // 向後兼容：檢查 productId
      if (bookingData.productId === service.serviceId) return true

      return false
    }

    return (
      <FormCard
        title={
          checkoutMode === 'booking'
            ? '選擇服務或商品（可加選）'
            : '步驟二 選擇技術服務'
        }
      >
        {!servicesOptionsReady || !sectionReady ? (
          <div
            style={{
              padding: '48px 24px',
              textAlign: 'center',
              minHeight: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Spin size='large' tip='載入服務選項中...' />
          </div>
        ) : (
          <>
            {checkoutMode === 'booking' && (
              <div
                style={{
                  marginBottom: 24,
                  padding: 16,
                  background:
                    selectedServices.length > 0 ? '#FAF9F7' : '#FAF9F7',
                  borderRadius: 8,
                  border: '1px solid #E8E3DC'
                }}
              >
                <div
                  style={{
                    fontSize: 14,
                    color: selectedServices.length > 0 ? '#2C2C2C' : '#8B8B8B',
                    fontWeight: 400
                  }}
                >
                  {selectedServices.length > 0
                    ? '預約服務已自動帶入，您可以加選其他服務或商品'
                    : '正在載入預約服務...'}
                </div>
              </div>
            )}
            {(() => {
              const servicesMissingDesigner = selectedServices.filter(
                (service) => {
                  const commissionRule =
                    serviceCommissionRules[service.serviceId]
                  const supportDoseTypes =
                    service.supportDoseTypes !== undefined
                      ? service.supportDoseTypes
                      : commissionRule?.supportDoseTypes || false
                  if (supportDoseTypes) {
                    return (
                      !service.firstDosePersonnelId &&
                      !service.secondDosePersonnelId
                    )
                  }
                  return !service.designerId && !service.personnelId
                }
              )
              if (servicesMissingDesigner.length > 0) {
                return (
                  <Hint
                    tone='warning'
                    title='請為每個服務選擇設計師'
                    style={{ marginBottom: 16 }}
                  >
                    {
                      <>
                        以下服務尚未選擇設計師，無法進行下一步：
                        <ul
                          style={{
                            margin: '8px 0 0',
                            paddingLeft: 20
                          }}
                        >
                          {servicesMissingDesigner.map((s) => (
                            <li key={s.key}>
                              {getServiceOrAdditionalById?.(s.serviceId)
                                ?.name ||
                                s.name ||
                                s.serviceName ||
                                '未命名服務'}
                              {s.supportDoseTypes
                                ? '（須選擇第一劑、第二劑操作人員與設計師）'
                                : ''}
                            </li>
                          ))}
                        </ul>
                      </>
                    }
                  </Hint>
                )
              }
              return null
            })()}
            <Space direction='vertical' size='large' style={{ width: '100%' }}>
              <div>
                {/* 服務卡片顯示（統一使用，不分桌面和手機） */}
                <div style={{ marginTop: 16 }}>
                  {selectedServices.map((service) => {
                    const isBooking = isBookingService(service)
                    // 檢查服務是否支持第一劑/第二劑
                    const commissionRule =
                      serviceCommissionRules[service.serviceId]
                    const supportDoseTypes =
                      service.supportDoseTypes !== undefined
                        ? service.supportDoseTypes
                        : commissionRule?.supportDoseTypes || false
                    return (
                      <ServiceCard
                        key={service.key}
                        style={
                          isBooking
                            ? {
                                border: `1px solid ${theme.primary}`,
                                background: '#fff7fa'
                              }
                            : {}
                        }
                      >
                        <ServiceCardHeader>
                          {isBooking && (
                            <BookingServiceTag>預約服務</BookingServiceTag>
                          )}
                          <ServiceCardDelete
                            type='text'
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              if (isBooking) {
                                Modal.confirm({
                                  title: '刪除預約帶入的服務？',
                                  content:
                                    '此服務將從本次結帳移除，完成結帳後預約紀錄也會一併更新。',
                                  okText: '確認刪除',
                                  cancelText: '取消',
                                  onOk: () => {
                                    setSelectedServices(
                                      selectedServices.filter(
                                        (s) => s.key !== service.key
                                      )
                                    )
                                  }
                                })
                              } else {
                                setSelectedServices(
                                  selectedServices.filter(
                                    (s) => s.key !== service.key
                                  )
                                )
                              }
                            }}
                          />
                        </ServiceCardHeader>
                        <ServiceCardRow>
                          <span className='label'>服務項目</span>
                          <FieldSelect
                            value={service.serviceId}
                            placeholder='選擇服務'
                            showSearch
                            optionFilterProp='children'
                            filterOption={(input, opt) =>
                              (opt?.label ?? '')
                                .toLowerCase()
                                .includes(input.toLowerCase())
                            }
                            options={(() => {
                              const groups = categories
                                .map((cat) => ({
                                  label: cat.name,
                                  options: services
                                    .filter(
                                      (s) =>
                                        s.category === cat._id ||
                                        s.category?._id === cat._id ||
                                        s.categoryId === cat._id
                                    )
                                    .map((svc) => ({
                                      value: svc._id,
                                      label: `${svc.name || svc.serviceName} - NT$ ${Number(
                                        svc.price || 0
                                      ).toLocaleString()}`
                                    }))
                                }))
                                .filter((g) => g.options.length > 0)
                              const uncategorized = services.filter(
                                (s) =>
                                  !categories.some(
                                    (c) =>
                                      c._id === s.category ||
                                      c._id === s.category?._id ||
                                      c._id === s.categoryId
                                  )
                              )
                              if (uncategorized.length > 0) {
                                groups.push({
                                  label: '其他',
                                  options: uncategorized.map((svc) => ({
                                    value: svc._id,
                                    label: `${svc.name || svc.serviceName} - NT$ ${Number(
                                      svc.price || 0
                                    ).toLocaleString()}`
                                  }))
                                })
                              }
                              if (additionalServices.length > 0) {
                                groups.push({
                                  label: '加選服務',
                                  options: additionalServices.map((svc) => ({
                                    value: svc._id,
                                    label: `${svc.name || svc.serviceName} - NT$ ${Number(
                                      svc.price || 0
                                    ).toLocaleString()}`
                                  }))
                                })
                              }
                              // 若目前選中的服務 ID 不在任一 option 中（例如加選尚未載入或 ID 格式不同），
                              // 動態加入一筆 option 以免 Select 只顯示 ID
                              const allOptionValues = new Set()
                              groups.forEach((g) =>
                                (g.options || []).forEach((opt) =>
                                  allOptionValues.add(opt.value)
                                )
                              )
                              if (
                                service.serviceId &&
                                !allOptionValues.has(service.serviceId)
                              ) {
                                const meta =
                                  getServiceOrAdditionalById(
                                    service.serviceId
                                  ) || service
                                const name =
                                  meta?.name ||
                                  meta?.serviceName ||
                                  String(service.serviceId)
                                const price = Number(
                                  meta?.price ??
                                    service.originalPrice ??
                                    service.price ??
                                    0
                                )
                                const extraGroup = groups.find(
                                  (g) => g.label === '加選服務'
                                )
                                const option = {
                                  value: service.serviceId,
                                  label: `${name} - NT$ ${price.toLocaleString()}`
                                }
                                if (extraGroup) {
                                  extraGroup.options = extraGroup.options || []
                                  extraGroup.options.push(option)
                                } else {
                                  groups.push({
                                    label: '加選服務',
                                    options: [option]
                                  })
                                }
                              }
                              return groups
                            })()}
                            onChange={(newServiceId) => {
                              const newSvc =
                                getServiceOrAdditionalById(newServiceId)
                              if (!newSvc) return
                              const cat = categories.find(
                                (c) =>
                                  c._id === newSvc.category ||
                                  c._id === newSvc.category?._id ||
                                  c._id === newSvc.categoryId
                              )
                              const quantity =
                                serviceQuantities[service.key] ??
                                service.quantity ??
                                1
                              const originalPrice = newSvc.price || 0
                              const discountedPrice =
                                membershipDiscountRate < 100
                                  ? Math.round(
                                      (originalPrice * membershipDiscountRate) /
                                        100
                                    )
                                  : originalPrice
                              const newPrice =
                                (applyMembershipDiscount
                                  ? discountedPrice
                                  : originalPrice) * quantity
                              const rule = serviceCommissionRules[newServiceId]
                              const supportDoseTypes =
                                rule?.supportDoseTypes ?? false
                              const updatedServices = selectedServices.map(
                                (s) =>
                                  s.key === service.key
                                    ? {
                                        ...s,
                                        serviceId: newSvc._id,
                                        categoryId:
                                          cat?._id ??
                                          newSvc.category ??
                                          newSvc.category?._id,
                                        categoryName: cat?.name ?? '-',
                                        name: newSvc.name ?? '-',
                                        originalPrice,
                                        basePrice: originalPrice,
                                        price: newPrice,
                                        quantity,
                                        supportDoseTypes,
                                        isManualPrice: false,
                                        designerId: null,
                                        designerName: null,
                                        assistantId: null,
                                        assistantName: null,
                                        personnelId: null,
                                        personnelName: null,
                                        assist: false,
                                        firstDosePersonnelId: null,
                                        firstDosePersonnelName: null,
                                        secondDosePersonnelId: null,
                                        secondDosePersonnelName: null
                                      }
                                    : s
                              )
                              setSelectedServices(updatedServices)
                              setServiceQuantities((prev) => ({
                                ...prev,
                                [service.key]: quantity
                              }))
                              delete priceInputRefs.current[service.key]
                            }}
                            style={{ width: '100%' }}
                          />
                        </ServiceCardRow>
                        <ServiceCardRow>
                          <span className='label'>價格</span>
                          <span
                            className='value'
                            style={{
                              fontSize: '16px',
                              fontWeight: '500',
                              color: '#2C2C2C'
                            }}
                          >
                            NT${' '}
                            {(() => {
                              // 直接顯示 service.price（已含數量的實收總價），
                              // 與 totalServiceAmount 計算保持一致
                              return (Number(service.price) || 0).toLocaleString()
                            })()}
                          </span>
                        </ServiceCardRow>
                        <ServiceCardRow>
                          {/* 數量1 */}
                          <span className='label'>數量</span>
                          <InputNumber
                            min={1}
                            precision={0}
                            value={
                              serviceQuantities[service.key] !== undefined
                                ? serviceQuantities[service.key]
                                : service.quantity !== undefined
                                  ? service.quantity
                                  : null
                            }
                            onChange={(value) => {
                              // 允許 null 或 undefined，讓用戶可以清空輸入框顯示空白
                              if (value === null || value === undefined) {
                                setServiceQuantities((prev) => ({
                                  ...prev,
                                  [service.key]: null
                                }))
                                // 更新服務的數量為 null（顯示空白），但在計算價格時使用 1
                                const updatedServices = selectedServices.map(
                                  (s) =>
                                    s.key === service.key
                                      ? { ...s, quantity: null }
                                      : s
                                )
                                setSelectedServices(updatedServices)
                              } else {
                                // 有值時，確保最小為1
                                const quantity = Math.max(1, value)

                                setServiceQuantities((prev) => ({
                                  ...prev,
                                  [service.key]: quantity
                                }))

                                // 計算單價（原價）
                                const unitPrice =
                                  service.originalPrice ||
                                  service.basePrice ||
                                  service.price ||
                                  0
                                // 當前實際收費
                                const currentPrice = service.price || 0
                                // 舊數量（如果為null，使用1）
                                const oldQuantity = service.quantity || 1

                                // 判斷是否為手動修改的價格
                                // 如果實際收費等於單價，說明用戶還沒手動修改，自動計算：單價 * 數量
                                // 如果實際收費不等於單價，說明用戶手動修改過，按比例調整：新價格 = 原價格 * (新數量 / 舊數量)
                                let newPrice
                                if (
                                  currentPrice === unitPrice ||
                                  currentPrice === 0 ||
                                  oldQuantity === 1
                                ) {
                                  // 自動計算：單價 * 數量
                                  newPrice = unitPrice * quantity
                                } else {
                                  // 用戶手動修改過，按比例調整：新價格 = 原價格 * (新數量 / 舊數量)
                                  newPrice = Math.round(
                                    (currentPrice * quantity) / oldQuantity
                                  )
                                }

                                // 更新服務的數量和價格
                                const updatedServices = selectedServices.map(
                                  (s) =>
                                    s.key === service.key
                                      ? {
                                          ...s,
                                          quantity: quantity,
                                          price: newPrice
                                        }
                                      : s
                                )
                                setSelectedServices(updatedServices)

                                // 更新實際收費輸入框的 ref
                                priceInputRefs.current[service.key] = newPrice
                              }
                            }}
                            style={{
                              width: '100%',
                              height: 44,
                              borderRadius: 10,
                              border: `1px solid ${theme.border}`
                            }}
                            className='quantity-input'
                          />
                        </ServiceCardRow>
                        {/* 根據抽成規則顯示不同的操作人員選擇 */}
                        {supportDoseTypes ? (
                          // 第一劑/第二劑模式（設計師、第一劑、第二劑皆必填）
                          <PersonnelFieldsContainer>
                            <ServiceCardRow>
                              <span className='label'>設計師</span>
                              <FieldSelect
                                value={service.designerId || null}
                                placeholder='選擇設計師（必填）'
                                allowClear={false}
                                onChange={(value) => {
                                  const designer = employees.find(
                                    (e) => e._id === value
                                  )
                                  const updatedServices = selectedServices.map(
                                    (s) =>
                                      s.key === service.key
                                        ? {
                                            ...s,
                                            designerId: value,
                                            designerName:
                                              designer?.name || null,
                                            personnelId: value // 向後兼容
                                          }
                                        : s
                                  )
                                  setSelectedServices(updatedServices)
                                }}
                              >
                                {employees.map((e) => (
                                  <Option key={e._id} value={e._id}>
                                    {e.name}
                                  </Option>
                                ))}
                              </FieldSelect>
                            </ServiceCardRow>
                            <ServiceCardRow>
                              <span className='label'>第一劑操作人員</span>
                              <FieldSelect
                                value={service.firstDosePersonnelId || null}
                                placeholder='選擇第一劑操作人員（必填）'
                                allowClear={false}
                                onChange={(value) => {
                                  const personnel = employees.find(
                                    (e) => e._id === value
                                  )
                                  const updatedServices = selectedServices.map(
                                    (s) =>
                                      s.key === service.key
                                        ? {
                                            ...s,
                                            firstDosePersonnelId: value,
                                            firstDosePersonnelName:
                                              personnel?.name || null
                                          }
                                        : s
                                  )
                                  setSelectedServices(updatedServices)
                                }}
                              >
                                {employees.map((e) => (
                                  <Option key={e._id} value={e._id}>
                                    {e.name}
                                  </Option>
                                ))}
                              </FieldSelect>
                            </ServiceCardRow>
                            <ServiceCardRow>
                              <span className='label'>第二劑操作人員</span>
                              <FieldSelect
                                value={service.secondDosePersonnelId || null}
                                placeholder='選擇第二劑操作人員（必填）'
                                allowClear={false}
                                onChange={(value) => {
                                  const personnel = employees.find(
                                    (e) => e._id === value
                                  )
                                  const updatedServices = selectedServices.map(
                                    (s) =>
                                      s.key === service.key
                                        ? {
                                            ...s,
                                            secondDosePersonnelId: value,
                                            secondDosePersonnelName:
                                              personnel?.name || null
                                          }
                                        : s
                                  )
                                  setSelectedServices(updatedServices)
                                }}
                              >
                                {employees.map((e) => (
                                  <Option key={e._id} value={e._id}>
                                    {e.name}
                                  </Option>
                                ))}
                              </FieldSelect>
                            </ServiceCardRow>
                          </PersonnelFieldsContainer>
                        ) : (
                          // 一般抽成模式
                          <>
                            <ServiceCardRow>
                              <span className='label'>設計師</span>
                              <FieldSelect
                                value={
                                  service.designerId ||
                                  service.personnelId ||
                                  null
                                }
                                placeholder='選擇設計師'
                                onChange={(value) => {
                                  const designer = employees.find(
                                    (e) => e._id === value
                                  )
                                  const updatedServices = selectedServices.map(
                                    (s) =>
                                      s.key === service.key
                                        ? {
                                            ...s,
                                            designerId: value,
                                            designerName: designer?.name || '-',
                                            personnelId: value, // 向後兼容
                                            personnelName: designer?.name || '-'
                                          }
                                        : s
                                  )
                                  setSelectedServices(updatedServices)
                                }}
                              >
                                {employees.map((e) => (
                                  <Option key={e._id} value={e._id}>
                                    {e.name}
                                  </Option>
                                ))}
                              </FieldSelect>
                            </ServiceCardRow>
                            <ServiceCardRow>
                              <span className='label'>選擇助理 (可選)</span>
                              <FieldSelect
                                value={service.assistantId || null}
                                placeholder='選擇助理（可選）'
                                allowClear
                                onChange={(value) => {
                                  const assistant = employees.find(
                                    (e) => e._id === value
                                  )
                                  const updatedServices = selectedServices.map(
                                    (s) =>
                                      s.key === service.key
                                        ? {
                                            ...s,
                                            assistantId: value,
                                            assistantName:
                                              assistant?.name || '-',
                                            assist: !!value // 向後兼容
                                          }
                                        : s
                                  )
                                  setSelectedServices(updatedServices)
                                }}
                              >
                                {employees.map((e) => (
                                  <Option key={e._id} value={e._id}>
                                    {e.name}
                                  </Option>
                                ))}
                              </FieldSelect>
                            </ServiceCardRow>
                          </>
                        )}
                        {/* 實際收費1 */}
                        <ServiceCardRow>
                          <span className='label'>實際收費</span>
                          <InputNumber
                            style={{
                              width: '100%',
                              height: 44,
                              borderRadius: 10,
                              border: `1px solid ${theme.border}`,
                              padding: '0 12px',
                              marginRight: '48px'
                            }}
                            value={
                              priceInputRefs.current[service.key] !== undefined
                                ? priceInputRefs.current[service.key]
                                : (service.price ?? undefined)
                            }
                            min={0}
                            onWheel={(e) => e?.stopPropagation?.()}
                            onKeyDown={(e) => {
                              if (!e) return
                              const blockKeys = [
                                'ArrowUp',
                                'ArrowDown',
                                'PageUp',
                                'PageDown',
                                'Home',
                                'End'
                              ]
                              if (blockKeys.includes(e.key)) {
                                e.preventDefault?.()
                                e.stopPropagation?.()
                              }
                              if (e.key === 'Enter') {
                                e.preventDefault?.()
                              }
                            }}
                            onChange={(value) => {
                              // 只更新 ref，不觸發狀態更新，避免重新渲染
                              priceInputRefs.current[service.key] = value
                            }}
                            onBlur={() => {
                              // 失焦時才更新狀態
                              const value = priceInputRefs.current[service.key]
                              if (value !== undefined) {
                                const updatedServices = selectedServices.map(
                                  (s) =>
                                    s.key === service.key
                                      ? { ...s, price: value }
                                      : s
                                )
                                setSelectedServices(updatedServices)
                                // 清除 ref，下次使用實際值
                                delete priceInputRefs.current[service.key]
                              }
                            }}
                          />
                        </ServiceCardRow>

                        {/* 會員折扣信息 */}
                        {customerMembershipTier &&
                          membershipDiscountRate < 100 &&
                          (() => {
                            // 必須用 originalPrice（原始定價）為折扣基準，
                            // 不可 fallback 到 service.price，否則每次套用後折扣基準下降，
                            // 導致可以無限重複套用（連鎖折扣 bug）
                            const originalPrice = service.originalPrice || 0
                            if (!originalPrice || originalPrice === 0) {
                              return null
                            }

                            const discountedPrice = Math.round(
                              (originalPrice * membershipDiscountRate) / 100
                            )
                            // 允許 ±1 誤差（四捨五入）
                            const currentPrice = service.price || originalPrice
                            const isApplied =
                              Math.abs(currentPrice - discountedPrice) <= 1

                            return (
                              <div
                                style={{
                                  marginTop: 12,
                                  padding: '16px',
                                  background: '#f5f5f7',
                                  borderRadius: 12,
                                  border: 'none'
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 13,
                                    color: '#6e6e73',
                                    lineHeight: '1.4',
                                    marginBottom: 12
                                  }}
                                >
                                  此會員為{' '}
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      color: '#1d1d1f'
                                    }}
                                  >
                                    {customerMembershipTier}
                                  </span>
                                  ，折扣後價格為{' '}
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      color: '#1d1d1f'
                                    }}
                                  >
                                    NT$ {discountedPrice.toLocaleString()}
                                  </span>
                                </div>
                                <Button
                                  type={isApplied ? 'default' : 'primary'}
                                  size='small'
                                  onClick={() => {
                                    const updatedServices =
                                      selectedServices.map((s) =>
                                        s.key === service.key
                                          ? { ...s, price: discountedPrice }
                                          : s
                                      )
                                    setSelectedServices(updatedServices)
                                    // 清除暫存的輸入值
                                    if (
                                      priceInputRefs.current[service.key] !==
                                      undefined
                                    ) {
                                      delete priceInputRefs.current[service.key]
                                    }
                                  }}
                                  style={{
                                    fontSize: 13,
                                    height: 32,
                                    borderRadius: 8,
                                    fontWeight: 500,
                                    border: isApplied
                                      ? '1px solid #d2d2d7'
                                      : 'none',
                                    background: isApplied
                                      ? '#ffffff'
                                      : 'rgba(0, 122, 255, 1)',
                                    color: isApplied ? '#1d1d1f' : '#ffffff',
                                    boxShadow: isApplied
                                      ? 'none'
                                      : '0 1px 3px rgba(0, 0, 0, 0.1)',
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isApplied) {
                                      e.currentTarget.style.background =
                                        'rgba(0, 122, 255, 0.9)'
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isApplied) {
                                      e.currentTarget.style.background =
                                        'rgba(0, 122, 255, 1)'
                                    }
                                  }}
                                >
                                  {isApplied ? '已套用' : '套用折扣'}
                                </Button>
                              </div>
                            )
                          })()}
                      </ServiceCard>
                    )
                  })}
                </div>

                {/* 加選服務按鈕 - 位於服務卡片列表下方 */}
                <Button
                  type='primary'
                  icon={<PlusOutlined />}
                  onClick={handleAddNewCard}
                  style={{
                    width: '100%',
                    height: '44px',
                    fontSize: '14px',
                    fontWeight: '500',
                    borderRadius: '10px',
                    padding: '0 18px',
                    background: theme.primary,
                    borderColor: theme.primary,
                    marginTop: '14px',
                    marginBottom: '14px',
                    boxShadow: '0 4px 10px rgba(213, 114, 138, 0.18)'
                  }}
                >
                  加選服務
                </Button>

                {/* 新增服務卡片 */}
                {newServiceCards.map((cardKey) => (
                  <ServiceCardForm key={cardKey} cardKey={cardKey} />
                ))}
              </div>

              {/* 商品 */}
              <div>
                {/* 已選擇的商品卡片 */}
                <div style={{ marginTop: 16 }}>
                  {(() => {
                    /**
                     * 卡片內容抽成具名函式，讓「一般商品」與
                     * 「展開編輯的耗材」共用同一份 —— 收合只是預設視圖，
                     * 沒有拿掉任何既有能力。
                     */
                    const renderProductCard = (product) => (
                    <ServiceCard key={product.key}>
                      <ServiceCardHeader>
                        <ServiceCardDelete
                          type='text'
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => {
                            setSelectedProducts(
                              selectedProducts.filter(
                                (p) => p.key !== product.key
                              )
                            )
                          }}
                        />
                      </ServiceCardHeader>
                      <ServiceCardRow>
                        <span className='label'>商品項目</span>
                        <FieldSelect
                          value={product.productId}
                          placeholder='選擇商品'
                          showSearch
                          optionFilterProp='label'
                          filterOption={(input, opt) =>
                            (opt?.label ?? '')
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                          options={(() => {
                            const groups = inventoryCategories
                              .map((cat) => ({
                                label: cat.name,
                                options: inventoryProducts
                                  .filter(
                                    (p) =>
                                      (p.categoryId === cat._id ||
                                        p.category?._id === cat._id ||
                                        p.category?.id === cat._id) &&
                                      p.status === 'active'
                                  )
                                  .map((p) => ({
                                    value: p._id,
                                    label: `${
                                      p.name || p.title || '商品'
                                    } - NT$ ${Number(
                                      p.suggestedPrice || p.price || 0
                                    ).toLocaleString()}`
                                  }))
                              }))
                              .filter((g) => g.options.length > 0)
                            const uncategorized = inventoryProducts.filter(
                              (p) =>
                                p.status === 'active' &&
                                !inventoryCategories.some(
                                  (c) =>
                                    c._id === p.categoryId ||
                                    c._id === p.category?._id
                                )
                            )
                            if (uncategorized.length > 0) {
                              groups.push({
                                label: '其他',
                                options: uncategorized.map((p) => ({
                                  value: p._id,
                                  label: `${
                                    p.name || p.title || '商品'
                                  } - NT$ ${Number(
                                    p.suggestedPrice || p.price || 0
                                  ).toLocaleString()}`
                                }))
                              })
                            }
                            return groups
                          })()}
                          onChange={(newProductId) => {
                            const newProduct = inventoryProducts.find(
                              (p) =>
                                p._id === newProductId || p.id === newProductId
                            )
                            if (!newProduct) return
                            const category = inventoryCategories.find(
                              (c) =>
                                c._id === newProduct.categoryId ||
                                c._id === newProduct.category?._id
                            )
                            const qty = product.quantity ?? 1
                            const unitPrice =
                              Number(
                                newProduct.suggestedPrice || newProduct.price
                              ) || 0
                            const totalPrice = qty * unitPrice
                            const updatedProducts = selectedProducts.map((p) =>
                              p.key === product.key
                                ? {
                                    ...p,
                                    productId: newProduct._id,
                                    name:
                                      newProduct.name ||
                                      newProduct.title ||
                                      '商品',
                                    categoryId:
                                      newProduct.categoryId ||
                                      newProduct.category?._id,
                                    categoryName: category?.name ?? '-',
                                    unitPrice,
                                    totalPrice,
                                    manufacturer: newProduct.manufacturer,
                                    capacity: newProduct.capacity,
                                    stock: newProduct.stock,
                                    lowStockThreshold:
                                      newProduct.lowStockThreshold || 10,
                                    unit: newProduct.unit || '件'
                                  }
                                : p
                            )
                            setSelectedProducts(updatedProducts)
                            setProductQuantityInputs((prev) => {
                              const next = { ...prev }
                              delete next[product.key]
                              return next
                            })
                          }}
                          style={{ width: '100%' }}
                        />
                      </ServiceCardRow>
                      {product.manufacturer && (
                        <ServiceCardRow>
                          <span className='label'>廠商</span>
                          <span className='value'>{product.manufacturer}</span>
                        </ServiceCardRow>
                      )}
                      {product.capacity && (
                        <ServiceCardRow>
                          <span className='label'>容量</span>
                          <span className='value'>{product.capacity}</span>
                        </ServiceCardRow>
                      )}
                      <ServiceCardRow>
                        <span className='label'>
                          數量（{product.unit || '件'}）
                        </span>
                        <InputNumber
                          // 服務搭配的耗材列允許小數量（0.03、0.5 等）；
                          // 一般銷售商品仍維持整數，避免誤輸入「1.5 瓶」之類的數字。
                          min={product.isConsumable ? 0.01 : 1}
                          step={product.isConsumable ? 0.01 : 1}
                          precision={product.isConsumable ? 2 : 0}
                          value={
                            productQuantityInputs[product.key] !== undefined
                              ? productQuantityInputs[product.key]
                              : (product.quantity ?? 1)
                          }
                          onChange={(value) => {
                            setProductQuantityInputs((prev) => ({
                              ...prev,
                              [product.key]: value
                            }))
                          }}
                          onBlur={() => {
                            const raw =
                              productQuantityInputs[product.key] !== undefined
                                ? productQuantityInputs[product.key]
                                : (product.quantity ?? 1)
                            // 耗材：保留小數並 round 到 2 位；一般商品：維持整數。
                            const computeQty = () => {
                              if (
                                raw === null ||
                                raw === undefined ||
                                raw === '' ||
                                Number.isNaN(Number(raw))
                              ) {
                                return 1
                              }
                              const n = Number(raw)
                              if (product.isConsumable) {
                                return (
                                  Math.round(Math.max(0.01, n) * 100) / 100
                                )
                              }
                              return Math.max(1, Math.floor(n))
                            }
                            const qty = computeQty()
                            const unitPrice = Number(product.unitPrice) || 0
                            // round 2 位避免 0.03 * 290 之類的浮點誤差
                            const totalPrice =
                              Math.round(qty * unitPrice * 100) / 100
                            setSelectedProducts((prev) =>
                              prev.map((p) =>
                                p.key === product.key
                                  ? { ...p, quantity: qty, totalPrice }
                                  : p
                              )
                            )
                            setProductQuantityInputs((prev) => {
                              const next = { ...prev }
                              delete next[product.key]
                              return next
                            })
                          }}
                          style={{
                            width: '100%',
                            height: 44,
                            borderRadius: 10,
                            border: `1px solid ${theme.border}`
                          }}
                          className='quantity-input'
                        />
                      </ServiceCardRow>
                      <ServiceCardRow>
                        <span className='label'>單價</span>
                        <InputNumber
                          min={0}
                          value={product.unitPrice ?? 0}
                          formatter={(value) =>
                            `NT$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                          }
                          parser={(value) =>
                            Number(value?.replace(/[NT$\s,]/g, '') || 0)
                          }
                          onChange={(value) => {
                            const unitPrice = Number(value) || 0
                            const qty = product.quantity ?? 1
                            const totalPrice = qty * unitPrice
                            const updatedProducts = selectedProducts.map((p) =>
                              p.key === product.key
                                ? {
                                    ...p,
                                    unitPrice,
                                    totalPrice
                                  }
                                : p
                            )
                            setSelectedProducts(updatedProducts)
                          }}
                          style={{
                            width: '100%',
                            height: 44,
                            borderRadius: 10,
                            border: `1px solid ${theme.border}`
                          }}
                        />
                      </ServiceCardRow>
                      <ServiceCardRow>
                        <span className='label'>小計</span>
                        <span
                          className='value'
                          style={{ color: '#d5728a', fontWeight: 700 }}
                        >
                          NT$ {(product.totalPrice || 0).toLocaleString()}
                        </span>
                      </ServiceCardRow>
                      {/* 會員折扣提示：以「商品原價」為基準計算（按多次不會疊扣） */}
                      {customerMembershipTier &&
                        membershipDiscountRate < 100 &&
                        (() => {
                          // 必須能在庫存資料找到對應商品才顯示折扣 note
                          // 避免商品還沒選或不在庫存（例如手動建立）就顯示
                          if (!product.productId) return null
                          const inventoryItem = inventoryProducts.find(
                            (p) =>
                              p._id === product.productId ||
                              p.id === product.productId
                          )
                          if (!inventoryItem) return null
                          const basePrice = Number(
                            inventoryItem.suggestedPrice ??
                              inventoryItem.price ??
                              0
                          )
                          if (basePrice <= 0) return null
                          const currentUnit = Number(product.unitPrice ?? 0)
                          const qty = Number(product.quantity ?? 1)

                          // 正確計算順序：先算總額再四捨五入，避免單價捨入誤差乘以數量後放大
                          // 錯誤（舊）：Math.round(unitPrice × rate) × qty → 捨入誤差 × qty
                          // 正確（新）：Math.round(unitPrice × qty × rate) = 總折扣後金額，再反推單價
                          const discountedTotal = Math.round(
                            (basePrice * qty * membershipDiscountRate) / 100
                          )
                          // 單價以「總額 ÷ 數量」反推，供套用至 unitPrice 欄位
                          const discountedUnit = qty > 0 ? Math.round(discountedTotal / qty) : 0
                          const alreadyApplied =
                            Math.abs(currentUnit - discountedUnit) <= 0 &&
                            currentUnit > 0

                          return (
                            <div
                              style={{
                                margin: '6px 0 4px',
                                padding: '8px 10px',
                                background: alreadyApplied ? '#f0f9ff' : '#fafafa',
                                border: `1px solid ${
                                  alreadyApplied ? '#bae6fd' : '#e5e7eb'
                                }`,
                                borderRadius: 8,
                                fontSize: 12,
                                lineHeight: 1.6,
                                color: '#0369a1',
                                display: 'flex',
                                flexWrap: 'wrap',
                                alignItems: 'center',
                                gap: 8,
                                justifyContent: 'space-between'
                              }}
                            >
                              <span>
                                此會員為{' '}
                                <b style={{ color: '#d5728a' }}>
                                  {customerMembershipTier}
                                </b>
                                （{membershipDiscountRate}折），折扣後價格為{' '}
                                <b style={{ color: '#0369a1' }}>
                                  NT$ {discountedTotal.toLocaleString()}
                                </b>
                                <span
                                  style={{ color: '#8c8c8c', marginLeft: 4 }}
                                >
                                  （原價 NT$ {basePrice.toLocaleString()}）
                                </span>
                              </span>
                              {alreadyApplied ? (
                                <Button size='small' disabled>
                                  已套用
                                </Button>
                              ) : (
                                <Button
                                  size='small'
                                  type='primary'
                                  onClick={() => {
                                    const updatedProducts = selectedProducts.map(
                                      (p) =>
                                        p.key === product.key
                                          ? {
                                              ...p,
                                              unitPrice: discountedUnit,
                                              totalPrice: discountedTotal
                                            }
                                          : p
                                    )
                                    setSelectedProducts(updatedProducts)
                                  }}
                                >
                                  套用折扣
                                </Button>
                              )}
                            </div>
                          )
                        })()}
                      {product.stock !== undefined && (
                        <ServiceCardRow>
                          <span className='label'>庫存</span>
                          <span
                            className='value'
                            style={{
                              color:
                                product.stock <=
                                (product.lowStockThreshold || 10)
                                  ? '#ff4d4f'
                                  : '#52c41a'
                            }}
                          >
                            {product.stock} {product.unit || '件'}
                          </span>
                        </ServiceCardRow>
                      )}
                      <ServiceCardRow>
                        <span className='label'>銷售人員 *</span>
                        <FieldSelect
                          value={
                            product.productSalesPersonnelId ||
                            (getDefaultEmployee()?._id ?? null)
                          }
                          placeholder='請選擇銷售人員'
                          onChange={(value) => {
                            const personnel = employees.find(
                              (e) => e._id === value
                            )
                            const updatedProducts = selectedProducts.map((p) =>
                              p.key === product.key
                                ? {
                                    ...p,
                                    productSalesPersonnelId: value,
                                    productSalesPersonnelName:
                                      personnel?.name || null
                                  }
                                : p
                            )
                            setSelectedProducts(updatedProducts)
                          }}
                        >
                          {employees.map((e) => (
                            <Option key={e._id} value={e._id}>
                              {e.name}
                            </Option>
                          ))}
                        </FieldSelect>
                      </ServiceCardRow>
                      {product.isConsumable && (
                        <ServiceCardRow>
                          <span className='label'>耗材來源</span>
                          <span className='value' style={{ color: '#7c3aed' }}>
                            自動帶入：{product.sourceServiceName || '服務'}
                            {product.consumableNote
                              ? ` · ${product.consumableNote}`
                              : ''}
                          </span>
                        </ServiceCardRow>
                      )}
                      <ServiceCardRow>
                        <span className='label'>業績計算</span>
                        <Checkbox
                          checked={product.includeInRevenue !== false}
                          onChange={(e) => {
                            const checked = e.target.checked
                            const updatedProducts = selectedProducts.map((p) =>
                              p.key === product.key
                                ? {
                                    ...p,
                                    includeInRevenue: checked,
                                    // 新增：同步告訴後端是否計入商品業績
                                    countAsSales: checked,
                                    // 耗材且勾起來算業績時，自動把單價帶回為建議售價
                                    // （否則維持 0，與「服務內附、不另外結帳」一致）
                                    ...(p.isConsumable
                                      ? checked
                                        ? p.unitPrice === 0
                                          ? (() => {
                                              const sp = inventoryProducts.find(
                                                (ip) =>
                                                  ip._id === p.productId ||
                                                  ip.id === p.productId
                                              )
                                              const newUnit = Number(
                                                sp?.suggestedPrice ??
                                                  sp?.price ??
                                                  0
                                              )
                                              return {
                                                unitPrice: newUnit,
                                                totalPrice:
                                                  newUnit * (p.quantity || 1)
                                              }
                                            })()
                                          : {}
                                        : { unitPrice: 0, totalPrice: 0 }
                                      : {})
                                  }
                                : p
                            )
                            setSelectedProducts(updatedProducts)
                          }}
                        >
                          {product.isConsumable
                            ? '加入業績計算（預設不算，勾選後依建議售價計入）'
                            : '加入業績計算'}
                        </Checkbox>
                      </ServiceCardRow>
                    </ServiceCard>
                    )

                    const consumables = selectedProducts.filter(
                      (p) => p.isConsumable
                    )
                    const normalProducts = selectedProducts.filter(
                      (p) => !p.isConsumable
                    )

                    return (
                      <>
                        {normalProducts.map((product) =>
                          renderProductCard(product)
                        )}

                        {consumables.length > 0 && (
                          <ConsumablePanel>
                            <div className='head'>
                              <span className='title'>
                                服務耗材<em>{consumables.length} 項</em>
                              </span>
                              <span className='note'>結帳後自動扣庫存</span>
                            </div>

                            {consumables.map((product) =>
                              expandedConsumables.includes(product.key)
                                ? (
                                  <div key={product.key}>
                                    {renderProductCard(product)}
                                    <button
                                      type='button'
                                      className='collapse'
                                      onClick={() =>
                                        setExpandedConsumables((k) =>
                                          k.filter((x) => x !== product.key)
                                        )}
                                    >
                                      收合
                                    </button>
                                  </div>
                                  )
                                : (
                                  <div className='row' key={product.key}>
                                    <span className='name'>{product.name}</span>
                                    <span className='qty'>
                                      ×{product.quantity}
                                      {product.unit ? ` ${product.unit}` : ''}
                                    </span>
                                    {/* 耗材預設不顯示金額 —— 多半只扣庫存不計價，
                                        列出金額會讓店員誤以為要跟客人收 */}
                                    {product.countAsSales && (
                                      <span className='billed'>計入業績</span>
                                    )}
                                    <button
                                      type='button'
                                      className='edit'
                                      onClick={() =>
                                        setExpandedConsumables((k) => [
                                          ...k,
                                          product.key
                                        ])}
                                    >
                                      編輯
                                    </button>
                                  </div>
                                  )
                            )}
                          </ConsumablePanel>
                        )}
                      </>
                    )
                  })()}
                </div>

                {/* 新增商品卡片 */}
                {newProductCards.map((cardKey) => {
                  const ProductCardForm = () => {
                    const [form] = Form.useForm()
                    const selectedCategoryId =
                      selectedProductCategoryIds[cardKey] || null
                    const formCategoryId = Form.useWatch('categoryId', form)
                    const selectedProductId = Form.useWatch('productId', form)
                    const quantity = Form.useWatch('quantity', form) || 1
                    const unitPriceWatched = Form.useWatch('unitPrice', form)

                    productCardFormsRef.current[cardKey] = form

                    useEffect(() => {
                      return () => {
                        delete productCardFormsRef.current[cardKey]
                      }
                    }, [cardKey])

                    useEffect(() => {
                      if (
                        selectedCategoryId &&
                        formCategoryId !== selectedCategoryId
                      ) {
                        form.setFieldsValue({ categoryId: selectedCategoryId })
                      }
                    }, [selectedCategoryId, formCategoryId, form])

                    // 自動帶入登入者本人作為銷售人員（非員工帳號則第一位，必選）
                    useEffect(() => {
                      if (employees.length > 0) {
                        const firstPerson = getDefaultEmployee()
                        if (firstPerson && firstPerson._id) {
                          const currentSalesPersonnel = form.getFieldValue(
                            'productSalesPersonnelId'
                          )
                          if (!currentSalesPersonnel) {
                            form.setFieldsValue({
                              productSalesPersonnelId: firstPerson._id
                            })
                          }
                        }
                      }
                      // eslint-disable-next-line react-hooks/exhaustive-deps
                    }, [employees, form])

                    // 根據選中的分類過濾商品選項（只顯示啟用且有庫存的商品）
                    const filteredProducts = selectedCategoryId
                      ? inventoryProducts.filter(
                          (p) =>
                            (p.categoryId === selectedCategoryId ||
                              p.category?._id === selectedCategoryId ||
                              p.category?.id === selectedCategoryId) &&
                            p.status === 'active' &&
                            (p.stock || 0) > 0
                        )
                      : inventoryProducts.filter(
                          (p) => p.status === 'active' && (p.stock || 0) > 0
                        )

                    // 當選擇商品時，自動填充單價和庫存信息
                    useEffect(() => {
                      if (selectedProductId && inventoryProducts.length > 0) {
                        const product = inventoryProducts.find(
                          (p) =>
                            p._id === selectedProductId ||
                            p.id === selectedProductId
                        )
                        if (product) {
                          const unitPrice =
                            product.suggestedPrice || product.price || 0
                          form.setFieldsValue({
                            unitPrice: unitPrice,
                            stock: product.stock,
                            lowStockThreshold: product.lowStockThreshold || 10,
                            unit: product.unit || '件'
                          })
                        }
                      }
                      // eslint-disable-next-line react-hooks/exhaustive-deps
                    }, [selectedProductId, inventoryProducts])

                    // 當數量或單價改變時，自動計算總價
                    useEffect(() => {
                      if (selectedProductId && quantity) {
                        const unitPrice = form.getFieldValue('unitPrice') || 0
                        const totalPrice = unitPrice * quantity
                        form.setFieldsValue({ totalPrice: totalPrice })
                      }
                      // eslint-disable-next-line react-hooks/exhaustive-deps
                    }, [quantity, selectedProductId])

                    const handleSaveProduct = () => {
                      form
                        .validateFields()
                        .then((values) => {
                          const product = inventoryProducts.find(
                            (p) =>
                              p._id === values.productId ||
                              p.id === values.productId
                          )
                          if (!product) {
                            message.error('商品不存在')
                            return
                          }

                          // 檢查庫存
                          if (values.quantity > (product.stock || 0)) {
                            message.error(
                              `庫存不足，目前庫存：${product.stock || 0} ${
                                product.unit || '件'
                              }`
                            )
                            return
                          }

                          const category = inventoryCategories.find(
                            (c) =>
                              c._id === product.categoryId ||
                              c._id === product.category?._id ||
                              c._id === product.category?.id
                          )

                          // 獲取銷售人員信息（必選，未選時帶入登入者本人；非員工帳號則第一位）
                          let salesPersonnelId =
                            values.productSalesPersonnelId || null
                          if (!salesPersonnelId && employees.length > 0) {
                            salesPersonnelId = getDefaultEmployee()._id
                          }
                          if (!salesPersonnelId) {
                            message.error('請選擇銷售人員')
                            return
                          }
                          const salesPersonnel = employees.find(
                            (e) => e._id === salesPersonnelId
                          )

                          const item = {
                            key: `product-${values.productId}-${Date.now()}`,
                            productId: values.productId,
                            categoryId:
                              product.categoryId ||
                              product.category?._id ||
                              product.category?.id,
                            categoryName: category?.name || '-',
                            name: product.name || product.title,
                            manufacturer: product.manufacturer,
                            capacity: product.capacity,
                            quantity: values.quantity,
                            unitPrice: values.unitPrice,
                            totalPrice:
                              values.totalPrice ||
                              values.unitPrice * values.quantity,
                            stock: product.stock,
                            lowStockThreshold: product.lowStockThreshold || 10,
                            unit: product.unit || '件',
                            productSalesPersonnelId: salesPersonnelId,
                            productSalesPersonnelName:
                              salesPersonnel?.name || null,
                            includeInRevenue: values.includeInRevenue ?? true // 預設加入業績計算
                          }
                          setSelectedProducts([...selectedProducts, item])
                          setNewProductCards(
                            newProductCards.filter((k) => k !== cardKey)
                          )
                          delete productCardFormsRef.current[cardKey]
                          setSelectedProductCategoryIds((prev) => {
                            const newIds = { ...prev }
                            delete newIds[cardKey]
                            return newIds
                          })
                        })
                        .catch(() => {
                          // validation failed
                        })
                    }

                    const handleCancelProduct = () => {
                      setNewProductCards(
                        newProductCards.filter((k) => k !== cardKey)
                      )
                      delete productCardFormsRef.current[cardKey]
                      setSelectedProductCategoryIds((prev) => {
                        const newIds = { ...prev }
                        delete newIds[cardKey]
                        return newIds
                      })
                    }

                    return (
                      <NewServiceCard>
                        <Form form={form} layout='vertical'>
                          <Form.Item
                            name='categoryId'
                            label='商品分類'
                            rules={[
                              { required: true, message: '請選擇商品分類' }
                            ]}
                          >
                            <ResponsiveSelect
                              placeholder='選擇分類'
                              getPopupContainer={() => document.body}
                              dropdownStyle={{ zIndex: 1050 }}
                              onChange={(val) => {
                                setSelectedProductCategoryIds((prev) => ({
                                  ...prev,
                                  [cardKey]: val
                                }))
                                form.setFieldsValue({
                                  categoryId: val,
                                  productId: undefined,
                                  unitPrice: undefined,
                                  totalPrice: undefined
                                })
                                form.resetFields([
                                  'productId',
                                  'unitPrice',
                                  'quantity',
                                  'totalPrice'
                                ])
                              }}
                            >
                              {inventoryCategories.length > 0 ? (
                                inventoryCategories.map((c) => (
                                  <Option key={c._id} value={c._id}>
                                    {c.name}
                                  </Option>
                                ))
                              ) : (
                                <Option disabled value=''>
                                  無可用分類
                                </Option>
                              )}
                            </ResponsiveSelect>
                          </Form.Item>

                          <Form.Item
                            name='productId'
                            label='商品'
                            rules={[{ required: true, message: '請選擇商品' }]}
                          >
                            <ResponsiveSelect
                              key={`product-${selectedCategoryId || 'none'}`}
                              placeholder='選擇商品'
                              getPopupContainer={() => document.body}
                              dropdownStyle={{ zIndex: 1050 }}
                              disabled={!selectedCategoryId}
                              showSearch
                              optionFilterProp='children'
                              allowClear
                              onChange={(val) => {
                                form.setFieldsValue({ productId: val })
                              }}
                            >
                              {filteredProducts.length > 0 ? (
                                filteredProducts.map((p) => (
                                  <Option
                                    key={p._id || p.id}
                                    value={p._id || p.id}
                                  >
                                    {p.name || p.title}{' '}
                                    {p.stock !== undefined
                                      ? `(庫存: ${p.stock}${p.unit || '件'})`
                                      : ''}
                                  </Option>
                                ))
                              ) : (
                                <Option disabled value=''>
                                  {selectedCategoryId
                                    ? '該分類暫無可用商品'
                                    : '請先選擇商品分類'}
                                </Option>
                              )}
                            </ResponsiveSelect>
                          </Form.Item>

                          <FormRow>
                            <Form.Item
                              name='quantity'
                              label='數量'
                              rules={[
                                { required: true, message: '請輸入數量' },
                                {
                                  type: 'number',
                                  min: 1,
                                  message: '數量必須大於0'
                                }
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <InputNumber
                                style={{
                                  width: '100%',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}
                                min={1}
                                precision={0}
                                onChange={(val) => {
                                  const unitPrice =
                                    form.getFieldValue('unitPrice') || 0
                                  const quantity = val || 1
                                  const totalPrice = unitPrice * quantity
                                  form.setFieldsValue({
                                    totalPrice: totalPrice
                                  })
                                }}
                              />
                            </Form.Item>

                            <Form.Item
                              name='unitPrice'
                              label='單價'
                              rules={[
                                { required: true, message: '請輸入單價' },
                                {
                                  type: 'number',
                                  min: 0,
                                  message: '單價必須大於等於0'
                                }
                              ]}
                              style={{ marginBottom: 0 }}
                            >
                              <InputNumber
                                className='price-input-center'
                                style={{ width: '100%' }}
                                min={0}
                                prefix='NT$'
                                onChange={(val) => {
                                  const quantity =
                                    form.getFieldValue('quantity') || 1
                                  const unitPrice = val || 0
                                  const totalPrice = unitPrice * quantity
                                  form.setFieldsValue({
                                    totalPrice: totalPrice
                                  })
                                }}
                              />
                            </Form.Item>
                          </FormRow>

                          <Form.Item
                            name='totalPrice'
                            label='小計'
                            style={{ marginBottom: 0 }}
                          >
                            <InputNumber
                              className='price-input-center'
                              style={{ width: '100%' }}
                              prefix='NT$'
                              disabled
                              readOnly
                            />
                          </Form.Item>

                          {/*
                            會員折扣提示（新增商品 modal 內版本）
                            折扣永遠以「商品原價」為基準計算，避免重複按按鈕造成連續折扣。
                            例：原價 999、80折 → 不論按幾次都是 799。
                          */}
                          {customerMembershipTier &&
                            membershipDiscountRate < 100 &&
                            (() => {
                              // 必須先選好商品才顯示折扣 note（避免空白狀態誤判）
                              if (!selectedProductId) return null
                              const selectedProduct = inventoryProducts.find(
                                (p) =>
                                  p._id === selectedProductId ||
                                  p.id === selectedProductId
                              )
                              if (!selectedProduct) return null
                              const basePrice = Number(
                                selectedProduct.suggestedPrice ??
                                  selectedProduct.price ??
                                  0
                              )
                              if (basePrice <= 0) return null
                              const currentUnit = Number(unitPriceWatched ?? 0)
                              const qty = Number(quantity ?? 1)

                              // 先算總額再四捨五入，避免單價捨入誤差乘以數量後放大
                              const discountedTotal = Math.round(
                                (basePrice * qty * membershipDiscountRate) / 100
                              )
                              const discountedUnit = qty > 0 ? Math.round(discountedTotal / qty) : 0
                              // 已套用：當前單價 = 折扣後單價（允許小幅誤差 ±1）
                              const alreadyApplied =
                                Math.abs(currentUnit - discountedUnit) <= 0 &&
                                currentUnit > 0

                              return (
                                <div
                                  style={{
                                    margin: '12px 0 4px',
                                    padding: '10px 12px',
                                    background: alreadyApplied
                                      ? '#f0f9ff'
                                      : '#fafafa',
                                    border: `1px solid ${
                                      alreadyApplied ? '#bae6fd' : '#e5e7eb'
                                    }`,
                                    borderRadius: 8,
                                    fontSize: 13,
                                    lineHeight: 1.6,
                                    color: '#0369a1',
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: 8,
                                    justifyContent: 'space-between'
                                  }}
                                >
                                  <span>
                                    此會員為{' '}
                                    <b style={{ color: '#d5728a' }}>
                                      {customerMembershipTier}
                                    </b>
                                    （{membershipDiscountRate}折），折扣後價格為{' '}
                                    <b style={{ color: '#0369a1' }}>
                                      NT$ {discountedTotal.toLocaleString()}
                                    </b>
                                    <span
                                      style={{
                                        color: '#8c8c8c',
                                        marginLeft: 4,
                                        fontSize: 12
                                      }}
                                    >
                                      （原價 NT$ {basePrice.toLocaleString()}）
                                    </span>
                                  </span>
                                  {alreadyApplied ? (
                                    <Button size='small' disabled>
                                      已套用
                                    </Button>
                                  ) : (
                                    <Button
                                      size='small'
                                      type='primary'
                                      onClick={() => {
                                        form.setFieldsValue({
                                          unitPrice: discountedUnit,
                                          totalPrice: discountedTotal
                                        })
                                      }}
                                    >
                                      套用折扣
                                    </Button>
                                  )}
                                </div>
                              )
                            })()}

                          <Form.Item
                            name='productSalesPersonnelId'
                            label='銷售人員'
                            rules={[
                              {
                                required: true,
                                message: '請選擇銷售人員'
                              }
                            ]}
                            style={{ marginBottom: 0 }}
                          >
                            <ResponsiveSelect
                              placeholder='請選擇銷售人員'
                              getPopupContainer={() => document.body}
                              dropdownStyle={{ zIndex: 1050 }}
                              showSearch
                              optionFilterProp='children'
                            >
                              {employees.map((e) => (
                                <Option key={e._id} value={e._id}>
                                  {e.name}
                                </Option>
                              ))}
                            </ResponsiveSelect>
                          </Form.Item>

                          <Form.Item
                            name='includeInRevenue'
                            label='加入業績計算'
                            valuePropName='checked'
                            initialValue={true}
                            style={{ marginTop: 12, marginBottom: 0 }}
                          >
                            <Checkbox>加入業績計算</Checkbox>
                          </Form.Item>

                          <div
                            style={{
                              width: '100%',
                              display: 'flex',
                              gap: '12px',
                              marginTop: '16px'
                            }}
                          >
                            <Button
                              style={{
                                flex: 1,
                                height: '48px',
                                fontSize: '15px',
                                fontWeight: '500',
                                borderRadius: '8px',
                                borderColor: '#D4D0C9',
                                color: '#2C2C2C'
                              }}
                              onClick={handleCancelProduct}
                            >
                              取消
                            </Button>
                            <Button
                              style={{
                                flex: 1,
                                height: '48px',
                                fontSize: '15px',
                                fontWeight: '500',
                                borderRadius: '8px',
                                background: theme.primary,
                                borderColor: theme.primary
                              }}
                              type='primary'
                              onClick={handleSaveProduct}
                            >
                              加入
                            </Button>
                          </div>
                        </Form>
                      </NewServiceCard>
                    )
                  }
                  return <ProductCardForm key={cardKey} />
                })}

                <Button
                  type='primary'
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const newKey = `product-${Date.now()}`
                    setNewProductCards([...newProductCards, newKey])
                  }}
                  style={{
                    width: '100%',
                    height: '44px',
                    fontSize: '14px',
                    fontWeight: '500',
                    borderRadius: '10px',
                    padding: '0 18px',
                    background: '#6A859E',
                    borderColor: '#6A859E',
                    marginTop: '14px',
                    marginBottom: '14px',
                    boxShadow: '0 4px 10px rgba(213, 114, 138, 0.18)'
                  }}
                >
                  加選商品
                </Button>
                <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>
                  (只會顯示啟用且有庫存的商品項目)
                </div>
              </div>

              {/* === 堂票購買 === */}
              <div style={{ marginTop: 16 }}>
                <div
                  style={{
                    fontWeight: 500,
                    marginBottom: 8,
                    color: '#444'
                  }}
                >
                  堂票購買
                </div>
                {selectedClassTickets.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    {selectedClassTickets.map((t, idx) => (
                      <TicketRow key={t.key}>
                        <div className='ct-tpl'>
                          <MobileFieldLabel>堂票範本</MobileFieldLabel>
                          <ResponsiveSelect
                            showSearch
                            optionFilterProp='label'
                            style={{ width: '100%' }}
                            placeholder='選擇堂票範本'
                            value={t.templateId || undefined}
                            options={classTicketTemplates.map((tpl) => ({
                              value: tpl._id,
                              label: `${tpl.name}（${tpl.totalCount} 張 / ${describeValidity(tpl, {
                                short: true
                              })}）`,
                              tpl
                            }))}
                            onChange={(value, opt) => {
                              const tpl = opt?.tpl
                              setSelectedClassTickets((arr) =>
                                arr.map((row, i) =>
                                  i === idx
                                    ? {
                                        ...row,
                                        templateId: value,
                                        templateName: tpl?.name || '',
                                        price:
                                          tpl?.price !== undefined
                                            ? tpl.price
                                            : row.price,
                                        totalCount: tpl?.totalCount,
                                        validityMode: tpl?.validityMode,
                                        validityDays: tpl?.validityDays,
                                        validUntil: tpl?.validUntil,
                                        // 後端已換算成店家時區的那一天，預覽／同意書優先用它，
                                        // 避免裝置時區≠店家時區時差一天
                                        validUntilDate: tpl?.validUntilDate
                                      }
                                    : row
                                )
                              )
                            }}
                          />
                        </div>
                        <div className='ct-price'>
                          <MobileFieldLabel>單價</MobileFieldLabel>
                          <InputNumber
                            min={0}
                            step={100}
                            style={{ width: '100%' }}
                            placeholder='單價'
                            value={t.price}
                            onChange={(v) =>
                              setSelectedClassTickets((arr) =>
                                arr.map((row, i) =>
                                  i === idx ? { ...row, price: v ?? 0 } : row
                                )
                              )
                            }
                          />
                        </div>
                        <div className='ct-qty'>
                          <MobileFieldLabel>數量</MobileFieldLabel>
                          <InputNumber
                            min={1}
                            max={99}
                            style={{ width: '100%' }}
                            value={t.quantity}
                            onChange={(v) =>
                              setSelectedClassTickets((arr) =>
                                arr.map((row, i) =>
                                  i === idx
                                    ? { ...row, quantity: Math.max(1, v || 1) }
                                    : row
                                )
                              )
                            }
                          />
                        </div>
                        <div className='ct-redeem'>
                          <MobileFieldLabel>本次核銷</MobileFieldLabel>
                          <InputNumber
                            min={0}
                            max={t.totalCount || 99}
                            style={{ width: '100%' }}
                            placeholder='0'
                            value={t.redeemCount ?? 0}
                            disabled={!t.templateId}
                            title='買了就在這張單先用掉幾堂。只扣堂數，不影響金額——這一堂的錢已經含在堂票售價裡了'
                            onChange={(v) =>
                              setSelectedClassTickets((arr) =>
                                arr.map((row, i) =>
                                  i === idx
                                    ? {
                                        ...row,
                                        redeemCount: Math.max(
                                          0,
                                          Math.min(
                                            row.totalCount || 99,
                                            v || 0
                                          )
                                        )
                                      }
                                    : row
                                )
                              )
                            }
                          />
                        </div>
                        <div className='ct-sales'>
                          <MobileFieldLabel>銷售人員</MobileFieldLabel>
                          <ResponsiveSelect
                            allowClear
                            showSearch
                            optionFilterProp='label'
                            style={{ width: '100%' }}
                            placeholder='銷售人員'
                            value={t.salesPersonnelId || undefined}
                            options={employees.map((emp) => ({
                              value: emp._id,
                              label: emp.name
                            }))}
                            onChange={(value, opt) =>
                              setSelectedClassTickets((arr) =>
                                arr.map((row, i) =>
                                  i === idx
                                    ? {
                                        ...row,
                                        salesPersonnelId: value || '',
                                        salesPersonnelName: opt?.label || ''
                                      }
                                    : row
                                )
                              )
                            }
                          />
                        </div>
                        <span
                          className='ct-subtotal'
                          style={{ color: '#666', fontSize: 13 }}
                        >
                          小計 ${' '}
                          {(
                            (Number(t.price) || 0) *
                            (Number(t.quantity) || 0)
                          ).toLocaleString()}
                        </span>
                        <Button
                          className='ct-remove'
                          type='text'
                          danger
                          onClick={() =>
                            setSelectedClassTickets((arr) =>
                              arr.filter((_, i) => i !== idx)
                            )
                          }
                        >
                          移除
                        </Button>
                      </TicketRow>
                    ))}
                  </div>
                )}
                <Button
                  type='primary'
                  icon={<PlusOutlined />}
                  onClick={() =>
                    setSelectedClassTickets((arr) => [
                      ...arr,
                      {
                        key: `ct-${Date.now()}`,
                        templateId: '',
                        templateName: '',
                        price: 0,
                        quantity: 1,
                        // 買了就在這張單先用掉幾堂；0 = 只買不用
                        redeemCount: 0,
                        // 銷售人員預設帶入登入者本人（非員工帳號則第一位）
                        salesPersonnelId: getDefaultEmployee()?._id || '',
                        salesPersonnelName: getDefaultEmployee()?.name || ''
                      }
                    ])
                  }
                  disabled={classTicketTemplates.length === 0}
                  style={{
                    width: '100%',
                    height: '44px',
                    fontSize: '14px',
                    fontWeight: '500',
                    borderRadius: '10px',
                    padding: '0 18px',
                    // 橘棕：與加選服務／加購商品的玫瑰色區分，
                    // 讓店員不用讀字就分得出哪一顆是堂票
                    background: '#c98a3d',
                    borderColor: '#c98a3d',
                    marginTop: '4px',
                    boxShadow: '0 4px 10px rgba(201, 138, 61, 0.22)'
                  }}
                >
                  加購堂票
                </Button>
                {selectedClassTickets.some((t) => (t.redeemCount || 0) > 0) && (
                  <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                    「本次核銷」會在結帳完成時直接扣掉堂數，不用再到堂票紀錄操作一次。只扣堂數、不影響金額 ——
                    這幾堂的錢已經含在上面的堂票售價裡了。
                  </div>
                )}
                {classTicketTemplates.length === 0 && (
                  <div
                    style={{ fontSize: 12, color: '#888', marginTop: 6 }}
                  >
                    尚未建立堂票範本，請先到「堂票模組 / 堂票設定」新增。
                  </div>
                )}
              </div>

              {/* ===== 使用堂票（核銷） =====
                  只列「這位客人有票、且票適用這個服務」的服務項目。
                  沒有可用的票時整塊不出現，不佔版面也不用解釋。 */}
              {(() => {
                const redeemableRows = (selectedServices || [])
                  .map((svc) => ({
                    svc,
                    tickets: usableClassTickets.filter((t) =>
                      isTicketApplicableToService(t, svc.serviceId)
                    )
                  }))
                  .filter((row) => row.tickets.length > 0)
                if (!redeemableRows.length) return null
                return (
                  <div style={{ marginTop: 20 }}>
                    <div
                      style={{
                        fontSize: 15,
                        fontWeight: 600,
                        color: '#1d1d1f',
                        marginBottom: 4
                      }}
                    >
                      使用堂票（核銷）
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: '#888',
                        marginBottom: 12
                      }}
                    >
                      這位客人有可用的堂票。選擇後該服務金額歸零，核銷會一併記在這張結帳單裡。
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10
                      }}
                    >
                      {redeemableRows.map(({ svc, tickets }) => {
                        const redemption = ticketRedemptions[svc.key]
                        return (
                          <div
                            key={svc.key}
                            style={{
                              padding: 14,
                              background: redemption ? '#fdf6ec' : '#fafafa',
                              borderRadius: 8,
                              border: `1px solid ${
                                redemption ? '#e8c99b' : '#e5e5e7'
                              }`
                            }}
                          >
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 500,
                                color: '#1d1d1f',
                                marginBottom: 8
                              }}
                            >
                              {svc.serviceName || svc.name || '服務'}
                              {redemption ? (
                                <span
                                  style={{
                                    marginLeft: 8,
                                    fontSize: 12,
                                    color: '#c98a3d'
                                  }}
                                >
                                  已核銷 · 原價 $
                                  {Number(
                                    redemption.prevPrice || 0
                                  ).toLocaleString()}
                                </span>
                              ) : null}
                            </div>
                            <Select
                              allowClear
                              style={{ width: '100%' }}
                              placeholder='不使用堂票'
                              value={redemption?.ticketId || undefined}
                              onChange={(value) =>
                                applyTicketRedemption(svc.key, value)
                              }
                              options={tickets.map((t) => ({
                                value: String(t._id),
                                label: `${t.templateName}（${t.ticketCode}） 剩 ${t.remainingCount}/${t.totalCount} 堂 · ${dayjs(
                                  t.expiresAt
                                ).format('YYYY-MM-DD')} 到期`
                              }))}
                            />
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })()}
            </Space>
          </>
        )}
      </FormCard>
    )
  }

  // 步驟四：選擇日期和時間 + 付費資訊
  const StepFourDateTime = () => {
    // 生成15分鐘間隔的時間選項（00:00 到 23:45）
    const timeOptions = useMemo(() => {
      const options = []
      for (let hour = 0; hour < 24; hour++) {
        for (let minute = 0; minute < 60; minute += 15) {
          const timeStr = `${String(hour).padStart(2, '0')}:${String(
            minute
          ).padStart(2, '0')}`
          options.push(timeStr)
        }
      }
      return options
    }, [])

    // 監聽開始時間
    const startTime = Form.useWatch('serviceStartTime', form)

    // 根據開始時間過濾結束時間選項（結束時間不能比開始時間早）
    const endTimeOptions = useMemo(() => {
      if (!startTime) {
        return timeOptions
      }
      return timeOptions.filter((time) => time > startTime)
    }, [startTime, timeOptions])

    // 設置日期預設值（預約結帳從預約獲取，現場結帳為當天）
    useEffect(() => {
      const currentDate = form.getFieldValue('serviceDate')
      if (!currentDate) {
        // 預約結帳模式：如果預約數據中有日期，使用預約日期；否則使用當天
        if (checkoutMode === 'booking' && bookingData?.date) {
          // 確保日期格式正確，使用 ISO 格式字符串
          const bookingDate = bookingData.date
          const dateValue =
            typeof bookingDate === 'string'
              ? dayjs(bookingDate)
              : dayjs(bookingDate)
          form.setFieldsValue({
            serviceDate: dateValue.isValid() ? dateValue : tzNow()
          })
        } else {
          // 現場結帳模式：使用當天
          form.setFieldsValue({
            serviceDate: tzNow()
          })
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkoutMode, bookingData])

    // 當開始時間改變時，如果結束時間比開始時間早，清空結束時間
    useEffect(() => {
      if (startTime) {
        const endTime = form.getFieldValue('serviceEndTime')
        if (endTime && endTime <= startTime) {
          form.setFieldsValue({ serviceEndTime: undefined })
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startTime])

    // 設置總付款金額為步驟三的總金額（服務 + 商品 - 折扣）
    // 注意：完全移除自動更新，改為只在套用折扣時手動更新，避免觸發表單驗證導致跳轉
    // useEffect 已移除，總金額更新完全由 handleApplyDiscount 控制
    // 總付款金額與上方總計一致：依當前所有項目即時計算，重新結帳載入時用 recheckoutLoadedTotal
    const mobileTotalDisplay =
      totalAmount > 0
        ? totalAmount
        : recheckoutLoadedTotal != null &&
            Number.isFinite(recheckoutLoadedTotal)
          ? recheckoutLoadedTotal
          : 0
    const mobileVoucher = Number(Form.useWatch('voucher', form) || 0)
    // 優惠券視同現金：應收不扣券，這裡算的是其餘付款方式需要收的金額
    const mobilePayableAfterVoucher = Math.max(
      0,
      mobileTotalDisplay - mobileVoucher
    )

    return (
      <>
        <FormCard
          title={
            checkoutMode === 'booking'
              ? '選擇日期和時間'
              : '步驟三 選擇日期和時間'
          }
        >
          <Form form={form} layout='vertical'>
            <Form.Item
              name='serviceDate'
              label='服務日期'
              rules={[{ required: true, message: '請選擇服務日期' }]}
              getValueFromEvent={(date) => {
                return date ? date.format('YYYY-MM-DD') : null
              }}
              getValueProps={(value) => {
                return {
                  value: value ? dayjs(value, 'YYYY-MM-DD') : null
                }
              }}
            >
              <DatePicker
                id='serviceDate'
                style={{ width: '100%' }}
                size='large'
                placeholder='選擇服務日期'
                format='YYYY-MM-DD'
                inputReadOnly
              />
            </Form.Item>

            <FormRow>
              <Form.Item
                name='serviceStartTime'
                label='服務開始時間'
                rules={[{ required: true, message: '請選擇開始時間' }]}
              >
                <MobileFriendlyTimeSelect
                  timeOptions={timeOptions}
                  placeholder='選擇開始時間'
                  selectHtmlId='serviceStartTimeMobileStep'
                />
              </Form.Item>

              <Form.Item
                name='serviceEndTime'
                label='服務結束時間'
                rules={[{ required: true, message: '請選擇結束時間' }]}
              >
                <MobileFriendlyTimeSelect
                  timeOptions={endTimeOptions.length > 0 ? endTimeOptions : []}
                  placeholder={
                    startTime ? '請選擇比開始時間晚的時間' : '請先選擇開始時間'
                  }
                  disabled={!startTime}
                  selectHtmlId='serviceEndTimeMobileStep'
                />
              </Form.Item>
            </FormRow>

            <Form.Item name='remarks' label='備註'>
              <div>
                <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>
                  快捷詞組 (編輯): 新增快捷詞組...
                </div>
                <TextArea id='remarks' rows={4} placeholder='請輸入備註' />
              </div>
            </Form.Item>
          </Form>
        </FormCard>

        <FormCard title='付費資訊'>
          <Form form={form} layout='vertical'>
            {/* 會員折扣顯示（僅顯示狀態，不可修改） */}
            {customerMembershipTier &&
              membershipDiscountRate < 100 &&
              (() => {
                // 檢查是否有服務應用了折扣
                const hasAppliedDiscount = selectedServices.some((s) => {
                  const discountedPrice = Math.round(
                    ((s.originalPrice || s.price || 0) *
                      membershipDiscountRate) /
                      100
                  )
                  return s.price === discountedPrice
                })
                return (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 16,
                      padding: '8px 12px',
                      background: hasAppliedDiscount ? '#f0f9ff' : '#fafafa',
                      borderRadius: 6,
                      border: `1px solid ${
                        hasAppliedDiscount ? '#bae6fd' : '#d9d9d9'
                      }`
                    }}
                  >
                    <span style={{ fontSize: 14, color: '#0369a1' }}>
                      會員折扣（{customerMembershipTier}{' '}
                      {membershipDiscountRate}
                      折）：
                    </span>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: hasAppliedDiscount ? '#0369a1' : '#8c8c8c'
                      }}
                    >
                      {hasAppliedDiscount ? '已套用' : '未套用'}
                    </span>
                  </div>
                )
              })()}

            <PaymentRow>
              <div>
                <Form.Item
                  name='storedValue'
                  label={
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <span>儲值金</span>
                      {loadingBalance ? (
                        <span style={{ fontSize: 12, color: '#999' }}>
                          載入中...
                        </span>
                      ) : selectedCustomerId ? (
                        customerBalance !== null && customerBalance > 0 ? (
                          <span style={{ fontSize: 12, color: '#666' }}>
                            ( 剩餘：NT$ {customerBalance.toLocaleString()} )
                          </span>
                        ) : (
                          <span style={{ fontSize: 12, color: '#999' }}>
                            ( 此客戶目前沒有儲值金。 )
                          </span>
                        )
                      ) : null}
                    </div>
                  }
                >
                  <PaymentInput>
                    <StoredValueInput id='storedValue' form={form} />
                    <Button icon={<PlusOutlined />} />
                    <Button icon={<ReloadOutlined />} />
                  </PaymentInput>
                </Form.Item>

                {customerBalanceInfo?.balanceByScheme?.length > 0 && (
                  <div
                    style={{
                      marginBottom: 12,
                      padding: '10px 12px',
                      background: '#f6ffed',
                      border: '1px solid #b7eb8f',
                      borderRadius: 8,
                      fontSize: 13
                    }}
                  >
                    <div
                      style={{
                        fontWeight: 500,
                        marginBottom: 6,
                        color: '#389e0d'
                      }}
                    >
                      各儲值活動餘額
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {customerBalanceInfo.balanceByScheme.map((s) => (
                        <li key={s.schemeKey} style={{ marginBottom: 4 }}>
                          {s.schemeKey} NT${' '}
                          {Number(s.balance || 0).toLocaleString()}
                          {(s.expiryDateDisplay || s.expiryDate) &&
                            `（${s.expiryDateDisplay || dayjs(s.expiryDate).format('YYYY/MM/DD')}）`}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <Form.Item
                  noStyle
                  shouldUpdate={(prev, cur) =>
                    prev.storedValue !== cur.storedValue
                  }
                >
                  {({ getFieldValue }) => {
                    const sv = Number(getFieldValue('storedValue') || 0)
                    const schemes = customerBalanceInfo?.balanceByScheme || []
                    if (sv <= 0 || schemes.length === 0) return null
                    return (
                      <Form.Item
                        name='storedValueSchemeKey'
                        label='從哪一儲值活動扣款'
                        style={{ marginBottom: 16 }}
                      >
                        <Radio.Group>
                          <Radio value=''>不指定儲值活動</Radio>
                          {schemes.map((s) => (
                            <Radio key={s.schemeKey} value={s.schemeKey}>
                              {s.schemeKey}（NT${' '}
                              {Number(s.balance || 0).toLocaleString()}）
                            </Radio>
                          ))}
                        </Radio.Group>
                      </Form.Item>
                    )
                  }}
                </Form.Item>

                {renderCheckoutCouponPicker()}
                <Form.Item name='voucher' label='優惠券'>
                  <PaymentInput>
                    <InputNumber
                      id='voucher'
                      style={{ width: '100%' }}
                      min={0}
                      prefix='NT$'
                      disabled={!!appliedStoreCouponId}
                    />
                  </PaymentInput>
                </Form.Item>

                <div style={{ marginBottom: 16 }}>
                  <div
                    style={{ marginBottom: 8, fontSize: 14, fontWeight: 500 }}
                  >
                    折扣率(%)
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 12,
                        color: '#999',
                        fontWeight: 400
                      }}
                    >
                      （輸入「折抵幾 %」，例如 10 = 打 9 折）
                    </span>
                  </div>
                  <div
                    style={{ display: 'flex', gap: 8, alignItems: 'center' }}
                  >
                    <InputNumber
                      id='discountPercentage'
                      style={{ flex: 1 }}
                      min={0}
                      max={100}
                      suffix='%'
                      value={tempDiscountPercentage}
                      onChange={(value) => {
                        const numValue = value || 0
                        const finalValue = numValue > 100 ? 100 : numValue
                        setTempDiscountPercentage(
                          finalValue < 0 ? 0 : finalValue
                        )
                      }}
                      placeholder='折抵百分比，例如 10'
                      onPressEnter={(e) => {
                        if (e && typeof e.preventDefault === 'function') {
                          e.preventDefault()
                        }
                        handleApplyDiscountPercentage()
                      }}
                      autoFocus={false}
                      onBlur={(e) => {
                        // 阻止失焦時觸發其他邏輯
                        e?.stopPropagation?.()
                      }}
                    />
                    <Button
                      type='primary'
                      htmlType='button'
                      onClick={(e) => {
                        if (e && typeof e.preventDefault === 'function') {
                          e.preventDefault()
                        }
                        handleApplyDiscountPercentage()
                      }}
                      disabled={
                        tempDiscountPercentage < 0 ||
                        tempDiscountPercentage > 100
                      }
                    >
                      套用
                    </Button>
                  </div>
                  {/* 折抵金額預覽（與桌機版、App 端一致） */}
                  {tempDiscountPercentage > 0 &&
                    tempDiscountPercentage <= 100 && (
                      <div
                        style={{ marginTop: 8, fontSize: 12, color: '#c2185b' }}
                      >
                        折抵 NT${' '}
                        {Math.round(
                          (subtotalAmount * tempDiscountPercentage) / 100
                        ).toLocaleString()}
                        {tempDiscountPercentage !== discountPercentage && (
                          <span style={{ color: '#999' }}>（尚未套用）</span>
                        )}
                      </div>
                    )}
                </div>
              </div>

              <div>
                <Form.Item name='cash' label='現金'>
                  <PaymentInput>
                    <CashInput id='cash' form={form} />
                    <Button icon={<ReloadOutlined />} />
                  </PaymentInput>
                </Form.Item>

                <Form.Item name='transfer' label='匯款'>
                  <PaymentInput>
                    <InputNumber
                      id='transfer'
                      style={{ width: '100%' }}
                      min={0}
                      prefix='NT$'
                    />
                    <Button icon={<ReloadOutlined />} />
                    <Button icon={<DeleteOutlined />} danger />
                  </PaymentInput>
                </Form.Item>

                <Form.Item name='deposit' label='定金'>
                  <PaymentInput>
                    <InputNumber
                      id='deposit'
                      style={{ width: '100%' }}
                      min={0}
                      prefix='NT$'
                    />
                    <Button icon={<DeleteOutlined />} danger />
                  </PaymentInput>
                </Form.Item>
              </div>
            </PaymentRow>

            {/* 移動端：將總付款金額放在單獨的容器中，確保在所有支付方式之後 */}
            <Form.Item
              name='totalPayment'
              label={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>總付款金額</span>
                  <span
                    style={{ fontWeight: 400, color: '#8c8c8c', fontSize: 13 }}
                  >
                    （＝應收金額，優惠券屬付款方式之一）
                  </span>
                  {customerMembershipTier && (
                    <Tag
                      color={
                        membershipTiers.find(
                          (t) => t.name === customerMembershipTier
                        )?.color || '#1890ff'
                      }
                      style={{ fontSize: 12, margin: 0 }}
                    >
                      {customerMembershipTier}
                    </Tag>
                  )}
                </div>
              }
            >
              <div
                style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: '#1a1a1a',
                  padding: '12px 0'
                }}
              >
                NT${' '}
                {(Number.isFinite(mobileTotalDisplay)
                  ? mobileTotalDisplay
                  : 0
                ).toLocaleString()}
              </div>
              {/* 檢查是否有服務應用了會員折扣 */}
              {(() => {
                const hasAppliedMembershipDiscount = selectedServices.some(
                  (s) => {
                    const discountedPrice = Math.round(
                      ((s.originalPrice || s.price || 0) *
                        membershipDiscountRate) /
                        100
                    )
                    return s.price === discountedPrice
                  }
                )
                if (
                  hasAppliedMembershipDiscount &&
                  customerMembershipTier &&
                  membershipDiscountRate < 100
                ) {
                  return (
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 11,
                        color: '#0369a1',
                        fontStyle: 'italic'
                      }}
                    >
                      * 已套用會員折扣（{customerMembershipTier}{' '}
                      {membershipDiscountRate}折），請注意避免重複套用折扣
                    </div>
                  )
                }
                return null
              })()}
              {(finalDiscountAmount > 0 || discountPercentage > 0) && (
                <div
                  style={{
                    marginTop: 8,
                    fontSize: 12,
                    color: '#666'
                  }}
                >
                  小計：NT${' '}
                  {(Number.isFinite(subtotalAmount)
                    ? subtotalAmount
                    : 0
                  ).toLocaleString()}
                  {discountPercentage > 0 && (
                    <span>
                      {' '}
                      - 折扣 {discountPercentage}%（折抵 NT${' '}
                      {(Number.isFinite(finalDiscountAmount)
                        ? finalDiscountAmount
                        : 0
                      ).toLocaleString()}
                      ）
                    </span>
                  )}
                  <span>
                    = 應收 NT${' '}
                    {(Number.isFinite(mobileTotalDisplay)
                      ? mobileTotalDisplay
                      : 0
                    ).toLocaleString()}
                  </span>
                </div>
              )}
              {mobileVoucher > 0 && (
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 12,
                    color: '#8c4a00'
                  }}
                >
                  其中優惠券折抵 NT$ {mobileVoucher.toLocaleString()}，其餘付款方式需收
                  NT${' '}
                  {(Number.isFinite(mobilePayableAfterVoucher)
                    ? mobilePayableAfterVoucher
                    : 0
                  ).toLocaleString()}
                </div>
              )}
            </Form.Item>
          </Form>
        </FormCard>
      </>
    )
  }

  // 處理返回結帳頁面
  const handleBackToCheckout = () => {
    setOrderSuccess(false)
    setOrderData(null)
    // 重置購買同意書提醒狀態，避免下一筆訂單誤彈
    setCheckoutConsent({
      open: false,
      subjectRef: null,
      autoSent: false,
      classTicketTemplateIds: '',
      variables: {}
    })
    form.resetFields()
    setSelectedServices([])
    setSelectedProducts([])
    setPaymentMethods({
      storedValue: 0,
      cash: 0,
      transfer: 0,
      deposit: 0,
      cardPayment: 0,
      mobilePayment: 0,
      paymentFee: 0
    })
    paymentFeeOverriddenRef.current = false
    setSelectedCustomerId(null)
    setCustomerBalance(null)
    setCustomerNameValue('')
    setCurrentStep(0)
    // 預約結帳完成後回到預約管理頁面
    if (checkoutMode === 'booking') {
      navigate('/booking-detail')
    }
  }

  // 成功頁面組件
  const OrderSuccessPage = () => {
    if (!orderData) return null

    const {
      orderId,
      customer,
      services: orderServices,
      products: orderProducts = [],
      amounts,
      time,
      remarks,
      payment,
      createdAt
    } = orderData
    // 計算「客戶已付金額」
    // ⚠ 只加總真正由客戶支付的欄位，避免把下列欄位誤算為已付：
    //   - receivable：是應收總額，本來就在 payment 物件裡，但不是付款
    //   - paymentFee：店家支付給收單行/第三方支付的手續費，是店家支出，不是客戶付款
    //   - cardFeeRate / mobilePaymentFeeRate：是費率（%），不是金額
    //   - storeCouponId / userCouponId / discount / storedValueSchemeKey：非金額欄位
    const PAID_FIELDS = [
      'storedValue',
      'voucher',
      'cash',
      'transfer',
      'deposit',
      'cardPayment',
      'mobilePayment',
      'bonus',
      'debt',
      'installment'
    ]
    const totalPaid = PAID_FIELDS.reduce(
      (sum, key) => sum + (Number(payment?.[key]) || 0),
      0
    )

    return (
      <PageContainer>
        <FormCard
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <CheckOutlined style={{ color: '#C1A268', fontSize: 24 }} />
              <span>訂單建立成功</span>
            </div>
          }
        >
          <div style={{ padding: '24px 0' }}>
            {/* 訂單編號 */}
            <div style={{ marginBottom: 24, textAlign: 'center' }}>
              <div style={{ fontSize: 14, color: '#666', marginBottom: 8 }}>
                訂單編號
              </div>
              <div
                style={{ fontSize: 20, fontWeight: 'bold', color: '#1890ff' }}
              >
                {orderId}
              </div>
              <div style={{ fontSize: 12, color: '#999', marginTop: 8 }}>
                {formatTz(createdAt, 'YYYY-MM-DD HH:mm:ss')}
              </div>
            </div>

            {/* 客戶資訊 */}
            <div style={{ marginBottom: 24 }}>
              <div
                style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 12 }}
              >
                客戶資訊
              </div>
              <div
                style={{ background: '#f5f5f5', padding: 16, borderRadius: 6 }}
              >
                <div style={{ marginBottom: 8 }}>
                  <span style={{ color: '#666', marginRight: 8 }}>姓名：</span>
                  <span style={{ fontWeight: 'bold' }}>{customer.name}</span>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <span style={{ color: '#666', marginRight: 8 }}>電話：</span>
                  <span style={{ fontWeight: 'bold' }}>{customer.phone}</span>
                </div>
                {customerMembershipTier && (
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ color: '#666', marginRight: 8 }}>
                      會員等級：
                    </span>
                    <Tag
                      color={
                        membershipTiers.find(
                          (t) => t.name === customerMembershipTier
                        )?.color || '#1890ff'
                      }
                      style={{ fontWeight: 'bold' }}
                    >
                      {customerMembershipTier}
                    </Tag>
                  </div>
                )}
                {customer.servicePersonnel && (
                  <div>
                    <span style={{ color: '#666', marginRight: 8 }}>
                      主要服務人員：
                    </span>
                    <span style={{ fontWeight: 'bold' }}>
                      {employees.find(
                        (e) => e._id === customer.servicePersonnel
                      )?.name || customer.servicePersonnel}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* 服務明細 */}
            {orderServices && orderServices.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div
                  style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 12 }}
                >
                  服務明細
                </div>
                <div
                  style={{
                    background: '#f5f5f5',
                    padding: 16,
                    borderRadius: 6
                  }}
                >
                  {(() => {
                    // 處理第一劑/第二劑：將相同 serviceId 的第一劑和第二劑合併
                    const processedServices = []
                    const serviceMap = new Map()

                    orderServices.forEach((service) => {
                      // 檢查是否為第一劑/第二劑模式
                      const isDoseType =
                        service.doseType ||
                        service.firstDosePersonnelId ||
                        service.secondDosePersonnelId ||
                        service.supportDoseTypes

                      if (isDoseType && service.serviceId) {
                        // 第一劑/第二劑模式：按 serviceId 分組
                        const key = service.serviceId
                        if (!serviceMap.has(key)) {
                          serviceMap.set(key, {
                            serviceId: service.serviceId,
                            categoryId: service.categoryId,
                            name: null,
                            price: service.price,
                            originalPrice: service.originalPrice,
                            firstDosePersonnelId: null,
                            firstDosePersonnelName: null,
                            secondDosePersonnelId: null,
                            secondDosePersonnelName: null,
                            supportDoseTypes: true
                          })
                        }

                        const grouped = serviceMap.get(key)

                        // 根據 doseType 或 firstDosePersonnelId/secondDosePersonnelId 設置信息
                        if (
                          service.doseType === 'first' ||
                          service.firstDosePersonnelId
                        ) {
                          grouped.firstDosePersonnelId =
                            service.firstDosePersonnelId || null
                          grouped.firstDosePersonnelName =
                            service.firstDosePersonnelName || null
                        } else if (
                          service.doseType === 'second' ||
                          service.secondDosePersonnelId
                        ) {
                          grouped.secondDosePersonnelId =
                            service.secondDosePersonnelId || null
                          grouped.secondDosePersonnelName =
                            service.secondDosePersonnelName || null
                        }

                        // 如果價格相同，使用第一個的價格
                        if (!grouped.price && service.price) {
                          grouped.price = service.price
                        }
                        if (!grouped.originalPrice && service.originalPrice) {
                          grouped.originalPrice = service.originalPrice
                        }
                      } else {
                        // 一般抽成模式：直接添加
                        processedServices.push(service)
                      }
                    })

                    // 將合併後的第一劑/第二劑服務添加到列表
                    serviceMap.forEach((grouped) => {
                      processedServices.push(grouped)
                    })

                    return processedServices.map((service, index) => {
                      const serviceInfo = getServiceOrAdditionalById(service.serviceId)
                      const categoryInfo = categories.find(
                        (c) => c._id === service.categoryId || c._id === serviceInfo?.category
                      )

                      // 檢查是否為第一劑/第二劑模式
                      const isDoseType =
                        service.firstDosePersonnelId ||
                        service.secondDosePersonnelId ||
                        service.supportDoseTypes

                      if (isDoseType) {
                        // 第一劑/第二劑模式顯示
                        return (
                          <div
                            key={`${service.serviceId || index}-${index}`}
                            style={{
                              padding: 12,
                              borderBottom:
                                index < processedServices.length - 1
                                  ? '1px solid #e8e8e8'
                                  : 'none',
                              marginBottom:
                                index < processedServices.length - 1 ? 12 : 0
                            }}
                          >
                            <div style={{ marginBottom: 8 }}>
                              <span
                                style={{ fontWeight: 'bold', fontSize: 15 }}
                              >
                                {serviceInfo?.name || '未知服務'}
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: 13,
                                color: '#666',
                                marginBottom: 4
                              }}
                            >
                              類別：{categoryInfo?.name || '未知類別'}
                            </div>
                            {(service.firstDosePersonnelName ||
                              service.secondDosePersonnelName) && (
                              <div
                                style={{
                                  fontSize: 13,
                                  color: '#666',
                                  marginBottom: 4,
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 12
                                }}
                              >
                                {service.firstDosePersonnelName && (
                                  <span>
                                    第一劑操作人員：
                                    {service.firstDosePersonnelName}
                                  </span>
                                )}
                                {service.secondDosePersonnelName && (
                                  <span>
                                    第二劑操作人員：
                                    {service.secondDosePersonnelName}
                                  </span>
                                )}
                              </div>
                            )}
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 'bold',
                                color: '#1890ff'
                              }}
                            >
                              NT$ {Number(service.price).toLocaleString()}
                            </div>
                          </div>
                        )
                      } else {
                        // 一般抽成模式顯示
                        const designerInfo = employees.find(
                          (e) =>
                            e._id ===
                            (service.designerId || service.personnelId)
                        )
                        const assistantInfo = employees.find(
                          (e) => e._id === service.assistantId
                        )
                        const designerName =
                          service.designerName ||
                          service.personnelName ||
                          designerInfo?.name ||
                          null
                        const assistantName =
                          service.assistantName || assistantInfo?.name || null

                        return (
                          <div
                            key={`${service.serviceId || index}-${index}`}
                            style={{
                              padding: 12,
                              borderBottom:
                                index < processedServices.length - 1
                                  ? '1px solid #e8e8e8'
                                  : 'none',
                              marginBottom:
                                index < processedServices.length - 1 ? 12 : 0
                            }}
                          >
                            <div style={{ marginBottom: 8 }}>
                              <span
                                style={{ fontWeight: 'bold', fontSize: 15 }}
                              >
                                {serviceInfo?.name || '未知服務'}
                              </span>
                              {service.assist && (
                                <span
                                  style={{
                                    marginLeft: 8,
                                    padding: '2px 8px',
                                    background: '#e6f7ff',
                                    color: '#1890ff',
                                    borderRadius: 4,
                                    fontSize: 12
                                  }}
                                >
                                  協助
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                fontSize: 13,
                                color: '#666',
                                marginBottom: 4
                              }}
                            >
                              類別：{categoryInfo?.name || '未知類別'}
                            </div>
                            {(designerName || assistantName) && (
                              <div
                                style={{
                                  fontSize: 13,
                                  color: '#666',
                                  marginBottom: 4,
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 12
                                }}
                              >
                                {designerName && (
                                  <span>設計師：{designerName}</span>
                                )}
                                {assistantName && (
                                  <span>助理：{assistantName}</span>
                                )}
                              </div>
                            )}
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 'bold',
                                color: '#1890ff'
                              }}
                            >
                              NT$ {Number(service.price).toLocaleString()}
                            </div>
                          </div>
                        )
                      }
                    })
                  })()}
                </div>
              </div>
            )}

            {/* 商品明細 */}
            {orderProducts && orderProducts.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div
                  style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 12 }}
                >
                  商品明細
                </div>
                <div
                  style={{
                    background: '#f5f5f5',
                    padding: 16,
                    borderRadius: 6
                  }}
                >
                  {orderProducts.map((product, index) => {
                    const productInfo = inventoryProducts.find(
                      (p) =>
                        p._id === product.productId ||
                        p.id === product.productId
                    )
                    const categoryInfo = inventoryCategories.find(
                      (c) =>
                        c._id === product.categoryId ||
                        c._id === productInfo?.categoryId
                    )
                    return (
                      <div
                        key={index}
                        style={{
                          padding: 12,
                          borderBottom:
                            index < orderProducts.length - 1
                              ? '1px solid #e8e8e8'
                              : 'none',
                          marginBottom:
                            index < orderProducts.length - 1 ? 12 : 0
                        }}
                      >
                        <div style={{ marginBottom: 8 }}>
                          <span style={{ fontWeight: 'bold', fontSize: 15 }}>
                            {product.name || productInfo?.name || '未知商品'}
                          </span>
                        </div>
                        <div
                          style={{
                            fontSize: 13,
                            color: '#666',
                            marginBottom: 4
                          }}
                        >
                          分類：
                          {categoryInfo?.name ||
                            product.categoryName ||
                            '未知分類'}
                        </div>
                        {product.manufacturer && (
                          <div
                            style={{
                              fontSize: 13,
                              color: '#666',
                              marginBottom: 4
                            }}
                          >
                            廠商：{product.manufacturer}
                          </div>
                        )}
                        <div
                          style={{
                            fontSize: 13,
                            color: '#666',
                            marginBottom: 4
                          }}
                        >
                          數量：{product.quantity} {product.unit || '件'}
                        </div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 'bold',
                            color: '#1890ff'
                          }}
                        >
                          NT${' '}
                          {Number(
                            product.totalPrice ||
                              product.unitPrice * product.quantity
                          ).toLocaleString()}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* 時間資訊 */}
            {time && (time.date || time.startTime || time.endTime) && (
              <div style={{ marginBottom: 24 }}>
                <div
                  style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 12 }}
                >
                  服務時間
                </div>
                <div
                  style={{
                    background: '#f5f5f5',
                    padding: 16,
                    borderRadius: 6
                  }}
                >
                  {time.date && (
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ color: '#666', marginRight: 8 }}>
                        日期：
                      </span>
                      <span style={{ fontWeight: 'bold' }}>{time.date}</span>
                    </div>
                  )}
                  {(time.startTime || time.endTime) && (
                    <div>
                      <span style={{ color: '#666', marginRight: 8 }}>
                        時段：
                      </span>
                      <span style={{ fontWeight: 'bold' }}>
                        {time.startTime || '--'} - {time.endTime || '--'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 備註 */}
            {remarks && (
              <div style={{ marginBottom: 24 }}>
                <div
                  style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 12 }}
                >
                  備註
                </div>
                <div
                  style={{
                    background: '#f5f5f5',
                    padding: 16,
                    borderRadius: 6
                  }}
                >
                  {remarks}
                </div>
              </div>
            )}

            {/* 金額明細 */}
            <div style={{ marginBottom: 24 }}>
              <div
                style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 12 }}
              >
                金額明細
              </div>
              <div
                style={{ background: '#f5f5f5', padding: 16, borderRadius: 6 }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 12,
                    paddingBottom: 12,
                    borderBottom: '1px solid #e8e8e8'
                  }}
                >
                  <span style={{ color: '#666' }}>服務小計：</span>
                  <span style={{ fontWeight: 'bold' }}>
                    NT$ {Number(amounts.servicesAmount || 0).toLocaleString()}
                  </span>
                </div>
                {amounts.productsAmount > 0 && (
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 12,
                      paddingBottom: 12,
                      borderBottom: '1px solid #e8e8e8'
                    }}
                  >
                    <span style={{ color: '#666' }}>商品小計：</span>
                    <span style={{ fontWeight: 'bold' }}>
                      NT$ {Number(amounts.productsAmount || 0).toLocaleString()}
                    </span>
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 12,
                    paddingBottom: 12,
                    borderBottom: '1px solid #e8e8e8'
                  }}
                >
                  <span style={{ color: '#666' }}>應收金額：</span>
                  <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
                    NT$ {Number(amounts.receivable || 0).toLocaleString()}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: 12,
                    paddingBottom: 12,
                    borderBottom: '1px solid #e8e8e8'
                  }}
                >
                  <span style={{ color: '#666' }}>已付金額：</span>
                  <span style={{ fontWeight: 'bold', color: '#C1A268' }}>
                    NT$ {Number(totalPaid).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* 付款方式 */}
            {totalPaid > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div
                  style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 12 }}
                >
                  付款方式
                </div>
                <div
                  style={{
                    background: '#f5f5f5',
                    padding: 16,
                    borderRadius: 6
                  }}
                >
                  {payment.cash > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ color: '#666', marginRight: 8 }}>
                        現金：
                      </span>
                      <span style={{ fontWeight: 'bold' }}>
                        NT$ {Number(payment.cash).toLocaleString()}
                      </span>
                    </div>
                  )}
                  {payment.storedValue > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ color: '#666', marginRight: 8 }}>
                        儲值金：
                      </span>
                      <span style={{ fontWeight: 'bold' }}>
                        NT$ {Number(payment.storedValue).toLocaleString()}
                      </span>
                    </div>
                  )}
                  {payment.voucher > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ color: '#666', marginRight: 8 }}>
                        優惠券：
                      </span>
                      <span style={{ fontWeight: 'bold' }}>
                        NT$ {Number(payment.voucher).toLocaleString()}
                      </span>
                    </div>
                  )}
                  {payment.transfer > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ color: '#666', marginRight: 8 }}>
                        匯款：
                      </span>
                      <span style={{ fontWeight: 'bold' }}>
                        NT$ {Number(payment.transfer).toLocaleString()}
                      </span>
                    </div>
                  )}
                  {payment.deposit > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ color: '#666', marginRight: 8 }}>
                        定金：
                      </span>
                      <span style={{ fontWeight: 'bold' }}>
                        NT$ {Number(payment.deposit).toLocaleString()}
                      </span>
                    </div>
                  )}
                  {payment.cardPayment > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ color: '#666', marginRight: 8 }}>
                        刷卡：
                      </span>
                      <span style={{ fontWeight: 'bold' }}>
                        NT$ {Number(payment.cardPayment).toLocaleString()}
                      </span>
                    </div>
                  )}
                  {payment.mobilePayment > 0 && (
                    <div style={{ marginBottom: 8 }}>
                      <span style={{ color: '#666', marginRight: 8 }}>
                        行動支付：
                      </span>
                      <span style={{ fontWeight: 'bold' }}>
                        NT$ {Number(payment.mobilePayment).toLocaleString()}
                      </span>
                    </div>
                  )}
                  {/* 客戶已付總額（以上付款方式加總） */}
                  <div
                    style={{
                      marginTop: 12,
                      paddingTop: 12,
                      borderTop: '1px dashed #d9d9d9',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <span style={{ color: '#666' }}>客戶已付總額：</span>
                    <span
                      style={{
                        fontWeight: 'bold',
                        fontSize: 16,
                        color: '#1890ff'
                      }}
                    >
                      NT$ {Number(totalPaid).toLocaleString()}
                    </span>
                  </div>
                  {/* 金流手續費 + 實際收入（店家真正到手的金額） */}
                  {payment.paymentFee > 0 && (
                    <>
                      <div
                        style={{
                          marginTop: 10,
                          padding: '8px 10px',
                          background: '#fff7e6',
                          border: '1px solid #ffd591',
                          borderRadius: 4,
                          fontSize: 13,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          color: '#ad6800'
                        }}
                      >
                        <span>
                          金流手續費（店家支出，不計入客戶付款）：
                        </span>
                        <span style={{ fontWeight: 'bold' }}>
                          − NT$ {Number(payment.paymentFee).toLocaleString()}
                        </span>
                      </div>
                      {/* 實際收入 = 客戶已付總額 − 金流手續費 */}
                      <div
                        style={{
                          marginTop: 8,
                          padding: '10px 12px',
                          background: '#f6ffed',
                          border: '1px solid #b7eb8f',
                          borderRadius: 4,
                          fontSize: 14,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          color: '#389e0d'
                        }}
                      >
                        <span>
                          實際收入（扣除手續費後實際入帳）：
                        </span>
                        <span style={{ fontWeight: 'bold', fontSize: 16 }}>
                          NT${' '}
                          {(
                            Number(totalPaid) - Number(payment.paymentFee)
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2
                          })}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* 操作按鈕 */}
            <div style={{ textAlign: 'center', marginTop: 32 }}>
              <Button
                type='primary'
                size='large'
                onClick={handleBackToCheckout}
                style={{ minWidth: 200 }}
              >
                繼續結帳
              </Button>
            </div>
          </div>
        </FormCard>
      </PageContainer>
    )
  }

  // 如果訂單成功，顯示成功頁面
  // 注意：購買同意書提醒 Modal 必須渲染在此分支內，
  // 否則結帳成功後整個步驟區塊（含 Modal）會被卸載而永遠不會顯示
  if (orderSuccess && orderData) {
    return (
      <>
        <OrderSuccessPage />
        {/* 購買同意書提醒（結帳完成後，含堂票購買時；後端已自動發送則僅供現場補簽） */}
        <PurchaseConsentModal
          open={checkoutConsent.open}
          reminderMode
          autoSent={checkoutConsent.autoSent}
          onClose={() => setCheckoutConsent((c) => ({ ...c, open: false }))}
          onSkip={() => setCheckoutConsent((c) => ({ ...c, open: false }))}
          onNoForm={() => setCheckoutConsent((c) => ({ ...c, open: false }))}
          onSigned={() => setCheckoutConsent((c) => ({ ...c, open: false }))}
          onSentRemote={() => setCheckoutConsent((c) => ({ ...c, open: false }))}
          businessId={user?.businessId}
          customerId={selectedCustomerId}
          customerName={checkoutConsent.variables?.customerName}
          target='classTicket'
          classTicketTemplateId={checkoutConsent.classTicketTemplateIds}
          subjectRef={checkoutConsent.subjectRef}
          variables={checkoutConsent.variables}
        />
      </>
    )
  }

  // 尚未就緒時顯示全頁 loading，避免進入結帳頁時多次重繪造成閃爍
  if (!pageReady) {
    return (
      <>
        <CheckoutGlobalStyle />
        <CheckoutPageLoadingWrap>
          <Spin size='large' tip='載入結帳頁面中...' />
        </CheckoutPageLoadingWrap>
      </>
    )
  }

  return (
    <>
      <CheckoutGlobalStyle />
      <PageContainer>
        <PageTitle>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap'
            }}
          >
            {(loadingBooking || loadingOrder || loadingDraft) && (
              <span style={{ fontSize: 14, color: '#666', fontWeight: 400 }}>
                {loadingDraft
                  ? '載入草稿中...'
                  : loadingOrder
                    ? '載入訂單中...'
                    : '載入預約信息中...'}
              </span>
            )}
          </div>
        </PageTitle>

        <CheckoutContentContainer>
          {/* 預約結帳模式：顯示預約信息卡片 */}
          {checkoutMode === 'booking' && renderBookingInfo()}
          <div>{renderStepContent()}</div>
          <ButtonGroup>
            <Button
              size='large'
              style={{ flex: 1 }}
              onClick={handleSaveDraft}
              loading={savingDraft}
            >
              儲存草稿
            </Button>
            <Button
              type='primary'
              size='large'
              style={{ flex: 1 }}
              icon={<CheckOutlined />}
              onClick={handleSubmit}
              loading={loading}
            >
              完成訂單
            </Button>
          </ButtonGroup>
        </CheckoutContentContainer>

        {/* 新增客戶 Modal */}
        <Modal
          title='新增客戶'
          open={addCustomerModalVisible}
          onCancel={() => {
            setAddCustomerModalVisible(false)
            addCustomerForm.resetFields()
          }}
          onOk={() => addCustomerForm.submit()}
          okText='新增'
          cancelText='取消'
          confirmLoading={addCustomerLoading}
          destroyOnClose
          maskClosable={false}
          width={500}
        >
          <Form
            form={addCustomerForm}
            layout='vertical'
            onFinish={handleAddCustomer}
          >
            <Form.Item
              name='name'
              label='姓名'
              rules={[{ required: true, message: '請輸入姓名' }]}
            >
              <Input placeholder='請輸入顧客姓名' />
            </Form.Item>

            <Form.Item
              name='phone'
              label='電話'
              rules={[
                {
                  validator: (_, value) => {
                    // 如果沒有輸入，則通過驗證（選填）
                    if (!value || value.trim() === '') {
                      return Promise.resolve()
                    }
                    // 如果有輸入，則驗證格式
                    if (/^09\d{8}$/.test(value)) {
                      return Promise.resolve()
                    }
                    return Promise.reject(
                      new Error('請輸入正確的手機號碼（09開頭的10位數字）')
                    )
                  }
                }
              ]}
            >
              <Input placeholder='請輸入顧客電話(09開頭的10位數字)' />
            </Form.Item>

            <Form.Item
              name='email'
              label='電子郵件'
              rules={[{ type: 'email', message: '請輸入有效的電子郵件' }]}
            >
              <Input placeholder='請輸入電子郵件' />
            </Form.Item>

            <Form.Item name='gender' label='性別'>
              <Radio.Group>
                <Radio value='male'>男</Radio>
                <Radio value='female'>女</Radio>
              </Radio.Group>
            </Form.Item>

            <Form.Item name='birthdate' label='生日'>
              <DatePicker style={{ width: '100%' }} format='YYYY-MM-DD' />
            </Form.Item>

            <Form.Item name='notes' label='備註'>
              <Input.TextArea rows={4} placeholder='請輸入備註' />
            </Form.Item>
          </Form>
        </Modal>
      </PageContainer>
    </>
  )
}

export default CheckoutPage
